import axios from 'axios';
import type { CustomerSignedCopyResponse } from '../types/ticket-signed-copy';
import type { BriefingTodayResponse } from '../types/morning-brief';
import type { CallSheetSmsResponse } from '../types/call-sheet';
import type { DashboardLayout, DashboardLayoutWrite, TicketPipeline } from '../types/dashboard-layout';
import type { ActionQueue } from '../types/action-queue';
import type { EquipmentStatusRead, PipelineRead } from '../types/overview-ops';
import type { PtJobs, ToolsInHole, WellsOnLocation, WorkOrders, WsJobs, WtJobs } from '../types/overview-operations';
import type { ActivityFeed } from '../types/overview-activity';
import type { OverviewBandRead } from '../types/overview-band';
import type { ApprovedVsInvoiced, Receivables, RevenuePulse, UnbilledWork } from '../types/overview-money';
import type { PayPeriodReopenResponse } from '../types/pay-period-reopen';
import type { PayrollAdjustmentUpdateResponse } from '../types/payroll-adjustment';
import type { UnifiedInvoiceRow } from '../types/unified-invoice';
import type { LinkBody as PriceBookLinkBody } from '../lib/price-book-missing';
import type { PriceBookLinkResponse } from '../types/price-book-link';
import type { CustomerContactMergeResponse } from '../types/customer-contact-merge';
import type { TimesheetAdminEditBody, TimesheetAdminEditResponse } from '../types/timesheet-admin-edit';
import type { VerticalCatalogResponse } from '../types/verticals';
import type {
  QboMappingListResponse, QboNeedsMappingResponse, QboCatalogResponse,
  QboCustomerCatalogResponse, QboDiscountAccountResponse, QboMappingSaveResponse,
  QboRateSheetItemsResponse, QboBulkMappingSaveResponse,
  CustomerUsage, LineItemTypeUsage, TaxLabelUsage,
  QboItemSummary, QboTaxCodeSummary,
  QboReadinessResponse, QboPushResponse,
} from '../types/qbo-mapping';
import { storage } from '../lib/storage';
import type { WorkLocation } from '../constants/work-locations';
import type { JobPayExemption } from '../constants/job-pay-exemption';
import { isNative } from '../lib/platform';
import type {
  EquipmentDocumentPayload,
  MaintenanceSchedulePayload,
  MarkPerformedPayload,
  BulkSchedulePayload,
} from '../types/equipment';
import type { ServiceTypePayload } from '../types/service-type';
import type { MaintenanceFlag, MaintenanceFlagsResponse } from '../types/maintenance-flags';
import type { TimeOffOverlapsResponse } from '../types/time-off-overlap';
import type { EquipmentTypePayload } from '../types/equipment-type';
import type { PreuseForEquipment, PreuseItemWrite, PreuseStarter, PreuseTemplate, PreuseTemplateDetail } from '../types/preuse';
import type {
  PcCertification, PcCertificationWrite, PcComponent, PcComponentDetail, PcComponentWrite, PcEventWrite, PcMeta,
  PcEnvelope, PcServiceEvent, PcSettings, PcSettingsWrite,
  PcCalibration, PcCalibrationWrite, PcDrill, PcDrillSettings, PcDrillStatus, PcDrillWrite, PcExtension, PcExtensionWrite,
  PcGaugeCalibration, PcModuleStatus, PcTest, PcTestDefault, PcTestDefaultWrite, PcTestWrite,
  PcClassRule, PcClassRuleRow, PcClassRuleWrite,
} from '../types/pressure-control';
import type { RadDoseWrite, RadEventWrite, RadLeakTestWrite, RadSettingsWrite, RadSourceWrite } from '../types/radiation';
import type { ApprovePreuseResult } from '../lib/preuse-extraction';
import type { WellInfoFillBody, WellInfoFillResponse } from '../types/well-info-fill';
import type { CasingCorrectionBody, CasingCorrectionResponse } from '../types/casing-string-correction';
import type { CertDaysImportRequest } from '../types/cert-day-import';
import type { LoadCreateBody, LoadUpdateResponse } from '../types/load';
import type { TruckingRateResponse, TruckingRateWrite, TruckingRatesListResponse } from '../types/trucking-rates';
import type { ApiEnvelope } from '../lib/api-envelope';
import type {
  MaintenanceAgentSettings,
  MaintenanceAgentSettingsSaved,
  ResolvedAssetPolicy,
  ServicePolicy,
} from '../types/service-policies';
import type { MaintenanceAgentMetrics, MaintenanceShadowReport } from '../types/maintenance-agent';
import type { PositionDeleteResponse, PositionWire, UserPositionsSet } from '../types/position';
import type { ClockRoundingSetting } from '../types/clock-rounding';
import type { PayrollSummaryReportData } from '../types/payroll-summary-report';
import type { ArAgingReportData } from '../types/ar-aging-report';
import type { TaxRateWrite, StandardTaxRate } from '../lib/company-tax-rates';
import type {
  DispatchJobsResponse,
  DispatchPhaseClockResponse,
  FieldPhaseEndResponse,
  DispatchFiltersResponse,
  EquipmentAlertsResponse,
  EquipmentOutResponse,
  TorqueSheetsResponse,
} from '../types/dispatch';
import type { DocumentSendResponse, TicketResendResponse } from '../types/document-send';
import type { QuotePricingSourcesResponse, QuotePricingItemsResponse, QuotePricingLine, CreateRentalOrderResponse, AcceptQuoteResponse, CreateQuoteJobResponse, CreatePtJobResponse, CreateNdtJobResponse, CreatePfJobResponse, CreateWtJobResponse, CreateWsJobResponse, QuoteCreatedRecordsResponse, QuoteToolTermRow } from '../types/quote-pricing';
import type { MoveJobResponse } from '../types/ticket-move-job';
import type {
  BillingFlag,
  BillingPreflightMetrics,
  BillingPreflightMode,
  BillingPreflightSettings,
  BillingShadowReport,
  TicketBillingFlags,
} from '../types/billing-flags';
import type { BulkReturnResponse } from '../types/equipment-bulk-return';
import type { EquipmentHistoryWire } from '../types/equipment-history';
import type { CertRemovalResponse } from '../types/cert-adjustment-removal';
import type {
  DhAssembly,
  DhAssemblyDetail,
  DhAssemblyWrite,
  DhComponent,
  DhComponentAlert,
  DhComponentDetail,
  DhComponentImportResult,
  DhComponentQr,
  DhComponentWrite,
  DhDashboard,
  DhGenerateInvoiceWrite,
  DhInvoice,
  DhInvoiceDetail,
  DhInvoiceSent,
  DhReport,
  DhDisassembleWrite,
  DhDisassemblyResult,
  DhRate,
  DhRateWrite,
  DhRun,
  DhRunPrice,
  DhRunWrite,
  DhWorkOrderWrite,
  DhWorkOrder,
  DhTemplate,
  DhTemplateDetail,
  DhTemplateWrite,
  DownholeEnvelope,
  DhTicket,
  DhTicketDetail,
  DhTicketRecipientPreview,
  DhTicketWrite,
  DhInvoiceRecipientPreview,
} from '../types/downhole';
import type {
  PtCalibration,
  PtCalibrationCreate,
  PtCertificate,
  PtCertificateListRow,
  PtEnvelope,
  PtInstrument,
  PtInstrumentCreate,
  PtInstrumentDetail,
  PtJob,
  PtJobCreate,
  PtJobDetail,
  PtJobPatch,
  PtSettings,
  PtSettingsUpdate,
  PtSignatures,
  PtSignatureWrite,
  PtSpecsPatch,
  PtTest,
  PtTestType,
  PtTestTypeCreate,
  PtTestWrite,
  PtConsumable,
  PtCustomerSettings,
  PtLineCreate,
  PtLinePatch,
  PtRate,
  PtRateCreate,
  PtRatePatch,
  PtTicket,
  PtTicketDetail,
  PtTicketMode,
  PtTicketSign,
  PtBillableJob,
  PtInvoice,
  PtInvoiceGenerate,
  PtInvoiceListRow,
  PtInvoiceRecipient,
  PtInvoiceSent,
} from '../types/pressure-testing';
import type {
  NdtCalibration, NdtCalibrationCreate, NdtCatalog, NdtCertWrite, NdtDecay, NdtDose, NdtDoseWrite,
  NdtEnvelope, NdtInstrument, NdtInstrumentDetail, NdtQualWrite, NdtSettings, NdtSettingsUpdate, NdtSource,
  NdtSourceDetail, NdtSourceLog, NdtSourceLogCreate, NdtSourceWrite, NdtSpecsWrite, NdtTechnicianCert,
  NdtWelder, NdtWelderQual, NdtWelderWrite, NdtCompliance,
  NdtJob, NdtJobDetail, NdtJobWrite, NdtCrew, NdtCrewWrite, NdtWeld, NdtWeldDetail, NdtWeldWrite, NdtExam,
  NdtExamWrite, NdtIndication, NdtIndicationWrite, NdtRepairRates, NdtShotWarning, NdtFieldJob, NdtFieldJobDetail,
  NdtFieldWeld, NdtFieldExam, NdtReport, NdtReportCreate, NdtReportUpdate, NdtReportWarning,
} from '../types/ndt-inspection';
import type {
  NdtFieldTicket,
  NdtFieldTicketDetail,
  NdtLineCreate,
  NdtLineUpdate,
  NdtRate,
  NdtTicket,
  NdtTicketDetail,
  NdtTicketHeaderUpdate,
  NdtTicketSign,
} from '../types/ndt-tickets';
import type { NdtBillableJob, NdtInvoice, NdtInvoiceEnvelope, NdtInvoiceGenerate, NdtInvoiceRecipient, NdtInvoiceRow } from '../types/ndt-invoices';
import type { NdtAiDigest, NdtAiRunResult, NdtAiWindow } from '../types/ndt-ai';
import type {
  EwCatalog, EwCustomerSettings, EwCustomerSettingsUpdate, EwEnvelope, EwNumbering, EwNumberingUpdate, EwSettings, EwSettingsUpdate, EwSite,
  EwSiteDeleted, EwSiteDetail, EwSiteWrite, EwJob, EwJobDetail, EwJobWrite, EwCrew, EwCrewWrite, EwJobEquipment,
  EwJobEquipmentWrite, EwRate, EwRateWrite,
  EwQuoteState, EwQuoteDeposit, EwDepositWrite, EwDepositReceivedWrite, CreateEwJobResponse,
  EwDailyLog, EwDailyLogSummary, EwDailyLogWrite, EwHazmat, EwHazmatWrite, EwLocate, EwLocateWrite, EwPermit,
  EwPermitWrite, EwPreUseCheck, EwRentIn, EwRentInWrite, EwWarning, EwWarningAck,
  EwFieldJob, EwFieldJobDetail, EwFieldLog, EwTruckBilling, EwTruckBillingBasis, EwEmployeeHours,
} from '../types/earthworks';
import type {
  EwBillableGroup,
  EwEnvelopeOf,
  EwFromChangeOrderWrite,
  EwFromQuoteWrite,
  EwFromTicketsWrite,
  EwHoldback,
  EwQuoteDraws,
  EwPayLink,
  EwQboPushResult,
  EwQboMappingNeeds,
  EwInvoice,
  EwInvoiceDraftWrite,
  EwInvoiceFilters,
  EwInvoiceRow,
  EwPaymentWrite,
  EwReleaseHoldbackWrite,
} from '../types/ew-invoices';
import type {
  EwTicketDetail, EwTicketList, EwTicketBuilt, EwTicketFilters, EwTicketLineWrite, EwPaperSignWrite, EwPhoneSignWrite,
  EwChangeOrder, EwChangeOrderDetail, EwCoWrite, EwCoSent, EwFieldTicket, EwFieldChangeOrder,
} from '../types/earthworks';
import type { AiBudget, EwAiQuoteCreated, EwAiRun, EwAiRunPatch } from '../types/ewAiQuote';
import type { EwQvaAccepted, EwQvaGenerated, EwQvaJob, EwQvaJobType, EwQvaNarrativeResult, EwQvaSuggestion, EwQvaTypeRow } from '../types/ewQva';
import type {
  WsActivityCode, WsActivityCodesMeta, WsActivityCodeWrite, WsCatalog, WsCrew, WsCrewMember, WsCrewMemberWrite,
  WsCrewWrite, WsCustomerSettings, WsCustomerSettingsUpdate, WsDeleted, WsEnvelope, WsRig, WsRigDetail, WsRigWrite,
  WsSettings, WsSettingsUpdate, WsStarterResult, WsWell, WsWellWrite,
  WsJob, WsJobDetail, WsJobWrite, WsJobEquipment, WsJobEquipmentWrite, WsRentIn, WsRentInWrite, WsBopWarning,
  WsWriteEnvelope, WsTour, WsTourSummary, WsTourWrite, WsMeterWarning, WsFieldMeta, WsFieldMyTours, WsFieldTour,
  WsFieldMeterWarning,
  WsStringEvent, WsStringEventWrite, WsTally, WsTallyWrite, WsTallyLineWrite, WsTourString, WsWellString, WsTourLinks,
  WsLinkCandidates, WsReport, WsReportDetail, WsReportWrite, WsFieldTourString, WsFieldTally, WsFieldBopRecorded,
} from '../types/well-servicing';
import type {
  WlCatalog, WlCrew, WlCrewMember, WlCrewMemberWrite, WlCrewWrite, WlCustomerSettings, WlCustomerSettingsUpdate,
  WlDeleted, WlEnvelope, WlEventResult, WlLine, WlLineDetail, WlLineEvent, WlLineEventWrite, WlLineState, WlLineWrite,
  WlSettings, WlSettingsUpdate, WlToolString, WlToolStringItemWrite, WlToolStringWrite, WlUnit, WlUnitDetail,
  WlUnitWrite, WlWell, WlWellWrite,
} from '../types/wireline-slickline';
import type {
  WlJob, WlJobDetail, WlJobUnit, WlJobUnitWrite, WlJobWrite, WlLinkCandidates, WlRentIn, WlRentInWrite, WlRun,
  WlRunSettings, WlRunWrite, WlSheet, WlSheetLinks, WlSheetSummary, WlSheetWrite, WlWarningEntry,
} from '../types/wireline-slickline-jobs';
import type { WlReport, WlReportDetail, WlReportTextWrite } from '../types/wireline-slickline-reports';
import type {
  PfCatalog, PfCustomerSettings, PfCustomerSettingsUpdate, PfEnvelope, PfSegment, PfSegmentWrite, PfSettings,
  PfSettingsUpdate, PfSite, PfSiteDeleted, PfSiteDetail, PfSiteWrite,
  PfJob, PfJobDetail, PfJobWrite, PfCrew, PfCrewWrite, PfJobEquipment, PfJobEquipmentWrite,
  PfRentIn, PfRentInWrite, PfDailyLog, PfDailyLogSummary, PfDailyLogWrite, PfLogWarning,
  PfPreUseCheck, PfFieldJob, PfFieldJobDetail, PfFieldLog, PfFieldLogWarning, PfFieldMeta,
} from '../types/pipeline-facility';
import type {
  WtCatalog, WtCustomerSettings, WtCustomerSettingsUpdate, WtEnvelope, WtSettings, WtSettingsUpdate, WtWell,
  WtWellDeleted, WtWellWrite,
  WtEquipmentSpec, WtEquipmentSpecWrite, WtPackage, WtPackageDetail, WtPackageUnit, WtPackageWrite, WtJob, WtJobDetail,
  WtJobWrite, WtCrewMember, WtCrewWrite, WtJobEquipment, WtJobEquipmentWrite, WtRentIn, WtRentInWrite, WtShiftLog,
  WtShiftLogSummary, WtShiftLogWrite, WtShiftLogWarning, WtFieldJob, WtFieldJobDetail, WtFieldShiftLog, WtFieldMeta,
  WtReadingFields, WtTestPeriod, WtReading, WtReadingWrite, WtSeries, WtDailyVolumes, WtDailyVolume, WtDailyVolumeWrite,
  WtFlag, WtReport, WtReportDetail, WtReportWrite, WtFieldReadings,
  WtImportParseResult, WtImportConfirmBody, WtImportResult,
} from '../types/well-testing';
import type {
  PfAnomaly,
  PfAnomalyImportBody,
  PfAnomalyImportResult,
  PfAnomalyParseResult,
  PfAnomalyWrite,
  PfDig,
  PfDigCreate,
  PfDigDetail,
  PfDigFeature,
  PfDigPatch,
  PfDigRepair,
  PfFeatureWrite,
  PfFieldDig,
  PfFieldDigDetail,
  PfFieldDigFeature,
  PfNdtCandidate,
  PfNdtLink,
  PfPtCandidate,
  PfPtLink,
  PfRepairWrite,
  PfReport,
  PfReportDetail,
  PfReportWrite,
  PfReportAiSummaryResult,
  PfWeld,
  PfWeldWrite,
} from '../types/pf-digs';
import type {
  PfRate,
  PfRateWrite,
  PfRentInBilling,
  PfTicket,
  PfTicketEdit,
  PfTicketHeaderWrite,
  PfTicketLineCreate,
  PfTicketLinePatch,
  PfTicketRecipient,
  PfTicketSendResult,
} from '../types/pf-tickets';
import type {
  WtRate,
  WtRateWrite,
  WtRentInBilling,
  WtTicket,
  WtTicketEdit,
  WtTicketHeaderWrite,
  WtTicketLineCreate,
  WtTicketLinePatch,
  WtTicketRecipient,
  WtTicketSendResult,
} from '../types/wt-tickets';
import type {
  WsRate,
  WsRateWrite,
  WsRentInBilling,
  WsTicket,
  WsTicketEdit,
  WsTicketHeaderWrite,
  WsTicketLineCreate,
  WsTicketLinePatch,
  WsTicketRecipient,
  WsTicketSendResult,
} from '../types/ws-tickets';
import type {
  WsBillableGroup,
  WsInvoice,
  WsInvoiceGenerate,
  WsInvoiceRecipient,
  WsInvoiceRow,
  WsInvoiceVoided,
} from '../types/ws-invoices';
import type { WsAiApproveBody, WsAiRunResult, WsAiStoredRun } from '../types/ws-ai';
import type {
  WtBillableGroup,
  WtInvoice,
  WtInvoiceGenerate,
  WtInvoiceRecipient,
  WtInvoiceRow,
  WtInvoiceVoided,
} from '../types/wt-invoices';
import type {
  PfBillableGroup,
  PfInvoice,
  PfInvoiceGenerate,
  PfInvoiceRecipient,
  PfInvoiceRow,
  PfInvoiceVoided,
} from '../types/pf-invoices';
import type { PfAiApproveBody, PfAiRunResult, PfAiStoredRun } from '../types/pf-ai';
import type { WtAiApproveBody, WtAiRunResult, WtAiStoredRun } from '../types/wt-ai';
import type {
  TimesheetSelfEditBody,
  TimesheetSelfEditResponse,
  TimesheetAuditResponse,
  TimesheetSubmitResponse,
  TimesheetResubmitResponse,
  ManualTimesheetBody,
  ManualTimesheetResponse,
} from '../types/timesheet-self-edit';
import type { ApiResponse } from '../types/api-manifest';
import type {
  Opportunity,
  OpportunityDetail,
  OpportunityLinkResult,
  OpportunityListParams,
  OpportunityWriteBody,
  OpportunityWriteResult,
  Pipeline,
  PipelineParams,
  StageProposal,
  StageProposalDecision,
} from '../types/opportunity';
import type { CustomerActivity, CustomerSnapshot } from '../types/customer-snapshot';
import type {
  SalesActivity,
  SalesActivityCreateBody,
  SalesActivityDetail,
  SalesActivityListParams,
  SalesActivityUpdateBody,
} from '../types/sales-activity';
import type { ClockEventResponse } from '../types/clock-events';
import type { ExpenseReportResponse, ExpenseReimbursedMarkResponse, ExpenseReimbursementSettingResponse } from '../types/expense-report';
import type { RentalInvoiceRecipientResponse, RentalInvoiceResponse, RentalInvoiceSendResponse } from '../types/rental-invoice';
import type { RentalDamageChargeAction, RentalDamageChargeListResponse, RentalDamageChargeResponse, RentalDamageChargeStatus } from '../types/rental-damage-charge';
import type { RentalDispatchUpdateResponse } from '../types/rental-dispatch';
import type { RentalOrderCancelResponse } from '../types/rental-order-cancel';
import type { LoadCancelResponse } from '../types/load-cancel';
import type { ExposureMonthWire } from '../types/safety-exposure-month';
import type { TicketLineItemsSaveResponse } from '../types/ticket-line-items-save';
import type { AiRuleListResponse, AiRuleTransitionResponse } from '../types/ai-rules';
import type { GpsConnectBody, GpsConnectionListResponse, GpsConnectResponse, GpsDisconnectResponse, GpsMatchReportResponse, GpsSyncResponse } from '../types/gps-connections';
import type { RentalPeriodCloseResponse, RentalPeriodListResponse } from '../types/rental-period';
import type { RentalAssetAvailability } from '../types/rental-availability';
import type { RentalDispatchCompletion } from '../types/rental-dispatch-complete';
import type { RentalBillingRules } from '../types/rental-haul';
import type { Checklist, LastPostTrip, TripDefectCertified, TripInspectionCreated, TripInspectionDetail, TripInspectionListRow } from '../types/trip-inspections';
import type { SwitchJobResponse } from '../types/timesheet-switch-job';
import type { ClockInJobsResponse } from '../types/clock-in-jobs';
import type { RentalChecklistResponse, RentalInspectionType } from '../types/rental-inspection';
import type { TruckingTimeOffEnvelope } from '../types/trucking-time-off';
import type { ScoutBrief, ScoutLabel, ScoutMetrics, ScoutMode, ScoutNudgeStatus, ScoutSettings, ScoutShadowReport } from '../types/scout';
import { onPublicTokenPage } from '../lib/public-routes';
import { loginPathFor } from '../lib/post-login-target';

const BACKEND_URL = 'https://api.field-iq.ai/api/v1';
/**
 * The API base, resolved once.
 *
 * Exported because it is the ONLY correct answer to "where is the API", and a
 * page that recomputes it gets a different one. Two spellings were loose in
 * the app before PR #317:
 *
 *   process.env.REACT_APP_API_URL || '/api/v1'   (ReportsDashboard, useTorqueProfile)
 *   process.env.REACT_APP_API_URL || 'https://api.field-iq.ai/api/v1'  (TicketReviewPage)
 *
 * Both are wrong on a Capacitor build, where the env var is not baked in and
 * the production URL is mandatory — `isNative` is what makes that decision and
 * neither spelling consults it. The first is also wrong on any web build whose
 * `REACT_APP_API_URL` build-arg is unset (see Dockerfile): a relative
 * `/api/v1` resolves against `app.field-iq.ai`, which has no API and answers
 * the SPA fallback `index.html` with a 200 — the same manufactured-200 trap
 * documented on `checkApiHealth` below.
 */
export const API_URL = isNative
  ? BACKEND_URL
  : (process.env.REACT_APP_API_URL || BACKEND_URL);

/* ── Request timeouts ───────────────────────────────────────────────────────
 *
 * Without a timeout a stalled TCP connection waits on the browser's own
 * default — minutes in Chrome, and effectively forever from the user's point
 * of view. On a rig road with one bar that is the difference between "Failed
 * to save, try again" and an app that looks frozen.
 *
 * 30s is the default. Normal CRUD against the API answers in well under 5s;
 * 30s leaves generous headroom for a slow LTE round trip while still failing
 * inside the window where the user is still looking at the screen.
 *
 * Endpoints that legitimately run longer get a longer budget instead of
 * raising the default for everything. Precedence, highest first:
 *
 *   1. an explicit `{ timeout }` at the call site
 *   2. FormData bodies (file uploads)      → UPLOAD_TIMEOUT_MS
 *   3. blob/arraybuffer responses          → DOCUMENT_TIMEOUT_MS
 *   4. the LONG_RUNNING url table below
 *   5. DEFAULT_TIMEOUT_MS
 *
 * The instance itself deliberately sets no `timeout`, so the interceptor can
 * tell "caller asked for 120s" from "nobody said anything" — axios leaves
 * config.timeout at 0 when it is unset.
 */
export const DEFAULT_TIMEOUT_MS = 30_000;
export const DOCUMENT_TIMEOUT_MS = 120_000; // PDF render + transfer of the blob
export const REPORT_TIMEOUT_MS = 90_000; // heavy aggregate SQL, JSON response
export const AI_TIMEOUT_MS = 120_000; // LLM round trip
export const BULK_TIMEOUT_MS = 120_000; // imports, bulk mutations, batch email
export const UPLOAD_TIMEOUT_MS = 300_000; // photos/docs off a truck on one bar

/**
 * URL patterns that need more than the default. Matched against the request
 * path (`config.url`), first match wins. Keep the comment on each group — the
 * next person needs to know why the endpoint is slow, not just that it is.
 */
const LONG_RUNNING: ReadonlyArray<readonly [RegExp, number]> = [
  // AI: Claude round trips. /tts also synthesises audio.
  [/\/ai-agent\//, AI_TIMEOUT_MS],
  [/\/ai-prefill(\/|$|\?)/, AI_TIMEOUT_MS],
  [/\/ai-suggest(\/|$|\?)/, AI_TIMEOUT_MS],
  [/\/surface-rentals\/ai\//, AI_TIMEOUT_MS],
  // Payroll generate: a 20s AI deadline server-side plus generation time;
  // answered in 46s before that deadline landed (api #593).
  [/\/payroll\/pay-periods\/[^/]+\/generate(\/|$|\?)/, AI_TIMEOUT_MS],

  // Server-rendered documents and file exports.
  [/\/pdf(\/|$|\?)/, DOCUMENT_TIMEOUT_MS],
  [/\/csv(\/|$|\?)/, DOCUMENT_TIMEOUT_MS],
  [/\/export(\/|$|\?)/, DOCUMENT_TIMEOUT_MS],
  [/\/download(\/|$|\?)/, DOCUMENT_TIMEOUT_MS],

  // Reports: multi-table aggregates over a whole date range.
  [/^\/reports\//, REPORT_TIMEOUT_MS],
  [/\/payroll\/reports\//, REPORT_TIMEOUT_MS],
  [/\/analytics\//, REPORT_TIMEOUT_MS],

  // Bulk work: CSV parse/commit, fan-out mutations, batch email sends.
  [/\/import(\/|$|\?)/, BULK_TIMEOUT_MS],
  [/\/bulk(-[a-z]+)?(\/|$|\?)/, BULK_TIMEOUT_MS],
  [/\/batch-send(\/|$|\?)/, BULK_TIMEOUT_MS],
  [/\/send-reminders(\/|$|\?)/, BULK_TIMEOUT_MS],
];

/** Timeout for a request path, ignoring body/response-type rules. */
export function timeoutForUrl(url: string): number {
  for (const [pattern, ms] of LONG_RUNNING) {
    if (pattern.test(url)) return ms;
  }
  return DEFAULT_TIMEOUT_MS;
}

/** Resolve the timeout for a request that did not specify one itself. */
export function resolveTimeout(config: {
  url?: string;
  data?: unknown;
  responseType?: string;
}): number {
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    return UPLOAD_TIMEOUT_MS;
  }
  if (config.responseType === 'blob' || config.responseType === 'arraybuffer') {
    return Math.max(DOCUMENT_TIMEOUT_MS, timeoutForUrl(config.url ?? ''));
  }
  return timeoutForUrl(config.url ?? '');
}

export const TIMEOUT_ERROR_MESSAGE =
  'The server took too long to respond. Check your connection and try again.';

/** True for a request the client gave up on, not one the user cancelled. */
export function isRequestTimeout(error: any): boolean {
  return error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT';
}

/**
 * Message to show a user for a failed request. Prefers the API's own error
 * text, falls back to the timeout copy, then to the caller's fallback.
 */
export function getApiErrorMessage(error: any, fallback: string): string {
  return error?.response?.data?.error || error?.userMessage || fallback;
}

/**
 * `getApiErrorMessage` for a request made with `responseType: 'blob'`.
 *
 * axios honours the response type on failures too, so the error body of a
 * document request arrives as a `Blob`, not parsed JSON — `response.data.error`
 * is `undefined` and every message the API took the trouble to write collapses
 * into the caller's fallback. That is why a 409 from the ticket PDF route
 * surfaces as "Failed to generate PDF" instead of the reason.
 *
 * Read the blob back to text, parse it, and hand off to `getApiErrorMessage`
 * for anything that isn't a JSON error body (a timeout, an opaque 502, an
 * actual PDF).
 */
export async function getBlobApiErrorMessage(error: any, fallback: string): Promise<string> {
  const body = error?.response?.data;
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    try {
      const parsed = JSON.parse(await readBlobText(body));
      if (typeof parsed?.error === 'string' && parsed.error) return parsed.error;
    } catch {
      // Not a JSON error body. Fall through to the normal reader.
    }
  }
  return getApiErrorMessage(error, fallback);
}

/**
 * `Blob.text()` where it exists, `FileReader` where it does not — jsdom and the
 * older WKWebViews a Capacitor build can still land on. Without the fallback
 * the read throws and every blob error message silently degrades to its
 * fallback, which is the failure this whole path exists to prevent.
 */
function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/* ── Streaming responses ────────────────────────────────────────────────────
 *
 * The AI agent's chat and voice endpoints answer with an SSE stream that
 * `AIAgentScreen` reads incrementally via `resp.body.getReader()`. Browser
 * axios cannot do that — it buffers the whole response before resolving — so
 * these two genuinely cannot go through `apiClient`, and they are the only
 * calls in this file that are not `apiClient` calls.
 *
 * They were plain `fetch`es until PR #317, and the audit that found them makes
 * the point worth recording: `no-raw-fetch` exempts all of `src/api/` on the
 * grounds that this directory OWNS fetch, and `unchecked-fetch-response` walks
 * `await fetch(...)` expressions, which `return fetch(...)` is not. So two
 * unbounded requests sat in the one file nobody was linting, behind a method
 * name that reads exactly like the fifty `apiClient` calls around it. A
 * directory-wide exemption is not free: it buys "the chokepoint may use the
 * primitive" and it silently also buys "and so may anything else that moves in
 * next door".
 *
 * WHAT IS BOUNDED HERE IS THE CONNECT PHASE, NOT THE STREAM.
 *
 * `fetch` resolves as soon as the response HEADERS arrive; the body arrives
 * afterwards, over as long as the model takes to talk. So the timer is cleared
 * the moment the promise settles. A budget that kept running would abort a
 * perfectly healthy answer mid-sentence — which is why the naive fix (wrap it
 * in the same 300s the uploads use and walk away) would be worse than the bug:
 * a long tool-using conversation would truncate at five minutes with no error,
 * looking exactly like the model stopping.
 *
 * The controller stays attached to the returned Response, so the abort path is
 * still available to a future caller that wants to cancel a stream in flight.
 * Nothing fires it after we clear the timer.
 */
async function connectStream(
  url: string,
  init: RequestInit,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<Response> {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } catch (err: any) {
    if (timedOut) {
      // `consumeSSEStream` shows `err.message` verbatim, so this sentence is
      // the one the user reads.
      throw new Error(TIMEOUT_ERROR_MESSAGE);
    }
    throw err;
  } finally {
    // Always — including on the success path, where the body has not been read
    // yet and must not be cut off.
    clearTimeout(timer);
  }
}

/* ── Connectivity probe ─────────────────────────────────────────────────────
 *
 * `online-detector` used to poll a *relative* `/health`. There is no
 * `public/health` in this app and `setupProxy` at the time forwarded only
 * `/api` and `/super-admin`, so that request hit the SPA fallback and came back as
 * `index.html` with a 200 — `res.ok` was true unconditionally. Verified
 * 2026-08-02: `GET https://app.field-iq.ai/health` → 200, `text/html`.
 *
 * On a device with no connectivity it was worse than a no-op. The service
 * worker's app-shell handler catches the failed fetch and answers from cache
 * (`caches.match('/index.html')`), so the probe returned a 200 *manufactured
 * locally*, and the poll flipped `isOnline` back to true 30s after the browser
 * had correctly reported a dead zone. On Capacitor the local asset server does
 * the same thing with no radio involved at all.
 *
 * What the API actually exposes (verified 2026-08-02 by request, no auth):
 *   GET https://api.field-iq.ai/health        → 200 {"status":"healthy",...}
 *                                               CORS: allow-origin app.field-iq.ai
 *   GET https://api.field-iq.ai/api/v1/health → 404
 * So the probe targets the API *origin*, not the `/api/v1` base, and no server
 * change is needed.
 *
 * TWO GUARDS, because "it returned 200" is exactly what fooled us last time:
 *   1. `res.ok` — necessary, not sufficient.
 *   2. the body must parse as a JSON object. `index.html` does not. This is
 *      what makes the SPA-fallback class of bug structurally unable to recur,
 *      including if someone later repoints the URL at something local.
 *
 * The cache-buster and `no-store` matter: the API answers `cache-control:
 * public, max-age=60`, so a device that drops out of coverage inside that
 * window could otherwise be handed its own cached 200.
 */
export const HEALTH_TIMEOUT_MS = 8_000;

/**
 * Why this is not a boolean.
 *
 * A probe that we aborted at 8s and a probe the network rejected outright are
 * not the same evidence. A dead radio *rejects* — `TypeError: Failed to fetch`
 * comes back in milliseconds, there is nothing to wait for. An 8s abort is
 * almost always the opposite: a live link that is slow (one bar on a rig road,
 * a cold API container, a tab the browser has been throttling). Collapsing both
 * to `false` is what let two slow-but-successful probes raise "Offline —
 * changes will sync" on a device that was online the whole time.
 *
 * So the caller gets the distinction and decides. `'error'` is evidence of a
 * dead link; `'timeout'` is evidence of a slow one.
 */
export type HealthProbeResult = 'ok' | 'timeout' | 'error';

function apiHealthUrl(): string {
  try {
    // API_URL is absolute in every shipped configuration (native always, web
    // unless REACT_APP_API_URL overrides it). A *relative* override would
    // resolve to the app's own origin here — which the JSON guard below then
    // rejects, so the failure mode is "reports offline in an unusual dev
    // config", never "reports online while offline".
    const base = new URL(
      API_URL,
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    );
    return `${base.origin}/health`;
  } catch {
    return 'https://api.field-iq.ai/health';
  }
}

/**
 * `'ok'` only when the API answered this request, now, with its health JSON.
 *
 * Deliberately not routed through `apiClient`: it must work signed-out, must
 * not attach a Bearer token (which would add a CORS preflight to a poll that
 * runs every 30s), and must never touch the 401-refresh-and-redirect path.
 */
export async function checkApiHealth(
  timeoutMs: number = HEALTH_TIMEOUT_MS
): Promise<HealthProbeResult> {
  const controller = new AbortController();
  // The only abort this function can produce is our own, so the flag is a
  // reliable way to read the rejection: `AbortError` vs a network rejection is
  // not portable across engines, and JavaScriptCore names it differently.
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    const res = await fetch(`${apiHealthUrl()}?_=${Date.now()}`, {
      method: 'GET',
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!res.ok) return 'error';
    // Throws on `index.html`, on an empty body, on anything that is not JSON.
    // The abort covers this read too, so a body that never finishes streaming
    // still lands on `timeout` rather than hanging the poll.
    const body = await res.json();
    return !!body && typeof body === 'object' ? 'ok' : 'error';
  } catch {
    return timedOut ? 'timeout' : 'error';
  } finally {
    clearTimeout(timer);
  }
}

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // No `timeout` on purpose — it is assigned per request below so that an
  // explicit call-site timeout stays distinguishable from an unset one.
});

// Request interceptor for auth token + FormData content-type fix + timeout
apiClient.interceptors.request.use(async (config) => {
  const token = await storage.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Let the browser set the correct multipart boundary for FormData
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  // Falsy means the call site did not ask for a specific budget.
  if (!config.timeout) {
    config.timeout = resolveTimeout(config);
  }
  return config;
});

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // A timeout arrives with no `response`, so every `err.response?.data?.error`
    // call site would fall through to its own generic copy. Tag it and give it
    // a real sentence so the failure is visible instead of an endless spinner.
    // NOTE: only timeouts are rewritten. ERR_NETWORK keeps axios's original
    // "Network Error" message — LoginScreen matches on it.
    if (isRequestTimeout(error)) {
      error.isTimeout = true;
      error.userMessage = TIMEOUT_ERROR_MESSAGE;
      error.message = TIMEOUT_ERROR_MESSAGE;
    }
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Don't redirect on auth endpoints — let the login screen handle the error itself
      const isAuthEndpoint = url.includes('/auth/');
      // On a magic-link page (lib/public-routes.ts) a 401 belongs to whatever
      // staff session the browser happens to hold, not to the reader — who has
      // no login. Clearing the token or redirecting there bounced a customer's
      // review link to /login.
      if (!isAuthEndpoint && !onPublicTokenPage()) {
        await storage.removeToken();
        // Carry the page as ?next= so sign-in lands back on it. A bare '/login'
        // here dropped it, and this fires first on a stale token (the logout's
        // push-device DELETE 401s), racing the router's own loginPathFor redirect.
        window.location.href = loginPathFor(window.location);
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Open an upload (cert/doc/attachment) in a new tab for download.
 *
 * Mints a short-lived (≤120s), single-purpose download token via an
 * authenticated request, then opens the proxy URL with ?dt=<token>. The 7-day
 * session JWT is NEVER placed in the URL (it leaked through logs/history/Referer
 * when passed as ?token=). The blank tab is opened synchronously to dodge
 * popup blockers, then redirected once the token arrives.
 */
/**
 * Open an equipment document in a new tab via the authenticated download
 * endpoint. The endpoint 302-redirects to a presigned R2 URL; since browser
 * XHR auto-follows redirects, we fetch the blob (auth interceptor adds the
 * Bearer header) and open it via an object URL. Same popup-blocker-dodge
 * pattern as openUploadDownload.
 */
export async function openEquipmentDocDownload(docId: string): Promise<void> {
  const win = window.open('', '_blank');
  try {
    const res = await apiClient.get(`/equipment/documents/${docId}/download`, {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    if (win) win.location.href = url;
    else window.open(url, '_blank');
  } catch (err) {
    if (win) win.close();
    throw err;
  }
}

/**
 * The URL an <img> or a new tab can load for one upload, given a token from
 * `api.uploads.downloadToken`. Same construction `openUploadDownload` below
 * makes; pulled out so a receipt thumbnail and a download link cannot drift.
 */
export function uploadDownloadUrl(uploadId: string, token: string): string {
  const base = apiClient.defaults.baseURL || '/api/v1';
  return `${base}/uploads/download/${uploadId}?dt=${encodeURIComponent(token)}`;
}

export async function openUploadDownload(uploadId: string): Promise<void> {
  const win = window.open('', '_blank');
  try {
    const res = await apiClient.post(`/uploads/download/${uploadId}/token`);
    const token = res.data?.data?.token;
    if (!token) throw new Error('No download token returned');
    const base = apiClient.defaults.baseURL || '/api/v1';
    const url = `${base}/uploads/download/${uploadId}?dt=${encodeURIComponent(token)}`;
    if (win) win.location.href = url;
    else window.open(url, '_blank');
  } catch (err) {
    if (win) win.close();
    throw err;
  }
}

/**
 * Mint a short-lived ticket for a WebSocket connection.
 *
 * Same trade as `openUploadDownload` above, for the same reason. A URL is not a
 * private channel: it lands in server access logs, proxy logs, browser history
 * and any `Referer`. The socket used to carry the 7-day session JWT there —
 * `?token=<jwt>` — which put a full-API credential in all four. This mints a
 * 60-second, single-user, single-tenant credential instead (API PR #234), so
 * what leaks into the logs is worthless a minute later.
 *
 * NEVER cache the return value. It is time-boxed rather than single-use, so a
 * cached one looks fine right up until it is 61 seconds old; mint a fresh one
 * per connection attempt, including every reconnect. It is one authenticated
 * POST — cheaper than the failure it prevents.
 *
 * Throws if the server returns no ticket. Callers must handle rejection: unlike
 * the `storage.getToken()` read this replaced, this is a network round trip and
 * failing is routine — offline, 401, or a 500 all land here.
 */
export async function mintSocketTicket(): Promise<string> {
  const res = await apiClient.post('/realtime/socket-ticket');
  const ticket = res.data?.data?.ticket;
  if (!ticket) throw new Error('No socket ticket returned');
  return ticket;
}

/**
 * Wire shape for a custom torque profile. snake_case because it is the API
 * body, not an app entity. The torque fields are nullable: a connection with
 * no shoulders has no shoulder torques, and the caller may be saving before
 * every field is filled.
 */
export interface TorqueProfilePayload {
  size: string;
  weight_ppf: number;
  grade: string;
  connection_type: string;
  torque_min?: number | null;
  torque_optimum?: number | null;
  torque_max?: number | null;
  torque_low_shoulder?: number | null;
  torque_high_shoulder?: number | null;
  /**
   * Whether this combo makes up to a shoulder (api#830, migration 0424). It
   * belongs to the combo, like the torques. Omitted on a create, the API
   * defaults it from its known-premium list or from shoulder values being sent;
   * omitted on an office edit, the stored flag stands. The API stores shoulder
   * torques only when it is true.
   */
  shouldered?: boolean;
}


/**
 * The four fields that identify one pipe combination. `resolve` and `remove`
 * both key on exactly these, and the server canonicalizes all four — so send
 * them as the form holds them. In particular the grade goes as typed:
 * hyphenated, uncollapsed, unmangled. Stripping hyphens client-side is what
 * turned K-55 into a key that could be saved and never found again
 * (API migrations 0285/0286/0287).
 */
export interface TorqueLookupKey {
  size: string;
  weightPpf: number;
  grade: string;
  connectionType: string;
}

/** The wire spelling of a lookup key. One place, so resolve and remove agree. */
function torqueQuery(k: TorqueLookupKey): Record<string, string> {
  return {
    size: k.size,
    weight_ppf: String(k.weightPpf),
    grade: k.grade,
    connection_type: k.connectionType,
  };
}

/** GET /mobile/push/devices row. Instants are ISO strings; null = never. */
/**
 * One person's portal link on a rental order (api#1235). `recipientEmail` is
 * null for a link not emailed to anyone (copied, or minted before per-person
 * links). `lastOpenedAt` is null until the link is first opened. There is
 * deliberately no token: this is who holds a link, not the link.
 */
export interface RentalPortalLink {
  id: string;
  recipientEmail: string | null;
  sentAt: string;
  lastOpenedAt: string | null;
  expiresAt: string;
  expired: boolean;
}

export interface PushDevice {
  deviceId: string;
  platform: 'web' | 'android' | 'ios';
  /** "iPhone", "Android phone", "Mac", ... from the device's user agent. */
  label: string;
  addedAt: string;
  /** Last push the push service accepted for it. */
  lastPushedAt: string | null;
  /** Last push the device reported ARRIVING — a dead install never does. */
  lastConfirmedAt: string | null;
  working: boolean;
  /** Why it is not working, when it is not. */
  problem: string | null;
  thisDevice: boolean;
}

export const api = {
  sync: {
    pull: (since: string) => apiClient.get('/sync/pull', { params: { since } }),
    push: (mutations: any[]) => apiClient.post('/sync/push', { mutations }),
  },

  search: {
    query: (q: string, limit = 5, signal?: AbortSignal) =>
      apiClient.get('/search', { params: { q, limit }, signal }),
  },

  auth: {
    login: (email: string, password: string) =>
      apiClient.post('/auth/login', { email, password }),
    me: () => apiClient.get('/auth/me'),
    validateInvite: (token: string) =>
      apiClient.get('/auth/invite/validate', { params: { token } }),
    setupPassword: (data: { token: string; password: string }) =>
      apiClient.post('/auth/invite/setup-password', data),
    forgotPassword: (email: string) =>
      apiClient.post('/auth/forgot-password', { email }),
    validateResetToken: (token: string) =>
      apiClient.get('/auth/reset-password/validate', { params: { token } }),
    resetPassword: (data: { token: string; password: string }) =>
      apiClient.post('/auth/reset-password', data),
  },

  roles: {
    list: () => apiClient.get('/roles'),
  },

  verticals: {
    // The vertical registry, all 21 keys with label and status (api#890).
    catalog: () => apiClient.get<VerticalCatalogResponse>('/verticals/catalog'),
  },

  users: {
    list: (params?: Record<string, string>) => apiClient.get('/users', { params }),
    get: (id: string) => apiClient.get(`/users/${id}`),
    create: (data: any) => apiClient.post('/users', data),
    update: (id: string, data: any) => apiClient.put(`/users/${id}`, data),
    /**
     * PUT /users/:id/positions (API 0425) — REPLACES the set of positions the
     * hand holds. Send the whole set, primary included: the API refuses a set
     * without the primary, and anything left out is removed. Change the primary
     * through `update` (positionId) first; that call adds it to the set.
     * admin / office_staff.
     */
    setPositions: (id: string, positionIds: string[]) =>
      apiClient.put<ApiEnvelope<UserPositionsSet>>(`/users/${id}/positions`, { positionIds }),
    delete: (id: string) => apiClient.delete(`/users/${id}`),
    resendInvite: (id: string) => apiClient.post(`/users/${id}/invite`),
    bulkInvite: (employeeIds: string[]) => apiClient.post('/team/invites/bulk', { employee_ids: employeeIds }),
    resetPassword: (id: string, data?: any) => apiClient.post(`/users/${id}/reset-password`, data || {}),
    addCertification: (id: string, data: any) => apiClient.post(`/users/${id}/certifications`, data),
    updateCertification: (id: string, certId: string, data: any) => apiClient.put(`/users/${id}/certifications/${certId}`, data),
    deleteCertification: (id: string, certId: string) => apiClient.delete(`/users/${id}/certifications/${certId}`),
    importFile: (formData: FormData) => apiClient.post('/users/import', formData),
    confirmImport: (data: any) => apiClient.post('/users/import/confirm', data),
    updateStatus: (id: string, data: { status: string; reason?: string }) => apiClient.patch(`/users/${id}/status`, data),
    getStatusHistory: (id: string) => apiClient.get(`/users/${id}/status-history`),
  },

  /**
   * GET /unified-invoices — every vertical's invoices through unified_invoices_view,
   * snake_case, paged (limit max 100). Finance → Invoices reads the NON-casing
   * verticals from here; casing rows still come from GET /invoices.
   */
  unifiedInvoices: {
    list: (params: { vertical: string; page?: number; limit?: number }) =>
      apiClient.get<{
        success: boolean;
        data: UnifiedInvoiceRow[];
        pagination?: { page: number; limit: number; total: number; totalPages: number };
      }>('/unified-invoices', { params }),
  },

  invoices: {
    list: (params?: Record<string, string>) => apiClient.get('/invoices', { params }),
    get: (id: string) => apiClient.get(`/invoices/${id}`),
    getPdf: (id: string) => apiClient.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
    /** `force: true` bypasses the known-bounce refusal (409 `recipient_suppressed`) — a real attempt, the provider decides. */
    send: (id: string, data?: { email?: string; force?: boolean }) => apiClient.post(`/invoices/${id}/send`, data || {}),
    generate: (data: any) => apiClient.post('/invoices/generate', data),
    update: (id: string, data: any) => apiClient.put(`/invoices/${id}`, data),
    recordPayment: (id: string, data: any) => apiClient.post(`/invoices/${id}/payment`, data),
    void: (id: string) => apiClient.post(`/invoices/${id}/void`),
    export: (id: string) => apiClient.get(`/invoices/${id}/export`, { responseType: 'blob' }),
    dashboard: () => apiClient.get('/invoices/summary/dashboard'),
    addLineItem: (id: string, data: any) => apiClient.post(`/invoices/${id}/line-items`, data),
    updateLineItem: (id: string, lineItemId: string, data: any) => apiClient.put(`/invoices/${id}/line-items/${lineItemId}`, data),
    deleteLineItem: (id: string, lineItemId: string) => apiClient.delete(`/invoices/${id}/line-items/${lineItemId}`),
    addTicket: (id: string, ticketId: string) => apiClient.post(`/invoices/${id}/add-ticket`, { ticketId }),
    removeTicket: (id: string, ticketId: string) => apiClient.post(`/invoices/${id}/remove-ticket`, { ticketId }),
    addDiscount: (id: string, data: any) => apiClient.post(`/invoices/${id}/add-discount`, data),
    recalculate: (id: string) => apiClient.post(`/invoices/${id}/recalculate`),
    auditLog: (id: string) => apiClient.get(`/invoices/${id}/audit-log`),
    createCreditMemo: (data: any) => apiClient.post('/invoices/credit-memo', data),
    sendReminders: () => apiClient.post('/invoices/send-reminders'),
    batchSend: (invoiceIds: string[]) => apiClient.post('/invoices/batch-send', { invoiceIds }),
    duplicate: (id: string) => apiClient.post(`/invoices/${id}/duplicate`),
    creditNote: (id: string, data: { amount: number; reason: string }) => apiClient.post(`/invoices/${id}/credit-note`, data),
    statement: (customerId: string, params?: { from?: string; to?: string }) => apiClient.get(`/invoices/statement/${customerId}`, { params }),
    createRecurring: (sourceInvoiceId: string, frequency: string) => apiClient.post('/invoices/recurring', { sourceInvoiceId, frequency }),
    getPaymentLink: (id: string) => apiClient.post(`/invoices/${id}/payment-link`),
  },

  lookups: {
    connectionTypes: () => apiClient.get('/lookups/connection-types'),
    createConnectionType: (name: string) => apiClient.post('/lookups/connection-types', { name }),
    deleteConnectionType: (id: string) => apiClient.delete(`/lookups/connection-types/${id}`),
    topDriveThreads: () => apiClient.get('/lookups/top-drive-threads'),
    createTopDriveThread: (name: string) => apiClient.post('/lookups/top-drive-threads', { name }),
    deleteTopDriveThread: (id: string) => apiClient.delete(`/lookups/top-drive-threads/${id}`),
  },

  quotes: {
    list: (params?: Record<string, string>) => apiClient.get('/quotes', { params }),
    get: (id: string) => apiClient.get(`/quotes/${id}`),
    create: (data: any) => apiClient.post('/quotes', data),
    createFromRateSheet: (data: any) => apiClient.post('/quotes/from-rate-sheet', data),
    update: (id: string, data: any) => apiClient.put(`/quotes/${id}`, data),
    getPdf: (id: string) => apiClient.get(`/quotes/${id}/pdf`, { responseType: 'blob' }),
    /** `force: true` bypasses the known-bounce refusal (409 `recipient_suppressed`) — a real attempt, the provider decides. */
    send: (id: string, data?: { email?: string; force?: boolean }) => apiClient.post<DocumentSendResponse>(`/quotes/${id}/send`, data || {}),
    /** A downhole accept is BINDING (api#956): the response carries `binding` — the customer tool rates and the assembly it made. */
    accept: (id: string) => apiClient.post<AcceptQuoteResponse>(`/quotes/${id}/accept`),
    decline: (id: string) => apiClient.post(`/quotes/${id}/decline`),
    convert: (id: string) => apiClient.post(`/quotes/${id}/convert`),
    addLineItem: (id: string, data: any) => apiClient.post(`/quotes/${id}/line-items`, data),
    updateLineItem: (id: string, lineItemId: string, data: any) => apiClient.put(`/quotes/${id}/line-items/${lineItemId}`, data),
    deleteLineItem: (id: string, lineItemId: string) => apiClient.delete(`/quotes/${id}/line-items/${lineItemId}`),
    addDiscount: (id: string, data: any) => apiClient.post(`/quotes/${id}/add-discount`, data),
    recalculate: (id: string) => apiClient.post(`/quotes/${id}/recalculate`),
    auditLog: (id: string) => apiClient.get(`/quotes/${id}/audit-log`),
    dashboard: () => apiClient.get('/quotes/summary/dashboard'),
    markExpired: () => apiClient.post('/quotes/mark-expired'),
    /** The tenant's quotable verticals, each with its rate store and whether it is ready (api#API-1). */
    pricingSources: () => apiClient.get<QuotePricingSourcesResponse>('/quotes/pricing-sources'),
    /** Items a quote for this customer may be priced from today, in the vertical's own store. */
    pricingItems: (vertical: string, customerId: string) =>
      apiClient.get<QuotePricingItemsResponse>(`/quotes/pricing-sources/${vertical}/items`, { params: { customerId } }),
    /** Create a quote for any ready vertical from picked items; the API re-reads each price (api API-2). */
    createFromPricingSource: (data: {
      vertical: string; customerId: string; lines: QuotePricingLine[]; validUntil?: string; notes?: string;
    }) => apiClient.post<{ success: boolean; data: { id: string } }>('/quotes/from-pricing-source', data),
    /** An accepted surface-rentals quote → a draft rental order (api API-2b). */
    createRentalOrder: (id: string, data: { startDate: string; expectedReturn?: string }) =>
      apiClient.post<CreateRentalOrderResponse>(`/quotes/${id}/create-rental-order`, data),
    /** An accepted casing quote → a job pinned to the quote's rate sheet (api#957). */
    createJob: (id: string, data: { jobType?: string } = {}) =>
      apiClient.post<CreateQuoteJobResponse>(`/quotes/${id}/create-job`, data),
    /** An accepted pressure-testing quote → a scheduled PT job (api#959). */
    createPtJob: (id: string, data: { scheduledDate?: string } = {}) =>
      apiClient.post<CreatePtJobResponse>(`/quotes/${id}/create-pt-job`, data),
    /** An accepted NDT quote → a scheduled NDT job (api#1166). 200 with alreadyCreated on a repeat. */
    createNdtJob: (id: string, data: { scheduledDate?: string } = {}) =>
      apiClient.post<CreateNdtJobResponse>(`/quotes/${id}/create-ndt-job`, data),
    /**
     * An accepted Pipeline quote → a scheduled Pipeline job on one of the quote
     * customer's sites (api#1232). admin / office_staff. 200 with alreadyCreated on a repeat.
     */
    createPfJob: (id: string, data: { siteId: string; segmentId?: string; scheduledDate?: string }) =>
      apiClient.post<CreatePfJobResponse>(`/quotes/${id}/create-pf-job`, data),
    /**
     * An accepted Well Testing quote → a scheduled Well Testing job on one of the
     * quote customer's wells (api#1258). admin / office_staff. 200 with alreadyCreated on a repeat.
     */
    createWtJob: (id: string, data: { wellId: string; scheduledDate?: string }) =>
      apiClient.post<CreateWtJobResponse>(`/quotes/${id}/create-wt-job`, data),
    /**
     * WS-5 (fieldiq-api#1295): an accepted Well Servicing quote → a Well
     * Servicing job on a picked well with its scopes. `wellId` and `scopes` are
     * required on the first call; a repeat returns 200 with alreadyCreated.
     */
    createWsJob: (id: string, data: { wellId: string; scopes: string[]; rigId?: string; scheduledDate?: string }) =>
      apiClient.post<CreateWsJobResponse>(`/quotes/${id}/create-ws-job`, data),
    /** An accepted Earthworks quote → an EWJ job on a site of the quote customer, with its lines (EW-2). */
    createEwJob: (id: string, data: {
      siteId: string; jobType: string; scheduledStart?: string; scheduledEnd?: string; supervisorId?: string; poNumber?: string;
    }) => apiClient.post<CreateEwJobResponse>(`/quotes/${id}/create-ew-job`, data),
    /** What an accepted quote created: jobs, invoices, tool rates, assemblies, PT jobs, rental orders. */
    createdRecords: (id: string) => apiClient.get<QuoteCreatedRecordsResponse>(`/quotes/${id}/created-records`),
    /** A draft downhole quote's minimum charge / lost in hole for one tool (api 0488). null = not quoted. */
    updateToolTerms: (id: string, dhRateId: string, data: { minimumCharge?: string | null; lihRate?: string | null }) =>
      apiClient.put<{ success: boolean; data: QuoteToolTermRow }>(`/quotes/${id}/tool-terms/${dhRateId}`, data),
  },

  accounting: {
    /**
     * QuickBooks Online invoice CSV (api#799), for the tenants with no QBO
     * connection to push through. Read-only server-side: it stamps nothing.
     *
     * `invoiceIds` MUST be non-empty. The route treats an absent or empty list
     * as "no id filter" and exports EVERY non-void invoice the company has, so
     * `[]` here would mean "all of them" while reading as "none of them". That
     * is refused here rather than trusted to every caller.
     */
    exportInvoicesCsv: (invoiceIds: string[]) => {
      if (invoiceIds.length === 0) {
        return Promise.reject(new Error('No invoices selected to export'));
      }
      return apiClient.post<Blob>('/accounting/invoices/export', { format: 'quickbooks', invoiceIds }, { responseType: 'blob' });
    },
  },

  quickbooks: {
    getAuthUrl: () => apiClient.get('/quickbooks/auth-url'),
    disconnect: () => apiClient.post('/quickbooks/disconnect'),
    status: () => apiClient.get('/quickbooks/status'),
    pushInvoice: (invoiceId: string) => apiClient.post<QboPushResponse>(`/quickbooks/push-invoice/${invoiceId}`),
    /**
     * Is this company set up to push AT ALL (API #887)? Local tables only — it
     * never calls QuickBooks — and the same function the push calls, so the two
     * cannot disagree. admin/office_staff only (strict).
     */
    readiness: () => apiClient.get<QboReadinessResponse>('/quickbooks/readiness'),

    // ── Mappings ──────────────────────────────────────────────────────────
    // A FieldIQ concept bound to an EXISTING QuickBooks object. Nothing here
    // creates anything in the tenant's books: an unmapped value refuses the
    // push (409 `QBO_MAPPING_REQUIRED`, carrying `mappingKind` and
    // `missingKeys`) and this is the screen that answers it.
    mappings: {
      /** Everything bound today, for this tenant + realm. */
      list: () => apiClient.get<QboMappingListResponse>('/quickbooks/mappings'),

      // What NEEDS mapping — derived from the tenant's own invoices, so the
      // list holds the handful that block a push rather than every value the
      // enum allows.
      lineItemTypesInUse: () => apiClient.get<QboNeedsMappingResponse<LineItemTypeUsage>>('/quickbooks/mappings/line-item-types'),
      customersInUse:     () => apiClient.get<QboNeedsMappingResponse<CustomerUsage>>('/quickbooks/mappings/customers-in-use'),
      taxLabelsInUse:     () => apiClient.get<QboNeedsMappingResponse<TaxLabelUsage>>('/quickbooks/mappings/tax-labels-in-use'),

      // The realm's own objects, to choose from. Live reads against QuickBooks.
      realmItems:            () => apiClient.get<QboCatalogResponse<QboItemSummary>>('/quickbooks/mappings/items'),
      realmCustomers:        () => apiClient.get<QboCustomerCatalogResponse>('/quickbooks/mappings/customers'),
      realmTaxCodes:         () => apiClient.get<QboCatalogResponse<QboTaxCodeSummary>>('/quickbooks/mappings/tax-codes'),
      realmDiscountAccounts: () => apiClient.get<QboDiscountAccountResponse>('/quickbooks/mappings/discount-accounts'),

      save: (body: { kind: string; local_key: string; qbo_id: string }) =>
        apiClient.put<QboMappingSaveResponse>('/quickbooks/mappings', body),
      remove: (kind: string, localKey: string) =>
        apiClient.delete<{ success: true }>(`/quickbooks/mappings/${kind}/${encodeURIComponent(localKey)}`),

      // Rate-sheet rows (API 0406) — the binding that tells casing tong from
      // integral tong when both are one `power_tongs` type.
      rateSheetItems: () => apiClient.get<QboRateSheetItemsResponse>('/quickbooks/mappings/rate-sheet-items'),
      /** Several rows → one QuickBooks item, all-or-nothing. */
      saveRateSheetItems: (body: { rate_sheet_item_ids: string[]; qbo_id: string }) =>
        apiClient.put<QboBulkMappingSaveResponse>('/quickbooks/mappings/rate-sheet-items', body),
    },
  },

  /**
   * Dynamics 365. NO `pushInvoice` — deleted, not hidden.
   *
   * The route is registered and reachable (admin/office_staff, strict) — it is
   * the invoice-push door on the d365 prefix, and its first line is
   * `getValidToken(companyId)`, which throws for a tenant with no connection.
   * No tenant HAS one and none ever has: `GET /dynamics365/callback` answers
   * 410 for every caller, because the flow resolved the tenant from an unsigned
   * OAuth state parameter. The recorded API manifest has the push as a 500.
   *
   * So it was a call that could only fail, sitting in this file in a costume
   * identical to `quickbooks.pushInvoice` one screen up, which works. fe#754
   * hid its button; this removes the call, because a caller-less method that
   * looks like its working sibling is how it gets wired back up.
   *
   * Reopening the flow is an API-side decision about the OAuth nonce. When it
   * is made, this line comes back with it — and not before.
   *
   * THE ROUTE IS DESCRIBED ABOVE RATHER THAN SPELLED, and that is not fussiness:
   * `EditScreens.write-door-keys.test.tsx` asserts this file contains no d365
   * push path, and a comment quoting the retired path is a live occurrence to
   * any reader that does not parse. This repo has shipped that exact mistake
   * twice — a probe for the absence of a string defeated by the prose
   * explaining the removal.
   *
   * The five below have no caller either, for a milder reason: they are reads
   * and a disconnect, so they answer rather than throw. Left in place; the
   * argument for removing them is "uncalled", which is a different argument.
   */
  dynamics365: {
    getAuthUrl: () => apiClient.get('/dynamics365/auth-url'),
    disconnect: () => apiClient.post('/dynamics365/disconnect'),
    status: () => apiClient.get('/dynamics365/status'),
    listCompanies: () => apiClient.get('/dynamics365/companies'),
    selectCompany: (data: any) => apiClient.post('/dynamics365/select-company', data),
  },

  customers: {
    list: () => apiClient.get('/customers'),
    get: (id: string) => apiClient.get(`/customers/${id}`),
    /** CRM_ROLES. Every figure `{ value, asOf, reason? }` (CRM-API-4). */
    snapshot: (id: string) => apiClient.get<ApiEnvelope<CustomerSnapshot>>(`/customers/${id}/snapshot`),
    /** CRM_ROLES. `quiet_days` keeps customers whose last activity is before today−N, or who have none. */
    activity: (params?: { quiet_days?: number }) =>
      apiClient.get<ApiEnvelope<CustomerActivity>>('/customers/activity', params ? { params } : undefined),
    create: (data: any) => apiClient.post('/customers', data),
    update: (id: string, data: any) => apiClient.put(`/customers/${id}`, data),
    delete: (id: string) => apiClient.delete(`/customers/${id}`),
    // Live references + merge escape hatch. DELETE returns 409 with
    // { error, data: { counts_per_table, total_live } } when references exist;
    // merge repoints all references to the survivor, then soft-deletes the dead id.
    getReferences: (id: string) => apiClient.get(`/customers/${id}/references`),
    merge: (deadId: string, survivorId: string) =>
      apiClient.post(`/customers/${deadId}/merge`, { survivorId }),
    lookup: (name: string) => apiClient.post('/customers/lookup', { name }),
    inviteToPortal: (id: string, data: { email: string; firstName?: string; lastName?: string }) =>
      apiClient.post(`/customers/${id}/invite`, data),
    importFile: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiClient.post('/customers/import', formData, { timeout: 120000 });
    },
    confirmImport: (data: any) => apiClient.post('/customers/import/confirm', data),
    listContacts: (customerId: string, rigName?: string) =>
      apiClient.get(`/customers/${customerId}/contacts`, { params: rigName ? { rig_name: rigName } : {} }),
    createContact: (customerId: string, data: any) => apiClient.post(`/customers/${customerId}/contacts`, data),
    updateContact: (customerId: string, contactId: string, data: any) =>
      apiClient.patch(`/customers/${customerId}/contacts/${contactId}`, data),
    deleteContact: (customerId: string, contactId: string) =>
      apiClient.delete(`/customers/${customerId}/contacts/${contactId}`),
    // The URL carries the contact that is KEPT; `fromId` is the one retired.
    mergeContacts: (customerId: string, keepId: string, fromId: string) =>
      apiClient.post<CustomerContactMergeResponse>(`/customers/${customerId}/contacts/${keepId}/merge`, { fromId }),
  },

  jobs: {
    list: () => apiClient.get('/jobs'),
    create: (data: any) => apiClient.post('/jobs', data),
    get: (id: string) => apiClient.get(`/jobs/${id}`),
    update: (id: string, data: any) => apiClient.put(`/jobs/${id}`, data),
    listRigs: (q?: string) => apiClient.get('/jobs/rigs', { params: q ? { q } : {} }),
    getLatestByRig: (rigName: string) => apiClient.get('/jobs/by-rig', { params: { rigName } }),
    getNextNumber: () => apiClient.get('/jobs/next-number'),
    listContacts: () => apiClient.get('/jobs/contacts'),
    getCallSheetPDF: (jobId: string) => apiClient.get(`/jobs/${jobId}/call-sheet/pdf`, { responseType: 'blob' }),
    emailCallSheet: (jobId: string) => apiClient.post(`/jobs/${jobId}/call-sheet/email`),
    // `phase` picks which crew is texted: wash → wash, both and unstated
    // hands; run → run, both and unstated. Required by the API on a job whose
    // wash is on a different day from the run (PHASE_REQUIRED).
    smsCallSheet: (jobId: string, phase: 'wash' | 'run') =>
      apiClient.post<CallSheetSmsResponse>(`/jobs/${jobId}/call-sheet/sms`, { phase }),
    delete: (id: string) => apiClient.delete(`/jobs/${id}`),
    listDeleted: () => apiClient.get('/jobs/deleted'),
    restore: (id: string) => apiClient.post(`/jobs/${id}/restore`),
    listAttachments: (jobId: string) => apiClient.get(`/jobs/${jobId}/attachments`),
    uploadAttachment: (jobId: string, file: File, onProgress?: (pct: number) => void) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiClient.post(`/jobs/${jobId}/attachments`, formData, {
        timeout: 300000,
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
    },
    deleteAttachment: (jobId: string, attachmentId: string) =>
      apiClient.delete(`/jobs/${jobId}/attachments/${attachmentId}`),
    addLinkAttachment: (jobId: string, data: { label: string; url: string }) =>
      apiClient.post(`/jobs/${jobId}/attachments/link`, data),
  },

  /**
   * Billing pre-flight flags on one ticket (api#908). Read: admin, office_staff,
   * supervisor. Answer: admin, office_staff. A 409 means the flag is no longer
   * `open` — someone else answered it, or a re-run superseded it.
   */
  billingFlags: {
    list: (ticketId: string) =>
      apiClient.get<ApiEnvelope<TicketBillingFlags>>(`/tickets/${ticketId}/billing-flags`),
    resolve: (ticketId: string, flagId: string, note?: string) =>
      apiClient.post<ApiEnvelope<BillingFlag>>(
        `/tickets/${ticketId}/billing-flags/${flagId}/resolve`,
        note ? { note } : {},
      ),
    dismiss: (ticketId: string, flagId: string, reason: string) =>
      apiClient.post<ApiEnvelope<BillingFlag>>(`/tickets/${ticketId}/billing-flags/${flagId}/dismiss`, { reason }),
  },
  /**
   * Maintenance watch flags on an asset or a job (api#944). Read: admin,
   * office_staff, supervisor, dispatcher. Answer: admin, office_staff,
   * supervisor. Both reads return `flags: []` outside `suggest`. A 409 means
   * the flag is no longer `open`.
   */
  /** The maintenance agent's per-tenant settings (mode; D5 left-on-site days — api#994). */
  maintenanceAgent: {
    settings: () => apiClient.get<ApiEnvelope<MaintenanceAgentSettings>>('/maintenance-agent/settings'),
    updateSettings: (body: { mode?: string; left_on_site_days?: number }) =>
      apiClient.put<ApiEnvelope<MaintenanceAgentSettingsSaved>>('/maintenance-agent/settings', body),
    /** api#1012. `month` is the TENANT-LOCAL month, 'YYYY-MM'. Reads: admin, office_staff. */
    metrics: (month: string) =>
      apiClient.get<ApiEnvelope<MaintenanceAgentMetrics>>('/maintenance-agent/metrics', { params: { month } }),
    shadowReport: (days: number) =>
      apiClient.get<ApiEnvelope<MaintenanceShadowReport>>('/maintenance-agent/shadow-report', { params: { days } }),
  },

  maintenanceFlags: {
    forEquipment: (equipmentId: string) =>
      apiClient.get<ApiEnvelope<MaintenanceFlagsResponse>>(`/equipment/${equipmentId}/maintenance-flags`),
    forJob: (jobId: string) =>
      apiClient.get<ApiEnvelope<MaintenanceFlagsResponse>>(`/jobs/${jobId}/maintenance-flags`),
    resolve: (flagId: string, note?: string) =>
      apiClient.post<ApiEnvelope<MaintenanceFlag>>(`/maintenance-flags/${flagId}/resolve`, note ? { note } : {}),
    dismiss: (flagId: string, reason: string) =>
      apiClient.post<ApiEnvelope<MaintenanceFlag>>(`/maintenance-flags/${flagId}/dismiss`, { reason }),
  },
  /**
   * The billing agent's tenant settings and reports (api#908, api#916). Reads:
   * admin, office_staff. The mode PUT is STRICT admin — an elevated dispatcher
   * and an owner are refused.
   */
  billingPreflight: {
    getSettings: () => apiClient.get<ApiEnvelope<BillingPreflightSettings>>('/billing-preflight/settings'),
    setMode: (mode: BillingPreflightMode) =>
      apiClient.put<ApiEnvelope<{ mode: BillingPreflightMode }>>('/billing-preflight/settings', { mode }),
    /** `from` / `to` are tenant-local months, 'YYYY-MM', inclusive. */
    metrics: (from: string, to: string) =>
      apiClient.get<ApiEnvelope<BillingPreflightMetrics>>('/billing-preflight/metrics', { params: { from, to } }),
    shadowReport: (days: number) =>
      apiClient.get<ApiEnvelope<BillingShadowReport>>('/billing-preflight/shadow-report', { params: { days } }),
  },
  tickets: {
    // `employee_id` is NOT a parameter here. The handler reads `archived` and
    // `jobId` only, so passing it narrows nothing and returns the whole ticket
    // book — a per-person question answered with the company's rows. It was
    // declared here and on a `ticketsByEmployee` helper (deleted in fe#723,
    // zero callers). Scoping tickets to one person is `GET /employee/my-tickets`.
    list: (params?: { archived?: boolean; jobId?: string }) => apiClient.get('/tickets', { params }),
    get: (id: string) => apiClient.get(`/tickets/${id}`),
    dashboard: () => apiClient.get('/tickets/summary/dashboard'),
    create: (data: any) => apiClient.post('/tickets', data),
    update: (id: string, data: any) => apiClient.put(`/tickets/${id}`, data),
    delete: (id: string) => apiClient.delete(`/tickets/${id}`),
    submit: (id: string) => apiClient.post(`/tickets/${id}/submit`),
    fieldApprove: (id: string) => apiClient.post(`/tickets/${id}/field-approve`),
    approve: (id: string) => apiClient.post(`/tickets/${id}/approve`),
    reject: (id: string, reason?: string) => apiClient.post(`/tickets/${id}/reject`, { reason }),
    // `knownLineItemIds`: absent → the API replaces the whole set; `[]` → may
    // add, may delete nothing. Never default one into the other. `draftId`:
    // only when AI prefill ran in this editing session (API 0421).
    updateLineItems: (
      id: string,
      lineItems: any[],
      // discountAware: the caller knows about discount lines, so one it leaves
      // out is removed rather than kept (lib/ticket-discount).
      extras: { knownLineItemIds?: string[]; draftId?: string; discountAware?: boolean } = {},
    ) => apiClient.put<TicketLineItemsSaveResponse>(`/tickets/${id}/line-items`, { lineItems, ...extras }),
    aiPrefill: (jobId: string) => apiClient.post('/tickets/ai-prefill', { jobId }),
    voiceInput: (jobId: string, audioBlob: Blob) => {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('jobId', jobId);
      return apiClient.post('/tickets/voice', formData);
    },
    getNextNumber: () => apiClient.get('/tickets/next-number'),
    /**
     * The ticket PDF. Answers **409** unless the ticket is office_approved,
     * customer_approved, customer_rejected or invoiced (API PR #220).
     *
     * `{ preview: true }` adds `?preview=1`, which renders a watermarked,
     * status-stamped `ticket-FT-XXXXX-PREVIEW.pdf` for any status. Pass it only
     * when the status is outside that list — see `ticketPdfNeedsPreview` in
     * `src/lib/ticket-status.ts`. The server ignores the flag on an
     * already-allowed ticket, so sending it unconditionally would be harmless
     * on the wire and dishonest in the code: the query string is the only place
     * a reader can see that these are two different documents.
     */
    getPdf: (id: string, opts?: { preview?: boolean }) =>
      apiClient.get(`/tickets/${id}/pdf`, {
        responseType: 'blob',
        ...(opts?.preview ? { params: { preview: 1 } } : {}),
      }),
    /**
     * The file a customer uploaded in place of signing the stamp (api#721).
     *
     * Authenticated and company-scoped, unlike the public review payload, which
     * carries only a filename and a timestamp. The response is the bytes, with
     * `Content-Disposition: attachment` and the server's own filename already
     * on it — read that header rather than composing a name from the ticket
     * number the way `getPdf` above has to.
     *
     * GATE THE BUTTON ON `customer_stamp_file` FROM `GET /tickets/:id`, NOT ON A
     * 404 FROM HERE. "this ticket has no stamped file", "no such ticket", "wrong
     * company" and "a field worker who cannot see it" are the SAME 404 with the
     * same body — `{success:false, error:'Not found'}` — so a 404 cannot tell
     * you which of those happened, and treating it as "no file" would report a
     * tenancy bug as an empty state.
     *
     * Typed `Blob`, not `ApiResponse<'GET /api/v1/tickets/:id/stamped-file'>`.
     * The manifest recorded this route's response as a **404** — the generator
     * probes without a stamped file present — so the manifest shape is the keys
     * of a refusal body, and naming it here would declare the success payload to
     * be `{ success, error }`. The success payload is bytes.
     */
    getStampedFile: (id: string) =>
      apiClient.get<Blob>(`/tickets/${id}/stamped-file`, { responseType: 'blob' }),
    /**
     * The customer's ink signature from the frozen approval stamp (api#1009) —
     * PNG, JPEG or WebP bytes, `Cache-Control: private, no-store`. Read through
     * `useStampSignatureUrl`, which holds it as an object URL and keeps no copy
     * once the preview closes.
     *
     * Gate the call on the stamp's `has_signature`, not on a 404 from here: no
     * stamp, no signature and another tenant's ticket are the same 404.
     *
     * Typed `Blob` for the reason `getStampedFile` gives: the manifest recorded
     * this route as the probe's 404, so its manifest shape is a refusal body.
     */
    getStampSignature: (id: string) =>
      apiClient.get<Blob>(`/tickets/${id}/stamp-signature`, { responseType: 'blob' }),
    /**
     * The office attaches a copy the customer signed outside FieldIQ (api#995).
     * Admin and office_staff only, `{ strict: true }` — see
     * `canAttachCustomerSignedCopy`.
     *
     * `approvedByName` / `approvedOn` belong ONLY to an office_approved ticket,
     * which this moves to customer_approved. At customer_approved or invoiced
     * the API attaches the file alone and REFUSES a name or date with a 400
     * rather than ignoring it, so the caller must not send them there.
     * `approvedOn` is a calendar day, YYYY-MM-DD; absent means the tenant's
     * today.
     *
     * NO Content-Type. The request interceptor strips it on multipart so the
     * browser can set the boundary; setting it here breaks the upload.
     */
    attachCustomerSignedCopy: (
      id: string,
      body: { file: File; approvedByName?: string; approvedOn?: string },
    ) => {
      const form = new FormData();
      form.append('file', body.file);
      if (body.approvedByName !== undefined) form.append('approved_by_name', body.approvedByName);
      if (body.approvedOn) form.append('approved_on', body.approvedOn);
      return apiClient.post<CustomerSignedCopyResponse>(`/tickets/${id}/customer-signed-copy`, form);
    },
    getCustomerContacts: (id: string) => apiClient.get(`/tickets/${id}/customer-contacts`),
    /**
     * `force` skips the API's known-bounce guard on all three doors below. The
     * provider's suppression list is account-wide and nothing on our side can
     * clear it, so a mailbox repaired under the SAME spelling is unsendable
     * forever without it. The API has accepted `force` on every one of these
     * since the guard landed; only invoices and quotes ever sent it, which is
     * why a suppressed ticket recipient could not be retried from the UI by
     * anyone in any role.
     */
    sendToCustomer: (id: string, data?: { email?: string; rigEmail?: string; saveRigEmail?: boolean; phone?: string; contactName?: string; saveContact?: boolean; force?: boolean }) =>
      apiClient.post(`/tickets/${id}/send-to-customer`, data || {}),
    reactivate: (id: string) => apiClient.post(`/tickets/${id}/reactivate`),
    /**
     * Move a ticket to another job (api#771).
     *
     * `reason` is REQUIRED and trimmed server-side; whitespace-only is a 400.
     * A move leaves no other trace — `job_id` afterwards is indistinguishable
     * from a ticket that was always on that job — so the audit row is the only
     * record and this string is the only part of it a human wrote.
     *
     * `acknowledgeRateSheet` is sent ONLY after a 409 `RATE_SHEET_DIFFERS`. It
     * acknowledges that the destination job carries a different rate sheet and
     * that the move does NOT re-price anything; sending it pre-emptively would
     * acknowledge a difference nobody was shown.
     *
     * Gate: `requireRole('admin', 'office_staff')`. See `canMoveTicketJob` in
     * src/lib/ticket-move-job.ts — it is NOT `canApproveTickets`.
     *
     * Refusals carry `code` plus a per-code detail object; read them with
     * `moveJobRefusalOf`. Branch on `code`, never on `error`.
     */
    moveJob: (
      id: string,
      body: { toJobId: string; reason: string; acknowledgeRateSheet?: boolean },
    ) => apiClient.post<MoveJobResponse>(`/tickets/${id}/move-job`, body),
    /**
     * `body.email` is what makes this a RESEND rather than a fresh lookup.
     * Posting no body hands the recipient back to the server's job → customer
     * chain. On a bounced ticket that chain has never been able to return the
     * address that bounced: before #640 it walked past the suppressed one and
     * promoted the next source (a different person), and since #640 it refuses
     * outright. `force` does not help a body-less call — it bypasses the check
     * on a BODY address, and there is none. Pass the address.
     */
    resendCustomerEmail: (id: string, body?: { email?: string; force?: boolean }) =>
      apiClient.post<TicketResendResponse>(`/tickets/${id}/resend-customer-email`, body || {}),
    sendCustomerEmailWithAddress: (id: string, body: { email: string; saveToCustomerRecord: boolean; force?: boolean }) =>
      apiClient.post(`/tickets/${id}/send-customer-email-with-address`, body),
    emailCustomer: (id: string, email?: string) =>
      apiClient.post(`/tickets/${id}/email-customer`, email ? { email } : {}),
    getPayAssignments: (ticketId: string) =>
      apiClient.get(`/tickets/${ticketId}/pay-assignments`),
    createPayAssignments: (ticketId: string, data: { assignments: any[]; replaceExisting?: boolean }) =>
      apiClient.post(`/tickets/${ticketId}/pay-assignments`, data),
    aiSuggestPay: (ticketId: string) =>
      apiClient.post(`/tickets/${ticketId}/pay-assignments/ai-suggest`),
    /**
     * Score the pay assignments currently ON SCREEN for a stale crew-size tier.
     * READ-ONLY — it returns findings and writes nothing.
     *
     * It takes the rows rather than reading them from the ticket because the
     * bug it exists for happens in UNSAVED state: the AI's suggestions live in
     * local state until Save, so a delete the office makes before saving is
     * invisible to the server.
     */
    tierCheckPayAssignments: (ticketId: string, data: { assignments: any[] }) =>
      apiClient.post(`/tickets/${ticketId}/pay-assignments/tier-check`, data),
    approvePayAssignments: (ticketId: string, data?: { assignmentIds?: string[] }) =>
      apiClient.post(`/tickets/${ticketId}/pay-assignments/approve`, data || {}),
    unapprovePayAssignments: (ticketId: string) =>
      apiClient.post(`/tickets/${ticketId}/pay-assignments/unapprove`),
    updatePayAssignment: (ticketId: string, assignmentId: string, data: any) =>
      apiClient.patch(`/tickets/${ticketId}/pay-assignments/${assignmentId}`, data),
    deletePayAssignment: (ticketId: string, assignmentId: string) =>
      apiClient.delete(`/tickets/${ticketId}/pay-assignments/${assignmentId}`),
    archive: (id: string) => apiClient.post(`/tickets/${id}/archive`),
    unarchive: (id: string) => apiClient.post(`/tickets/${id}/unarchive`),
    listDeleted: () => apiClient.get('/tickets/deleted'),
    restore: (id: string) => apiClient.post(`/tickets/${id}/restore`),
  },

  dispatch: {
    /**
     * The response generic is what carries `DispatchJob` to the caller. Without
     * it `r.data.data.jobs` in DispatchScreen is `any` and the board's job list
     * is untyped end to end, which is how a dropped column stayed declared in
     * the screen's interface with every check green.
     *
     * It is an assertion, not a runtime check — it cannot fail on its own. The
     * thing that actually goes red when this type stops matching the wire is the
     * annotated fixtures in the DispatchScreen suites; see types/dispatch.ts.
     */
    jobs: (params?: Record<string, string>) =>
      apiClient.get<DispatchJobsResponse>('/dispatch/jobs', { params }),
    /**
     * Same assertion-not-a-check caveat as `jobs` above: this generic carries
     * `FilterData` to the caller so the screen stops writing `as FilterData`,
     * but it cannot fail on its own. The annotated `filterData()` fixture in
     * DispatchScreen.date.test.tsx is what goes red when this stops matching.
     */
    filters: () => apiClient.get<DispatchFiltersResponse>('/dispatch/filters'),
    /**
     * The office's confirm queue: jobs a field hand has declared finished that
     * nobody has closed yet (API 0337).
     *
     * THIS IS THE DURABLE HALF OF THE SIGNAL. A Slack post fires at the moment
     * of the tap, but most tenants have no webhook configured and
     * sendSlackNotification returns silently when that is so. If this queue is
     * not on screen, the feature has no office-facing half at all.
     *
     * Office-gated (admin / office_staff / supervisor / dispatcher) — a field
     * worker gets 403.
     */
    workFinishedJobs: () => apiClient.get('/dispatch/jobs/work-finished'),
    updateJob: (id: string, data: any) =>
      apiClient.patch(`/dispatch/jobs/${id}`, data),
    /**
     * Record that a phase began or finished (API 0403). The first phase start on
     * a job also moves it to active and runs the equipment cascade server-side;
     * an end writes its one column and never touches the status.
     */
    startPhase: (id: string, phase: 'wash' | 'run') =>
      apiClient.post<DispatchPhaseClockResponse>(`/dispatch/jobs/${id}/phase/${phase}/start`),
    endPhase: (id: string, phase: 'wash' | 'run') =>
      apiClient.post<DispatchPhaseClockResponse>(`/dispatch/jobs/${id}/phase/${phase}/end`),
    /**
     * Clear a wrongly recorded phase START (api#1028). Office only — admin,
     * office_staff, dispatcher. 409 with a sentence when the phase has ended or
     * has no recorded start (a pre-0403 carried-over run included). Never
     * touches status. Clears started_at only when both phase starts end up null.
     */
    clearPhaseStart: (id: string, phase: 'wash' | 'run') =>
      apiClient.delete<DispatchPhaseClockResponse>(`/dispatch/jobs/${id}/phase/${phase}/start`),
    /**
     * Resume an ENDED phase (API 0534): opens a new stint now, clears the
     * span's end, moves on_hold to active. 409 with a sentence when the job is
     * closed, the phase never started or is still running, or its start
     * predates phase clocks. The board refetches rather than reading the body.
     */
    resumePhase: (id: string, phase: 'wash' | 'run') =>
      apiClient.post<DispatchPhaseClockResponse>(`/dispatch/jobs/${id}/phase/${phase}/resume`),
    updateCrew: (id: string, data: any) =>
      apiClient.patch(`/dispatch/jobs/${id}/crew`, data),
    updateEquipment: (id: string, data: any) =>
      apiClient.patch(`/dispatch/jobs/${id}/equipment`, data),
    getAvailableCrew: (id: string) =>
      apiClient.get(`/dispatch/jobs/${id}/available-crew`),
    getAvailableEquipment: (id: string) =>
      apiClient.get(`/dispatch/jobs/${id}/available-equipment`),
    getTasks: (id: string) =>
      apiClient.get(`/dispatch/jobs/${id}/tasks`),
    createTask: (id: string, data: any) =>
      apiClient.post(`/dispatch/jobs/${id}/tasks`, data),
    updateTask: (id: string, taskId: string, data: any) =>
      apiClient.patch(`/dispatch/jobs/${id}/tasks/${taskId}`, data),
    deleteTask: (id: string, taskId: string) =>
      apiClient.delete(`/dispatch/jobs/${id}/tasks/${taskId}`),
    timeOff: (params?: any) => apiClient.get('/dispatch/time-off', { params }),
    approveTimeOff: (id: string) => apiClient.post(`/dispatch/time-off/${id}/approve`),
    /** Re-dates a pending or approved request. Status is left unchanged. */
    editTimeOff: (id: string, data: { startDate: string; endDate: string }) =>
      apiClient.patch(`/dispatch/time-off/${id}`, data),
    rejectTimeOff: (id: string, reason?: string) => apiClient.post(`/dispatch/time-off/${id}/reject`, { reason }),
    timeOffCalendar: (from: string, to: string) => apiClient.get('/dispatch/time-off/calendar', { params: { from, to } }),
    /**
     * Gear that is OUT and owes somebody an answer — the office chase list.
     *
     * Three classes: `finished_job` (the job ended and nobody said where the
     * asset went), `left_on_site` (a hand declared it stays on the lease) and
     * `maintenance`. AlertsScreen renders the first two; see lib/equipmentAlerts.
     *
     * THIS ENDPOINT SHIPPED WITH NO CALLER. Nothing in src/ referenced it
     * outside the generated api-manifest, so its `days_since_job_closed` — the
     * only place the age of a stranded asset is computed anywhere — reached
     * nobody. Meanwhile AlertsScreen answered the same question by pulling the
     * whole equipment table and filtering on status in the browser, which can
     * see the status and nothing else.
     */
    equipmentAlerts: () =>
      apiClient.get<EquipmentAlertsResponse>('/dispatch/equipment-alerts'),
    /**
     * Every asset not in the yard — left_on_site, in_use, reserved — with
     * serial, location, job and the day it got there (tenant zone, API-side).
     * Feeds the board's "Equipment on location" strip.
     */
    equipmentOut: () =>
      apiClient.get<EquipmentOutResponse>('/dispatch/equipment-out'),
    /** Needs PDF queue. Generic carries the row shape; see types/dispatch.ts. */
    torqueSheets: () => apiClient.get<TorqueSheetsResponse>('/dispatch/torque-sheets'),
    clearTorqueSheet: (id: string) => apiClient.post(`/dispatch/torque-sheets/${id}/clear`),
  },

  uploads: {
    getPresignedUrl: (data: { fileName: string; contentType: string; entityType: string; entityId: string }) =>
      apiClient.post('/uploads/presigned-url', data),
    confirm: (data: { key: string; size: number }) =>
      apiClient.post('/uploads/confirm', data),
    listForEntity: (entityType: string, entityId: string) =>
      apiClient.get(`/uploads/${entityType}/${entityId}`),
    delete: (id: string) => apiClient.delete(`/uploads/${id}`),
    /**
     * Mint the ≤120s single-purpose token `GET /uploads/download/:id?dt=`
     * accepts. Pair with `uploadDownloadUrl` for an <img> src — the GET
     * verifies the token and redirects to a fresh signed R2 URL, so the
     * browser follows it without an Authorization header.
     */
    downloadToken: (id: string) => apiClient.post(`/uploads/download/${id}/token`),
  },

  rateSheets: {
    getConfig: () => apiClient.get('/rate-sheets/config'),
    list: () => apiClient.get('/rate-sheets'),
    /**
     * Every priced item across every ACTIVE rate sheet, one flat array, each row
     * carrying the sheet it came from. Feeds the ticket line-item Description
     * picker — see src/lib/rate-sheet-items.ts.
     *
     * Not role-gated: the four rate-sheet GETs were left open deliberately so
     * field crews can read prices. Server-side this already drops inactive and
     * soft-deleted items, expired sheets, and the items orphaned by a deleted
     * sheet (70 of TNT's 343 rows point at a sheet id that no longer exists and
     * are unpriceable).
     *
     * `customer_id` WIDENS rather than narrows — it returns that customer's own
     * bid contracts PLUS the company-wide books, which together are what may
     * legitimately be billed to them. Passing it is how another customer's
     * contract stays out of this ticket's picker. `rate_sheet_id` is the
     * narrowing filter and the picker deliberately does not use it.
     */
    items: (params?: { customer_id?: string; rate_sheet_id?: string }) =>
      apiClient.get('/rate-sheets/items', { params }),
    get: (id: string) => apiClient.get(`/rate-sheets/${id}`),
    create: (data: any) => apiClient.post('/rate-sheets', data),
    update: (id: string, data: any) => apiClient.patch(`/rate-sheets/${id}`, data),
    delete: (id: string) => apiClient.delete(`/rate-sheets/${id}`),
    importFile: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiClient.post('/rate-sheets/import', formData);
    },
    confirmImport: (data: any) => apiClient.post('/rate-sheets/import/confirm', data),
    /**
     * Casing ticket lines with no price-book link, grouped (api#1040). Roles:
     * admin, office_staff, supervisor, dispatcher. See lib/price-book-missing.ts.
     */
    missingItems: () => apiClient.get<ApiResponse<'GET /api/v1/rate-sheets/missing-items'>>('/rate-sheets/missing-items'),
    /**
     * Add a book item (or name an existing one) and link the chosen lines to it,
     * in one transaction. camelCase body; invoiced lines only on
     * `includeInvoiced: true`. A duplicate name + unit on the sheet is a 409
     * carrying `existingItemId`.
     */
    linkMissingItems: (body: PriceBookLinkBody) =>
      apiClient.post<PriceBookLinkResponse>('/rate-sheets/missing-items/link', body),
  },

  employee: {
    dashboard: () => apiClient.get('/employee/dashboard'),
    myJobs: (params?: { status?: string; date?: string }) => apiClient.get('/employee/my-jobs', { params }),
    myJob: (id: string) => apiClient.get(`/employee/my-jobs/${id}`),
    /**
     * Field crew starts a job (pending | scheduled | on_hold → active), or
     * resumes a held one — same endpoint, the UI just labels it differently.
     *
     * Crew-gated server-side and 404s a worker who is not on the crew, by
     * design, so job ids cannot be probed. Note that GET /employee/my-jobs/:id
     * is only company-scoped, so a worker CAN open a job detail they are not
     * crewed on; the 404 here is reachable and the caller must handle it.
     *
     * Idempotent: an already-active job returns 200 with `changed: false` and
     * writes nothing.
     */
    startJob: (id: string, phase?: 'wash' | 'run') =>
      // `phase` is which clock the hand's button starts (API 0403). It is SENT
      // or OMITTED, never defaulted: an omitted phase stamps no phase clock,
      // where a guessed 'run' would stamp a run start on a wash day. Never
      // 'both' — wash and run are always started separately (Jason,
      // 2026-10-11); the API still accepts it from old builds and stamps the
      // wash only.
      apiClient.post<ApiResponse<'POST /api/v1/employee/my-jobs/:id/start'>>(
        `/employee/my-jobs/${id}/start`,
        phase ? { phase } : undefined,
      ),
    /**
     * Field crew puts a running job on hold (active → on_hold). 409s anything
     * that is not active.
     *
     * "Stop" is ON HOLD, never completed. Setting `completed` remains an office
     * action and there is still no field endpoint for it — but a hand is no
     * longer stuck: `markWorkFinished` below records that the work is done
     * without touching the status.
     */
    stopJob: (id: string) => apiClient.post(`/employee/my-jobs/${id}/stop`),
    /**
     * A hand ends the wash or the run (api#1031). Crew-gated like start — 404
     * off the crew. 409 with a sentence when the phase was never started or has
     * already ended. One column; the job's status is untouched.
     */
    endPhase: (id: string, phase: 'wash' | 'run') =>
      apiClient.post<FieldPhaseEndResponse>(`/employee/my-jobs/${id}/phase/${phase}/end`),
    /**
     * The crew is back on an ENDED phase (API 0534) — the field door of
     * `dispatch.resumePhase`, crew-gated like End. Same 409 sentences.
     */
    resumePhase: (id: string, phase: 'wash' | 'run') =>
      apiClient.post<FieldPhaseEndResponse>(`/employee/my-jobs/${id}/phase/${phase}/resume`),
    /**
     * A hand declares the work done (API 0337). Records a FACT; the job's
     * status is untouched and stays active/on_hold until the office confirms
     * by closing it.
     *
     * Deliberately not a status change: ~25 API sites filter jobs.status
     * positively and would silently exclude a new member — GET
     * /employee/my-equipment among them, which is the screen this same hand
     * needs in order to say where the equipment went.
     *
     * Accepts active and on_hold. 409s pending/scheduled (nothing started) and
     * cancelled. An already-completed job is a 200 no-op, not an error.
     *
     * Idempotent and authorship-preserving: a second tap — even by a different
     * hand — returns `changed: false` and does not move the instant or
     * reassign it.
     */
    markWorkFinished: (id: string) =>
      apiClient.post(`/employee/my-jobs/${id}/work-finished`),
    /**
     * A hand withdraws the declaration, while the office has not confirmed.
     *
     * NOT a fat-finger undo — `markWorkFinished` is already forgiving there,
     * since a second tap is a no-op preserving the first hand's authorship.
     * This is for the WRONG JOB: a hand on a two-job day taps the one at the
     * top of the list and needs a way back.
     *
     * Any crew member may withdraw, not only whoever declared — a hand who
     * tapped for a crew already driving away has to be correctable by the one
     * person still on site.
     *
     * 409s once the office has closed the job; confirmation IS the move to
     * completed, so there is no separate confirmed flag to consult.
     */
    unmarkWorkFinished: (id: string) =>
      apiClient.delete(`/employee/my-jobs/${id}/work-finished`),
    /**
     * A hand fills in the well details dispatch did not have (API #683).
     *
     * FILL-ONLY. A blank is filled; a value dispatch entered is never
     * overwritten, and an attempt to is a 409 whose message NAMES the value on
     * file so the hand can decide whether to phone the office. Correction stays
     * with the office.
     *
     * Body keys are `lsd` and `well_name`, mapped to `well_id_lsd` / `well_name`
     * server-side. `lsd` is the key BOTH read endpoints already emit
     * (`lsd: row.well_id_lsd`) and the one this screen reads as `job.lsd` —
     * sending `well_id_lsd` here would have one screen reading one name and
     * writing another.
     *
     * ONLINE ONLY, and it is THIS SIDE that enforces it. `well_id_lsd` and
     * `well_name` are both in sync's `entityFields.jobs`, and API #667's role
     * gate on that door refuses a `field_worker` but NOT an admin — who reaches
     * the same handler, because crew membership gates field workers alone. So
     * never route this through `useOfflineMutation`: the queue would accept an
     * admin's job write and the API would apply it on reconnect, hours later,
     * against a value someone else may have filled in the meantime.
     *
     * Sending a value already on file is a 200 with `changed: false` and NO
     * write — the row's version and updated_at are untouched, so a form that
     * resubmits both boxes on every Save does not invalidate every offline
     * client's job cache on each tap.
     */
    fillWellInfo: (id: string, data: WellInfoFillBody) =>
      apiClient.put<WellInfoFillResponse>(`/employee/my-jobs/${id}/well-info`, data),
    /**
     * A hand CORRECTS one casing string's spec. ONLINE ONLY, for the same reason
     * as fillWellInfo and more so: `expected` is compared against what is on file
     * at the moment the request lands, and a queued correction replayed hours
     * later would be judged against a spec the office may have changed since.
     * Never route through `useOfflineMutation`.
     *
     * 409 means the string moved since the form opened; the body carries
     * `current` and the API's sentence names who changed it.
     */
    correctCasingString: (id: string, stringNumber: number, data: CasingCorrectionBody) =>
      apiClient.put<CasingCorrectionResponse>(`/employee/my-jobs/${id}/casing-strings/${stringNumber}`, data),
    myTickets: (params?: { status?: string }) => apiClient.get('/employee/my-tickets', { params }),
    createTicket: (data: { jobId: string }) => apiClient.post('/employee/my-tickets', data),
    updateTicket: (id: string, data: any) => apiClient.put(`/employee/my-tickets/${id}`, data),
    submitTicket: (id: string, data?: any) => apiClient.post(`/employee/my-tickets/${id}/field-approve`, data),
    aiPrefillMyTicket: (id: string) => apiClient.post(`/employee/my-tickets/${id}/ai-prefill`),
    deleteTicket: (id: string) => apiClient.delete(`/employee/my-tickets/${id}`),
    voiceTicket: (jobId: string, audioBlob: Blob) => {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('jobId', jobId);
      return apiClient.post('/employee/my-tickets/voice', formData);
    },
    /**
     * Field crew assign/remove equipment on a job they are crewed on.
     *
     * Same table the office modal writes (`job_equipment_assignments`), so a row
     * written here shows on the ticket, the PDFs, job detail, the dispatch board
     * and the call sheet with no change to any of them. `assigned_source` is
     * stamped 'field' server-side — provenance only, never ownership: anyone on
     * the crew adds, anyone removes anything, last write stands.
     *
     * ONLINE ONLY. Deliberately NOT in the offline sync queue and must not be
     * added to it. Equipment is picked at the shop where there is signal; an
     * offline queue here would let a hand believe gear was booked while the row
     * sat in IndexedDB.
     *
     * Statuses the caller must handle:
     *   403 — the tenant flag is off. Checked BEFORE crew membership, so this
     *         never masquerades as a missing job.
     *   404 — not crewed on this job, or an equipment id this tenant does not
     *         own. Deliberately not 403, so job ids cannot be probed.
     *   409 — `code: 'cert_warning'` with `warnings[]`, and NOTHING was written.
     *         Re-send the identical body with `force: true` to proceed. Branch
     *         on the CODE, never the error string.
     *
     * A 200 can also carry a non-empty `warnings[]` — that is a successful
     * forced assign, not an error. Same array shape in both, so one renderer
     * serves the confirm dialog and the post-write toast.
     */
    assignJobEquipment: (
      jobId: string,
      body: { add?: string[]; remove?: string[]; force?: boolean },
    ) => apiClient.post(`/employee/my-jobs/${jobId}/equipment`, body),
    myEquipment: () => apiClient.get('/employee/my-equipment'),
    // Field equipment detail in one round trip: equipment row + certification{}
    // + usage_log[] (newest first, capped at 25) + usage_log_total_count.
    myEquipmentDetail: (equipmentId: string) => apiClient.get(`/employee/my-equipment/${equipmentId}`),
    getEquipmentCertUrl: (equipmentId: string) => apiClient.get(`/employee/equipment-cert/${equipmentId}`),
    createEquipmentCheck: (data: any) => apiClient.post('/employee/equipment-checks', data),
    myPay: () => apiClient.get('/employee/my-pay'),
    myCertifications: () => apiClient.get('/employee/my-certifications'),
    createCertification: (data: {
      certification_type: string;
      custom_type?: string;
      certificate_number: string;
      issued_date?: string;
      expiry_date?: string;
      issuing_body?: string;
    }) => apiClient.post('/employee/my-certifications', data),
    updateCertification: (
      certId: string,
      data: {
        certificate_number?: string;
        expiry_date?: string;
        issued_date?: string;
        issuing_body?: string;
      },
    ) => apiClient.patch(`/employee/my-certifications/${certId}`, data),
    /**
     * Claude Vision read of a photographed safety ticket. EXTRACTS ONLY — it
     * never inserts or updates a row; the worker confirms the values and the
     * existing create/update endpoints do the write.
     *
     * Three outcomes the caller must keep apart (see `CertScanOutcome`):
     *   200 + error:true → unreadable photo, offer a retake
     *   502             → Claude is down, "try again later", NOT a retake
     *   400             → the upload itself was rejected
     *
     * `validateStatus` lets the 502 and 400 resolve instead of throwing, so
     * the screen branches on one shape rather than half in catch.
     *
     * NOTE: no Content-Type. The request interceptor strips it on multipart so
     * the browser can set the multipart boundary; setting it here breaks the
     * upload.
     */
    scanCertification: (photo: Blob, fileName = 'cert-scan.jpg') => {
      const formData = new FormData();
      formData.append('photo', photo, fileName);
      return apiClient.post('/employee/my-certifications/scan', formData, {
        validateStatus: (status) => status === 200 || status === 400 || status === 502,
      });
    },
    uploadCertDocument: (certId: string, data: { fileName: string; contentType: string }) =>
      apiClient.post(`/employee/my-certifications/${certId}/document`, data),
    confirmCertDocument: (certId: string, data: { key: string; size: number }) =>
      apiClient.post(`/employee/my-certifications/${certId}/document/confirm`, data),
    getCertDocument: (certId: string) =>
      apiClient.get(`/employee/my-certifications/${certId}/document`),
    deleteCertDocument: (certId: string) =>
      apiClient.delete(`/employee/my-certifications/${certId}/document`),
    myProfile: () => apiClient.get('/employee/my-profile'),
    updateProfile: (data: any) => apiClient.patch('/employee/my-profile', data),
    changePassword: (data: { currentPassword: string; newPassword: string }) => apiClient.post('/employee/my-profile/change-password', data),
    timeOff: (params?: any) => apiClient.get('/employee/time-off', { params }),
    submitTimeOff: (data: any) => apiClient.post('/employee/time-off', data),
    /** Re-dates one of my own pending or approved requests. Status is left unchanged. */
    editTimeOff: (id: string, data: { startDate: string; endDate: string }) =>
      apiClient.patch(`/employee/time-off/${id}`, data),
    cancelTimeOff: (id: string) => apiClient.delete(`/employee/time-off/${id}`),
    /**
     * Who else in the company is already APPROVED off across a window, for the
     * request form to show before a hand submits. Five columns and nothing
     * else — no absence type, no reason. Deliberately not
     * `dispatch.timeOffCalendar`, which carries the type and is the office's.
     */
    timeOffOverlaps: (from: string, to: string) =>
      apiClient.get<TimeOffOverlapsResponse>('/employee/time-off/overlaps', { params: { from, to } }),
    /** No window: the next 60 days from the tenant's today, decided by the API. */
    upcomingTimeOff: () =>
      apiClient.get<TimeOffOverlapsResponse>('/employee/time-off/overlaps'),
  },

  equipment: {
    list: (params?: Record<string, string>) => apiClient.get('/equipment', { params }),
    get: (id: string) => apiClient.get(`/equipment/${id}`),
    create: (data: any) => apiClient.post('/equipment', data),
    update: (id: string, data: any) => apiClient.put(`/equipment/${id}`, data),
    delete: (id: string) => apiClient.delete(`/equipment/${id}`),
    certSummary: () => apiClient.get('/equipment/cert-summary'),
    usage: (id: string) => apiClient.get(`/equipment/${id}/usage`),
    /** Sign-out history, newest first (api#985). Any tenant user; `atLocal` is in the tenant's zone. */
    history: (id: string) => apiClient.get<EquipmentHistoryWire>(`/equipment/${id}/history`),

    // ── The append-only certification-day ledger (API migration 0356) ───────
    //
    // Three calls, one ledger, and no `update`/`delete` by design: a correction
    // is a NEW ROW carrying a signed delta and the id of the entry it
    // supersedes. If you find yourself wanting a PATCH here, the thing you want
    // is `correctCertAdjustment`.
    //
    // Every one is gated server-side to {admin, office_staff, dispatcher} —
    // the READ included, because the ledger is an office record. The usage log
    // a field hand sees comes from `usage` above and is not gated.
    certAdjustments: (id: string) => apiClient.get(`/equipment/${id}/cert-adjustments`),
    /**
     * ── THE TWO KINDS TAKE DIFFERENT FIELDS, AND THE UNION SAYS SO ──────────
     *
     * Since API migration 0388 an opening balance is ABSOLUTE: it states the
     * TOTAL certification days as of a date and the server works out the delta
     * that reaches it. A historical day is still additive — one run off a paper
     * log. Before 0388 both were `+=`, which is how TNT typed 121 days of paper
     * log onto a unit already carrying a hand-typed 122 and got 243.
     *
     * The union is what stops that recurring on this side: `total` on one arm,
     * `days` on the other, and neither arm accepts the wrong one. The server
     * refuses a mismatch by name rather than reinterpreting it — 340 read as a
     * delta and 340 read as a total agree only when the counter is nought —
     * but a compile error is cheaper than a 400.
     *
     * The two override flags are deliberate acts, never defaults. See the
     * confirm panels in CertAdjustments.tsx: the server states what is being
     * overridden and the person has to read it.
     */
    addCertAdjustment: (id: string, data: ({
      kind: 'opening_balance';
      /** The TOTAL as of `workDate`, not a number to add. */
      total: number;
    } | {
      kind: 'historical_day';
      days: number;
    }) & {
      workDate?: string | null;
      rigName?: string | null;
      customerName?: string | null;
      note?: string | null;
      /** Proceed although the counter holds days no entry explains. */
      acknowledgeUnexplainedResidual?: boolean;
      /** Proceed although this unit already has an entry on this date. */
      allowDuplicateWorkDate?: boolean;
    }) => apiClient.post(`/equipment/${id}/cert-adjustments`, data),
    /**
     * `days` is the CORRECTED TOTAL for the entry, not a delta — the server
     * computes the delta against what the row currently holds, inside the
     * transaction, so two people correcting at once cannot both subtract.
     * `reason` is required and the server refuses a blank one.
     */
    correctCertAdjustment: (id: string, adjustmentId: string, data: {
      days: number;
      reason: string;
      workDate?: string | null;
      rigName?: string | null;
      customerName?: string | null;
    }) => apiClient.post(`/equipment/${id}/cert-adjustments/${adjustmentId}/corrections`, data),
    /**
     * Remove entries from the count — recoverably, with ONE reason for the
     * whole selection (API 0390).
     *
     * An ARRAY at every layer, and a single removal is an array of one. The API
     * has one door for both, so a single-row helper here would be a second
     * shape for the same request; the reason is per SELECTION rather than per
     * row, which is what stops a batch stamping the same sentence onto 107
     * entries.
     *
     * The entries are NOT gone: they stay on the ledger struck through, with
     * who removed them and why, and leave the counter, the usage log, the
     * certification PDF and the CSV. That is what makes them restorable, and
     * what lets a CAODC auditor be answered about one.
     */
    deleteCertAdjustments: (id: string, data: { ids: string[]; reason: string }) =>
      apiClient.post<CertRemovalResponse>(`/equipment/${id}/cert-adjustments/delete`, data),
    /**
     * Put removed entries back, counter and all.
     *
     * No reason of its own — the deletion's reason and actor sit on the row
     * until the moment it is restored, after which the entry simply stands
     * again. Can legitimately 409: a deleted opening balance stops blocking a
     * new one, so if one was entered since, restoring the old one would leave
     * two.
     */
    restoreCertAdjustments: (id: string, data: { ids: string[] }) =>
      apiClient.post<CertRemovalResponse>(`/equipment/${id}/cert-adjustments/restore`, data),
    // ── Certification days from a spreadsheet (API migration 0357) ─────────
    //
    // Three calls, and the order matters: read the file, see what WOULD happen,
    // then commit. `certDaysImportPreview` writes nothing — it is safe to call
    // on every keystroke of the operator's column mapping.
    //
    // The same {rows, columns, mapping, resolutions} goes to preview and to
    // commit. Commit does NOT take the preview's output: the server rebuilds
    // the preview itself before writing, so the only thing this client
    // contributes to a match is the operator's resolutions. Sending the preview
    // rows back instead would let a bug here record a hand-picked unit in the
    // ledger as a serial match.
    //
    // Gated server-side to {admin, office_staff, dispatcher} on all three,
    // including the read-shaped ones — the preview returns the tenant's whole
    // equipment roster and every cert counter in it.
    certDaysImportFile: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      // NO Content-Type. The interceptor strips it on multipart so the browser
      // can write the boundary; setting it by hand breaks the upload.
      return apiClient.post('/equipment/cert-days/import', formData, { timeout: 120000 });
    },
    certDaysImportPreview: (data: CertDaysImportRequest) =>
      apiClient.post('/equipment/cert-days/import/preview', data),
    certDaysImportConfirm: (data: CertDaysImportRequest) =>
      apiClient.post('/equipment/cert-days/import/confirm', data),
    transfer: (id: string, toYardId: string) => apiClient.post(`/equipment/${id}/transfer`, { toYardId }),
    importFile: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return apiClient.post('/equipment/import', formData, { timeout: 120000 });
    },
    confirmImport: (data: any) => apiClient.post('/equipment/import/confirm', data),
    /**
     * Declare where a piece of gear ended up. The three disposition endpoints
     * below are what closes out equipment on a finished job: completing a job
     * releases `reserved` gear automatically, but `in_use` gear stays assigned
     * until a hand says which of these happened to it.
     */
    leftOnSite: (id: string, data: { reason: string; jobId?: string | null }) =>
      apiClient.post(`/equipment/${id}/left-on-site`, data),
    // `destinationYardId` is required — the API will not accept a return that
    // does not name the yard the asset came back to.
    returnFromSite: (id: string, data: { destinationYardId: string }) =>
      apiClient.post(`/equipment/${id}/return`, data),
    transferToJob: (id: string, data: { destinationJobId: string; reason?: string }) =>
      apiClient.post(`/equipment/${id}/transfer-to-job`, data),
    verifyPhoto: async (id: string, photo: File) => {
      const form = new FormData();
      form.append('photo', photo);
      const { data } = await apiClient.post(`/equipment/${id}/verify`, form);
      return data;
    },
    bulkDelete: (ids: string[]) => apiClient.post('/equipment/bulk-delete', { ids }),
    bulkStatus: (ids: string[], status: string) => apiClient.post('/equipment/bulk-status', { ids, status }),
    bulkLocation: (ids: string[], location: string) => apiClient.post('/equipment/bulk-location', { ids, location }),
    // Many assets, ONE destination yard. Distinct from bulkStatus(ids,'available'),
    // which closes the assignment rows and DISCARDS where the gear went.
    bulkReturn: (ids: string[], destinationYardId: string) =>
      apiClient.post<BulkReturnResponse>('/equipment/bulk-return', { ids, destinationYardId }),
    // Asset documents (registration, insurance, inspections, CVIP, etc.)
    getDocuments: (equipmentId: string) => apiClient.get(`/equipment/${equipmentId}/documents`),
    createDocument: (equipmentId: string, formData: FormData) =>
      apiClient.post(`/equipment/${equipmentId}/documents`, formData),
    updateDocument: (docId: string, data: Partial<EquipmentDocumentPayload>) =>
      apiClient.patch(`/equipment/documents/${docId}`, data),
    deleteDocument: (docId: string) => apiClient.delete(`/equipment/documents/${docId}`),
  },

  serviceLocations: {
    list: () => apiClient.get('/service-locations'),
    create: (data: any) => apiClient.post('/service-locations', data),
    update: (id: string, data: any) => apiClient.put(`/service-locations/${id}`, data),
    delete: (id: string) => apiClient.delete(`/service-locations/${id}`),
  },

  reports: {
    equipmentCertPdf: () => apiClient.get('/reports/equipment-cert-usage/pdf', { responseType: 'blob' }),
    equipmentCertPdfSingle: (equipmentId: string) => apiClient.get(`/reports/equipment-cert-usage/${equipmentId}/pdf`, { responseType: 'blob' }),
    equipmentCertCsv: () => apiClient.get('/reports/equipment-cert-usage/csv', { responseType: 'blob' }),
    timeOffDashboard: () => apiClient.get('/reports/dashboard/time-off'),
    /**
     * Hours & Jobs by Employee, one worker's rows (api#1193): field and shop
     * rows, their subtotals and the grand total. `/detail`, not a bare
     * `/:userId`, which would shadow the API's `/:type/export`. Pay is
     * stripped for a role that may not read it. Dates are tenant-local days.
     */
    crewHoursDetail: (userId: string, params: { dateFrom: string; dateTo: string }) =>
      apiClient.get<ApiResponse<'GET /api/v1/reports/by-crew/:userId/detail'>>(`/reports/by-crew/${encodeURIComponent(userId)}/detail`, { params }),
    /**
     * The seven panels of the reports overview tab.
     *
     * They were seven raw `fetch`es in ReportsDashboard until PR #317, none of
     * which checked `res.ok`: a 500 parsed as JSON, `data` came back
     * `undefined`, and the panel rendered empty. An empty revenue panel is
     * indistinguishable from a real zero — the same failure mode
     * report-response-paths.test.tsx exists for, arriving by a different route.
     * They also built their own `Bearer` header from `localStorage`, which is
     * not where the token lives on native (see `storage.getToken`).
     *
     * `^/reports/` matches LONG_RUNNING above, so each of these inherits
     * REPORT_TIMEOUT_MS rather than the 30s default — they are multi-table
     * aggregates over a whole period.
     */
    dashboardMetrics: (period: string) =>
      apiClient.get('/reports/dashboard/metrics', { params: { period } }),
    dashboardRecentJobs: () => apiClient.get('/reports/dashboard/recent-jobs'),
    dashboardByCustomer: (period: string) =>
      apiClient.get('/reports/dashboard/by-customer', { params: { period } }),
    dashboardByWell: () => apiClient.get('/reports/dashboard/by-well'),
    dashboardByEquipment: () => apiClient.get('/reports/dashboard/by-equipment'),
    dashboardFinancial: (period: string) =>
      apiClient.get('/reports/dashboard/financial', { params: { period } }),
    dashboardPayroll: (period: string) =>
      apiClient.get('/reports/dashboard/payroll', { params: { period } }),
    revenueWeek: (period?: string) => apiClient.get('/reports/dashboard/revenue-week', { params: period ? { period } : {} }),
    revenueAnalytics: (months?: number) => apiClient.get('/reports/analytics/revenue', { params: months ? { months } : {} }),
    customerProfitability: (months?: number) => apiClient.get('/reports/analytics/customer-profitability', { params: months ? { months } : {} }),
    crewProductivity: (months?: number) => apiClient.get('/reports/analytics/crew-productivity', { params: months ? { months } : {} }),
    invoiceAging: () => apiClient.get('/reports/analytics/invoice-aging'),
    jobCompletion: (months?: number) => apiClient.get('/reports/analytics/job-completion', { params: months ? { months } : {} }),
    safetyAnalytics: () => apiClient.get('/reports/analytics/safety'),
    equipmentUtilization: (months?: number) => apiClient.get('/reports/analytics/equipment-utilization', { params: months ? { months } : {} }),
    payrollTimesheets: (dateFrom: string, dateTo: string) => apiClient.get('/reports/payroll-timesheets', { params: { dateFrom, dateTo } }),
    payrollBonus: (dateFrom: string, dateTo: string) => apiClient.get('/reports/payroll-bonus', { params: { dateFrom, dateTo } }),
    // Aliases used by individual report pages
    //
    // READERS FOR THE /reports/by-* FAMILY HAVE BEEN REMOVED ACROSS TWO PRs,
    // AND THE ENDPOINTS THEY NAMED ARE ALL STILL READ EVERY DAY. That is
    // not a contradiction, and it is the thing to understand before adding
    // another one here: UnifiedReports fetches
    // `apiClient.get(selectedReport.endpoint)` straight off its catalog row,
    // so the whole family runs through ONE generic call and never through a
    // named alias at all.
    //
    // fe#761 removed `revenueByWell`, `revenueByDate`, `crewReport` and
    // `equipmentUtilizationReport` with the four standalone pages that were
    // their only callers. fe#762 removed the positional `byWell` / `byDate` /
    // `byCrew` / `byEquipment` readers, which had no caller at all and had
    // been left alone in the previous PR, plus `revenueByCustomer`, whose one
    // caller was `/reports/customer` — deleted in the same change because the
    // catalog's `rev-customer` row runs the same endpoint under the same name.
    //
    // `byCustomer` and `byItemType` go the same way for the same reason: zero
    // callers, measured, not assumed. `payrollTimesheets` and `payrollBonus`
    // also have zero callers but are KEPT, and the difference is the point —
    // each one names an endpoint the catalog carries as a HIDDEN row
    // (`pay-timesheets`, `pay-bonus`), so each is waiting
    // on an API-side implementation rather than being residue of a deleted
    // page. Deleting a reader for a report that is coming back is work to
    // redo; see HIDDEN_REPORT_IDS in UnifiedReports.tsx for what each is
    // waiting on.
    //
    // `safety` was the third, and is gone rather than kept: api#804 DELETED
    // GET /reports/safety instead of implementing it (it now answers 404), so
    // there is no report coming back for a reader to wait on. The live safety
    // figures are `safetyAnalytics` above.
    financialSummary: (params: { dateFrom?: string; dateTo?: string }) => apiClient.get('/reports/financial', { params }),
    /**
     * Payroll summary — operations and trucking side by side, never added
     * (api#794). PAY_READ, so a 403 is an ordinary outcome for a supervisor or
     * a field role and the screen owes it a refusal branch.
     *
     * `start_date` / `end_date` are SNAKE, unlike the `dateFrom` / `dateTo` the
     * casing report endpoints above take. That is the API's spelling for this
     * route, not a slip here — the two halves of the reports router were
     * written years apart. Both dates are optional; omitted, the server
     * defaults to the current month in the TENANT's zone, which is the right
     * default and not one to reproduce client-side.
     *
     * @see src/types/payroll-summary-report.ts for the payload and for why it
     *      is typed by hand rather than through the manifest.
     */
    payrollSummary: (params: { start_date?: string; end_date?: string }) =>
      apiClient.get<ApiEnvelope<PayrollSummaryReportData>>('/reports/payroll-summary', { params }),
    /**
     * AR aging per customer (api#796) — the same partition and the same four
     * buckets `/reports/financial` reports tenant-wide, grouped by customer.
     * FINANCIAL_READ.
     *
     * `dateFrom` / `dateTo` are CAMEL here, matching `/reports/financial`,
     * whose figures these rows sum to. Omitted, the server defaults to all
     * time up to today in the tenant's zone.
     *
     * NOT `/reports/analytics/invoice-aging`, which is an older and different
     * rule — invoices by `due_date`, `sent`/`overdue` only, five buckets
     * starting at `current`. The two do not agree and are not meant to.
     *
     * @see src/types/ar-aging-report.ts — every money key is `number | null`
     *      and the null is the whole point.
     */
    arAging: (params: { dateFrom?: string; dateTo?: string }) =>
      apiClient.get<ApiEnvelope<ArAgingReportData>>('/reports/ar-aging', { params }),
    exportCsv: (type: string, params: { dateFrom?: string; dateTo?: string; groupBy?: string }) => apiClient.get(`/reports/${type}/export`, { params, responseType: 'blob' }),
  },

  safety: {
    dashboard: () => apiClient.get('/safety/dashboard'),
    meetings: (params?: any) => apiClient.get('/safety/meetings', { params }),
    getMeeting: (id: string) => apiClient.get(`/safety/meetings/${id}`),
    createMeeting: (data: any) => apiClient.post('/safety/meetings', data),
    updateMeeting: (id: string, data: any) => apiClient.put(`/safety/meetings/${id}`, data),
    deleteMeeting: (id: string) => apiClient.delete(`/safety/meetings/${id}`),
    jsaList: (params?: any) => apiClient.get('/safety/jsa', { params }),
    getJsa: (id: string) => apiClient.get(`/safety/jsa/${id}`),
    createJsa: (data: any) => apiClient.post('/safety/jsa', data),
    updateJsa: (id: string, data: any) => apiClient.put(`/safety/jsa/${id}`, data),
    submitJsa: (id: string) => apiClient.post(`/safety/jsa/${id}/submit`),
    approveJsa: (id: string) => apiClient.post(`/safety/jsa/${id}/approve`),
    rejectJsa: (id: string, data?: any) => apiClient.post(`/safety/jsa/${id}/reject`, data || {}),
    deleteJsa: (id: string) => apiClient.delete(`/safety/jsa/${id}`),
    incidents: (params?: any) => apiClient.get('/safety/incidents', { params }),
    getIncident: (id: string) => apiClient.get(`/safety/incidents/${id}`),
    createIncident: (data: any) => apiClient.post('/safety/incidents', data),
    updateIncident: (id: string, data: any) => apiClient.put(`/safety/incidents/${id}`, data),
    // Equipment inspections live under `casingInspections` below — the
    // /safety/inspections/* routes were removed with their dead tables.

    // PDF / Email / SMS
    getMeetingPdf: (id: string) => apiClient.get(`/safety/meetings/${id}/pdf`, { responseType: 'blob' }),
    emailMeeting: (id: string, data?: any) => apiClient.post(`/safety/meetings/${id}/email`, data || {}),
    smsMeeting: (id: string, data?: any) => apiClient.post(`/safety/meetings/${id}/sms`, data || {}),

    getJsaPdf: (id: string) => apiClient.get(`/safety/jsa/${id}/pdf`, { responseType: 'blob' }),
    emailJsa: (id: string, data?: any) => apiClient.post(`/safety/jsa/${id}/email`, data || {}),
    smsJsa: (id: string, data?: any) => apiClient.post(`/safety/jsa/${id}/sms`, data || {}),

    getIncidentPdf: (id: string) => apiClient.get(`/safety/incidents/${id}/pdf`, { responseType: 'blob' }),
    emailIncident: (id: string, data?: any) => apiClient.post(`/safety/incidents/${id}/email`, data || {}),
    smsIncident: (id: string, data?: any) => apiClient.post(`/safety/incidents/${id}/sms`, data || {}),

    teamCertSummary: () => apiClient.get('/safety/team-cert-summary'),
    teamCerts: (status?: string) => apiClient.get('/safety/team-certs', { params: { status } }),
    compliance: () => apiClient.get('/safety/compliance'),
    nearMisses: () => apiClient.get('/safety/near-misses'),
    /**
     * File a near miss.
     *
     * The type is set server-side and is not ours to send: the incidents
     * endpoint now rejects a near miss outright (400), so this route is the
     * only way to file one.
     */
    createNearMiss: (data: any) => apiClient.post('/safety/near-misses', {
      title: data.title,
      description: data.description,
      location: data.location,
      incidentDate: data.date,
      severityLevel: data.severity,
      immediateActions: data.immediateAction,
      preventiveActions: data.preventiveAction,
    }),

    // ── Safety metrics (0312) ──
    /**
     * The six rates for a window. Every rate may be null with a reason code —
     * see `lib/safety-metrics.ts`. Never coerce a null value to 0 at a call
     * site; that is the defect this endpoint was written to fix.
     */
    metrics: (window: 'rolling12' | 'ytd') =>
      apiClient.get('/safety/metrics', { params: { window } }),

    /**
     * Monthly exposure (api#969), one entry per month in the window. Hours are
     * automatic (approved clock-in + tickets) unless the office edited the
     * month. Omit from/to for the rolling-12 window the metrics use.
     */
    exposureMonths: (params?: { from?: string; to?: string }) =>
      apiClient.get<ApiResponse<'GET /api/v1/safety/exposure-months'>>('/safety/exposure-months', { params }),
    /**
     * Edit a month. `hoursWorked: number` saves an override; OMIT it to leave
     * the hours alone (sending it would freeze the month's auto figure).
     * kmDriven / employeeCount never touch the hours. `kmDriven: null` clears
     * a recorded figure back to NULL; a number, 0 included, is stored as typed.
     */
    saveExposureMonth: (
      month: string,
      body: { hoursWorked?: number; kmDriven?: number | null; employeeCount?: number },
    ) => apiClient.put<ApiEnvelope<ExposureMonthWire>>(`/safety/exposure-months/${month}`, body),
    /** Clear the hours override; the month returns to its auto figure. */
    clearExposureOverride: (month: string) =>
      apiClient.delete<ApiEnvelope<ExposureMonthWire>>(`/safety/exposure-months/${month}/override`),

    /** Rate bases and window configuration. Create-on-read: never 404s. */
    metricSettings: () => apiClient.get('/safety/metric-settings'),
    updateMetricSettings: (data: any) => apiClient.put('/safety/metric-settings', data),

    /** Company-scoped JSA templates backing the JSA form's Template select. */
    jsaTemplates: () => apiClient.get('/safety/jsa-templates'),

    // Custom form templates
    templates: (params?: any) => apiClient.get('/safety/templates', { params }),
    getTemplate: (id: string) => apiClient.get(`/safety/templates/${id}`),
    createTemplate: (data: any) => apiClient.post('/safety/templates', data),
    updateTemplate: (id: string, data: any) => apiClient.put(`/safety/templates/${id}`, data),
    cloneTemplate: (id: string) => apiClient.post(`/safety/templates/${id}/clone`),
    updateTemplateStatus: (id: string, data: { status: string }) => apiClient.patch(`/safety/templates/${id}/status`, data),
    deleteTemplate: (id: string) => apiClient.delete(`/safety/templates/${id}`),

    // Custom form submissions
    submissions: (params?: any) => apiClient.get('/safety/submissions', { params }),
    getSubmission: (id: string) => apiClient.get(`/safety/submissions/${id}`),
    /** Jobs a form's Job picker offers (api#1103): open + finished in the last 14 days, role-scoped. */
    formJobs: (params?: { q?: string; limit?: number }) => apiClient.get<ApiResponse<'GET /api/v1/safety/form-jobs'>>('/safety/form-jobs', { params }),
    createSubmission: (data: any) => apiClient.post('/safety/submissions', data),
    updateSubmission: (id: string, data: any) => apiClient.put(`/safety/submissions/${id}`, data),
    reviewSubmission: (id: string, data: any) => apiClient.patch(`/safety/submissions/${id}/review`, data),
    uploadSubmissionPhotos: (id: string, formData: FormData) => apiClient.post(`/safety/submissions/${id}/photos`, formData),
    uploadSubmissionSignature: (id: string, data: any) => apiClient.post(`/safety/submissions/${id}/signatures`, data),
    /** The submission as a PDF (bytes, not a URL). Any role in the company, a field worker included. */
    getSubmissionPdf: (id: string) => apiClient.get<Blob>(`/safety/submissions/${id}/pdf`, { responseType: 'blob' }),
    /** CSV of the submissions list under the same filters as `submissions` (no `q`). */
    exportSubmissionsCsv: (params?: Record<string, string>) =>
      apiClient.get<Blob>('/safety/submissions/export.csv', { params, responseType: 'blob' }),

    // Compliance Training
    trainingCertifications: () => apiClient.get('/safety/training/certifications'),
    trainingCourses: () => apiClient.get('/safety/training/courses'),
    createTrainingCourse: (data: any) => apiClient.post('/safety/training/courses', data),
    updateTrainingCourse: (id: string, data: any) => apiClient.put(`/safety/training/courses/${id}`, data),
    trainingCourseQuestions: (id: string) => apiClient.get(`/safety/training/courses/${id}/questions`),
    saveTrainingCourseQuestions: (id: string, data: any) => apiClient.post(`/safety/training/courses/${id}/questions`, data),

    // Employee-facing training
    myTrainingCertifications: () => apiClient.get('/safety/training/my-certifications'),
    takeTrainingCourse: (courseId: string) => apiClient.get(`/safety/training/courses/${courseId}/take`),
    submitTrainingAttempt: (data: any) => apiClient.post('/safety/training/attempts', data),
    downloadCertPdf: (certId: string) => apiClient.get(`/safety/training/certifications/${certId}/pdf`, { responseType: 'blob' }),
  },

  /**
   * The forms library and the AI Form Replicator.
   *
   * SEPARATE FROM `api.safety.templates`. `/safety/templates` is the editor's
   * list — snake_case, `is_system` / `field_count`, every non-archived row.
   * `/forms/templates` is the tenant's RESOLVED library — camelCase, with
   * `isEnabled` / `isDefault` / `source` computed server-side. They return
   * different counts on the same tenant by design (12 vs 16 on Smith Casing:
   * the library omits drafts unless asked). Do not treat one as a casing
   * variant of the other.
   */
  forms: {
    /**
     * The resolved library, every category in one call.
     *
     * `includeDrafts` is admin/dispatcher only and answers 403 for everyone
     * else — deliberately, rather than quietly returning fewer rows. Callers
     * must fall back to the strict read on 403, not hide the page.
     */
    /**
     * The forms a FIELD user may open, on any vertical.
     *
     * NOT `/field/forms`. Every `/api/v1/field/*` route resolves a
     * `trucking_drivers` row first and 404s without one, so a casing hand — who
     * has no driver record and never will — got "No driver record linked to
     * this account" instead of the safety library every tenant has. This route
     * is scoped to the company, needs no driver, and returns
     * `getFormsForDriver`'s group shape key for key, plus `fieldCount`,
     * `category` and `isDefault`.
     *
     * Auth only, no role gate: a field worker reaching it is the point.
     */
    available: () => apiClient.get('/forms/available'),

    templates: (opts?: { includeDrafts?: boolean; category?: string }) =>
      apiClient.get('/forms/templates', {
        params: {
          ...(opts?.includeDrafts ? { include_drafts: 1 } : {}),
          ...(opts?.category ? { category: opts.category } : {}),
        },
      }),

    /**
     * Write one category's library config. snake_case on the body — the write
     * side of this API takes snake_case even though the read side answers
     * camelCase.
     *
     * SEND THE WHOLE CATEGORY, ALWAYS. The handler clears that category's
     * existing default before applying the payload, so a PUT that omits
     * `default_template_id` does not "leave the default alone" — it removes it.
     * Same for entries: this is a statement of the category's end state, not a
     * patch of the rows that changed.
     *
     * Returns the re-resolved library FOR THAT CATEGORY, so the caller does not
     * need a refetch. Note it is resolved WITHOUT include_drafts: any draft in
     * the category is absent from the response and must be preserved by the
     * caller rather than read as deleted.
     */
    saveLibrary: (data: {
      category: string;
      entries: Array<{ template_id: string; is_enabled: boolean; sort_order?: number }>;
      default_template_id: string | null;
    }) => apiClient.put('/forms/library', data),

    /** draft -> active. Admin strict; 422 when the template has no fields. */
    publishTemplate: (id: string) => apiClient.post(`/forms/templates/${id}/publish`),

    /**
     * Upload a form to be replicated. Multipart: `file` plus
     * `ownership_attested`, which is a stored fact on the row and not just a
     * gate — the server rejects the upload without it.
     *
     * Content-Type is deliberately NOT set here. The request interceptor strips
     * it for FormData so the browser can set the multipart boundary; setting it
     * manually breaks the upload.
     *
     * Rasterization is synchronous, so this call carries the page-count and
     * page-limit failures rather than surfacing them later.
     */
    createSourceDocument: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('ownership_attested', 'true');
      // No Content-Type — see the FormData branch in the request interceptor.
      return apiClient.post('/forms/source-documents', formData);
    },

    /**
     * Queue an extraction run against an uploaded document. 202 on success.
     *
     * With no `target` the body is exactly `{ source_document_id }`, as it
     * always was. A pre-use run (api#1210) adds `target: 'preuse_template'` and
     * the equipment type the draft will be for; the 202 then also echoes both.
     */
    createExtraction: (
      sourceDocumentId: string,
      target?: { target: 'preuse_template'; equipment_type_id: string },
    ) =>
      apiClient.post<ApiEnvelope<{ id: string; status: string; target?: string; equipment_type_id?: string | null }>>('/forms/extractions', {
        source_document_id: sourceDocumentId,
        ...(target ? { target: target.target, equipment_type_id: target.equipment_type_id } : {}),
      }),

    /** One run. Returns the raw DB row — snake_case, unlike /forms/templates. */
    extraction: (id: string) => apiClient.get(`/forms/extractions/${id}`),

    /**
     * Runs for this tenant, newest first, optionally filtered by status.
     *
     * snake_case rows, plus `proposed_name` lifted out of the schema JSON by
     * the handler. This is how a `needs_review` run is found again — nothing
     * else in the app knows one exists.
     */
    extractions: (params?: { status?: string }) =>
      apiClient.get('/forms/extractions', { params: params?.status ? { status: params.status } : {} }),

    /**
     * Signed page images for the run's source scan, in order.
     *
     * THE URLS ARE A LEASE, NOT A REFERENCE. They are presigned R2 URLs with a
     * 3600s TTL (verified against a live response). Persisting one — in state,
     * in localStorage, in a long-lived query cache — buys a broken image with
     * no error some time later. Call this on mount and re-call it while the
     * screen is open; the R2 key is the durable thing and it is not exposed
     * here on purpose.
     */
    extractionPages: (id: string) => apiClient.get(`/forms/extractions/${id}/pages`),

    /**
     * A reviewer's edits to the proposed schema. `needs_review` only; 409 after.
     *
     * FULL REPLACEMENT, not a patch of the keys you name — whatever you send
     * becomes `proposed_schema_json`. Send the whole document back, including
     * top-level keys you did not author (`notes`, `field_confidence`): the
     * server preserves arbitrary top-level keys verbatim, and dropping one
     * deletes it.
     *
     * 422 on a schema the renderer cannot draw, with `errors: [{path, message}]`
     * where `path` is dotted with an index — `fields[3].field_type`. Those are
     * meant to be shown ON the offending field, not collapsed into a toast.
     */
    updateExtraction: (id: string, proposedSchemaJson: unknown) =>
      apiClient.patch(`/forms/extractions/${id}`, { proposed_schema_json: proposedSchemaJson }),

    /**
     * The ONLY path that creates a template from a run. Admin strict.
     *
     * `publish` DEFAULTS TO FALSE SERVER-SIDE, which lands the new template in
     * `draft` — invisible to the strict library read, and reachable only by a
     * second act on a different screen. That is the dead end #343 was written
     * to close, so this call site states `publish` rather than inheriting it.
     *
     * Note the error vocabulary differs from `updateExtraction`: a bad body is
     * a 400 carrying raw ZOD issues, whose `path` is an ARRAY (`["category"]`),
     * not a dotted string. See `normalizeSchemaErrors`.
     */
    approveExtraction: (
      id: string,
      data: { name: string; category: string; vertical?: string; publish: boolean },
    ) => apiClient.post(`/forms/extractions/${id}/approve`, data),

    /**
     * Admin strict, like approve — office_staff may edit a run they may not
     * dispose of. The reason is stored on `error_message`; there is no
     * dedicated column for it.
     */
    rejectExtraction: (id: string, reason: string) =>
      apiClient.post(`/forms/extractions/${id}/reject`, { reason }),

    /**
     * A pre-use run → a DRAFT equipment pre-use template (api#1210). Admin
     * STRICT. Never publishes: the draft is published from its own page.
     * `name` wins over the proposal's; both keys optional, nothing else allowed
     * (the body schema is strict). 201 with `{ templateId, status: 'draft',
     * itemCount, criticalCount, criticalUnconfirmed, template }`.
     */
    approvePreuseExtraction: (id: string, data: { name?: string; description?: string }) =>
      apiClient.post<ApiEnvelope<ApprovePreuseResult>>(`/forms/extractions/${id}/approve-preuse`, data),
  },

  casingInspections: {
    getTemplate: (type: string) => apiClient.get(`/casing/inspection-templates/${type}`),
    list: (params?: Record<string, string>) => apiClient.get('/casing/inspection-submissions', { params }),
    get: (id: string) => apiClient.get(`/casing/inspection-submissions/${id}`),
    create: (data: any) => apiClient.post('/casing/inspection-submissions', data),
    update: (id: string, data: any) => apiClient.patch(`/casing/inspection-submissions/${id}`, data),
    getPdf: (id: string) => apiClient.get(`/casing/inspection-submissions/${id}/pdf`, { responseType: 'blob' }),
  },

  torqueProfiles: {
    /**
     * Upsert this tenant's custom makeup-torque profile for one pipe
     * combination. Keyed server-side on
     * (company_id, size, weight_ppf, grade, connection_type).
     *
     * A rejected save here means a tong operator runs pipe to whatever numbers
     * were on screen before, so this MUST go through apiClient: a raw fetch
     * resolves normally on a 403/400/500 and the caller cannot tell a stored
     * profile from a dropped one. See useTorqueProfile.saveTorqueProfile.
     */
    save: (data: TorqueProfilePayload) => apiClient.post('/torque-profiles', data),

    /**
     * THE resolver. Tenant custom first, API RP 5C1 library second — the same
     * `resolveTorque` the call-sheet PDF calls (API #313), so the screen and the
     * paper cannot print different makeup torque for one pipe.
     *
     * Returns `source: 'company' | 'api_spec' | null` and the `sourceLabel` to
     * display. A miss is a 200 with `source: null`, not an error: the caller
     * renders "—" and offers to save a value.
     */
    resolve: (params: TorqueLookupKey, config?: { signal?: AbortSignal }) =>
      apiClient.get('/torque-profiles/resolve', { params: torqueQuery(params), ...config }),

    /**
     * Drop this tenant's override and hand the combination back to the library
     * (API #314). A HARD delete — the row is gone, not nulled, so nothing is
     * left to shadow the published value or to own the save's upsert key.
     *
     * Deleting a combination that carries no override answers 200 with
     * `deleted: 0`. That is the requested outcome, not a failure.
     */
    remove: (params: TorqueLookupKey) =>
      apiClient.delete('/torque-profiles', { params: torqueQuery(params) }),
  },

  payroll: {
    listPayPeriods: () => apiClient.get('/payroll/pay-periods'),
    createPayPeriod: (data: { periodStart: string; periodEnd: string; periodType?: string }) =>
      apiClient.post('/payroll/pay-periods', data),
    getPayPeriod: (id: string) => apiClient.get(`/payroll/pay-periods/${id}`),
    generatePayroll: (id: string) => apiClient.post(`/payroll/pay-periods/${id}/generate`),
    savePayroll: (id: string, data: { entries: any[]; employeeSummaries?: any[] }) =>
      apiClient.post(`/payroll/pay-periods/${id}/save`, data),
    approvePayPeriod: (id: string) => apiClient.post(`/payroll/pay-periods/${id}/approve`),
    exportPayroll: (id: string, format?: string) =>
      apiClient.post(`/payroll/pay-periods/${id}/export`, null, {
        params: format ? { format } : {},
        responseType: 'blob',
      }),
    getPayStub: (periodId: string, userId: string) =>
      apiClient.get(`/payroll/pay-periods/${periodId}/employees/${userId}`),
    addAdjustment: (periodId: string, data: { userId: string; adjustmentType: string; description: string; amount: number }) =>
      apiClient.post(`/payroll/pay-periods/${periodId}/adjustments`, data),
    listAdjustments: (periodId: string) =>
      apiClient.get(`/payroll/pay-periods/${periodId}/adjustments`),
    // camelCase body (api#1127); any subset. 409 on a closed period or an
    // expense-sourced adjustment.
    updateAdjustment: (periodId: string, adjId: string, data: { adjustmentType?: string; description?: string; amount?: number }) =>
      apiClient.patch<PayrollAdjustmentUpdateResponse>(`/payroll/pay-periods/${periodId}/adjustments/${adjId}`, data),
    deleteAdjustment: (periodId: string, adjId: string) =>
      apiClient.delete(`/payroll/pay-periods/${periodId}/adjustments/${adjId}`),
    autoCreatePeriod: () => apiClient.post('/payroll/pay-periods/auto-create'),
    availableEmployees: (periodId: string) =>
      apiClient.get(`/payroll/pay-periods/${periodId}/available-employees`),
    addEmployee: (periodId: string, data: any) =>
      apiClient.post(`/payroll/pay-periods/${periodId}/add-employee`, data),
    getPayRates: (userId?: string) =>
      apiClient.get('/payroll/pay-rates', { params: userId ? { userId } : {} }),
    savePayRate: (data: {
      userId: string;
      lineItemType: string;
      singleTechPercentage?: number | null;
      dualTechPercentage?: number | null;
      tripleTechPercentage?: number | null;
      quadTechPercentage?: number | null;
      dayRateAmount?: number | null;
      hourlyRateAmount?: number | null;
      /**
       * Whether hours priced from this row enter the overtime engine (API
       * 0384). Strict boolean; the API treats an absent key as false.
       */
      overtimeEligible?: boolean;
    }) => apiClient.post('/payroll/pay-rates', data),
    deletePayRate: (id: string) => apiClient.delete(`/payroll/pay-rates/${id}`),
    /** Per-company line item type usage vs. configured pay rates. */
    getRateCoverage: () => apiClient.get('/payroll/rate-coverage'),
    /**
     * Approved SHOP hours for a period, priced per worker.
     *
     * NOT part of getRateCoverage, and it cannot be. That endpoint reads
     * `ticket_line_items`; shop time is not a line item and never will be, so
     * `shop` can never appear there however many hands have shop hours and no
     * rate. This reports its own gaps instead.
     *
     * requireRole('admin', 'office_staff') and NON-strict, so a dispatcher on a
     * tenant with `dispatcher_admin_access` also passes. Everyone else gets a
     * 403 — which the caller must render as a refusal, never as an empty period.
     */
    shopPreview: (periodId: string) =>
      apiClient.get(`/payroll/pay-periods/${periodId}/shop-preview`),

    /**
     * Whether a worker is deliberately not paid from jobs.
     *
     * Both halves are `requireRole('admin', { strict: true })` — see
     * `canSetJobPayExemption` for why the UI must gate on the literal admin role
     * and not on `isAdminRole`. Everyone else gets a 403 from either call.
     *
     * The READ here is not the only way to see the value: `jobPayExemption` also
     * rides on `safeUser`, so `GET /users/:id` carries it for any authenticated
     * colleague, which is what the employee profile renders. This endpoint exists
     * so the setter has a door of its own; a screen that only needs to DISPLAY
     * the arrangement should read the user, not this.
     */
    getJobPayExemption: (userId: string) =>
      apiClient.get(`/payroll/workers/${userId}/job-pay-exemption`),

    /**
     * Set the exemption, or clear it back to job-paid.
     *
     * `jobPayExemption` must be sent EXPLICITLY — `null` clears, a value sets.
     * The API refuses a body with the key absent rather than treating it as null,
     * because a typo'd key or a half-wired form would otherwise silently clear an
     * exemption an admin had set: an unpaid worker quietly becoming paid again,
     * with a 200 and nothing on screen to show it happened.
     *
     * The parameter is therefore REQUIRED and its type includes `null` rather
     * than being optional. `setJobPayExemption(id)` must not typecheck — that is
     * the shape of the call the API is refusing, and the refusal is worth
     * nothing if this wrapper can produce it.
     */
    setJobPayExemption: (userId: string, jobPayExemption: JobPayExemption | null) =>
      apiClient.put(`/payroll/workers/${userId}/job-pay-exemption`, { jobPayExemption }),
    /**
     * How a person's personal expenses are paid back (api#1024): null = the
     * default rule, or 'payroll' / 'report'. Admin STRICT, like job-pay-exemption.
     * The key is sent EXPLICITLY — the API refuses a body without it, so the
     * Default option must send null, never omit it and never send 'report'.
     */
    setExpenseReimbursement: (userId: string, expenseReimbursement: 'payroll' | 'report' | null) =>
      apiClient.put<ExpenseReimbursementSettingResponse>(`/payroll/workers/${userId}/expense-reimbursement`, { expenseReimbursement }),
  },

  rentals: {
    dashboard: (params?: Record<string, string>) => apiClient.get('/surface-rentals/dashboard', { params }),
    assets: {
      list: (params?: Record<string, string>) => apiClient.get('/surface-rentals/assets', { params }),
      get: (id: string) => apiClient.get(`/surface-rentals/assets/${id}`),
      create: (data: any) => apiClient.post('/surface-rentals/assets', data),
      update: (id: string, data: any) => apiClient.put(`/surface-rentals/assets/${id}`, data),
      /**
       * What would stop this asset going out for the days (api#966): bookings,
       * open rentals and open maintenance. `to` omitted is open-ended.
       * `exclude_order` leaves one order's own booking out.
       */
      availability: (id: string, params: { from?: string; to?: string; exclude_order?: string }) =>
        apiClient.get<ApiEnvelope<RentalAssetAvailability>>(`/surface-rentals/assets/${id}/availability`, { params }),
      importFile: (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return apiClient.post('/surface-rentals/assets/import', formData, { timeout: 120000 });
      },
      confirmImport: (data: any) => apiClient.post('/surface-rentals/assets/import/confirm', data),
    },
    orders: {
      list: (params?: Record<string, string>) => apiClient.get('/surface-rentals/orders', { params }),
      /** The next order number the server would assign (`RO-<year>-<NNNNN>`), reserved by nothing (api#940). */
      nextNumber: () => apiClient.get<ApiEnvelope<{ order_number: string }>>('/surface-rentals/orders/next-number'),
      get: (id: string) => apiClient.get(`/surface-rentals/orders/${id}`),
      create: (data: any) => apiClient.post('/surface-rentals/orders', data),
      update: (id: string, data: any) => apiClient.put(`/surface-rentals/orders/${id}`, data),
      close: (id: string) => apiClient.post(`/surface-rentals/orders/${id}/close`),
      /**
       * Email one person a portal link (api#1235): replaces only the link this
       * address already held; everyone else's keeps working. 409 PORTAL_LINK_DEAD, 502 EMAIL_NOT_SENT.
       */
      sendPortalLink: (id: string, email: string) =>
        apiClient.post<ApiEnvelope<{ sentTo: string; expiresAt: string; portalUrl: string }>>(`/surface-rentals/orders/${id}/portal-link/send`, { email }),
      /** Who holds a portal link to this order — never the link itself (api#1235). */
      portalLinks: (id: string) =>
        apiClient.get<ApiEnvelope<RentalPortalLink[]>>(`/surface-rentals/orders/${id}/portal-links`),
      /** Revoke one person's portal link; nobody else's is touched (api#1235). */
      revokePortalLink: (id: string, linkId: string) =>
        apiClient.delete<ApiEnvelope<{ id: string; recipientEmail: string | null; revoked: true }>>(`/surface-rentals/orders/${id}/portal-links/${linkId}`),
      /**
       * Cancel an order that has not started (fe#815 cancel): a reason is
       * required. Refused 409 ORDER_NOT_CANCELLABLE once equipment has gone out,
       * an invoice exists or it is closed; cascades to its open dispatches.
       */
      cancel: (id: string, reason: string) =>
        apiClient.post<RentalOrderCancelResponse>(`/surface-rentals/orders/${id}/cancel`, { reason }),
      /** ONE asset per call: the route reads a single `asset_id` (#815: `{asset_ids: []}` was a 400). */
      addAsset: (id: string, data: { asset_id: string; applied_rate?: number }) => apiClient.post(`/surface-rentals/orders/${id}/assets`, data),
      /**
       * The assets on ONE order. The POST sibling above has always existed;
       * this GET did not, and the screen that wanted it reached for
       * `orders.list({ order_id })` instead.
       *
       * `GET /surface-rentals/orders` reads only `status` and `customer_id` —
       * `order_id` is dropped, so that call returned 200 with EVERY order in
       * the company and the caller priced a field ticket off them. A filter the
       * server ignores is not a filter; it is a wrong answer with a 200 on it.
       */
      getAssets: (id: string) => apiClient.get(`/surface-rentals/orders/${id}/assets`),
      createPeriod: (id: string, data: any) => apiClient.post(`/surface-rentals/orders/${id}/periods`, data),
      /** Every rental period on ONE order, open and closed (`off_rent_at` null = still billing). */
      listPeriods: (id: string) => apiClient.get<RentalPeriodListResponse>(`/surface-rentals/orders/${id}/periods`),
    },
    periods: {
      /**
       * An asset's rental periods (api#951). `open: true` returns only the ones
       * still billing (`off_rent_at` null), newest first. `asset_id` is
       * required. Rows are `rp.*` plus asset_number, asset_name, category and
       * order_number.
       */
      listForAsset: (assetId: string, params: { open?: boolean } = {}) =>
        apiClient.get<RentalPeriodListResponse>('/surface-rentals/periods', {
          params: { asset_id: assetId, ...(params.open !== undefined ? { open: String(params.open) } : {}) },
        }),
      /**
       * Stops billing on a period and sets its asset to `available`. The API
       * reads `off_rent_at` (an ISO instant) and `standby_days`; it ignores
       * `end_date`. There is no way to reopen a closed period.
       */
      close: (id: string, data: { off_rent_at: string }) =>
        apiClient.patch<RentalPeriodCloseResponse>(`/surface-rentals/periods/${id}/close`, data),
    },
    checkout: {
      checklist: () => apiClient.get('/surface-rentals/checkout/checklist'),
    },
    /**
     * Damage charges (fieldiq-api#1037, #815 G): pending on a return that found
     * damage; the office prices, waives (with a reason) or reopens one. Billing
     * roles read; admin and office staff write. A billed charge is 409.
     */
    damageCharges: {
      forOrder: (orderId: string) =>
        apiClient.get<RentalDamageChargeListResponse>(`/surface-rentals/orders/${orderId}/damage-charges`),
      list: (params?: { status?: RentalDamageChargeStatus }) =>
        apiClient.get<RentalDamageChargeListResponse>('/surface-rentals/damage-charges', { params }),
      update: (id: string, body: RentalDamageChargeAction) =>
        apiClient.patch<RentalDamageChargeResponse>(`/surface-rentals/damage-charges/${id}`, body),
    },
    invoices: {
      list: (params?: Record<string, string>) => apiClient.get('/surface-rentals/invoices', { params }),
      get: (id: string) => apiClient.get(`/surface-rentals/invoices/${id}`),
      // The order id is a PATH segment. There is no body-driven `/generate`,
      // and no route that generates an invoice from a TICKET — see the PR body.
      generateFromOrder: (orderId: string) =>
        apiClient.post(`/surface-rentals/invoices/generate-from-order/${orderId}`),
      // No `/status` sub-route: PUT on the invoice COALESCEs `status` in.
      updateStatus: (id: string, data: { status: string }) => apiClient.put(`/surface-rentals/invoices/${id}`, data),
      /**
       * PUT on the invoice (api#989): absent keeps, a value sets, null clears
       * (draft only). Once sent, only notes, payment fields and a status move
       * are accepted; anything else is 409 INVOICE_LOCKED naming the fields.
       */
      update: (id: string, data: Record<string, unknown>) => apiClient.put<RentalInvoiceResponse>(`/surface-rentals/invoices/${id}`, data),
      /** Who a send would go to, or `code: 'NO_BILLING_EMAIL'` with a message (api#963). */
      recipient: (id: string) => apiClient.get<RentalInvoiceRecipientResponse>(`/surface-rentals/invoices/${id}/recipient`),
      /** `email` (not `to`) overrides the resolved address; `cc` is up to 10. */
      send: (id: string, body: { email?: string; contact_name?: string; cc?: string[] }) =>
        apiClient.post<RentalInvoiceSendResponse>(`/surface-rentals/invoices/${id}/send`, body),
      pdf: (id: string) => apiClient.get<Blob>(`/surface-rentals/invoices/${id}/pdf`, { responseType: 'blob' }).then((r) => r.data),
    },
    tickets: {
      list: (params?: Record<string, string>) => apiClient.get('/surface-rentals/tickets', { params }),
      get: (id: string) => apiClient.get(`/surface-rentals/tickets/${id}`),
      create: (data: any) => apiClient.post('/surface-rentals/tickets', data),
      aiPrefill: (orderId: string, payload: Record<string, unknown>) =>
        apiClient.post('/surface-rentals/tickets/ai-prefill', { orderId, ...payload }),
    },
    dispatch: {
      list: (params?: Record<string, string>) => apiClient.get('/surface-rentals/dispatches', { params }),
      get: (id: string) => apiClient.get(`/surface-rentals/dispatches/${id}`),
      create: (data: any) => apiClient.post('/surface-rentals/dispatches', data),
      /**
       * Absent keeps, a value sets, null clears (api#989); notes only once
       * completed or cancelled (409 DISPATCH_LOCKED). A re-price of the
       * trucking load rides on the response (api#1014).
       */
      update: (id: string, data: Record<string, unknown>) => apiClient.put<RentalDispatchUpdateResponse>(`/surface-rentals/dispatches/${id}`, data),
      /**
       * A delivery or a return in ONE transaction (api#945): per asset a
       * structured inspection, then a period opened / closed, or on a Fail a
       * work order and maintenance; then the dispatch completed. All or nothing.
       * A resend of the same assets replays the stored result (`replayed`).
       */
      // `haul_hours` (api#1054): optional, 0–999.99; absent = not recorded.
      deliver: (id: string, body: { assets: Record<string, unknown>[]; delivered_at: string; haul_hours?: number }) =>
        apiClient.post<ApiEnvelope<RentalDispatchCompletion>>(`/surface-rentals/dispatches/${id}/deliver`, body),
      return: (id: string, body: { assets: Record<string, unknown>[]; returned_at: string; haul_hours?: number }) =>
        apiClient.post<ApiEnvelope<RentalDispatchCompletion>>(`/surface-rentals/dispatches/${id}/return`, body),
      /**
       * The office corrects a dispatch's haul hours (api#1054; admin, office
       * staff). null clears. 409 HAUL_HOURS_INVOICED once the haul is on a
       * non-void invoice.
       */
      haulHours: (id: string, haulHours: number | null) =>
        apiClient.patch<ApiEnvelope<Record<string, unknown>>>(`/surface-rentals/dispatches/${id}/haul-hours`, { haul_hours: haulHours }),
    },
    fleet: (params?: Record<string, string>) => apiClient.get('/surface-rentals/fleet', { params }),
    maintenance: {
      workOrders: {
        list: (params?: Record<string, string>) => apiClient.get('/surface-rentals/maintenance/work-orders', { params }),
        get: (id: string) => apiClient.get(`/surface-rentals/maintenance/work-orders/${id}`),
        create: (data: any) => apiClient.post('/surface-rentals/maintenance/work-orders', data),
        updateStatus: (id: string, data: { status: string }) => apiClient.patch(`/surface-rentals/maintenance/work-orders/${id}/status`, data),
        /** A key left out keeps its value; null clears it (api#980). Send only what changed (#815). */
        updateCosts: (id: string, data: { parts_cost?: number | null; labour_cost?: number | null; third_party_cost?: number | null; cost_notes?: string | null }) =>
          apiClient.patch(`/surface-rentals/maintenance/work-orders/${id}/costs`, data),
      },
      costReport: (params?: Record<string, string>) => apiClient.get('/surface-rentals/maintenance/cost-report', { params }),
      schedules: {
        list: (params?: Record<string, string>) => apiClient.get('/surface-rentals/maintenance/schedules', { params }),
        create: (data: any) => apiClient.post('/surface-rentals/maintenance/schedules', data),
      },
    },
    billing: {
      calculate: (orderId: string) => apiClient.get(`/surface-rentals/billing/calculate/${orderId}`),
      dashboardSummary: () => apiClient.get('/surface-rentals/billing/dashboard-summary'),
    },
    priceBooks: {
      list: (params?: Record<string, string>) => apiClient.get('/surface-rentals/price-books', { params }),
      create: (data: Record<string, unknown>) => apiClient.post('/surface-rentals/price-books', data),
      update: (id: string, data: Record<string, unknown>) => apiClient.patch(`/surface-rentals/price-books/${id}`, data),
      delete: (id: string) => apiClient.delete(`/surface-rentals/price-books/${id}`),
    },
    ai: {
      dispatchSuggest: (data: Record<string, unknown>) => apiClient.post('/surface-rentals/dispatch/ai-suggest', data),
      dailySummary: () => apiClient.get('/surface-rentals/ai/daily-summary'),
      utilizationReport: () => apiClient.get('/surface-rentals/ai/utilization-report'),
    },
    inspections: {
      list: (params?: Record<string, string>) => apiClient.get('/surface-rentals/inspections', { params }),
      get: (id: string) => apiClient.get(`/surface-rentals/inspections/${id}`),
      create: (data: Record<string, unknown>) => apiClient.post('/surface-rentals/inspections', data),
      /** The asset's checklist: base list plus its category's family (api#861). */
      checklist: (assetId: string, inspectionType: RentalInspectionType) =>
        apiClient.get<ApiEnvelope<RentalChecklistResponse>>('/surface-rentals/inspections/checklist', {
          params: { asset_id: assetId, inspection_type: inspectionType },
        }),
    },
    settings: {
      billingRules: {
        get: () => apiClient.get<ApiEnvelope<RentalBillingRules>>('/surface-rentals/settings/billing-rules'),
        update: (data: Record<string, unknown>) => apiClient.patch<ApiEnvelope<RentalBillingRules>>('/surface-rentals/settings/billing-rules', data),
      },
    },
    assetCategories: {
      list: () => apiClient.get('/surface-rentals/asset-categories'),
      create: (data: { name: string }) => apiClient.post('/surface-rentals/asset-categories', data),
      update: (id: string, data: Record<string, unknown>) => apiClient.patch(`/surface-rentals/asset-categories/${id}`, data),
      delete: (id: string) => apiClient.delete(`/surface-rentals/asset-categories/${id}`),
    },
  },

  company: {
    getLocale: () => apiClient.get('/company/locale'),
  },

  /**
   * Downhole Tools — /api/v1/downhole/* on the dh_* tables (api#882 reads).
   * Every response carries `meta: { unit_system, currency, time_zone, today }`;
   * read it with `unwrapDownhole` (hooks/useDownhole.ts) rather than dropping it.
   * Measured values are IMPERIAL as stored, whatever the tenant's unit system.
   */
  downhole: {
    dashboard: () => apiClient.get<DownholeEnvelope<DhDashboard>>('/downhole/dashboard'),
    components: {
      /** `status` must be a dh_component_status value or the API answers 400. */
      list: (params?: { status?: string; category?: string; q?: string }) =>
        apiClient.get<DownholeEnvelope<DhComponent[]>>('/downhole/components', { params }),
      /** Wear at or past `threshold` percent (default 85), or an inspection overdue on the tenant's day. */
      alerts: (params?: { threshold?: number }) =>
        apiClient.get<DownholeEnvelope<DhComponentAlert[]>>('/downhole/components/alerts', { params }),
      get: (id: string) => apiClient.get<DownholeEnvelope<DhComponentDetail>>(`/downhole/components/${id}`),
      /**
       * api#900. Admin, office_staff and dispatcher only. Every measure IMPERIAL
       * (lib/downhole-component-input.ts); an unknown key is a 400. A 409 carries
       * `code` (api#913): `DUPLICATE_SERIAL` (same serial, same category) or
       * `SERIAL_OTHER_CATEGORY`, which `confirm_distinct: true` overrides.
       */
      create: (body: DhComponentWrite) => apiClient.post<DownholeEnvelope<DhComponent>>('/downhole/components', body),
      /** Specs, serial, category, and status only `available ↔ retired`. Opening balances are a 400. */
      update: (id: string, body: DhComponentWrite) =>
        apiClient.patch<DownholeEnvelope<DhComponent>>(`/downhole/components/${id}`, body),
      /** At most 500 rows. One invalid row imports nothing (400 with `errors[]`). */
      import: (rows: Record<string, unknown>[]) =>
        apiClient.post<DownholeEnvelope<DhComponentImportResult>>('/downhole/components/import', { rows }),
      /** A read: PNG and SVG of the component's page. No write. */
      qr: (id: string) => apiClient.get<DownholeEnvelope<DhComponentQr>>(`/downhole/components/${id}/qr`),
    },
    /**
     * Assemblies (api#883 writes). Every write answers with the full assembly,
     * so a caller never re-derives the string. The string is editable only
     * while `building` (409 otherwise); `makeup_torque` and `hole_size` are
     * IMPERIAL — convert a metric input with toStoredImperial before sending.
     */
    assemblies: {
      list: (params?: { status?: string; customer_id?: string; q?: string }) =>
        apiClient.get<DownholeEnvelope<DhAssembly[]>>('/downhole/assemblies', { params }),
      get: (id: string) => apiClient.get<DownholeEnvelope<DhAssemblyDetail>>(`/downhole/assemblies/${id}`),
      create: (body: DhAssemblyWrite) => apiClient.post<DownholeEnvelope<DhAssemblyDetail>>('/downhole/assemblies', body),
      update: (id: string, body: DhAssemblyWrite) =>
        apiClient.patch<DownholeEnvelope<DhAssemblyDetail>>(`/downhole/assemblies/${id}`, body),
      /** building → ready, ready → in_hole, in_hole → laid_down. */
      transition: (id: string, action: 'ready' | 'dispatch' | 'return') =>
        apiClient.post<DownholeEnvelope<DhAssemblyDetail>>(`/downhole/assemblies/${id}/${action}`),
      addComponent: (id: string, body: { component_id: string; position?: number; makeup_torque?: number | null; notes?: string | null }) =>
        apiClient.post<DownholeEnvelope<DhAssemblyDetail>>(`/downhole/assemblies/${id}/components`, body),
      /** A full permutation of the current string onto 1..n. */
      reorder: (id: string, order: { component_id: string; position: number }[]) =>
        apiClient.put<DownholeEnvelope<DhAssemblyDetail>>(`/downhole/assemblies/${id}/components/reorder`, { order }),
      removeComponent: (id: string, componentId: string) =>
        apiClient.delete<DownholeEnvelope<DhAssemblyDetail>>(`/downhole/assemblies/${id}/components/${componentId}`),
      /**
       * api#902. Only while `building` and with no runs (409 otherwise); the
       * string goes back to `available` in the same transaction.
       */
      remove: (id: string) =>
        apiClient.delete<{ success: boolean; data: { id: string; deleted: true; released_component_ids: string[] } }>(
          `/downhole/assemblies/${id}`,
        ),
      /**
       * api#905. A laid_down assembly only; one checklist line per tool still
       * in the string (none for a lost-in-hole tool). Every build role.
       * `od_measurement` is INCHES.
       */
      disassemble: (id: string, body: DhDisassembleWrite) =>
        apiClient.post<DownholeEnvelope<DhDisassemblyResult>>(`/downhole/assemblies/${id}/disassemble`, body),
    },
    /**
     * Runs (api#902 writes). Admin, office_staff, dispatcher and field_worker.
     * `run_in_at` / `run_out_at` are sent as the tenant's wall clock with no
     * offset ('2026-10-05T14:30'), which the API reads in the company's zone.
     * Measures IMPERIAL; footage, C&D hours and run_number are the server's.
     */
    runs: {
      /** `from` / `to` are tenant days, 'YYYY-MM-DD', on the run-in day. */
      list: (params?: { assembly_id?: string; from?: string; to?: string }) =>
        apiClient.get<DownholeEnvelope<DhRun[]>>('/downhole/runs', { params }),
      forAssembly: (assemblyId: string) => apiClient.get<DownholeEnvelope<DhRun[]>>(`/downhole/assemblies/${assemblyId}/runs`),
      /** One run, with the same billing state as the lists. */
      get: (id: string) => apiClient.get<DownholeEnvelope<DhRun>>(`/downhole/runs/${id}`),
      /** Open (run_in_at only, in_hole), closed (both), or `not_run: true`. */
      create: (assemblyId: string, body: DhRunWrite) =>
        apiClient.post<DownholeEnvelope<DhRun>>(`/downhole/assemblies/${assemblyId}/runs`, body),
      /** Close (run_out_at), correct, LIH set/clear. An invoiced run takes notes only. */
      update: (id: string, body: DhRunWrite) => apiClient.patch<DownholeEnvelope<DhRun>>(`/downhole/runs/${id}`, body),
      /** api#906: the line-by-line price; writes nothing. 409 RUN_STILL_OPEN for an open run. Read: admin, office_staff, dispatcher. */
      price: (id: string) => apiClient.get<DownholeEnvelope<DhRunPrice>>(`/downhole/runs/${id}/price`),
      /** Subtracts what the run added. Refused once invoiced or once the assembly is torn down. */
      remove: (id: string) => apiClient.delete<{ success: boolean; data: { id: string; deleted: true } }>(`/downhole/runs/${id}`),
    },
    /** Work orders (api#882 reads, api#905 writes). */
    workOrders: {
      /**
       * `assigned_to` (a user id, or 'me' for the caller) and `unassigned=true`
       * filter on the server (api#914); the two cannot be combined (400).
       */
      list: (params?: { status?: string; open?: 'true'; work_type?: string; component_id?: string; assigned_to?: string; unassigned?: 'true' }) =>
        apiClient.get<DownholeEnvelope<DhWorkOrder[]>>('/downhole/work-orders', { params }),
      get: (id: string) => apiClient.get<DownholeEnvelope<DhWorkOrder>>(`/downhole/work-orders/${id}`),
      /**
       * api#905. Status along WORK_ORDER_TRANSITIONS; completing an inspection-
       * type order needs `inspection_result`. Every build role.
       */
      update: (id: string, body: DhWorkOrderWrite) =>
        apiClient.patch<DownholeEnvelope<DhWorkOrder>>(`/downhole/work-orders/${id}`, body),
      /**
       * api#905. Opens an order on a tool that is in the shop (available,
       * maintenance or failed — 409 COMPONENT_NOT_IN_SHOP otherwise); an
       * available tool moves to maintenance. `inspection` posts to
       * /inspections, which takes the inspection types only and writes a
       * description when none is given; /work-orders needs one. Every build role.
       */
      open: (componentId: string, kind: 'inspection' | 'work_order', body: DhWorkOrderWrite) =>
        apiClient.post<DownholeEnvelope<DhWorkOrder>>(
          `/downhole/components/${componentId}/${kind === 'inspection' ? 'inspections' : 'work-orders'}`,
          body,
        ),
    },
    /**
     * Invoices, read only. The only invoice WRITE path is unified-invoices
     * (api docs/downhole-rebuild.md), and invoice create/send/PDF is batch 4 on
     * the API — not built.
     */
    invoices: {
      /** `from` / `to` are tenant days on the CREATED date, not the due date. */
      list: (params?: { status?: string; customer_id?: string; from?: string; to?: string }) =>
        apiClient.get<DownholeEnvelope<DhInvoice[]>>('/downhole/invoices', { params }),
      get: (id: string) => apiClient.get<DownholeEnvelope<DhInvoiceDetail>>(`/downhole/invoices/${id}`),
      /**
       * api#910, through unified-invoices (the one invoice write door). Admin
       * and office_staff. `run_ids` (1–100) must share one customer and one
       * jurisdiction. Refusals are 409s with `code`; RUNS_UNPRICED carries a
       * flat `problems[]` — /runs/:id/price has the structured detail.
       */
      generate: (body: DhGenerateInvoiceWrite) =>
        apiClient.post<{ success: boolean; data: DhInvoice }>('/unified-invoices/downhole/generate', body),
      /**
       * Emails the PDF to the customer's accounting (else main) contact.
       * API-A: an optional `cc` (at most 10; 400 CC_INVALID otherwise); no body
       * is the send it always was.
       */
      send: (id: string, body: { cc?: string[] } = {}) =>
        apiClient.post<{ success: boolean; data: DhInvoiceSent }>(`/unified-invoices/${id}/send`, body),
      /** API-A: who the send would go to, without sending. Admin, office staff. */
      recipient: (id: string) =>
        apiClient.get<{ success: boolean; data: DhInvoiceRecipientPreview }>(`/unified-invoices/${id}/recipient`),
      /** The PDF as a Blob (Content-Type application/pdf). Errors arrive as Blobs too: read them with getBlobApiErrorMessage. */
      pdf: (id: string) => apiClient.get<Blob>(`/unified-invoices/${id}/pdf`, { responseType: 'blob' }),
      /** Void (admin only). The runs become billable again; there is no rebill route — rebill is void, then generate. */
      voidInvoice: (id: string) => apiClient.delete<{ success: boolean; data: unknown }>(`/unified-invoices/${id}`),
    },
    /**
     * Field tickets (0461; api#923, #925, #927). The customer's sign-off before
     * a run is billed. Read: admin, office_staff, dispatcher (a ticket carries
     * prices). Write: admin, office_staff. None of these reads carries `meta`.
     *
     *   draft → office-approve (prices snapshotted) → send-to-customer → the
     *   company man approves or rejects on /ticket-review/:token; or the office
     *   records an approval from a stamped copy (customer-approval).
     *
     * A substantive PATCH un-signs an office-approved or rejected ticket back to
     * draft (`reverted_to_draft: true`); a customer-approved one takes `notes`
     * only (409 TICKET_CUSTOMER_APPROVED). Every 409 carries a `code`.
     */
    tickets: {
      list: (params?: { assembly_id?: string; customer_id?: string; status?: string }) =>
        apiClient.get<{ success: boolean; data: DhTicket[] }>('/downhole/tickets', { params }),
      get: (id: string) => apiClient.get<{ success: boolean; data: DhTicketDetail }>(`/downhole/tickets/${id}`),
      /** Closed or not-run runs of ONE assembly, one customer, one province/state, not ticketed or invoiced. */
      create: (body: DhTicketWrite) => apiClient.post<{ success: boolean; data: DhTicketDetail }>('/downhole/tickets', body),
      update: (id: string, body: DhTicketWrite) =>
        apiClient.patch<{ success: boolean; data: DhTicketDetail; reverted_to_draft: boolean }>(`/downhole/tickets/${id}`, body),
      /** Soft delete; frees the runs. Refused once customer-approved. */
      remove: (id: string) => apiClient.delete<{ success: boolean; data: { id: string; deleted: true } }>(`/downhole/tickets/${id}`),
      /** From draft or customer_rejected. Prices every run now; 409 RUNS_UNPRICED carries `problems[]`. No body. */
      officeApprove: (id: string) => apiClient.post<{ success: boolean; data: DhTicketDetail }>(`/downhole/tickets/${id}/office-approve`),
      /**
       * Emails the review link. Calling it again is a resend (same link, new
       * 7-day expiry). `email` overrides the customer's main contact / accounting
       * email. Answers `sent_to`. A failed email is a 502 EMAIL_SEND_FAILED.
       */
      send: (id: string, body: { email?: string; contact_name?: string; cc?: string[] } = {}) =>
        apiClient.post<{ success: boolean; data: DhTicketDetail; review_url: string; sent_to: string; sent_cc?: string[] }>(
          `/downhole/tickets/${id}/send-to-customer`,
          body,
        ),
      /**
       * API-A: who the send would go to, without sending — the send's own
       * resolver. `email` / `contact_name` preview what typing them would do.
       * Admin, office staff.
       */
      recipient: (id: string, params?: { email?: string; contact_name?: string }) =>
        apiClient.get<{ success: boolean; data: DhTicketRecipientPreview }>(`/downhole/tickets/${id}/recipient`, { params }),
      /** The signed PDF (office_approved and after; a draft is a 409). Errors arrive as Blobs. */
      pdf: (id: string) => apiClient.get<Blob>(`/downhole/tickets/${id}/pdf`, { responseType: 'blob' }),
      /** The stamped copy recorded with an office approval, hash-checked. Errors arrive as Blobs. */
      stampedFile: (id: string) => apiClient.get<Blob>(`/downhole/tickets/${id}/stamped-file`, { responseType: 'blob' }),
      /**
       * The office records the customer's approval from a stamped PDF / PNG /
       * JPEG (20 MB): `file`, `approved_by_name`, `approved_on` (a tenant day,
       * not in the future, not before the office approval). Office-approved only.
       */
      recordApproval: (id: string, form: FormData) =>
        // No Content-Type: the request interceptor strips it on multipart so
        // the browser sets the boundary.
        apiClient.post<{ success: boolean; data: DhTicketDetail }>(`/downhole/tickets/${id}/customer-approval`, form),
    },
    /** `start_date` / `end_date` are tenant days; omitted, the API uses the last 30 days. */
    reports: {
      get: (report: DhReport, params?: { start_date?: string; end_date?: string }) =>
        apiClient.get<DownholeEnvelope<unknown[]>>(`/downhole/reports/${report}`, { params }),
    },
    /**
     * The rate card (api#906). Read: admin, office_staff, dispatcher. Write:
     * admin, office_staff. A rate keys one category OR one component, for a
     * customer or (customer_id null) the company default, over calendar days.
     * Overlap with a live row → 409 RATE_OVERLAP (`conflicting_rate_id`).
     * DELETE is soft; restore can overlap too.
     */
    rates: {
      list: (params?: { customer_id?: string; category?: string; component_id?: string; on?: string; include_deleted?: 'true' }) =>
        apiClient.get<DownholeEnvelope<DhRate[]>>('/downhole/rates', { params }),
      create: (body: DhRateWrite) => apiClient.post<DownholeEnvelope<DhRate>>('/downhole/rates', body),
      update: (id: string, body: DhRateWrite) => apiClient.patch<DownholeEnvelope<DhRate>>(`/downhole/rates/${id}`, body),
      remove: (id: string) => apiClient.delete<DownholeEnvelope<DhRate>>(`/downhole/rates/${id}`),
      restore: (id: string) => apiClient.post<DownholeEnvelope<DhRate>>(`/downhole/rates/${id}/restore`),
    },
    templates: {
      /** Archived templates are listed with is_active false; `active: 'true'` leaves them out (api#902). */
      list: (params?: { active?: 'true' | 'false' }) =>
        apiClient.get<DownholeEnvelope<DhTemplate[]>>('/downhole/templates', { params }),
      get: (id: string) => apiClient.get<DownholeEnvelope<DhTemplateDetail>>(`/downhole/templates/${id}`),
      /** 409 on a duplicate name. There is no update route. */
      create: (body: DhTemplateWrite) => apiClient.post<DownholeEnvelope<DhTemplateDetail>>('/downhole/templates', body),
      /** DELETE archives (is_active false) — a template is never deleted; built assemblies keep the pointer. */
      archive: (id: string) => apiClient.delete<DownholeEnvelope<DhTemplateDetail>>(`/downhole/templates/${id}`),
      restore: (id: string) => apiClient.post<DownholeEnvelope<DhTemplateDetail>>(`/downhole/templates/${id}/restore`),
    },
  },

  /**
   * Pressure Testing — /api/v1/pressure-testing/* (fieldiq-api#907, bundle 1).
   * Every response carries `meta` (unit_system, pressure_unit, time_zone, today,
   * cal_warn_days); read it with `unwrapPt` (hooks/usePressureTesting.ts).
   *
   * PRESSURES ARE IN THE TENANT'S UNIT BOTH WAYS. The API converts kPa ↔ psi on
   * read and on write, so a spec body carries the figure the reader typed —
   * converting here as well would store psi × 6.89 twice.
   *
   * An instrument is an EQUIPMENT row with a specs row: `instruments.create`
   * attaches specs to an id from `api.equipment.create`, it does not make one.
   */
  pressureTesting: {
    settings: {
      get: () => apiClient.get<PtEnvelope<PtSettings>>('/pressure-testing/settings'),
      /** Answers `warnings` beside the data — ABSA outside Alberta saves, with a warning. */
      update: (body: PtSettingsUpdate) => apiClient.put<PtEnvelope<PtSettings>>('/pressure-testing/settings', body),
    },
    instruments: {
      /** `status` and `kind` filter server-side; `q` searches name, serial, model, maker. */
      list: (params?: { status?: string; kind?: string; q?: string }) =>
        apiClient.get<PtEnvelope<PtInstrument[]>>('/pressure-testing/instruments', { params }),
      get: (id: string) => apiClient.get<PtEnvelope<PtInstrumentDetail>>(`/pressure-testing/instruments/${id}`),
      /** 404 for an unknown equipment id, 409 when it already has specs. */
      create: (body: PtInstrumentCreate) => apiClient.post<PtEnvelope<PtInstrument>>('/pressure-testing/instruments', body),
      updateSpecs: (id: string, body: PtSpecsPatch) =>
        apiClient.patch<PtEnvelope<PtInstrument>>(`/pressure-testing/instruments/${id}/specs`, body),
      calibrations: (id: string) =>
        apiClient.get<PtEnvelope<PtCalibration[]>>(`/pressure-testing/instruments/${id}/calibrations`),
      /** Append-only: a correction is a new row. Answers the new row plus the instrument. */
      recordCalibration: (id: string, body: PtCalibrationCreate) =>
        apiClient.post<PtEnvelope<PtCalibration> & { instrument: PtInstrument }>(
          `/pressure-testing/instruments/${id}/calibrations`,
          body,
        ),
    },
    /**
     * Never-calibrated, overdue and due-soon instruments — never-calibrated first,
     * then most overdue (api#915). Retired equipment never alerts.
     */
    calibrationAlerts: () => apiClient.get<PtEnvelope<PtInstrument[]>>('/pressure-testing/calibrations/alerts'),

    /** Bundle 2 (fieldiq-api#915). Writing a type is PT_ADMIN; DELETE deactivates. */
    testTypes: {
      list: () => apiClient.get<PtEnvelope<PtTestType[]>>('/pressure-testing/test-types'),
      create: (body: PtTestTypeCreate) =>
        apiClient.post<PtEnvelope<PtTestType> & { created: boolean }>('/pressure-testing/test-types', body),
    },
    /**
     * Test jobs. `status`, `q`, `from` / `to` (scheduled_date) filter
     * server-side; the list is unpaginated. A PATCH carries a status change
     * (Start / Complete / Cancel) or fields; a refused Complete is a 400 with
     * `code: 'tests_incomplete'` and `pending_tests`.
     */
    jobs: {
      list: (params?: { status?: string; q?: string; from?: string; to?: string }) =>
        apiClient.get<PtEnvelope<PtJob[]>>('/pressure-testing/jobs', { params }),
      get: (id: string) => apiClient.get<PtEnvelope<PtJobDetail>>(`/pressure-testing/jobs/${id}`),
      create: (body: PtJobCreate) => apiClient.post<PtEnvelope<PtJob>>('/pressure-testing/jobs', body),
      update: (id: string, body: PtJobPatch) => apiClient.patch<PtEnvelope<PtJob>>(`/pressure-testing/jobs/${id}`, body),
      /**
       * The field door to completion (PT-3, api#917): PT_DISPATCH on any job, or
       * the job's own technician. Same rules as PATCH {status:'complete'} — a 400
       * `tests_incomplete` lists the tests in `pending_tests`.
       */
      complete: (id: string) => apiClient.post<PtEnvelope<PtJob>>(`/pressure-testing/jobs/${id}/complete`),
    },
    /**
     * Tests. The calibration guard runs at the API; a refusal is a 400 naming
     * the `field` and listing every failure in `errors`.
     */
    tests: {
      create: (jobId: string, body: PtTestWrite) =>
        apiClient.post<PtEnvelope<PtTest>>(`/pressure-testing/jobs/${jobId}/tests`, body),
      update: (id: string, body: PtTestWrite) => apiClient.patch<PtEnvelope<PtTest>>(`/pressure-testing/tests/${id}`, body),
      /**
       * The hold window is stamped by the SERVER (PT-3). The body carries the
       * start pressure only — never started_at / ended_at, which a field worker
       * is refused for (`hold_server_only`). Both answer `server_now` beside the
       * test so a device can correct its own clock against the server's.
       */
      holdStart: (id: string, body: { start_pressure?: number }) =>
        apiClient.post<PtEnvelope<PtTest> & { server_now?: string }>(`/pressure-testing/tests/${id}/hold/start`, body),
      holdStop: (id: string) =>
        apiClient.post<PtEnvelope<PtTest> & { server_now?: string }>(`/pressure-testing/tests/${id}/hold/stop`),
      /** Claim a CONFIRMED 'pt_test_chart' upload (entityId = this test) onto the test. */
      attachChart: (id: string, key: string) =>
        apiClient.post<PtEnvelope<PtTest>>(`/pressure-testing/tests/${id}/chart`, { key }),
    },
    /** The technician's own jobs: active, scheduled today or later, completed in the last 7 days. */
    field: {
      myJobs: () => apiClient.get<PtEnvelope<PtJob[]>>('/pressure-testing/field/my-jobs'),
    },
    /**
     * Certificates (PT-4, api#921). One per job, drafted when the job
     * completes; only a person issues it. Draft / issue / reject are
     * PT_DISPATCH. An issued certificate is immutable in the database.
     *
     *  - `draft` (re)builds the draft for a COMPLETE job: it creates one when
     *    the job has none, and is the only way back from rejected. Refused
     *    `already_issued` on an issued one.
     *  - `issue` assigns {prefix}-{YYYY}-{NNNN}, renders and stores the PDF,
     *    and with `{ email: true }` sends it to the job's customer contact.
     *    Refusals: already_issued, certificate_rejected, job_not_complete,
     *    no_contact_email.
     *  - `reject` needs a reason (reason_required); a draft only.
     *  - `pdf` is the stored PDF once issued, else a "DRAFT — NOT ISSUED" preview.
     */
    certificates: {
      list: (params?: { status?: string; customer_id?: string; from?: string; to?: string; q?: string }) =>
        apiClient.get<PtEnvelope<PtCertificateListRow[]>>('/pressure-testing/certificates', { params }),
      get: (id: string) => apiClient.get<PtEnvelope<PtCertificate>>(`/pressure-testing/certificates/${id}`),
      draft: (jobId: string) =>
        apiClient.post<PtEnvelope<PtCertificate>>(`/pressure-testing/jobs/${jobId}/certificate/draft`),
      issue: (id: string, body: { email?: boolean }) =>
        apiClient.post<PtEnvelope<PtCertificate>>(`/pressure-testing/certificates/${id}/issue`, body),
      reject: (id: string, reason: string) =>
        apiClient.post<PtEnvelope<PtCertificate>>(`/pressure-testing/certificates/${id}/reject`, { reason }),
      pdf: (id: string) => apiClient.get<Blob>(`/pressure-testing/certificates/${id}/pdf`, { responseType: 'blob' }),
    },
    /**
     * A technician's or witness's signature on a job, as a PNG data URL (≤1 MB).
     * A re-sign replaces. Allowed until the certificate is issued
     * (`certificate_issued`, 409). Answers the job's two signatures.
     */
    signatures: {
      create: (jobId: string, body: PtSignatureWrite) =>
        apiClient.post<PtEnvelope<PtSignatures>>(`/pressure-testing/jobs/${jobId}/signatures`, body),
    },
    /**
     * PT-5 (api#932): the rate card. Every route is PT_ADMIN. GET hides expired
     * rows unless `include_expired`; `customer_id: 'default'` is the company's
     * own rows. PATCH changes price, currency, dates and notes only — the key
     * is fixed (`rate_key_immutable`). DELETE is a soft delete. Overlapping live
     * rows for one key are a 409 `rate_overlap`.
     */
    rates: {
      list: (params?: { kind?: string; customer_id?: string; include_expired?: 'true' }) =>
        apiClient.get<PtEnvelope<PtRate[]>>('/pressure-testing/rates', { params }),
      create: (body: PtRateCreate) => apiClient.post<PtEnvelope<PtRate>>('/pressure-testing/rates', body),
      update: (id: string, body: PtRatePatch) => apiClient.patch<PtEnvelope<PtRate>>(`/pressure-testing/rates/${id}`, body),
      remove: (id: string) => apiClient.delete<PtEnvelope<{ id: string; deleted: boolean }>>(`/pressure-testing/rates/${id}`),
    },
    /** PT_READ — the consumables a line may name for a customer on a day. Names and units, no prices. */
    consumables: (params?: { customer_id?: string; on?: string }) =>
      apiClient.get<PtEnvelope<PtConsumable[]>>('/pressure-testing/consumables', { params }),
    /**
     * A customer's ticket mode (PT_DISPATCH). PUT `{ ticket_mode: null }` clears
     * it back to the company default. It applies to jobs created afterwards.
     * `list` (PT-6, api#939) is every customer's mode in one read.
     */
    customerSettings: {
      list: () => apiClient.get<PtEnvelope<PtCustomerSettings[]>>('/pressure-testing/customers/settings'),
      get: (customerId: string) =>
        apiClient.get<PtEnvelope<PtCustomerSettings>>(`/pressure-testing/customers/${customerId}/settings`),
      update: (customerId: string, ticketMode: PtTicketMode | null) =>
        apiClient.put<PtEnvelope<PtCustomerSettings>>(`/pressure-testing/customers/${customerId}/settings`, { ticket_mode: ticketMode }),
    },
    /**
     * Field tickets (PT-5). Every write answers the whole ticket. Lines change
     * on a draft only (`ticket_not_draft`); sign refuses `no_lines` and
     * `unrated_lines` (with `lines`); a signed ticket is corrected by void + a
     * new ticket (`forJob.create` on a complete job, or close-day). Only
     * PT_DISPATCH sends `rate` (403 `rate_office_only` otherwise).
     */
    tickets: {
      list: (params?: { status?: string; customer_id?: string; job_id?: string; from?: string; to?: string }) =>
        apiClient.get<PtEnvelope<PtTicket[]>>('/pressure-testing/field-tickets', { params }),
      get: (id: string) => apiClient.get<PtEnvelope<PtTicketDetail>>(`/pressure-testing/field-tickets/${id}`),
      pdf: (id: string) => apiClient.get<Blob>(`/pressure-testing/field-tickets/${id}/pdf`, { responseType: 'blob' }),
      forJob: (jobId: string) => apiClient.get<PtEnvelope<PtTicket[]>>(`/pressure-testing/jobs/${jobId}/tickets`),
      /** PT_DISPATCH — a replacement on a COMPLETE job (after a void). */
      create: (jobId: string) => apiClient.post<PtEnvelope<PtTicketDetail>>(`/pressure-testing/jobs/${jobId}/tickets`),
      /** per_day only; `work_date` is the tenant's day. Answers `created` and `lines_added`. */
      closeDay: (jobId: string, workDate: string) =>
        apiClient.post<PtEnvelope<PtTicketDetail> & { created?: boolean; lines_added?: number }>(
          `/pressure-testing/jobs/${jobId}/tickets/close-day`,
          { work_date: workDate },
        ),
      addLine: (ticketId: string, body: PtLineCreate) =>
        apiClient.post<PtEnvelope<PtTicketDetail> & { line_id?: string }>(`/pressure-testing/field-tickets/${ticketId}/lines`, body),
      updateLine: (lineId: string, body: PtLinePatch) =>
        apiClient.patch<PtEnvelope<PtTicketDetail>>(`/pressure-testing/field-ticket-lines/${lineId}`, body),
      /** An automatic line needs a reason (`reason_required`). */
      removeLine: (lineId: string, reason: string | null) =>
        apiClient.delete<PtEnvelope<PtTicketDetail>>(`/pressure-testing/field-ticket-lines/${lineId}`, {
          data: reason ? { reason } : {},
        }),
      reprice: (ticketId: string) =>
        apiClient.post<PtEnvelope<PtTicketDetail> & { lines_repriced?: number }>(`/pressure-testing/field-tickets/${ticketId}/reprice`),
      sign: (ticketId: string, body: PtTicketSign) =>
        apiClient.post<PtEnvelope<PtTicketDetail>>(`/pressure-testing/field-tickets/${ticketId}/sign`, body),
      approve: (ticketId: string) =>
        apiClient.post<PtEnvelope<PtTicketDetail>>(`/pressure-testing/field-tickets/${ticketId}/approve`),
      void: (ticketId: string, reason: string) =>
        apiClient.post<PtEnvelope<PtTicketDetail>>(`/pressure-testing/field-tickets/${ticketId}/void`, { reason }),
    },
    /**
     * Invoices (PT-6, fieldiq-api#939) — PT's own doors. Built only from
     * approved field tickets. Responses carry no `meta`. Refusals are 409s
     * (400s for a malformed body) with plain-English `error` and a `code`.
     * Write: admin, office_staff; read: those plus dispatcher.
     *
     * There is no PT list read: `list` is the unified register's PT branch
     * (GET /unified-invoices?vertical=pressure_testing), read only.
     */
    invoices: {
      list: (params?: { status?: string; customerId?: string; page?: number; limit?: number }) =>
        apiClient.get<{ success: boolean; data: PtInvoiceListRow[]; pagination?: { page: number; limit: number; total: number; totalPages: number } }>(
          '/unified-invoices',
          { params: { ...params, vertical: 'pressure_testing' } },
        ),
      billable: () => apiClient.get<PtEnvelope<PtBillableJob[]>>('/pressure-testing/invoices/billable'),
      generate: (body: PtInvoiceGenerate) => apiClient.post<PtEnvelope<PtInvoice>>('/pressure-testing/invoices/generate', body),
      get: (id: string) => apiClient.get<PtEnvelope<PtInvoice>>(`/pressure-testing/invoices/${id}`),
      recipient: (id: string) => apiClient.get<PtEnvelope<PtInvoiceRecipient>>(`/pressure-testing/invoices/${id}/recipient`),
      /** `to` is an address typed for this send; absent, the API's resolver picks one. */
      send: (id: string, body: { to?: string; cc?: string[] }) =>
        apiClient.post<PtEnvelope<PtInvoiceSent>>(`/pressure-testing/invoices/${id}/send`, body),
      /** The PDF as a Blob. Errors arrive as Blobs too: read them with getBlobApiErrorMessage. */
      pdf: (id: string) => apiClient.get<Blob>(`/pressure-testing/invoices/${id}/pdf`, { responseType: 'blob' }),
      /** Frees the invoice's tickets to bill again. A void invoice is final. */
      void: (id: string, reason: string) =>
        apiClient.post<PtEnvelope<PtInvoice & { released_ticket_ids: string[] }>>(`/pressure-testing/invoices/${id}/void`, { reason }),
    },
  },

  /**
   * Pipeline & Facility — /api/v1/pipeline-facility/* (fieldiq-api#1196, PF-1).
   * Every response is `{ success, data, meta }`; read it with `unwrapPf`
   * (hooks/usePipelineFacility.ts). READS are camelCase, WRITES snake_case.
   * Chainage, OD / WT and MOP travel in the tenant's units (`meta.units`) both
   * ways; the API converts, so nothing here does.
   */
  /**
   * Earthworks (EW-1). camelCase reads, snake_case writes; every body carries
   * meta (units, currency, holdbackLabel). Refusals carry a `code` — read them
   * with `ewRefusal` (lib/earthworks).
   */
  earthworks: {
    meta: () => apiClient.get<EwEnvelope<EwCatalog>>('/earthworks/meta'),
    settings: {
      get: () => apiClient.get<EwEnvelope<EwSettings>>('/earthworks/settings'),
      update: (body: EwSettingsUpdate) => apiClient.put<EwEnvelope<EwSettings>>('/earthworks/settings', body),
      /** Prefix + next number per series; GET admin/office, PUT admin only (api 0617). */
      numbering: {
        get: () => apiClient.get<EwEnvelope<EwNumbering>>('/earthworks/settings/numbering'),
        update: (body: EwNumberingUpdate) => apiClient.put<EwEnvelope<EwNumbering>>('/earthworks/settings/numbering', body),
      },
    },
    customerSettings: {
      get: (customerId: string) =>
        apiClient.get<EwEnvelope<EwCustomerSettings>>(`/earthworks/customers/${customerId}/settings`),
      update: (customerId: string, body: EwCustomerSettingsUpdate) =>
        apiClient.put<EwEnvelope<EwCustomerSettings>>(`/earthworks/customers/${customerId}/settings`, body),
    },
    sites: {
      /** Filters: kind, customerId, status, q (name, address, city, legal land, customer). */
      list: (params?: { kind?: string; customerId?: string; status?: string; q?: string }) =>
        apiClient.get<EwEnvelope<EwSite[]>>('/earthworks/sites', { params }),
      get: (id: string) => apiClient.get<EwEnvelope<EwSiteDetail>>(`/earthworks/sites/${id}`),
      create: (body: EwSiteWrite) => apiClient.post<EwEnvelope<EwSiteDetail>>('/earthworks/sites', body),
      /** 409 `site_has_jobs` when a site with jobs is moved to another customer. */
      update: (id: string, body: EwSiteWrite) => apiClient.patch<EwEnvelope<EwSiteDetail>>(`/earthworks/sites/${id}`, body),
      /** 409 `site_has_live_jobs` while a draft, scheduled or in-progress job sits on it. */
      remove: (id: string) => apiClient.delete<EwEnvelope<EwSiteDeleted>>(`/earthworks/sites/${id}`),
    },
    jobs: {
      /** Filters: status, jobType, siteId, customerId, from/to (scheduled start), q. A field hand sees only crewed jobs. */
      list: (params?: { status?: string; jobType?: string; siteId?: string; customerId?: string; from?: string; to?: string; q?: string }) =>
        apiClient.get<EwEnvelope<EwJob[]>>('/earthworks/jobs', { params }),
      get: (id: string) => apiClient.get<EwEnvelope<EwJobDetail>>(`/earthworks/jobs/${id}`),
      create: (body: EwJobWrite) => apiClient.post<EwEnvelope<EwJobDetail>>('/earthworks/jobs', body),
      /** A status move is a PATCH of `status`; cancelling takes `cancel_reason`. */
      update: (id: string, body: EwJobWrite) => apiClient.patch<EwEnvelope<EwJobDetail>>(`/earthworks/jobs/${id}`, body),
      /** Draft or scheduled only. */
      remove: (id: string) => apiClient.delete<EwEnvelope<{ id: string; deleted: boolean }>>(`/earthworks/jobs/${id}`),
      /** 0600: how trucks bill on the job. Admin / office only. */
      setTruckBilling: (id: string, body: { truck_billing_basis: EwTruckBillingBasis }) =>
        apiClient.put<EwEnvelope<EwTruckBilling>>(`/earthworks/jobs/${id}/billing`, body),
      /** Employee Hours: clock-in vs daily-log hours by worker and day (office roles). */
      employeeHours: (id: string, params?: { from?: string; to?: string }) =>
        apiClient.get<EwEnvelope<EwEmployeeHours>>(`/earthworks/jobs/${id}/employee-hours`, { params }),
    },
    /** One worker's Employee Hours across every earthworks job (office roles). */
    employees: {
      hours: (userId: string, params?: { from?: string; to?: string }) =>
        apiClient.get<EwEnvelope<EwEmployeeHours>>(`/earthworks/employees/${userId}/hours`, { params }),
    },
    crew: {
      add: (jobId: string, body: EwCrewWrite) => apiClient.post<EwEnvelope<EwCrew>>(`/earthworks/jobs/${jobId}/crew`, body),
      update: (id: string, body: EwCrewWrite) => apiClient.patch<EwEnvelope<EwCrew>>(`/earthworks/crew/${id}`, body),
      remove: (id: string) => apiClient.delete<EwEnvelope<{ id: string; deleted: boolean }>>(`/earthworks/crew/${id}`),
    },
    jobEquipment: {
      add: (jobId: string, body: EwJobEquipmentWrite) =>
        apiClient.post<EwEnvelope<EwJobEquipment>>(`/earthworks/jobs/${jobId}/equipment`, body),
      update: (id: string, body: EwJobEquipmentWrite) =>
        apiClient.patch<EwEnvelope<EwJobEquipment>>(`/earthworks/job-equipment/${id}`, body),
      remove: (id: string) => apiClient.delete<EwEnvelope<{ id: string; deleted: boolean }>>(`/earthworks/job-equipment/${id}`),
    },
    rates: {
      /** Filters: customer_id ('default' = the tenant's default card), kind, on (a calendar day). */
      list: (params?: { customer_id?: string; kind?: string; on?: string }) =>
        apiClient.get<EwEnvelope<EwRate[]>>('/earthworks/rates', { params }),
      get: (id: string) => apiClient.get<EwEnvelope<EwRate>>(`/earthworks/rates/${id}`),
      /** 409 `rate_overlap` when another rate with this code covers some of the same days for the same customer. */
      create: (body: EwRateWrite) => apiClient.post<EwEnvelope<EwRate>>('/earthworks/rates', body),
      update: (id: string, body: EwRateWrite) => apiClient.patch<EwEnvelope<EwRate>>(`/earthworks/rates/${id}`, body),
      remove: (id: string) => apiClient.delete<EwEnvelope<{ id: string; deleted: boolean }>>(`/earthworks/rates/${id}`),
    },
    /** EW-2: the office half of an Earthworks quote — link state, deposit (recorded only), the job it became. */
    quotes: {
      state: (quoteId: string) => apiClient.get<EwEnvelope<EwQuoteState>>(`/earthworks/quotes/${quoteId}/state`),
      /** 409 `deposit_received` once received; 409 `quote_closed` on a declined / expired quote. */
      setDeposit: (quoteId: string, body: EwDepositWrite) =>
        apiClient.put<EwEnvelope<EwQuoteDeposit>>(`/earthworks/quotes/${quoteId}/deposit`, body),
      removeDeposit: (quoteId: string) => apiClient.delete<EwEnvelope<null>>(`/earthworks/quotes/${quoteId}/deposit`),
      markReceived: (quoteId: string, body: EwDepositReceivedWrite) =>
        apiClient.post<EwEnvelope<EwQuoteDeposit>>(`/earthworks/quotes/${quoteId}/deposit/received`, body),
      undoReceived: (quoteId: string) =>
        apiClient.delete<EwEnvelope<EwQuoteDeposit>>(`/earthworks/quotes/${quoteId}/deposit/received`),
    },
    /**
     * EW-3: AI quoting (fieldiq-api 0583). A run is a DRAFT. Claude reads the photos or
     * plans as text, and the API computes and prices every line from the rate card.
     * `create` answers 202, or 402 `AI_CAP_REACHED` at the monthly cap, carrying the budget.
     */
    aiQuotes: {
      list: () => apiClient.get<EwEnvelope<EwAiRun[]>>('/earthworks/ai-quotes'),
      budget: () => apiClient.get<EwEnvelope<AiBudget>>('/earthworks/ai-quotes/budget'),
      get: (id: string) => apiClient.get<EwEnvelope<EwAiRun>>(`/earthworks/ai-quotes/${id}`),
      // FormData: no Content-Type here — the request interceptor leaves it to
      // the browser so the multipart boundary is set.
      create: (form: FormData) => apiClient.post<EwEnvelope<EwAiRun>>('/earthworks/ai-quotes', form),
      update: (id: string, body: EwAiRunPatch) => apiClient.patch<EwEnvelope<EwAiRun>>(`/earthworks/ai-quotes/${id}`, body),
      retry: (id: string) => apiClient.post<EwEnvelope<EwAiRun>>(`/earthworks/ai-quotes/${id}/retry`, {}),
      /** 422 INPUTS_MISSING / NO_PRICED_LINES, 409 ALREADY_QUOTED. */
      createQuote: (id: string) => apiClient.post<EwEnvelope<EwAiQuoteCreated>>(`/earthworks/ai-quotes/${id}/quote`, {}),
    },
    /**
     * EW-8 (fieldiq-api 0609): quoted vs actual. The API computes every figure,
     * variance and suggestion; a suggestion is a draft the office accepts or
     * dismisses. Accepting changes the NEXT AI quote's default only.
     */
    qva: {
      job: (jobId: string) => apiClient.get<EwEnvelope<EwQvaJob>>(`/earthworks/jobs/${jobId}/quoted-vs-actual`),
      index: () => apiClient.get<EwEnvelope<EwQvaTypeRow[]>>('/earthworks/quoted-vs-actual'),
      jobType: (jobType: string) =>
        apiClient.get<EwEnvelope<EwQvaJobType>>(`/earthworks/quoted-vs-actual/job-types/${encodeURIComponent(jobType)}`),
      /** 400 `no_ai_quote`, 409 `below_threshold`. */
      generate: (jobType: string) =>
        apiClient.post<EwEnvelope<EwQvaGenerated>>(`/earthworks/quoted-vs-actual/job-types/${encodeURIComponent(jobType)}/suggestions`, {}),
      accept: (id: string) => apiClient.post<EwEnvelope<EwQvaAccepted>>(`/earthworks/quoted-vs-actual/suggestions/${id}/accept`, {}),
      dismiss: (id: string) => apiClient.post<EwEnvelope<EwQvaSuggestion>>(`/earthworks/quoted-vs-actual/suggestions/${id}/dismiss`, {}),
      /** 422 `narrative_discarded`, 502 `narrative_failed`, 402 at the AI cap. */
      narrative: (jobType: string) =>
        apiClient.post<EwEnvelope<EwQvaNarrativeResult>>(`/earthworks/quoted-vs-actual/job-types/${encodeURIComponent(jobType)}/narrative`, {}),
    },
    /**
     * EW-4 (fieldiq-api#1284): daily logs. Office reads camelCase. One log per
     * job per day: a second POST for the day is 409 `daily_log_exists`.
     */
    dailyLogs: {
      list: (jobId: string) => apiClient.get<EwEnvelope<EwDailyLogSummary[]>>(`/earthworks/jobs/${jobId}/daily-logs`),
      create: (jobId: string, body: EwDailyLogWrite & { work_date: string }) =>
        apiClient.post<EwEnvelope<EwDailyLog>>(`/earthworks/jobs/${jobId}/daily-logs`, body),
      get: (id: string) => apiClient.get<EwEnvelope<EwDailyLog>>(`/earthworks/daily-logs/${id}`),
      update: (id: string, body: EwDailyLogWrite) => apiClient.patch<EwEnvelope<EwDailyLog>>(`/earthworks/daily-logs/${id}`, body),
      /** Dispatch roles. 409 `job_closed` on a completed / cancelled job. */
      remove: (id: string) => apiClient.delete<EwEnvelope<{ id: string; deleted: boolean }>>(`/earthworks/daily-logs/${id}`),
    },
    /** That day's pre-use checks on the job's units (date defaults to the tenant's today). */
    preUseChecks: (jobId: string, date?: string) =>
      apiClient.get<EwEnvelope<EwPreUseCheck[]>>(`/earthworks/jobs/${jobId}/pre-use-checks`, { params: date ? { date } : undefined }),
    rentIns: {
      list: (jobId: string) => apiClient.get<EwEnvelope<EwRentIn[]>>(`/earthworks/jobs/${jobId}/rent-ins`),
      create: (jobId: string, body: EwRentInWrite) => apiClient.post<EwEnvelope<EwRentIn>>(`/earthworks/jobs/${jobId}/rent-ins`, body),
      /** 409 `rent_in_hours_outside_dates` when logged hours fall outside the new window. */
      update: (id: string, body: EwRentInWrite) => apiClient.patch<EwEnvelope<EwRentIn>>(`/earthworks/rent-ins/${id}`, body),
      /** 409 `rent_in_has_hours` once a log carries its hours. */
      remove: (id: string) => apiClient.delete<EwEnvelope<{ id: string; deleted: boolean }>>(`/earthworks/rent-ins/${id}`),
    },
    locates: {
      list: (siteId: string, date?: string) =>
        apiClient.get<EwEnvelope<EwLocate[]>>(`/earthworks/sites/${siteId}/locates`, { params: date ? { date } : undefined }),
      create: (siteId: string, body: EwLocateWrite) => apiClient.post<EwEnvelope<EwLocate>>(`/earthworks/sites/${siteId}/locates`, body),
      update: (id: string, body: EwLocateWrite) => apiClient.patch<EwEnvelope<EwLocate>>(`/earthworks/locates/${id}`, body),
      remove: (id: string) => apiClient.delete<EwEnvelope<{ id: string; deleted: boolean }>>(`/earthworks/locates/${id}`),
    },
    permits: {
      list: (jobId: string) => apiClient.get<EwEnvelope<EwPermit[]>>(`/earthworks/jobs/${jobId}/permits`),
      create: (jobId: string, body: EwPermitWrite) => apiClient.post<EwEnvelope<EwPermit>>(`/earthworks/jobs/${jobId}/permits`, body),
      update: (id: string, body: EwPermitWrite) => apiClient.patch<EwEnvelope<EwPermit>>(`/earthworks/permits/${id}`, body),
      remove: (id: string) => apiClient.delete<EwEnvelope<{ id: string; deleted: boolean }>>(`/earthworks/permits/${id}`),
    },
    hazmat: {
      list: (jobId: string) => apiClient.get<EwEnvelope<EwHazmat[]>>(`/earthworks/jobs/${jobId}/hazmat`),
      create: (jobId: string, body: EwHazmatWrite) => apiClient.post<EwEnvelope<EwHazmat>>(`/earthworks/jobs/${jobId}/hazmat`, body),
      update: (id: string, body: EwHazmatWrite) => apiClient.patch<EwEnvelope<EwHazmat>>(`/earthworks/hazmat/${id}`, body),
      remove: (id: string) => apiClient.delete<EwEnvelope<{ id: string; deleted: boolean }>>(`/earthworks/hazmat/${id}`),
    },
    /** Warnings never block work. Ack is the "Carry on" record. */
    warnings: {
      list: (jobId: string, date?: string) =>
        apiClient.get<EwEnvelope<EwWarning[]>>(`/earthworks/jobs/${jobId}/warnings`, { params: date ? { date } : undefined }),
      ack: (jobId: string, body: { kinds: string[]; work_date?: string }) =>
        apiClient.post<EwEnvelope<EwWarning[]>>(`/earthworks/jobs/${jobId}/warnings/ack`, body),
      acks: (jobId: string) => apiClient.get<EwEnvelope<EwWarningAck[]>>(`/earthworks/jobs/${jobId}/warning-acks`),
    },
    /** The /field surface: SNAKE_CASE reads, crew only (403 `not_on_crew`). */
    field: {
      myJobs: () => apiClient.get<EwEnvelope<EwFieldJob[]>>('/earthworks/field/my-jobs'),
      myJob: (id: string) => apiClient.get<EwEnvelope<EwFieldJobDetail>>(`/earthworks/field/my-jobs/${id}`),
      /** `data: null` when no log is written for the day; `day_warnings` still carries the warnings. */
      log: (jobId: string, day: string) =>
        apiClient.get<EwEnvelope<EwFieldLog | null>>(`/earthworks/field/jobs/${jobId}/daily-logs/${day}`),
      /** An upsert by day: 201 created, 200 updated. */
      saveLog: (jobId: string, day: string, body: EwDailyLogWrite) =>
        apiClient.put<EwEnvelope<EwFieldLog>>(`/earthworks/field/jobs/${jobId}/daily-logs/${day}`, body),
      /** EW-5. A field_worker's ticket reads carry no prices. */
      tickets: (jobId: string) => apiClient.get<EwEnvelope<EwFieldTicket[]>>(`/earthworks/field/jobs/${jobId}/tickets`),
      ticket: (id: string) => apiClient.get<EwEnvelope<EwFieldTicket>>(`/earthworks/field/tickets/${id}`),
      createTicket: (jobId: string, body: { work_date?: string } = {}) =>
        apiClient.post<EwEnvelope<EwFieldTicket>>(`/earthworks/field/jobs/${jobId}/tickets`, body),
      /** The customer signs on the phone. 409 `ticket_unpriced` / `ticket_empty` / `ticket_not_draft`. */
      signTicket: (id: string, body: EwPhoneSignWrite) =>
        apiClient.post<EwEnvelope<EwFieldTicket>>(`/earthworks/field/tickets/${id}/sign`, body),
      paperTicket: (id: string, body: EwPaperSignWrite) =>
        apiClient.post<EwEnvelope<EwFieldTicket>>(`/earthworks/field/tickets/${id}/paper`, body),
      changeOrders: (jobId: string) =>
        apiClient.get<EwEnvelope<EwFieldChangeOrder[]>>(`/earthworks/field/jobs/${jobId}/change-orders`),
      changeOrder: (id: string) => apiClient.get<EwEnvelope<EwFieldChangeOrder>>(`/earthworks/field/change-orders/${id}`),
      signChangeOrder: (id: string, body: EwPhoneSignWrite) =>
        apiClient.post<EwEnvelope<EwFieldChangeOrder>>(`/earthworks/field/change-orders/${id}/sign`, body),
    },
    /**
     * EW-5 (fieldiq-api#1290): field tickets (EWT-0001). Lines change only while
     * a draft; a signature (phone or paper photo) fixes the subtotal; the office
     * approves (admin, office_staff). A customer-signed copy attaches in ANY
     * status, after invoicing included. Every upload_id is a confirmed `ew_job`
     * upload against the ticket's JOB.
     */
    tickets: {
      list: (params?: EwTicketFilters) => apiClient.get<EwTicketList>('/earthworks/tickets', { params }),
      get: (id: string) => apiClient.get<EwEnvelope<EwTicketDetail>>(`/earthworks/tickets/${id}`),
      /** 409 `ticket_exists` (carrying existingId) when the job / day already has a live ticket. */
      create: (jobId: string, body: { work_date?: string } = {}) =>
        apiClient.post<EwEnvelope<EwTicketDetail> & { built?: EwTicketBuilt }>(`/earthworks/jobs/${jobId}/tickets`, body),
      rebuild: (id: string) =>
        apiClient.post<EwEnvelope<EwTicketDetail> & { built?: EwTicketBuilt }>(`/earthworks/tickets/${id}/rebuild`, {}),
      update: (id: string, body: { notes: string | null }) => apiClient.patch<EwEnvelope<EwTicketDetail>>(`/earthworks/tickets/${id}`, body),
      addLine: (id: string, body: EwTicketLineWrite) => apiClient.post<EwEnvelope<EwTicketDetail>>(`/earthworks/tickets/${id}/lines`, body),
      updateLine: (id: string, lineId: string, body: EwTicketLineWrite) =>
        apiClient.patch<EwEnvelope<EwTicketDetail>>(`/earthworks/tickets/${id}/lines/${lineId}`, body),
      removeLine: (id: string, lineId: string) => apiClient.delete<EwEnvelope<EwTicketDetail>>(`/earthworks/tickets/${id}/lines/${lineId}`),
      paper: (id: string, body: EwPaperSignWrite) => apiClient.post<EwEnvelope<EwTicketDetail>>(`/earthworks/tickets/${id}/paper`, body),
      signedCopy: (id: string, body: { upload_id: string; notes?: string }) =>
        apiClient.post<EwEnvelope<EwTicketDetail>>(`/earthworks/tickets/${id}/signed-copy`, body),
      approve: (id: string) => apiClient.post<EwEnvelope<EwTicketDetail>>(`/earthworks/tickets/${id}/approve`, {}),
      /** 409 `ticket_invoiced` once on an invoice. */
      void: (id: string, body: { reason: string }) => apiClient.post<EwEnvelope<EwTicketDetail>>(`/earthworks/tickets/${id}/void`, body),
    },
    /** EW-5: change orders (EWC-0001). Approval is the phone signature or the customer's link. */
    changeOrders: {
      list: (params?: { job_id?: string; status?: string }) =>
        apiClient.get<EwEnvelope<EwChangeOrder[]>>('/earthworks/change-orders', { params }),
      get: (id: string) => apiClient.get<EwEnvelope<EwChangeOrderDetail>>(`/earthworks/change-orders/${id}`),
      create: (jobId: string, body: EwCoWrite) =>
        apiClient.post<EwEnvelope<EwChangeOrderDetail>>(`/earthworks/jobs/${jobId}/change-orders`, body),
      /** An edit of a SENT change order returns it to draft and closes its link. */
      update: (id: string, body: EwCoWrite) => apiClient.patch<EwEnvelope<EwChangeOrderDetail>>(`/earthworks/change-orders/${id}`, body),
      addLine: (id: string, body: EwTicketLineWrite) =>
        apiClient.post<EwEnvelope<EwChangeOrderDetail>>(`/earthworks/change-orders/${id}/lines`, body),
      updateLine: (id: string, lineId: string, body: EwTicketLineWrite) =>
        apiClient.patch<EwEnvelope<EwChangeOrderDetail>>(`/earthworks/change-orders/${id}/lines/${lineId}`, body),
      removeLine: (id: string, lineId: string) =>
        apiClient.delete<EwEnvelope<EwChangeOrderDetail>>(`/earthworks/change-orders/${id}/lines/${lineId}`),
      /** Mints (or rotates) the customer link; emails it when `email` is given. */
      send: (id: string, body: { email?: string } = {}) => apiClient.post<EwCoSent>(`/earthworks/change-orders/${id}/send`, body),
      void: (id: string, body: { reason: string }) =>
        apiClient.post<EwEnvelope<EwChangeOrderDetail>>(`/earthworks/change-orders/${id}/void`, body),
    },
    /**
     * EW-6 (fieldiq-api#1300): invoices (EWI-). Reads for the price-reading
     * roles; every write admin / office_staff. Refusals are 409 with a `code`,
     * read through ewRefusal (lib/earthworks). The list itself lives in
     * Finance → Invoices; these are the Earthworks record and generate doors.
     */
    invoices: {
      list: (params?: EwInvoiceFilters) => apiClient.get<EwEnvelopeOf<EwInvoiceRow[]>>('/earthworks/invoices', { params }),
      /** Approved tickets on no live invoice, grouped by customer + province / state + currency. */
      billable: () => apiClient.get<EwEnvelopeOf<EwBillableGroup[]>>('/earthworks/invoices/billable'),
      get: (id: string) => apiClient.get<EwEnvelopeOf<EwInvoice>>(`/earthworks/invoices/${id}`),
      /** `job_id` OR `ticket_ids`, never both. */
      fromTickets: (body: EwFromTicketsWrite) => apiClient.post<EwEnvelopeOf<EwInvoice>>('/earthworks/invoices/from-tickets', body),
      /** An accepted quote; `pct` 100 (default) bills it in full, less is a progress draw. */
      fromQuote: (body: EwFromQuoteWrite) => apiClient.post<EwEnvelopeOf<EwInvoice>>('/earthworks/invoices/from-quote', body),
      /** An APPROVED change order's lines not yet on a ticket or another invoice. */
      fromChangeOrder: (body: EwFromChangeOrderWrite) =>
        apiClient.post<EwEnvelopeOf<EwInvoice>>('/earthworks/invoices/from-change-order', body),
      releaseHoldback: (body: EwReleaseHoldbackWrite) =>
        apiClient.post<EwEnvelopeOf<EwInvoice>>('/earthworks/invoices/release-holdback', body),
      /** A DRAFT's paperwork only (due date, PO, AFE, cost code, notes); never its money. */
      update: (id: string, body: EwInvoiceDraftWrite) => apiClient.patch<EwEnvelopeOf<EwInvoice>>(`/earthworks/invoices/${id}`, body),
      /** Draft → sent: the office sent it itself (no email from here yet). */
      markSent: (id: string) => apiClient.post<EwEnvelopeOf<EwInvoice>>(`/earthworks/invoices/${id}/mark-sent`, {}),
      recordPayment: (id: string, body: EwPaymentWrite) =>
        apiClient.post<EwEnvelopeOf<EwInvoice>>(`/earthworks/invoices/${id}/payments`, body),
      /** Final: releases the tickets and un-applies a deposit. */
      void: (id: string, body: { reason: string }) => apiClient.post<EwEnvelopeOf<EwInvoice>>(`/earthworks/invoices/${id}/void`, body),
      /** EW-6b: make (201) or return (200) the live no-login "Pay now" link. A sent invoice only. */
      createPayLink: (id: string) => apiClient.post<EwEnvelopeOf<EwPayLink>>(`/earthworks/invoices/${id}/pay-link`, {}),
      /** EW-6b: revoke the link; returns the invoice with payLink null. */
      revokePayLink: (id: string) => apiClient.delete<EwEnvelopeOf<EwInvoice>>(`/earthworks/invoices/${id}/pay-link`),
      /**
       * S10 (fieldiq-api#1316): push a sent or paid invoice to QuickBooks — every line
       * signed (work, holdback, deposit), so QuickBooks' total is FieldIQ's.
       * 409 with a `code` when it refuses: QBO_MAPPING_REQUIRED, QBO_ALREADY_PUSHED,
       * QBO_PUSH_IN_PROGRESS, QBO_DOCNUMBER_EXISTS, QBO_RECONNECT_REQUIRED, QBO_NOT_CONNECTED.
       */
      qboPush: (id: string) => apiClient.post<EwEnvelopeOf<EwQboPushResult>>(`/earthworks/invoices/${id}/qbo-push`, {}),
    },
    /** S10: the ew: items, tax codes and customers sent invoices need bound before a push. */
    qboMappingNeeds: () => apiClient.get<EwEnvelopeOf<EwQboMappingNeeds>>('/earthworks/qbo/mapping-needs'),
    /** EW-6: a job's holdback / retainage — withheld, released, outstanding. */
    jobHoldback: (jobId: string) => apiClient.get<EwEnvelopeOf<EwHoldback>>(`/earthworks/jobs/${jobId}/holdback`),
    /** EW-6: the invoices made from a quote, and how much of it is drawn / left (the API's sum). */
    quoteDraws: (quoteId: string) => apiClient.get<EwEnvelopeOf<EwQuoteDraws>>(`/earthworks/quotes/${quoteId}/invoice-draws`),
  },
  /**
   * Well Servicing — /api/v1/well-servicing/* (fieldiq-api#1269, WS-1).
   * camelCase reads, snake_case writes; every body but the activity-code list
   * carries the tenant meta (country decides UWI vs API number). Refusals carry
   * a `code` — read them with `wsRefusal` (lib/well-servicing).
   */
  wellServicing: {
    meta: () => apiClient.get<WsEnvelope<WsCatalog>>('/well-servicing/meta'),
    settings: {
      get: () => apiClient.get<WsEnvelope<WsSettings>>('/well-servicing/settings'),
      update: (body: WsSettingsUpdate) => apiClient.put<WsEnvelope<WsSettings>>('/well-servicing/settings', body),
    },
    customerSettings: {
      get: (customerId: string) =>
        apiClient.get<WsEnvelope<WsCustomerSettings>>(`/well-servicing/customers/${customerId}/settings`),
      update: (customerId: string, body: WsCustomerSettingsUpdate) =>
        apiClient.put<WsEnvelope<WsCustomerSettings>>(`/well-servicing/customers/${customerId}/settings`, body),
    },
    wells: {
      /** Filters: customerId, fluidType, provinceState, q (name, UWI, API number, licence, location, field, pool, customer). */
      list: (params?: { customerId?: string; fluidType?: string; provinceState?: string; q?: string }) =>
        apiClient.get<WsEnvelope<WsWell[]>>('/well-servicing/wells', { params }),
      get: (id: string) => apiClient.get<WsEnvelope<WsWell>>(`/well-servicing/wells/${id}`),
      /** 409 `duplicate_well` (field, existingWellId) when a live well holds the same UWI or API number. */
      create: (body: WsWellWrite) => apiClient.post<WsEnvelope<WsWell>>('/well-servicing/wells', body),
      update: (id: string, body: WsWellWrite) => apiClient.patch<WsEnvelope<WsWell>>(`/well-servicing/wells/${id}`, body),
      remove: (id: string) => apiClient.delete<WsEnvelope<WsDeleted>>(`/well-servicing/wells/${id}`),
    },
    rigs: {
      list: () => apiClient.get<WsEnvelope<WsRig[]>>('/well-servicing/rigs'),
      /** The rig plus its crews and their members. */
      get: (id: string) => apiClient.get<WsEnvelope<WsRigDetail>>(`/well-servicing/rigs/${id}`),
      /** 409 `rig_exists_for_equipment` / `duplicate_rig_number` (existingRigId). */
      create: (body: WsRigWrite) => apiClient.post<WsEnvelope<WsRig>>('/well-servicing/rigs', body),
      update: (id: string, body: WsRigWrite) => apiClient.patch<WsEnvelope<WsRig>>(`/well-servicing/rigs/${id}`, body),
      /** 409 `rig_has_crews` while the rig has a live crew. */
      remove: (id: string) => apiClient.delete<WsEnvelope<WsDeleted>>(`/well-servicing/rigs/${id}`),
    },
    crews: {
      /** 409 `duplicate_crew_name`. */
      create: (rigId: string, body: WsCrewWrite) => apiClient.post<WsEnvelope<WsCrew>>(`/well-servicing/rigs/${rigId}/crews`, body),
      update: (id: string, body: WsCrewWrite) => apiClient.patch<WsEnvelope<WsCrew>>(`/well-servicing/crews/${id}`, body),
      /** Removes the crew's members with it. */
      remove: (id: string) => apiClient.delete<WsEnvelope<WsDeleted>>(`/well-servicing/crews/${id}`),
    },
    crewMembers: {
      /** 409 `already_on_crew`; 400 for a terminated user or a customer portal login. */
      add: (crewId: string, body: WsCrewMemberWrite) =>
        apiClient.post<WsEnvelope<WsCrewMember>>(`/well-servicing/crews/${crewId}/members`, body),
      update: (id: string, body: WsCrewMemberWrite) =>
        apiClient.patch<WsEnvelope<WsCrewMember>>(`/well-servicing/crew-members/${id}`, body),
      remove: (id: string) => apiClient.delete<WsEnvelope<WsDeleted>>(`/well-servicing/crew-members/${id}`),
    },
    activityCodes: {
      /** active: 'true' | 'false' | 'all' (default all). meta is { total, starterMissing }, not the tenant meta. */
      list: (params?: { active?: 'true' | 'false' | 'all' }) =>
        apiClient.get<WsEnvelope<WsActivityCode[], WsActivityCodesMeta>>('/well-servicing/activity-codes', { params }),
      /** Inserts the starter codes the tenant does not hold; skips the rest. 201 when any inserted, else 200. */
      starter: () => apiClient.post<WsEnvelope<WsStarterResult>>('/well-servicing/activity-codes/starter'),
      /** 409 `duplicate_activity_code` (existingId). */
      create: (body: WsActivityCodeWrite) => apiClient.post<WsEnvelope<WsActivityCode>>('/well-servicing/activity-codes', body),
      /** Codes are never deleted: retire with `active: false`. */
      update: (id: string, body: WsActivityCodeWrite) =>
        apiClient.patch<WsEnvelope<WsActivityCode>>(`/well-servicing/activity-codes/${id}`, body),
    },
    // ─── WS-2 jobs and tour sheets (fieldiq-api#1282) ───
    jobs: {
      /** Filters: status, customerId, wellId, rigId, from, to (planned dates), q. */
      list: (params?: { status?: string; customerId?: string; wellId?: string; rigId?: string; from?: string; to?: string; q?: string }) =>
        apiClient.get<WsEnvelope<WsJob[]>>('/well-servicing/jobs', { params }),
      /** The job plus its owned equipment and rent-ins. */
      get: (id: string) => apiClient.get<WsEnvelope<WsJobDetail>>(`/well-servicing/jobs/${id}`),
      /** 409 `well_customer_mismatch`, `job_number_contended`. */
      create: (body: WsJobWrite) => apiClient.post<WsEnvelope<WsJobDetail>>('/well-servicing/jobs', body),
      /** Fields and/or a status move. 409 `invalid_transition` (allowed), `job_closed`, `job_has_draft_tours`, `rig_has_tours`. */
      update: (id: string, body: WsJobWrite) => apiClient.patch<WsWriteEnvelope<WsJobDetail>>(`/well-servicing/jobs/${id}`, body),
      /** Only a draft or scheduled job with no sheets. 409 `job_not_deletable` / `job_has_tours`. */
      remove: (id: string) => apiClient.delete<WsEnvelope<WsDeleted>>(`/well-servicing/jobs/${id}`),
      /** 409 `bop_stack_refused` (failures[], checkedIds) — the shared pressure-control guard decides. */
      setBopStack: (id: string, equipmentId: string) =>
        apiClient.put<WsWriteEnvelope<WsJobDetail, WsBopWarning>>(`/well-servicing/jobs/${id}/bop-stack`, { equipment_id: equipmentId }),
      clearBopStack: (id: string) => apiClient.delete<WsEnvelope<WsJobDetail>>(`/well-servicing/jobs/${id}/bop-stack`),
    },
    jobEquipment: {
      list: (jobId: string) => apiClient.get<WsEnvelope<WsJobEquipment[]>>(`/well-servicing/jobs/${jobId}/equipment`),
      /** 409 `already_on_job`. */
      add: (jobId: string, body: WsJobEquipmentWrite) =>
        apiClient.post<WsEnvelope<WsJobEquipment>>(`/well-servicing/jobs/${jobId}/equipment`, body),
      /** 409 `unit_hours_outside_dates`. */
      update: (id: string, body: WsJobEquipmentWrite) =>
        apiClient.patch<WsEnvelope<WsJobEquipment>>(`/well-servicing/job-equipment/${id}`, body),
      /** 409 `unit_has_hours`. */
      remove: (id: string) => apiClient.delete<WsEnvelope<WsDeleted>>(`/well-servicing/job-equipment/${id}`),
    },
    rentIns: {
      list: (jobId: string) => apiClient.get<WsEnvelope<WsRentIn[]>>(`/well-servicing/jobs/${jobId}/rent-ins`),
      /** 400 `invalid_rent_dates`. */
      create: (jobId: string, body: WsRentInWrite) =>
        apiClient.post<WsEnvelope<WsRentIn>>(`/well-servicing/jobs/${jobId}/rent-ins`, body),
      /** 409 `rent_in_hours_outside_dates`. */
      update: (id: string, body: WsRentInWrite) => apiClient.patch<WsEnvelope<WsRentIn>>(`/well-servicing/rent-ins/${id}`, body),
      /** 409 `rent_in_has_hours`. */
      remove: (id: string) => apiClient.delete<WsEnvelope<WsDeleted>>(`/well-servicing/rent-ins/${id}`),
    },
    tours: {
      /** The job's sheets, newest first. */
      list: (jobId: string) => apiClient.get<WsEnvelope<WsTourSummary[]>>(`/well-servicing/jobs/${jobId}/tours`),
      /** 409 `tour_exists` (existingId), 400 `future_day`, `job_not_tourable`. */
      create: (jobId: string, body: WsTourWrite) =>
        apiClient.post<WsWriteEnvelope<WsTour, WsMeterWarning>>(`/well-servicing/jobs/${jobId}/tours`, body),
      get: (id: string) => apiClient.get<WsEnvelope<WsTour>>(`/well-servicing/tours/${id}`),
      /** A set present REPLACES that set. 409 `tour_submitted` / `tour_approved` once off draft. */
      update: (id: string, body: WsTourWrite) =>
        apiClient.patch<WsWriteEnvelope<WsTour, WsMeterWarning>>(`/well-servicing/tours/${id}`, body),
      /** 409 `activities_do_not_cover_tour` (gaps), `tour_has_no_crew`. */
      submit: (id: string) => apiClient.post<WsEnvelope<WsTour>>(`/well-servicing/tours/${id}/submit`),
      approve: (id: string) => apiClient.post<WsEnvelope<WsTour>>(`/well-servicing/tours/${id}/approve`),
      /** Back to draft with the reason the hand will see. 409 `tour_not_submitted`. */
      returnToDraft: (id: string, reason: string) =>
        apiClient.post<WsEnvelope<WsTour>>(`/well-servicing/tours/${id}/return`, { reason }),
      remove: (id: string) => apiClient.delete<WsEnvelope<WsDeleted>>(`/well-servicing/tours/${id}`),
    },
    /** The field hand's surface. Reads and writes are snake_case. */
    field: {
      myTours: () => apiClient.get<WsEnvelope<WsFieldMyTours, WsFieldMeta>>('/well-servicing/field/my-tours'),
      getTour: (id: string) => apiClient.get<WsEnvelope<WsFieldTour, WsFieldMeta>>(`/well-servicing/field/tours/${id}`),
      /** Upsert of the hand's sheet for that day: 201 created, 200 updated. 403 `not_on_crew`. */
      saveTour: (jobId: string, workDate: string, body: WsTourWrite) =>
        apiClient.put<WsWriteEnvelope<WsFieldTour, WsFieldMeterWarning>>(
          `/well-servicing/field/jobs/${jobId}/tours/${workDate}`, body),
      submitTour: (id: string) => apiClient.post<WsEnvelope<WsFieldTour, WsFieldMeta>>(`/well-servicing/field/tours/${id}/submit`),
      // ─── WS-3 (fieldiq-api#1286): a DRAFT sheet's string events and tallies. 409 tour_submitted once off draft.
      tourString: (tourId: string) =>
        apiClient.get<WsEnvelope<WsFieldTourString, WsFieldMeta>>(`/well-servicing/field/tours/${tourId}/string`),
      /** Returns the sheet's whole string (events + tallies), snake_case. */
      addStringEvent: (tourId: string, body: WsStringEventWrite) =>
        apiClient.post<WsEnvelope<WsFieldTourString, WsFieldMeta>>(`/well-servicing/field/tours/${tourId}/string-events`, body),
      /** 409 event_is_correction for a correction row. */
      removeStringEvent: (id: string) => apiClient.delete<WsEnvelope<WsDeleted>>(`/well-servicing/field/string-events/${id}`),
      createTally: (tourId: string, body: WsTallyWrite) =>
        apiClient.post<WsEnvelope<WsFieldTally, WsFieldMeta>>(`/well-servicing/field/tours/${tourId}/tallies`, body),
      /** Replaces the tally's lines. */
      setTallyLines: (id: string, lines: WsTallyLineWrite[]) =>
        apiClient.put<WsEnvelope<WsFieldTally, WsFieldMeta>>(`/well-servicing/field/tallies/${id}/lines`, { lines }),
      removeTally: (id: string) => apiClient.delete<WsEnvelope<WsDeleted>>(`/well-servicing/field/tallies/${id}`),
      // ─── fieldiq-api#1324 (0605): a BOP pressure test or crew drill recorded ON a draft sheet,
      // PENDING until the office approves the sheet. Bodies are POST /pressure-control/tests | /drills'.
      // 403 position_cannot_record (not rig manager / driller on the sheet), 404 not on it,
      // 409 not a draft, 400 test_outside_tour / drill_outside_tour.
      recordPressureTest: (tourId: string, body: PcTestWrite) =>
        apiClient.post<WsEnvelope<WsFieldBopRecorded, WsFieldMeta>>(`/well-servicing/field/tours/${tourId}/pressure-tests`, body),
      recordDrill: (tourId: string, body: PcDrillWrite) =>
        apiClient.post<WsEnvelope<WsFieldBopRecorded, WsFieldMeta>>(`/well-servicing/field/tours/${tourId}/drills`, body),
      /** A PENDING record off a draft sheet, and deleted: the hand who recorded it or the office. 409 record_approved. */
      removePressureTest: (tourId: string, recordId: string) =>
        apiClient.delete<WsEnvelope<{ id: string; deleted: boolean }>>(`/well-servicing/field/tours/${tourId}/pressure-tests/${recordId}`),
      removeDrill: (tourId: string, recordId: string) =>
        apiClient.delete<WsEnvelope<{ id: string; deleted: boolean }>>(`/well-servicing/field/tours/${tourId}/drills/${recordId}`),
    },
    // ─── WS-3 string, tallies, installed string, links, well report (fieldiq-api#1286) ───
    string: {
      /** The job's events, voided included and flagged. */
      jobEvents: (jobId: string) => apiClient.get<WsEnvelope<WsStringEvent[]>>(`/well-servicing/jobs/${jobId}/string-events`),
      tour: (tourId: string) => apiClient.get<WsEnvelope<WsTourString>>(`/well-servicing/tours/${tourId}/string`),
      /** Draft sheet: the crew. Submitted / approved: the office only, with correction_reason. */
      addEvent: (tourId: string, body: WsStringEventWrite) =>
        apiClient.post<WsEnvelope<WsStringEvent>>(`/well-servicing/tours/${tourId}/string-events`, body),
      /** The office, with a reason. 409 event_voided. */
      voidEvent: (id: string, reason: string) =>
        apiClient.post<WsEnvelope<WsStringEvent>>(`/well-servicing/string-events/${id}/void`, { reason }),
      /** A draft sheet's event that corrects nothing. */
      removeEvent: (id: string) => apiClient.delete<WsEnvelope<WsDeleted>>(`/well-servicing/string-events/${id}`),
      /** The installed string at a well — a fold, never stored. as_of 'YYYY-MM-DD' folds sheets on or before it. */
      well: (wellId: string, asOf?: string | null) =>
        apiClient.get<WsEnvelope<WsWellString>>(`/well-servicing/wells/${wellId}/string`, { params: asOf ? { as_of: asOf } : undefined }),
    },
    tallies: {
      list: (jobId: string) => apiClient.get<WsEnvelope<WsTally[]>>(`/well-servicing/jobs/${jobId}/tallies`),
      get: (id: string) => apiClient.get<WsEnvelope<WsTally>>(`/well-servicing/tallies/${id}`),
      create: (tourId: string, body: WsTallyWrite) => apiClient.post<WsEnvelope<WsTally>>(`/well-servicing/tours/${tourId}/tallies`, body),
      /** Header and/or lines. 400 lines_required when the unit changes without the lines. */
      update: (id: string, body: WsTallyWrite) => apiClient.patch<WsEnvelope<WsTally>>(`/well-servicing/tallies/${id}`, body),
      setLines: (id: string, lines: WsTallyLineWrite[]) =>
        apiClient.put<WsEnvelope<WsTally>>(`/well-servicing/tallies/${id}/lines`, { lines }),
      remove: (id: string) => apiClient.delete<WsEnvelope<WsDeleted>>(`/well-servicing/tallies/${id}`),
      /** The office, with a reason. */
      void: (id: string, reason: string) => apiClient.post<WsEnvelope<WsTally>>(`/well-servicing/tallies/${id}/void`, { reason }),
    },
    tourLinks: {
      candidates: (tourId: string) =>
        apiClient.get<WsEnvelope<WsLinkCandidates>>(`/well-servicing/tours/${tourId}/pressure-control-candidates`),
      /** A SET: replaces the sheet's tests. 400 test_outside_tour, 409 test_linked_elsewhere. */
      setPressureTests: (tourId: string, ids: string[]) =>
        apiClient.put<WsEnvelope<WsTourLinks>>(`/well-servicing/tours/${tourId}/pressure-tests`, { pressure_test_ids: ids }),
      setDrills: (tourId: string, ids: string[]) =>
        apiClient.put<WsEnvelope<WsTourLinks>>(`/well-servicing/tours/${tourId}/drills`, { drill_ids: ids }),
    },
    reports: {
      list: (jobId: string) => apiClient.get<WsEnvelope<WsReport[]>>(`/well-servicing/jobs/${jobId}/reports`),
      /** Issued: the frozen content. Draft: a live preview. */
      get: (id: string) => apiClient.get<WsEnvelope<WsReportDetail>>(`/well-servicing/reports/${id}`),
      /** 409 report_exists, job_not_scheduled, job_cancelled. */
      create: (jobId: string, body: WsReportWrite) =>
        apiClient.post<WsEnvelope<WsReportDetail>>(`/well-servicing/jobs/${jobId}/reports`, body),
      /** A draft only (409 report_issued). */
      update: (id: string, body: WsReportWrite) => apiClient.patch<WsEnvelope<WsReportDetail>>(`/well-servicing/reports/${id}`, body),
      remove: (id: string) => apiClient.delete<WsEnvelope<WsDeleted>>(`/well-servicing/reports/${id}`),
      /** 409 job_not_completed, already_issued. */
      issue: (id: string) => apiClient.post<WsEnvelope<WsReportDetail>>(`/well-servicing/reports/${id}/issue`),
      /** Issued: the stored file. Draft: a preview stamped DRAFT. */
      pdf: (id: string) => apiClient.get<Blob>(`/well-servicing/reports/${id}/pdf`, { responseType: 'blob' }),
    },
    // ── WS-4 (fieldiq-api#1294): the rate card and field tickets ──
    rates: {
      /** customer_id: a customer's uuid, or 'default' for the default card. on: YYYY-MM-DD. */
      list: (params?: { customer_id?: string; kind?: string; on?: string }) =>
        apiClient.get<WsEnvelope<WsRate[]>>('/well-servicing/rates', { params }),
      get: (id: string) => apiClient.get<WsEnvelope<WsRate>>(`/well-servicing/rates/${id}`),
      /** Currency is stamped by the server; never sent. 409 `rate_overlap`; 400 `third_party_rate`, `fuel_unit`, `currency_missing`, … */
      create: (body: WsRateWrite) => apiClient.post<WsEnvelope<WsRate>>('/well-servicing/rates', body),
      /** 409 `rate_overlap`, `rate_in_use`. */
      update: (id: string, body: WsRateWrite) => apiClient.patch<WsEnvelope<WsRate>>(`/well-servicing/rates/${id}`, body),
      /** 409 `rate_in_use` (a rent-in bills at it). */
      remove: (id: string) => apiClient.delete<WsEnvelope<{ id: string; deleted: boolean }>>(`/well-servicing/rates/${id}`),
    },
    rentInBilling: {
      get: (rentInId: string) => apiClient.get<WsEnvelope<WsRentInBilling>>(`/well-servicing/rent-ins/${rentInId}/billing-rate`),
      /** rate_id null clears it. 400 `rate_not_rent_in`, `rate_other_customer`. */
      set: (rentInId: string, rateId: string | null) =>
        apiClient.put<WsEnvelope<WsRentInBilling>>(`/well-servicing/rent-ins/${rentInId}/billing-rate`, { rate_id: rateId }),
    },
    tickets: {
      list: (params?: { job_id?: string; customer_id?: string; status?: string }) =>
        apiClient.get<WsEnvelope<WsTicket[]>>('/well-servicing/tickets', { params }),
      get: (id: string) => apiClient.get<WsEnvelope<WsTicket>>(`/well-servicing/tickets/${id}`),
      /** Draft one from the job's office-approved tour sheets. work_date only on a per-day job. */
      create: (jobId: string, body: { work_date?: string }) =>
        apiClient.post<WsEnvelope<WsTicket>>(`/well-servicing/jobs/${jobId}/tickets`, body),
      /** Redraws the 'auto' lines; office lines are kept. May return a signed ticket to draft (`refreshed.reverted`). */
      refresh: (id: string) => apiClient.post<WsEnvelope<WsTicket> & { refreshed?: unknown }>(`/well-servicing/tickets/${id}/refresh`),
      update: (id: string, body: WsTicketHeaderWrite) => apiClient.patch<WsTicketEdit>(`/well-servicing/tickets/${id}`, body),
      /** 409 `ticket_customer_approved`, `ticket_void`. */
      remove: (id: string) => apiClient.delete<WsEnvelope<{ id: string; deleted: boolean }>>(`/well-servicing/tickets/${id}`),
      addLine: (id: string, body: WsTicketLineCreate) => apiClient.post<WsTicketEdit>(`/well-servicing/tickets/${id}/lines`, body),
      updateLine: (id: string, lineId: string, body: WsTicketLinePatch) =>
        apiClient.patch<WsTicketEdit>(`/well-servicing/tickets/${id}/lines/${lineId}`, body),
      removeLine: (id: string, lineId: string) => apiClient.delete<WsTicketEdit>(`/well-servicing/tickets/${id}/lines/${lineId}`),
      /** The office's approval. 409 `ticket_unpriced` (names `lines`), `ticket_empty`, `tax_unconfigured`. */
      officeApprove: (id: string) => apiClient.post<WsEnvelope<WsTicket>>(`/well-servicing/tickets/${id}/approve`),
      recipient: (id: string) => apiClient.get<WsEnvelope<WsTicketRecipient>>(`/well-servicing/tickets/${id}/recipient`),
      /** A failed email is 502 `email_send_failed` and still carries `data.reviewUrl`. */
      send: (id: string, body: { email?: string; cc?: string[] }) =>
        apiClient.post<WsTicketSendResult>(`/well-servicing/tickets/${id}/send`, body),
      /** Office-signed statuses only (a draft or void ticket is 409). */
      pdf: (id: string) => apiClient.get<Blob>(`/well-servicing/tickets/${id}/pdf`, { responseType: 'blob' }),
      stampedFile: (id: string) => apiClient.get<Blob>(`/well-servicing/tickets/${id}/stamped-file`, { responseType: 'blob' }),
      /**
       * The office records the photo of the signed paper ticket: JSON naming a
       * confirmed `ws_ticket` upload (presigned flow, useWsTickets
       * uploadWsTicketFile). Once only: 409 `approval_already_recorded`.
       */
      recordApproval: (id: string, body: { upload_id: string; approved_by_name: string; approved_on: string }) =>
        apiClient.post<WsEnvelope<WsTicket>>(`/well-servicing/tickets/${id}/customer-approval`, body),
      /** 400 `reason_required`, 409 `ticket_void`. */
      void: (id: string, reason: string) =>
        apiClient.post<WsEnvelope<WsTicket>>(`/well-servicing/tickets/${id}/void`, { reason }),
    },
    /**
     * WS-5 (fieldiq-api#1295): invoices from customer-approved field tickets.
     * Reads camelCase, writes snake_case; refusals are `{ code, error }` — read
     * with `wsInvoiceRefusal` (lib/ws-invoices.ts). The PDF is a blob. Mark
     * paid is Finance's door (PATCH /unified-invoices/:id).
     */
    invoices: {
      list: (params?: { status?: string; customer_id?: string; job_id?: string }) =>
        apiClient.get<WsEnvelope<WsInvoiceRow[]>>('/well-servicing/invoices', { params }),
      /** One group per customer + province/state + currency — the set one invoice may bill. */
      billable: () => apiClient.get<WsEnvelope<WsBillableGroup[]>>('/well-servicing/invoices/billable'),
      /** 201 with the detail. 409 `ticket_already_invoiced`, `ticket_not_customer_approved`, `ticket_jurisdiction_changed`, `tickets_different_*`, `tickets_mixed_currency`, `tax_unconfigured`. */
      generate: (body: WsInvoiceGenerate) => apiClient.post<WsEnvelope<WsInvoice>>('/well-servicing/invoices/generate', body),
      get: (id: string) => apiClient.get<WsEnvelope<WsInvoice>>(`/well-servicing/invoices/${id}`),
      pdf: (id: string) => apiClient.get<Blob>(`/well-servicing/invoices/${id}/pdf`, { responseType: 'blob' }),
      /** The office's write role only. `to` null carries `no_billing_email`. */
      recipient: (id: string) => apiClient.get<WsEnvelope<WsInvoiceRecipient>>(`/well-servicing/invoices/${id}/recipient`),
      /** draft → sent once the email is accepted. 502 `email_not_sent` (the invoice is unchanged); 409 `tax_fallback_blocked`, `no_billing_email`, `invoice_not_sendable`. */
      send: (id: string, body: { to?: string; cc?: string[] }) =>
        apiClient.post<WsEnvelope<WsInvoice & { sentToName?: string | null }>>(`/well-servicing/invoices/${id}/send`, body),
      /**
       * Finance's door (PATCH /unified-invoices/:id, the view's well_servicing
       * branch). Refusals: 409 invoice_not_sent / invoice_void / invoice_already_paid.
       */
      markPaid: (id: string) => apiClient.patch<{ success: boolean }>(`/unified-invoices/${id}`, { status: 'paid' }),
      /** Final. 400 `reason_required`. Echoes `releasedTicketIds`: the tickets are billable again. */
      void: (id: string, reason: string) => apiClient.post<WsEnvelope<WsInvoiceVoided>>(`/well-servicing/invoices/${id}/void`, { reason }),
    },
    /**
     * WS-6 (fieldiq-api#1296): the AI drafts. Admin, office staff and
     * supervisor only. Every run is a HITL draft; nothing is published until
     * approved. Refusals are `{ error, code }` — read with `wsAiRefusal`
     * (lib/ws-ai.ts).
     */
    ai: {
      /** One tour sheet. 404 for another tenant's or a deleted sheet. */
      tourSummary: (tourId: string) =>
        apiClient.post<{ success: boolean; data: WsAiRunResult }>(`/well-servicing/ai/tour-summary/${tourId}`, {}),
      /** 409 ticket_customer_approved / ticket_void. */
      ticketPreflight: (ticketId: string) =>
        apiClient.post<{ success: boolean; data: WsAiRunResult }>(`/well-servicing/ai/ticket-preflight/${ticketId}`, {}),
      jobPreflight: (jobId: string) =>
        apiClient.post<{ success: boolean; data: WsAiRunResult }>(`/well-servicing/ai/job-preflight/${jobId}`, {}),
      /** A DRAFT report only: 409 report_issued otherwise. */
      wellReportDraft: (reportId: string) =>
        apiClient.post<{ success: boolean; data: WsAiRunResult }>(`/well-servicing/ai/well-report-draft/${reportId}`, {}),
      complianceDigest: () =>
        apiClient.post<{ success: boolean; data: WsAiRunResult }>('/well-servicing/ai/compliance-digest', {}),
      /** Newest first, at most 20. `outcome` (agent_runs.outcome) is filtered before the limit — fieldiq-api#1307. */
      digests: (params?: { agent?: string; jobId?: string; outcome?: string }) =>
        apiClient.get<{ success: boolean; data: WsAiStoredRun[] }>('/well-servicing/ai/digests', { params }),
      digest: (id: string) => apiClient.get<{ success: boolean; data: WsAiStoredRun }>(`/well-servicing/ai/digests/${id}`),
      /** 400 narrative_required / narrative_too_long / invalid_observations; 409 not_pending, no_draft, report_missing, report_issued. */
      approve: (id: string, body: WsAiApproveBody = {}) =>
        apiClient.post<{ success: boolean; data: WsAiStoredRun }>(`/well-servicing/ai/digests/${id}/approve`, body),
      /** 409 not_pending. */
      dismiss: (id: string) => apiClient.post<{ success: boolean; data: WsAiStoredRun }>(`/well-servicing/ai/digests/${id}/dismiss`, {}),
    },
  },
  /**
   * Wireline & Slickline — /api/v1/wireline-slickline/* (fieldiq-api#1336, WL-1).
   * camelCase reads, snake_case writes; every body carries the tenant meta
   * (country decides UWI vs API number; units name depth, length, diameter and
   * force). Refusals carry a `code` and a sentence — read them with
   * `wlRefusal` (lib/wireline-slickline).
   */
  wirelineSlickline: {
    meta: () => apiClient.get<WlEnvelope<WlCatalog>>('/wireline-slickline/meta'),
    settings: {
      get: () => apiClient.get<WlEnvelope<WlSettings>>('/wireline-slickline/settings'),
      update: (body: WlSettingsUpdate) => apiClient.put<WlEnvelope<WlSettings>>('/wireline-slickline/settings', body),
    },
    customerSettings: {
      get: (customerId: string) =>
        apiClient.get<WlEnvelope<WlCustomerSettings>>(`/wireline-slickline/customers/${customerId}/settings`),
      update: (customerId: string, body: WlCustomerSettingsUpdate) =>
        apiClient.put<WlEnvelope<WlCustomerSettings>>(`/wireline-slickline/customers/${customerId}/settings`, body),
    },
    wells: {
      /** The shared wells register. Filters: customerId, fluidType, provinceState, q. */
      list: (params?: { customerId?: string; fluidType?: string; provinceState?: string; q?: string }) =>
        apiClient.get<WlEnvelope<WlWell[]>>('/wireline-slickline/wells', { params }),
      get: (id: string) => apiClient.get<WlEnvelope<WlWell>>(`/wireline-slickline/wells/${id}`),
      /** 409 `duplicate_well` (field, existingWellId). */
      create: (body: WlWellWrite) => apiClient.post<WlEnvelope<WlWell>>('/wireline-slickline/wells', body),
      update: (id: string, body: WlWellWrite) => apiClient.patch<WlEnvelope<WlWell>>(`/wireline-slickline/wells/${id}`, body),
      /** 409 `well_has_live_jobs`. */
      remove: (id: string) => apiClient.delete<WlEnvelope<WlDeleted>>(`/wireline-slickline/wells/${id}`),
    },
    units: {
      list: () => apiClient.get<WlEnvelope<WlUnit[]>>('/wireline-slickline/units'),
      /** The unit plus its crews and their members. */
      get: (id: string) => apiClient.get<WlEnvelope<WlUnitDetail>>(`/wireline-slickline/units/${id}`),
      /** 409 `unit_exists_for_equipment` / `duplicate_unit_number` (existingUnitId). */
      create: (body: WlUnitWrite) => apiClient.post<WlEnvelope<WlUnit>>('/wireline-slickline/units', body),
      update: (id: string, body: WlUnitWrite) => apiClient.patch<WlEnvelope<WlUnit>>(`/wireline-slickline/units/${id}`, body),
      /** 409 `unit_has_crews`. */
      remove: (id: string) => apiClient.delete<WlEnvelope<WlDeleted>>(`/wireline-slickline/units/${id}`),
    },
    crews: {
      /** 409 `duplicate_crew_name`. */
      create: (unitId: string, body: WlCrewWrite) => apiClient.post<WlEnvelope<WlCrew>>(`/wireline-slickline/units/${unitId}/crews`, body),
      update: (id: string, body: WlCrewWrite) => apiClient.patch<WlEnvelope<WlCrew>>(`/wireline-slickline/crews/${id}`, body),
      remove: (id: string) => apiClient.delete<WlEnvelope<WlDeleted>>(`/wireline-slickline/crews/${id}`),
    },
    crewMembers: {
      /** 409 `already_on_crew`; 400 for a terminated user or a customer portal login. */
      add: (crewId: string, body: WlCrewMemberWrite) =>
        apiClient.post<WlEnvelope<WlCrewMember>>(`/wireline-slickline/crews/${crewId}/members`, body),
      update: (id: string, body: WlCrewMemberWrite) =>
        apiClient.patch<WlEnvelope<WlCrewMember>>(`/wireline-slickline/crew-members/${id}`, body),
      remove: (id: string) => apiClient.delete<WlEnvelope<WlDeleted>>(`/wireline-slickline/crew-members/${id}`),
    },
    lines: {
      /** Each line carries its folded `state`. Filter: lineType. */
      list: (params?: { lineType?: string }) => apiClient.get<WlEnvelope<WlLine[]>>('/wireline-slickline/lines', { params }),
      /** The line, its state and its event log in the API's order. */
      get: (id: string) => apiClient.get<WlEnvelope<WlLineDetail>>(`/wireline-slickline/lines/${id}`),
      state: (id: string) => apiClient.get<WlEnvelope<WlLineState>>(`/wireline-slickline/lines/${id}/state`),
      events: (id: string) => apiClient.get<WlEnvelope<WlLineEvent[]>>(`/wireline-slickline/lines/${id}/events`),
      /** 409 `duplicate_line_number` (existingLineId). */
      create: (body: WlLineWrite) => apiClient.post<WlEnvelope<WlLine>>('/wireline-slickline/lines', body),
      update: (id: string, body: WlLineWrite) => apiClient.patch<WlEnvelope<WlLine>>(`/wireline-slickline/lines/${id}`, body),
      /** 409 `line_has_events`. */
      remove: (id: string) => apiClient.delete<WlEnvelope<WlDeleted>>(`/wireline-slickline/lines/${id}`),
      /** 201 { event, state }. 409 already_spooled / exceeds_length / not_spooled / after_retired. */
      addEvent: (id: string, body: WlLineEventWrite) =>
        apiClient.post<WlEnvelope<WlEventResult>>(`/wireline-slickline/lines/${id}/events`, body),
    },
    lineEvents: {
      /** A correction is a void with a reason, then a new event. 409 `already_voided`. */
      void: (id: string, body: { reason: string }) =>
        apiClient.post<WlEnvelope<WlEventResult>>(`/wireline-slickline/line-events/${id}/void`, body),
    },
    toolStrings: {
      list: () => apiClient.get<WlEnvelope<WlToolString[]>>('/wireline-slickline/tool-strings'),
      get: (id: string) => apiClient.get<WlEnvelope<WlToolString>>(`/wireline-slickline/tool-strings/${id}`),
      /** 409 `duplicate_tool_string_name`. */
      create: (body: WlToolStringWrite) => apiClient.post<WlEnvelope<WlToolString>>('/wireline-slickline/tool-strings', body),
      /** The header only (name, line type, notes). */
      update: (id: string, body: WlToolStringWrite) =>
        apiClient.patch<WlEnvelope<WlToolString>>(`/wireline-slickline/tool-strings/${id}`, body),
      /** Replaces every item, in the order sent (at most 100). */
      replaceItems: (id: string, body: { items: WlToolStringItemWrite[] }) =>
        apiClient.put<WlEnvelope<WlToolString>>(`/wireline-slickline/tool-strings/${id}/items`, body),
      remove: (id: string) => apiClient.delete<WlEnvelope<WlDeleted>>(`/wireline-slickline/tool-strings/${id}`),
    },
    // ─── WL-2 (fieldiq-api#1338): jobs, units and rent-ins on a job, day sheets, runs ───
    jobs: {
      /** Filters: status, customerId, wellId, unitId, from, to, q. A field_worker sees only jobs they are on. */
      list: (params?: Record<string, string>) => apiClient.get<WlEnvelope<WlJob[]>>('/wireline-slickline/jobs', { params }),
      /** The job, its units and (with WL_RENT_IN_READ) its rent-ins. */
      get: (id: string) => apiClient.get<WlEnvelope<WlJobDetail>>(`/wireline-slickline/jobs/${id}`),
      /** 400 well_customer_mismatch. */
      create: (body: WlJobWrite) => apiClient.post<WlEnvelope<WlJobDetail>>('/wireline-slickline/jobs', body),
      /** 409 job_closed / invalid_transition / job_has_draft_sheets / pressure_stack_refused. Returns warnings. */
      update: (id: string, body: WlJobWrite) =>
        apiClient.patch<WlEnvelope<WlJobDetail> & { warnings?: WlWarningEntry[] }>(`/wireline-slickline/jobs/${id}`, body),
      /** 409 job_not_deletable / job_has_sheets. */
      remove: (id: string) => apiClient.delete<WlEnvelope<WlDeleted>>(`/wireline-slickline/jobs/${id}`),
      /** Set or clear (equipment_id null) a unit's stack. 409 pressure_stack_refused (failures, checkedIds). */
      setPressureStack: (id: string, body: { unit_id: string; equipment_id: string | null }) =>
        apiClient.put<WlEnvelope<WlJobDetail> & { warnings?: WlWarningEntry[] }>(`/wireline-slickline/jobs/${id}/pressure-stack`, body),
      units: (id: string) => apiClient.get<WlEnvelope<WlJobUnit[]>>(`/wireline-slickline/jobs/${id}/units`),
      /** 409 already_on_job. */
      addUnit: (id: string, body: WlJobUnitWrite) => apiClient.post<WlEnvelope<WlJobUnit>>(`/wireline-slickline/jobs/${id}/units`, body),
      rentIns: (id: string) => apiClient.get<WlEnvelope<WlRentIn[]>>(`/wireline-slickline/jobs/${id}/rent-ins`),
      addRentIn: (id: string, body: WlRentInWrite) => apiClient.post<WlEnvelope<WlRentIn>>(`/wireline-slickline/jobs/${id}/rent-ins`, body),
      sheets: (id: string) => apiClient.get<WlEnvelope<WlSheetSummary[]>>(`/wireline-slickline/jobs/${id}/sheets`),
      /** 409 sheet_exists (existingId); 400 unit_required / unit_not_on_job / future_day / job_not_scheduled. */
      openSheet: (id: string, body: WlSheetWrite) =>
        apiClient.post<WlEnvelope<WlSheet> & { warnings?: WlWarningEntry[] }>(`/wireline-slickline/jobs/${id}/sheets`, body),
    },
    jobUnits: {
      /** 409 unit_sheet_outside_dates. */
      update: (id: string, body: WlJobUnitWrite) => apiClient.patch<WlEnvelope<WlJobUnit>>(`/wireline-slickline/job-units/${id}`, body),
      /** 409 unit_has_sheets. */
      remove: (id: string) => apiClient.delete<WlEnvelope<WlDeleted>>(`/wireline-slickline/job-units/${id}`),
    },
    rentIns: {
      /** 409 rent_in_hours_outside_dates; 400 invalid_rent_dates. */
      update: (id: string, body: WlRentInWrite) => apiClient.patch<WlEnvelope<WlRentIn>>(`/wireline-slickline/rent-ins/${id}`, body),
      /** 409 rent_in_has_hours. */
      remove: (id: string) => apiClient.delete<WlEnvelope<WlDeleted>>(`/wireline-slickline/rent-ins/${id}`),
    },
    sheets: {
      get: (id: string) => apiClient.get<WlEnvelope<WlSheet>>(`/wireline-slickline/sheets/${id}`),
      /** Draft only. Returns meter warnings (meter_below_previous). */
      update: (id: string, body: WlSheetWrite) =>
        apiClient.patch<WlEnvelope<WlSheet> & { warnings?: WlWarningEntry[] }>(`/wireline-slickline/sheets/${id}`, body),
      /** 400 sheet_has_no_crew. */
      submit: (id: string) => apiClient.post<WlEnvelope<WlSheet>>(`/wireline-slickline/sheets/${id}/submit`, {}),
      /** WL_SHEET_APPROVE. Returns footage warnings (footage_mismatch). */
      approve: (id: string) =>
        apiClient.post<WlEnvelope<WlSheet> & { warnings?: WlWarningEntry[] }>(`/wireline-slickline/sheets/${id}/approve`, {}),
      /** WL_SHEET_APPROVE. A submitted sheet back to draft, with the reason the crew reads. */
      returnToCrew: (id: string, body: { reason: string }) =>
        apiClient.post<WlEnvelope<WlSheet>>(`/wireline-slickline/sheets/${id}/return`, body),
      /** WL_SHEET_APPROVE, draft only. */
      remove: (id: string) => apiClient.delete<WlEnvelope<WlDeleted>>(`/wireline-slickline/sheets/${id}`),
      /** 409 rig_up_test_required / runs_overlap; 400 run_outside_sheet / shots_fired_exceed_loaded / … Returns warnings. */
      addRun: (id: string, body: WlRunWrite) =>
        apiClient.post<WlEnvelope<WlRun> & { warnings?: WlWarningEntry[] }>(`/wireline-slickline/sheets/${id}/runs`, body),
      setPressureTests: (id: string, body: { pressure_test_ids: string[] }) =>
        apiClient.put<WlEnvelope<WlSheetLinks>>(`/wireline-slickline/sheets/${id}/pressure-tests`, body),
      setDrills: (id: string, body: { drill_ids: string[] }) =>
        apiClient.put<WlEnvelope<WlSheetLinks>>(`/wireline-slickline/sheets/${id}/drills`, body),
      linkCandidates: (id: string) =>
        apiClient.get<WlEnvelope<WlLinkCandidates>>(`/wireline-slickline/sheets/${id}/pressure-control-candidates`),
      /** WL-3 (api#1339): the run log of every run on the sheet, as a PDF. WL_READ. */
      runLogPdf: (id: string) => apiClient.get<Blob>(`/wireline-slickline/sheets/${id}/run-log/pdf`, { responseType: 'blob' }),
    },
    runs: {
      get: (id: string) => apiClient.get<WlEnvelope<WlRun>>(`/wireline-slickline/runs/${id}`),
      update: (id: string, body: Partial<WlRunWrite>) =>
        apiClient.patch<WlEnvelope<WlRun> & { warnings?: WlWarningEntry[] }>(`/wireline-slickline/runs/${id}`, body),
      remove: (id: string) => apiClient.delete<WlEnvelope<WlDeleted>>(`/wireline-slickline/runs/${id}`),
      /** WL-3 (api#1339): the run log of this one run, as a PDF. WL_READ. */
      pdf: (id: string) => apiClient.get<Blob>(`/wireline-slickline/runs/${id}/pdf`, { responseType: 'blob' }),
    },
    /**
     * WL-3 (api#1339): the end-of-job service report, WLR-NNNN. draft → issued
     * → void, one live report per job. Create / refresh / issue are
     * WL_DISPATCH; void is WL_ADMIN and needs a reason. Issue is refused
     * (409 job_not_completed / no_approved_sheets) and an issued report is
     * immutable (409 report_issued); the API's sentence is shown.
     */
    reports: {
      list: (params?: { job_id?: string; status?: string }) =>
        apiClient.get<WlEnvelope<WlReport[]>>('/wireline-slickline/reports', { params }),
      get: (id: string) => apiClient.get<WlEnvelope<WlReportDetail>>(`/wireline-slickline/reports/${id}`),
      /** 409 report_exists (with existingId) / job_not_scheduled / job_cancelled. Returns content warnings. */
      create: (jobId: string, body: WlReportTextWrite = {}) =>
        apiClient.post<WlEnvelope<WlReportDetail> & { warnings?: WlWarningEntry[] }>(`/wireline-slickline/jobs/${jobId}/reports`, body),
      /** A draft only: rebuilds the content from the rows of now; may change title / summary / comments. */
      refresh: (id: string, body: WlReportTextWrite = {}) =>
        apiClient.post<WlEnvelope<WlReportDetail> & { warnings?: WlWarningEntry[] }>(`/wireline-slickline/reports/${id}/refresh`, body),
      issue: (id: string) =>
        apiClient.post<WlEnvelope<WlReportDetail> & { warnings?: WlWarningEntry[] }>(`/wireline-slickline/reports/${id}/issue`, {}),
      voidReport: (id: string, body: { reason: string }) =>
        apiClient.post<WlEnvelope<WlReportDetail>>(`/wireline-slickline/reports/${id}/void`, body),
      pdf: (id: string) => apiClient.get<Blob>(`/wireline-slickline/reports/${id}/pdf`, { responseType: 'blob' }),
    },
    runSettings: {
      get: () => apiClient.get<WlEnvelope<WlRunSettings>>('/wireline-slickline/run-settings'),
      /** WL_ADMIN. */
      update: (body: { rig_up_test_required: boolean }) =>
        apiClient.put<WlEnvelope<WlRunSettings>>('/wireline-slickline/run-settings', body),
    },
    /** The field surface. SNAKE_CASE on the wire; the hooks camelize it (lib/wireline-slickline-jobs). */
    field: {
      mySheets: () => apiClient.get<unknown>('/wireline-slickline/field/my-sheets'),
      sheet: (id: string) => apiClient.get<unknown>(`/wireline-slickline/field/sheets/${id}`),
      /** Create-or-update the day's sheet. unit_id when the job has several units. */
      saveSheet: (jobId: string, workDate: string, body: WlSheetWrite) =>
        apiClient.put<unknown>(`/wireline-slickline/field/jobs/${jobId}/sheets/${workDate}`, body),
      submit: (id: string) => apiClient.post<unknown>(`/wireline-slickline/field/sheets/${id}/submit`, {}),
      addRun: (id: string, body: WlRunWrite) => apiClient.post<unknown>(`/wireline-slickline/field/sheets/${id}/runs`, body),
      updateRun: (id: string, body: Partial<WlRunWrite>) => apiClient.patch<unknown>(`/wireline-slickline/field/runs/${id}`, body),
      removeRun: (id: string) => apiClient.delete<unknown>(`/wireline-slickline/field/runs/${id}`),
      /** 403 position_cannot_record (a helper). Recorded PENDING until the office approves the sheet. */
      recordTest: (id: string, body: object) => apiClient.post<unknown>(`/wireline-slickline/field/sheets/${id}/pressure-tests`, body),
      recordDrill: (id: string, body: object) => apiClient.post<unknown>(`/wireline-slickline/field/sheets/${id}/drills`, body),
      removeRecord: (id: string, kind: 'test' | 'drill', recordId: string) =>
        apiClient.delete<unknown>(`/wireline-slickline/field/sheets/${id}/${kind === 'test' ? 'pressure-tests' : 'drills'}/${recordId}`),
    },
  },
  pipelineFacility: {
    meta: () => apiClient.get<PfEnvelope<PfCatalog>>('/pipeline-facility/meta'),
    settings: {
      get: () => apiClient.get<PfEnvelope<PfSettings>>('/pipeline-facility/settings'),
      update: (body: PfSettingsUpdate) => apiClient.put<PfEnvelope<PfSettings>>('/pipeline-facility/settings', body),
    },
    customerSettings: {
      get: (customerId: string) =>
        apiClient.get<PfEnvelope<PfCustomerSettings>>(`/pipeline-facility/customers/${customerId}/settings`),
      update: (customerId: string, body: PfCustomerSettingsUpdate) =>
        apiClient.put<PfEnvelope<PfCustomerSettings>>(`/pipeline-facility/customers/${customerId}/settings`, body),
    },
    sites: {
      /** Filters: kind, customerId, status, q (name, licence, line, LSD, customer). */
      list: (params?: { kind?: string; customerId?: string; status?: string; q?: string }) =>
        apiClient.get<PfEnvelope<PfSite[]>>('/pipeline-facility/sites', { params }),
      get: (id: string) => apiClient.get<PfEnvelope<PfSiteDetail>>(`/pipeline-facility/sites/${id}`),
      create: (body: PfSiteWrite) => apiClient.post<PfEnvelope<PfSiteDetail>>('/pipeline-facility/sites', body),
      /** 409 when a pipeline with segments is changed to a facility. */
      update: (id: string, body: PfSiteWrite) => apiClient.patch<PfEnvelope<PfSiteDetail>>(`/pipeline-facility/sites/${id}`, body),
      /** Soft delete; its live segments go with it. */
      remove: (id: string) => apiClient.delete<PfEnvelope<PfSiteDeleted>>(`/pipeline-facility/sites/${id}`),
    },
    segments: {
      list: (siteId: string) => apiClient.get<PfEnvelope<PfSegment[]>>(`/pipeline-facility/sites/${siteId}/segments`),
      /** 409 `code: 'segment_overlap'` naming `conflictingSegmentId`; 400 on a facility. */
      create: (siteId: string, body: PfSegmentWrite) =>
        apiClient.post<PfEnvelope<PfSegment>>(`/pipeline-facility/sites/${siteId}/segments`, body),
      update: (id: string, body: PfSegmentWrite) => apiClient.patch<PfEnvelope<PfSegment>>(`/pipeline-facility/segments/${id}`, body),
      remove: (id: string) => apiClient.delete<PfEnvelope<{ id: string; deleted: boolean }>>(`/pipeline-facility/segments/${id}`),
    },
    /**
     * PF-2 (fieldiq-api#1200). Jobs and their crew / owned equipment / rent-ins /
     * daily logs. camelCase reads, snake_case writes; chainage in tenant units.
     * Refusals carry a `code` — read them with `pfRefusal` (lib/pipeline-facility).
     */
    jobs: {
      /** Filters: status, siteId, customerId, from/to (scheduled start), q. A field hand sees only crewed jobs. */
      list: (params?: { status?: string; siteId?: string; customerId?: string; from?: string; to?: string; q?: string }) =>
        apiClient.get<PfEnvelope<PfJob[]>>('/pipeline-facility/jobs', { params }),
      get: (id: string) => apiClient.get<PfEnvelope<PfJobDetail>>(`/pipeline-facility/jobs/${id}`),
      create: (body: PfJobWrite) => apiClient.post<PfEnvelope<PfJobDetail>>('/pipeline-facility/jobs', body),
      /** A status move is a PATCH of `status`; cancelling needs `cancel_reason`. */
      update: (id: string, body: PfJobWrite) => apiClient.patch<PfEnvelope<PfJobDetail>>(`/pipeline-facility/jobs/${id}`, body),
      /** Draft or scheduled with no live logs only. */
      remove: (id: string) => apiClient.delete<PfEnvelope<{ id: string; deleted: boolean }>>(`/pipeline-facility/jobs/${id}`),
    },
    crew: {
      add: (jobId: string, body: PfCrewWrite) => apiClient.post<PfEnvelope<PfCrew>>(`/pipeline-facility/jobs/${jobId}/crew`, body),
      update: (id: string, body: PfCrewWrite) => apiClient.patch<PfEnvelope<PfCrew>>(`/pipeline-facility/crew/${id}`, body),
      remove: (id: string) => apiClient.delete<PfEnvelope<{ id: string; deleted: boolean }>>(`/pipeline-facility/crew/${id}`),
    },
    jobEquipment: {
      add: (jobId: string, body: PfJobEquipmentWrite) =>
        apiClient.post<PfEnvelope<PfJobEquipment>>(`/pipeline-facility/jobs/${jobId}/equipment`, body),
      update: (id: string, body: PfJobEquipmentWrite) =>
        apiClient.patch<PfEnvelope<PfJobEquipment>>(`/pipeline-facility/job-equipment/${id}`, body),
      remove: (id: string) => apiClient.delete<PfEnvelope<{ id: string; deleted: boolean }>>(`/pipeline-facility/job-equipment/${id}`),
    },
    rentIns: {
      add: (jobId: string, body: PfRentInWrite) => apiClient.post<PfEnvelope<PfRentIn>>(`/pipeline-facility/jobs/${jobId}/rent-ins`, body),
      update: (id: string, body: PfRentInWrite) => apiClient.patch<PfEnvelope<PfRentIn>>(`/pipeline-facility/rent-ins/${id}`, body),
      remove: (id: string) => apiClient.delete<PfEnvelope<{ id: string; deleted: boolean }>>(`/pipeline-facility/rent-ins/${id}`),
    },
    dailyLogs: {
      list: (jobId: string) => apiClient.get<PfEnvelope<PfDailyLogSummary[]>>(`/pipeline-facility/jobs/${jobId}/daily-logs`),
      get: (id: string) => apiClient.get<PfEnvelope<PfDailyLog>>(`/pipeline-facility/daily-logs/${id}`),
      /** 201 `{ data, warnings, meta }`; 409 `daily_log_exists` names `existingId`. */
      create: (jobId: string, body: PfDailyLogWrite) =>
        apiClient.post<PfEnvelope<PfDailyLog> & { warnings?: PfLogWarning[] }>(`/pipeline-facility/jobs/${jobId}/daily-logs`, body),
      update: (id: string, body: PfDailyLogWrite) =>
        apiClient.patch<PfEnvelope<PfDailyLog> & { warnings?: PfLogWarning[] }>(`/pipeline-facility/daily-logs/${id}`, body),
      remove: (id: string) => apiClient.delete<PfEnvelope<{ id: string; deleted: boolean }>>(`/pipeline-facility/daily-logs/${id}`),
    },
    /** Pre-use checks filed on the job's units that day (default the tenant's today). */
    preUseChecks: (jobId: string, date?: string) =>
      apiClient.get<PfEnvelope<PfPreUseCheck[]> & { date?: string }>(`/pipeline-facility/jobs/${jobId}/pre-use-checks`, {
        params: date ? { date } : undefined,
      }),
    /**
     * PF-3a (fieldiq-api#1219). ILI anomalies, digs (features, repairs), welds,
     * NDT / PT links and dig reports. camelCase reads, snake_case writes;
     * lengths in tenant units. Refusals carry a `code` — read them with
     * `pfDigRefusal` (lib/pf-digs).
     */
    anomalies: {
      /** Filters: siteId, segmentId, featureType, q, undug ('true' = not yet on a dig). */
      list: (params?: { siteId?: string; segmentId?: string; featureType?: string; q?: string; undug?: string }) =>
        apiClient.get<PfEnvelope<PfAnomaly[]>>('/pipeline-facility/anomalies', { params }),
      get: (id: string) => apiClient.get<PfEnvelope<PfAnomaly>>(`/pipeline-facility/anomalies/${id}`),
      /** 409 `duplicate_anomaly` / `feature_id_conflict` name `existingId`. */
      create: (body: PfAnomalyWrite) => apiClient.post<PfEnvelope<PfAnomaly>>('/pipeline-facility/anomalies', body),
      update: (id: string, body: PfAnomalyWrite) => apiClient.patch<PfEnvelope<PfAnomaly>>(`/pipeline-facility/anomalies/${id}`, body),
      /** 409 `anomaly_in_use` names the `digs`. */
      remove: (id: string) => apiClient.delete<PfEnvelope<{ id: string; deleted: boolean }>>(`/pipeline-facility/anomalies/${id}`),
      /** All-or-nothing validation (400 names `rows[i].field`); 201 when any inserted, 200 when every row was skipped. */
      importConfirm: (body: PfAnomalyImportBody) =>
        apiClient.post<PfEnvelope<PfAnomalyImportResult>>('/pipeline-facility/anomalies/import/confirm', body),
      /**
       * PF-3b (fieldiq-api#1224): the AI reads the operator's PDF / XLSX / CSV and
       * proposes rows. Saves nothing. FormData: file, site_id, ili_vendor?, ili_run_date?.
       * No Content-Type here — the request interceptor leaves it to the browser so the
       * multipart boundary is set. 503 `ai_unavailable`, 502 `ai_parse_failed`.
       */
      importParse: (form: FormData) =>
        apiClient.post<PfEnvelope<PfAnomalyParseResult>>('/pipeline-facility/anomalies/import/parse', form),
      /** PF-3b: abandon a parse (its agent run → dismissed). */
      dismissParse: (runId: string) =>
        apiClient.post<PfEnvelope<{ runId: string; outcome: string }>>(`/pipeline-facility/anomalies/import/runs/${runId}/dismiss`),
    },
    digs: {
      list: (params?: { jobId?: string; siteId?: string; status?: string; q?: string }) =>
        apiClient.get<PfEnvelope<PfDig[]>>('/pipeline-facility/digs', { params }),
      forJob: (jobId: string) => apiClient.get<PfEnvelope<PfDig[]>>(`/pipeline-facility/jobs/${jobId}/digs`),
      get: (id: string) => apiClient.get<PfEnvelope<PfDigDetail>>(`/pipeline-facility/digs/${id}`),
      create: (jobId: string, body: PfDigCreate) => apiClient.post<PfEnvelope<PfDigDetail>>(`/pipeline-facility/jobs/${jobId}/digs`, body),
      /** The dig sheet, and a status move (`status`) through the API's machine. */
      update: (id: string, body: PfDigPatch) => apiClient.patch<PfEnvelope<PfDigDetail>>(`/pipeline-facility/digs/${id}`, body),
      /** Planned digs only (409 `dig_not_planned`). */
      remove: (id: string) => apiClient.delete<PfEnvelope<{ id: string; deleted: boolean }>>(`/pipeline-facility/digs/${id}`),
      addFeature: (digId: string, body: PfFeatureWrite) =>
        apiClient.post<PfEnvelope<PfDigFeature>>(`/pipeline-facility/digs/${digId}/features`, body),
      addRepair: (digId: string, body: PfRepairWrite) =>
        apiClient.post<PfEnvelope<PfDigRepair>>(`/pipeline-facility/digs/${digId}/repairs`, body),
      /** Draft the dig report; 409 `report_exists` names `existingId`. */
      createReport: (digId: string, body: PfReportWrite) =>
        apiClient.post<PfEnvelope<PfReportDetail>>(`/pipeline-facility/digs/${digId}/report`, body),
    },
    digFeatures: {
      update: (id: string, body: PfFeatureWrite) => apiClient.patch<PfEnvelope<PfDigFeature>>(`/pipeline-facility/dig-features/${id}`, body),
      remove: (id: string) => apiClient.delete<PfEnvelope<{ id: string; deleted: boolean }>>(`/pipeline-facility/dig-features/${id}`),
    },
    digRepairs: {
      update: (id: string, body: PfRepairWrite) => apiClient.patch<PfEnvelope<PfDigRepair>>(`/pipeline-facility/dig-repairs/${id}`, body),
      remove: (id: string) => apiClient.delete<PfEnvelope<{ id: string; deleted: boolean }>>(`/pipeline-facility/dig-repairs/${id}`),
    },
    welds: {
      list: (params?: { jobId?: string; digRepairId?: string; q?: string }) =>
        apiClient.get<PfEnvelope<PfWeld[]>>('/pipeline-facility/welds', { params }),
      forJob: (jobId: string) => apiClient.get<PfEnvelope<PfWeld[]>>(`/pipeline-facility/jobs/${jobId}/welds`),
      get: (id: string) => apiClient.get<PfEnvelope<PfWeld>>(`/pipeline-facility/welds/${id}`),
      /** 409 `weld_number_taken`. `ndt_report_upload_id` is refused here — attach it with `update`. */
      create: (jobId: string, body: PfWeldWrite) => apiClient.post<PfEnvelope<PfWeld>>(`/pipeline-facility/jobs/${jobId}/welds`, body),
      update: (id: string, body: PfWeldWrite) => apiClient.patch<PfEnvelope<PfWeld>>(`/pipeline-facility/welds/${id}`, body),
      remove: (id: string) => apiClient.delete<PfEnvelope<{ id: string; deleted: boolean }>>(`/pipeline-facility/welds/${id}`),
      /** 409 `already_linked` names `existingId`. */
      linkNdt: (weldId: string, body: { ndt_weld_uid: string }) =>
        apiClient.post<PfEnvelope<PfNdtLink>>(`/pipeline-facility/welds/${weldId}/ndt-links`, body),
      unlinkNdt: (linkId: string) => apiClient.delete<PfEnvelope<{ id: string; deleted: boolean }>>(`/pipeline-facility/weld-ndt-links/${linkId}`),
    },
    ptLinks: {
      create: (body: { pt_test_id: string; dig_repair_id?: string | null; segment_id?: string | null }) =>
        apiClient.post<PfEnvelope<PfPtLink>>('/pipeline-facility/pt-links', body),
      remove: (id: string) => apiClient.delete<PfEnvelope<{ id: string; deleted: boolean }>>(`/pipeline-facility/pt-links/${id}`),
      forSegment: (segmentId: string) => apiClient.get<PfEnvelope<PfPtLink[]>>(`/pipeline-facility/segments/${segmentId}/pt-links`),
    },
    /** Read-only pickers over this tenant's NDT welds and PT tests (PF_DISPATCH). */
    linkCandidates: {
      ndtWelds: (q?: string) =>
        apiClient.get<PfEnvelope<PfNdtCandidate[]>>('/pipeline-facility/link-candidates/ndt-welds', { params: q ? { q } : undefined }),
      ptTests: (q?: string) =>
        apiClient.get<PfEnvelope<PfPtCandidate[]>>('/pipeline-facility/link-candidates/pt-tests', { params: q ? { q } : undefined }),
    },
    reports: {
      /** No meta on the list. */
      list: (params?: { jobId?: string; digId?: string; status?: string }) =>
        apiClient.get<PfEnvelope<PfReport[]>>('/pipeline-facility/reports', { params }),
      get: (id: string) => apiClient.get<PfEnvelope<PfReportDetail>>(`/pipeline-facility/reports/${id}`),
      /** Draft only (409 `report_issued`). */
      update: (id: string, body: PfReportWrite) => apiClient.patch<PfEnvelope<PfReportDetail>>(`/pipeline-facility/reports/${id}`, body),
      remove: (id: string) => apiClient.delete<PfEnvelope<{ id: string; deleted: boolean }>>(`/pipeline-facility/reports/${id}`),
      /** The dig must be backfilled (409 `dig_not_backfilled`); issuing moves the dig to reported. */
      issue: (id: string) => apiClient.post<PfEnvelope<PfReportDetail>>(`/pipeline-facility/reports/${id}/issue`),
      /** A draft renders a DRAFT PDF; an issued one returns its stored PDF. */
      pdf: (id: string) => apiClient.get<Blob>(`/pipeline-facility/reports/${id}/pdf`, { responseType: 'blob' }),
      /**
       * PF-3b (fieldiq-api#1224): the AI drafts the summary into a pending run; the
       * report is not changed. Draft reports only (409 `report_issued`); 502 `ai_draft_failed`.
       */
      aiSummary: (id: string) =>
        apiClient.post<PfEnvelope<PfReportAiSummaryResult>>(`/pipeline-facility/reports/${id}/ai-summary`, undefined, { timeout: AI_TIMEOUT_MS }),
      /** `summary` is the person's edit; omit it to approve the draft as written. */
      approveAiSummary: (id: string, body: { run_id: string; summary?: string }) =>
        apiClient.post<PfEnvelope<PfReportDetail>>(`/pipeline-facility/reports/${id}/ai-summary/approve`, body),
      dismissAiSummary: (id: string, body: { run_id: string }) =>
        apiClient.post<PfEnvelope<{ runId: string; outcome: string }>>(`/pipeline-facility/reports/${id}/ai-summary/dismiss`, body),
    },
    /**
     * PF-4 (fieldiq-api#1227). The rate card, a rent-in's billing rate, and field
     * tickets drafted from the daily logs. camelCase reads, snake_case writes.
     * Refusals carry a `code` — read them with `pfTicketRefusal` (lib/pf-tickets).
     */
    rates: {
      /** customer_id: a customer's uuid, or 'default' for the default card. on: YYYY-MM-DD. */
      list: (params?: { customer_id?: string; kind?: string; on?: string }) =>
        apiClient.get<PfEnvelope<PfRate[]>>('/pipeline-facility/rates', { params }),
      get: (id: string) => apiClient.get<PfEnvelope<PfRate>>(`/pipeline-facility/rates/${id}`),
      /** Currency is stamped by the server; never sent. 409 `rate_overlap`, `currency_missing`. */
      create: (body: PfRateWrite) => apiClient.post<PfEnvelope<PfRate>>('/pipeline-facility/rates', body),
      /** 409 `rate_overlap`, `rate_in_use`. */
      update: (id: string, body: PfRateWrite) => apiClient.patch<PfEnvelope<PfRate>>(`/pipeline-facility/rates/${id}`, body),
      remove: (id: string) => apiClient.delete<PfEnvelope<{ id: string; deleted: boolean }>>(`/pipeline-facility/rates/${id}`),
    },
    rentInBilling: {
      get: (rentInId: string) => apiClient.get<PfEnvelope<PfRentInBilling>>(`/pipeline-facility/rent-ins/${rentInId}/billing-rate`),
      /** rate_id null clears it. 400 `rate_not_rent_in`, `rate_other_customer`. */
      set: (rentInId: string, rateId: string | null) =>
        apiClient.put<PfEnvelope<PfRentInBilling>>(`/pipeline-facility/rent-ins/${rentInId}/billing-rate`, { rate_id: rateId }),
    },
    tickets: {
      list: (params?: { job_id?: string; customer_id?: string; status?: string }) =>
        apiClient.get<PfEnvelope<PfTicket[]>>('/pipeline-facility/tickets', { params }),
      get: (id: string) => apiClient.get<PfEnvelope<PfTicket>>(`/pipeline-facility/tickets/${id}`),
      /** Draft one from the job's daily logs. work_date only on a per-day job. */
      create: (jobId: string, body: { work_date?: string }) =>
        apiClient.post<PfEnvelope<PfTicket>>(`/pipeline-facility/jobs/${jobId}/tickets`, body),
      /** Redraws the 'auto' lines; manual lines are kept. May return a signed ticket to draft. */
      refresh: (id: string) => apiClient.post<PfEnvelope<PfTicket> & { refreshed?: unknown }>(`/pipeline-facility/tickets/${id}/refresh`),
      update: (id: string, body: PfTicketHeaderWrite) => apiClient.patch<PfTicketEdit>(`/pipeline-facility/tickets/${id}`, body),
      remove: (id: string) => apiClient.delete<PfEnvelope<{ id: string; deleted: boolean }>>(`/pipeline-facility/tickets/${id}`),
      addLine: (id: string, body: PfTicketLineCreate) => apiClient.post<PfTicketEdit>(`/pipeline-facility/tickets/${id}/lines`, body),
      updateLine: (id: string, lineId: string, body: PfTicketLinePatch) =>
        apiClient.patch<PfTicketEdit>(`/pipeline-facility/tickets/${id}/lines/${lineId}`, body),
      removeLine: (id: string, lineId: string) => apiClient.delete<PfTicketEdit>(`/pipeline-facility/tickets/${id}/lines/${lineId}`),
      /** 409 `ticket_unpriced` (names `lines`), `ticket_empty`, `tax_unconfigured`. */
      officeApprove: (id: string) => apiClient.post<PfEnvelope<PfTicket>>(`/pipeline-facility/tickets/${id}/office-approve`),
      recipient: (id: string, params?: { email?: string; contact_name?: string }) =>
        apiClient.get<PfEnvelope<PfTicketRecipient>>(`/pipeline-facility/tickets/${id}/recipient`, { params }),
      /** A failed email is 502 `email_send_failed` and still carries `data.reviewUrl`. */
      send: (id: string, body: { email?: string; cc?: string[] }) =>
        apiClient.post<PfTicketSendResult>(`/pipeline-facility/tickets/${id}/send-to-customer`, body),
      /** Office-signed statuses only (a draft is 409). */
      pdf: (id: string) => apiClient.get<Blob>(`/pipeline-facility/tickets/${id}/pdf`, { responseType: 'blob' }),
      stampedFile: (id: string) => apiClient.get<Blob>(`/pipeline-facility/tickets/${id}/stamped-file`, { responseType: 'blob' }),
      /**
       * The office records the photo of the signed paper ticket / LEM. FormData:
       * file, approved_by_name, approved_on. No Content-Type here — the request
       * interceptor leaves it to the browser so the multipart boundary is set.
       * Once only: 409 `approval_already_recorded`.
       */
      recordApproval: (id: string, form: FormData) =>
        apiClient.post<PfEnvelope<PfTicket>>(`/pipeline-facility/tickets/${id}/customer-approval`, form),
    },
    /**
     * PF-5a (fieldiq-api#1229): invoices from customer-approved field tickets.
     * Reads camelCase, writes snake_case; refusals are `{ code, error }` — read
     * with `pfInvoiceRefusal` (lib/pf-invoices.ts). The PDF is a blob.
     */
    invoices: {
      list: (params?: { status?: string; customer_id?: string; job_id?: string }) =>
        apiClient.get<PfEnvelope<PfInvoiceRow[]>>('/pipeline-facility/invoices', { params }),
      /** One group per customer + province/state + currency — the set one invoice may bill. */
      billable: () => apiClient.get<PfEnvelope<PfBillableGroup[]>>('/pipeline-facility/invoices/billable'),
      /** 201 with the detail. 409 `ticket_already_invoiced`, `ticket_not_customer_approved`, `tickets_different_*`, `tax_unconfigured`. */
      generate: (body: PfInvoiceGenerate) => apiClient.post<PfEnvelope<PfInvoice>>('/pipeline-facility/invoices/generate', body),
      get: (id: string) => apiClient.get<PfEnvelope<PfInvoice>>(`/pipeline-facility/invoices/${id}`),
      pdf: (id: string) => apiClient.get<Blob>(`/pipeline-facility/invoices/${id}/pdf`, { responseType: 'blob' }),
      /** The office's write role only. `to` null carries `no_billing_email`. */
      recipient: (id: string) => apiClient.get<PfEnvelope<PfInvoiceRecipient>>(`/pipeline-facility/invoices/${id}/recipient`),
      /** draft → sent once the email is accepted. 502 `email_not_sent`; 409 `tax_fallback_blocked`, `no_billing_email`. */
      send: (id: string, body: { to?: string; cc?: string[] }) =>
        apiClient.post<PfEnvelope<PfInvoice & { sentToName?: string | null }>>(`/pipeline-facility/invoices/${id}/send`, body),
      /**
       * Mark paid is Finance's door since api#1232: PATCH /unified-invoices/:id
       * `{ status: 'paid' }` runs the Pipeline mark-paid (sent | approved → paid).
       * POST /pipeline-facility/invoices/:id/mark-paid is a deprecated alias and
       * is not called. Refusals: 409 invoice_not_sent / invoice_void / invoice_already_paid.
       */
      markPaid: (id: string) => apiClient.patch<{ success: boolean }>(`/unified-invoices/${id}`, { status: 'paid' }),
      /** Final. Echoes `releasedTicketIds`: the tickets are billable again. */
      void: (id: string, reason: string) => apiClient.post<PfEnvelope<PfInvoiceVoided>>(`/pipeline-facility/invoices/${id}/void`, { reason }),
    },
    /**
     * PF-6 (fieldiq-api#1236): the AI drafts — daily summary, ticket / job
     * preflight, compliance digest. Every run is a HITL draft (agent_runs,
     * outcome 'pending'); approve (optionally edited) or dismiss through the
     * PF doors below. camelCase, `{ success, data }` with no meta. Roles:
     * admin, office_staff, supervisor. A run calls the model and can take a
     * while; the deterministic `data` comes back even when the draft does not.
     * Refusals are `{ error, code }` — read with `pfAiRefusal` (lib/pf-ai.ts).
     */
    ai: {
      /** 400 job_id_required; 409 no_daily_log; 404. work_date defaults to the tenant's today. */
      dailySummary: (body: { job_id: string; work_date?: string }) =>
        apiClient.post<{ success: boolean; data: PfAiRunResult }>('/pipeline-facility/ai/daily-summary', body),
      /** 409 ticket_customer_approved; 404. */
      ticketPreflight: (ticketId: string) =>
        apiClient.post<{ success: boolean; data: PfAiRunResult }>(`/pipeline-facility/ai/ticket-preflight/${ticketId}`, {}),
      jobPreflight: (jobId: string) =>
        apiClient.post<{ success: boolean; data: PfAiRunResult }>(`/pipeline-facility/ai/job-preflight/${jobId}`, {}),
      complianceDigest: () =>
        apiClient.post<{ success: boolean; data: PfAiRunResult }>('/pipeline-facility/ai/compliance-digest', {}),
      /** Newest first, at most 20. */
      digests: (params?: { agent?: string; jobId?: string }) =>
        apiClient.get<{ success: boolean; data: PfAiStoredRun[] }>('/pipeline-facility/ai/digests', { params }),
      digest: (id: string) => apiClient.get<{ success: boolean; data: PfAiStoredRun }>(`/pipeline-facility/ai/digests/${id}`),
      /** 400 narrative_required / narrative_too_long / invalid_observations; 409 not_pending, no_draft. */
      approve: (id: string, body: PfAiApproveBody = {}) =>
        apiClient.post<{ success: boolean; data: PfAiStoredRun }>(`/pipeline-facility/ai/digests/${id}/approve`, body),
      /** 409 not_pending. */
      dismiss: (id: string) => apiClient.post<{ success: boolean; data: PfAiStoredRun }>(`/pipeline-facility/ai/digests/${id}/dismiss`, {}),
    },
    /** The phone surface. SNAKE_CASE both ways, including `meta`; crew members only. */
    field: {
      myJobs: () => apiClient.get<{ success: boolean; data: PfFieldJob[]; meta: PfFieldMeta }>('/pipeline-facility/field/my-jobs'),
      myJob: (id: string) =>
        apiClient.get<{ success: boolean; data: PfFieldJobDetail; meta: PfFieldMeta }>(`/pipeline-facility/field/my-jobs/${id}`),
      /** `data: null` when no log has been written for that day. */
      log: (jobId: string, workDate: string) =>
        apiClient.get<{ success: boolean; data: PfFieldLog | null; meta: PfFieldMeta }>(
          `/pipeline-facility/field/jobs/${jobId}/daily-logs/${workDate}`),
      /** Upsert by day: 201 created, 200 updated. */
      saveLog: (jobId: string, workDate: string, body: PfDailyLogWrite) =>
        apiClient.put<{ success: boolean; data: PfFieldLog; warnings?: PfFieldLogWarning[]; meta: PfFieldMeta }>(
          `/pipeline-facility/field/jobs/${jobId}/daily-logs/${workDate}`, body),
      /** PF-3a: digs on jobs I am crewed on (scheduled / in progress). snake_case. */
      digs: () => apiClient.get<{ success: boolean; data: PfFieldDig[]; meta: PfFieldMeta }>('/pipeline-facility/field/digs'),
      dig: (id: string) =>
        apiClient.get<{ success: boolean; data: PfFieldDigDetail; meta: PfFieldMeta }>(`/pipeline-facility/field/digs/${id}`),
      /** The dig sheet and a status step — same rules as the office PATCH. */
      updateDig: (id: string, body: PfDigPatch) =>
        apiClient.patch<{ success: boolean; data: PfFieldDigDetail; meta: PfFieldMeta }>(`/pipeline-facility/field/digs/${id}`, body),
      addFeature: (digId: string, body: PfFeatureWrite) =>
        apiClient.post<{ success: boolean; data: PfFieldDigFeature; meta: PfFieldMeta }>(`/pipeline-facility/field/digs/${digId}/features`, body),
      updateFeature: (id: string, body: PfFeatureWrite) =>
        apiClient.patch<{ success: boolean; data: PfFieldDigFeature; meta: PfFieldMeta }>(`/pipeline-facility/field/dig-features/${id}`, body),
    },
  },

  /**
   * Well Testing & Flowback — /api/v1/well-testing/* (fieldiq-api#1241, WT-1).
   * Every response is `{ success, data, meta }`; read it with `unwrapWt`
   * (hooks/useWellTesting.ts). READS are camelCase, WRITES snake_case.
   */
  wellTesting: {
    meta: () => apiClient.get<WtEnvelope<WtCatalog>>('/well-testing/meta'),
    /**
     * WT-6 (fieldiq-api#1261): the AI drafts — shift summary, ticket / job
     * preflight, test report draft, compliance digest. Every run is a HITL
     * draft (agent_runs, outcome 'pending'); approve (optionally edited) or
     * dismiss through the WT doors below. `{ success, data }` with NO meta —
     * read with the hooks in hooks/useWtAi.ts, not `unwrapWt`. Roles: admin,
     * office_staff, supervisor. Refusals are `{ error, code }` — `wtAiRefusal`.
     */
    ai: {
      /** 400 job_id_required / invalid_shift; 409 nothing_to_summarise; 404. work_date defaults to the tenant's today. */
      shiftSummary: (body: { job_id: string; shift: 'day' | 'night'; work_date?: string }) =>
        apiClient.post<{ success: boolean; data: WtAiRunResult }>('/well-testing/ai/shift-summary', body),
      ticketPreflight: (ticketId: string) =>
        apiClient.post<{ success: boolean; data: WtAiRunResult }>(`/well-testing/ai/ticket-preflight/${ticketId}`, {}),
      jobPreflight: (jobId: string) =>
        apiClient.post<{ success: boolean; data: WtAiRunResult }>(`/well-testing/ai/job-preflight/${jobId}`, {}),
      complianceDigest: () =>
        apiClient.post<{ success: boolean; data: WtAiRunResult }>('/well-testing/ai/compliance-digest', {}),
      /** Newest first, at most 20. 400 invalid_agent. */
      digests: (params?: { agent?: string; jobId?: string }) =>
        apiClient.get<{ success: boolean; data: WtAiStoredRun[] }>('/well-testing/ai/digests', { params }),
      digest: (id: string) => apiClient.get<{ success: boolean; data: WtAiStoredRun }>(`/well-testing/ai/digests/${id}`),
      /** 400 narrative_required / narrative_too_long / invalid_observations; 409 not_pending, no_draft, report_issued. */
      approve: (id: string, body: WtAiApproveBody = {}) =>
        apiClient.post<{ success: boolean; data: WtAiStoredRun }>(`/well-testing/ai/digests/${id}/approve`, body),
      /** 409 not_pending. */
      dismiss: (id: string) => apiClient.post<{ success: boolean; data: WtAiStoredRun }>(`/well-testing/ai/digests/${id}/dismiss`, {}),
    },
    // ── WT-4 (fieldiq-api#1253): the rate card and field tickets ──
    rates: {
      /** customer_id: a customer's uuid, or 'default' for the default card. on: YYYY-MM-DD. */
      list: (params?: { customer_id?: string; kind?: string; on?: string }) =>
        apiClient.get<WtEnvelope<WtRate[]>>('/well-testing/rates', { params }),
      get: (id: string) => apiClient.get<WtEnvelope<WtRate>>(`/well-testing/rates/${id}`),
      /** Currency is stamped by the server; never sent. 409 `rate_overlap`; 400 `third_party_rate`, `currency_missing`, … */
      create: (body: WtRateWrite) => apiClient.post<WtEnvelope<WtRate>>('/well-testing/rates', body),
      /** 409 `rate_overlap`, `rate_in_use`. */
      update: (id: string, body: WtRateWrite) => apiClient.patch<WtEnvelope<WtRate>>(`/well-testing/rates/${id}`, body),
      remove: (id: string) => apiClient.delete<WtEnvelope<{ id: string; deleted: boolean }>>(`/well-testing/rates/${id}`),
    },
    rentInBilling: {
      get: (rentInId: string) => apiClient.get<WtEnvelope<WtRentInBilling>>(`/well-testing/rent-ins/${rentInId}/billing-rate`),
      /** rate_id null clears it. 400 `rate_not_rent_in`, `rate_other_customer`. */
      set: (rentInId: string, rateId: string | null) =>
        apiClient.put<WtEnvelope<WtRentInBilling>>(`/well-testing/rent-ins/${rentInId}/billing-rate`, { rate_id: rateId }),
    },
    tickets: {
      list: (params?: { job_id?: string; customer_id?: string; status?: string }) =>
        apiClient.get<WtEnvelope<WtTicket[]>>('/well-testing/tickets', { params }),
      get: (id: string) => apiClient.get<WtEnvelope<WtTicket>>(`/well-testing/tickets/${id}`),
      /** Draft one from the job's shift logs. work_date only on a per-day job. */
      create: (jobId: string, body: { work_date?: string }) =>
        apiClient.post<WtEnvelope<WtTicket>>(`/well-testing/jobs/${jobId}/tickets`, body),
      /** Redraws the 'auto' lines; office lines are kept. May return a signed ticket to draft. */
      refresh: (id: string) => apiClient.post<WtEnvelope<WtTicket> & { refreshed?: unknown }>(`/well-testing/tickets/${id}/refresh`),
      update: (id: string, body: WtTicketHeaderWrite) => apiClient.patch<WtTicketEdit>(`/well-testing/tickets/${id}`, body),
      /** 409 `ticket_customer_approved`, `ticket_void`. */
      remove: (id: string) => apiClient.delete<WtEnvelope<{ id: string; deleted: boolean }>>(`/well-testing/tickets/${id}`),
      addLine: (id: string, body: WtTicketLineCreate) => apiClient.post<WtTicketEdit>(`/well-testing/tickets/${id}/lines`, body),
      updateLine: (id: string, lineId: string, body: WtTicketLinePatch) =>
        apiClient.patch<WtTicketEdit>(`/well-testing/tickets/${id}/lines/${lineId}`, body),
      removeLine: (id: string, lineId: string) => apiClient.delete<WtTicketEdit>(`/well-testing/tickets/${id}/lines/${lineId}`),
      /** 409 `ticket_unpriced` (names `lines`), `ticket_empty`, `tax_unconfigured`. */
      officeApprove: (id: string) => apiClient.post<WtEnvelope<WtTicket>>(`/well-testing/tickets/${id}/office-approve`),
      recipient: (id: string, params?: { email?: string; contact_name?: string }) =>
        apiClient.get<WtEnvelope<WtTicketRecipient>>(`/well-testing/tickets/${id}/recipient`, { params }),
      /** A failed email is 502 `email_send_failed` and still carries `data.reviewUrl`. */
      send: (id: string, body: { email?: string; cc?: string[] }) =>
        apiClient.post<WtTicketSendResult>(`/well-testing/tickets/${id}/send-to-customer`, body),
      /** Office-signed statuses only (a draft or void ticket is 409). */
      pdf: (id: string) => apiClient.get<Blob>(`/well-testing/tickets/${id}/pdf`, { responseType: 'blob' }),
      stampedFile: (id: string) => apiClient.get<Blob>(`/well-testing/tickets/${id}/stamped-file`, { responseType: 'blob' }),
      /**
       * The office records the photo of the signed paper ticket. FormData:
       * file, approved_by_name, approved_on. No Content-Type here — the request
       * interceptor leaves it to the browser so the multipart boundary is set.
       * Once only: 409 `approval_already_recorded`.
       */
      recordApproval: (id: string, form: FormData) =>
        apiClient.post<WtEnvelope<WtTicket>>(`/well-testing/tickets/${id}/customer-approval`, form),
      /** 400 `reason_required`, 409 `ticket_void`. */
      void: (id: string, reason: string) =>
        apiClient.post<WtEnvelope<WtTicket>>(`/well-testing/tickets/${id}/void`, { reason }),
    },
    /**
     * WT-5a (fieldiq-api#1254): invoices from customer-approved field tickets.
     * Reads camelCase, writes snake_case; refusals are `{ code, error }` — read
     * with `wtInvoiceRefusal` (lib/wt-invoices.ts). The PDF is a blob. There is
     * no mark-paid door here (Finance's, WT-5b).
     */
    invoices: {
      list: (params?: { status?: string; customer_id?: string; job_id?: string }) =>
        apiClient.get<WtEnvelope<WtInvoiceRow[]>>('/well-testing/invoices', { params }),
      /** One group per customer + province/state + currency — the set one invoice may bill. */
      billable: () => apiClient.get<WtEnvelope<WtBillableGroup[]>>('/well-testing/invoices/billable'),
      /** 201 with the detail. 409 `ticket_already_invoiced`, `ticket_not_customer_approved`, `tickets_different_*`, `tickets_mixed_currency`, `tax_unconfigured`. */
      generate: (body: WtInvoiceGenerate) => apiClient.post<WtEnvelope<WtInvoice>>('/well-testing/invoices/generate', body),
      get: (id: string) => apiClient.get<WtEnvelope<WtInvoice>>(`/well-testing/invoices/${id}`),
      pdf: (id: string) => apiClient.get<Blob>(`/well-testing/invoices/${id}/pdf`, { responseType: 'blob' }),
      /** The office's write role only. `to` null carries `no_billing_email`. */
      recipient: (id: string) => apiClient.get<WtEnvelope<WtInvoiceRecipient>>(`/well-testing/invoices/${id}/recipient`),
      /** draft → sent once the email is accepted. 502 `email_not_sent` (the invoice is unchanged); 409 `tax_fallback_blocked`, `no_billing_email`, `invoice_not_sendable`. */
      send: (id: string, body: { to?: string; cc?: string[] }) =>
        apiClient.post<WtEnvelope<WtInvoice & { sentToName?: string | null }>>(`/well-testing/invoices/${id}/send`, body),
      /** Final. 400 `reason_required`. Echoes `releasedTicketIds`: the tickets are billable again. */
      void: (id: string, reason: string) => apiClient.post<WtEnvelope<WtInvoiceVoided>>(`/well-testing/invoices/${id}/void`, { reason }),
    },
    settings: {
      get: () => apiClient.get<WtEnvelope<WtSettings>>('/well-testing/settings'),
      /** A nullable key sent as null turns that flag off. */
      update: (body: WtSettingsUpdate) => apiClient.put<WtEnvelope<WtSettings>>('/well-testing/settings', body),
    },
    customerSettings: {
      get: (customerId: string) =>
        apiClient.get<WtEnvelope<WtCustomerSettings>>(`/well-testing/customers/${customerId}/settings`),
      update: (customerId: string, body: WtCustomerSettingsUpdate) =>
        apiClient.put<WtEnvelope<WtCustomerSettings>>(`/well-testing/customers/${customerId}/settings`, body),
    },
    wells: {
      /** Filters: customerId, fluidType, provinceState, q (name, UWI, API number, licence, location, field, pool, customer). */
      list: (params?: { customerId?: string; fluidType?: string; provinceState?: string; q?: string }) =>
        apiClient.get<WtEnvelope<WtWell[]>>('/well-testing/wells', { params }),
      get: (id: string) => apiClient.get<WtEnvelope<WtWell>>(`/well-testing/wells/${id}`),
      /** 409 `code: 'duplicate_well'` naming the existing well (`existingWellId`, `field`). */
      create: (body: WtWellWrite) => apiClient.post<WtEnvelope<WtWell>>('/well-testing/wells', body),
      update: (id: string, body: WtWellWrite) => apiClient.patch<WtEnvelope<WtWell>>(`/well-testing/wells/${id}`, body),
      /** Soft delete; frees the well's UWI / API number. */
      remove: (id: string) => apiClient.delete<WtEnvelope<WtWellDeleted>>(`/well-testing/wells/${id}`),
    },
    // ── WT-2 (fieldiq-api#1245): packages, jobs, crew, rent-ins, shift logs ──
    equipmentSpecs: {
      list: (params?: { componentKind?: string }) =>
        apiClient.get<WtEnvelope<WtEquipmentSpec[]>>('/well-testing/equipment-specs', { params }),
      /** `data: null` when the unit has no spec yet. */
      get: (equipmentId: string) => apiClient.get<WtEnvelope<WtEquipmentSpec | null>>(`/well-testing/equipment-specs/${equipmentId}`),
      /** An upsert: 201 when created, 200 when updated. */
      save: (equipmentId: string, body: WtEquipmentSpecWrite) =>
        apiClient.put<WtEnvelope<WtEquipmentSpec>>(`/well-testing/equipment-specs/${equipmentId}`, body),
      remove: (equipmentId: string) => apiClient.delete<WtEnvelope<{ id: string; deleted: boolean }>>(`/well-testing/equipment-specs/${equipmentId}`),
    },
    packages: {
      list: () => apiClient.get<WtEnvelope<WtPackage[]>>('/well-testing/packages'),
      get: (id: string) => apiClient.get<WtEnvelope<WtPackageDetail>>(`/well-testing/packages/${id}`),
      create: (body: WtPackageWrite) => apiClient.post<WtEnvelope<WtPackageDetail>>('/well-testing/packages', body),
      update: (id: string, body: WtPackageWrite) => apiClient.patch<WtEnvelope<WtPackageDetail>>(`/well-testing/packages/${id}`, body),
      /** 409 package_on_open_job. */
      remove: (id: string) => apiClient.delete<WtEnvelope<{ id: string; deleted: boolean }>>(`/well-testing/packages/${id}`),
      /** 409 unit_in_other_package. */
      addUnit: (id: string, body: { equipment_id: string; sort_order?: number | null; notes?: string | null }) =>
        apiClient.post<WtEnvelope<WtPackageUnit>>(`/well-testing/packages/${id}/units`, body),
      removeUnit: (unitId: string) => apiClient.delete<WtEnvelope<{ id: string; deleted: boolean }>>(`/well-testing/package-units/${unitId}`),
    },
    jobs: {
      /** A field hand's list is the jobs they are crewed on. */
      list: (params?: { status?: string; customerId?: string; wellId?: string; from?: string; to?: string; q?: string }) =>
        apiClient.get<WtEnvelope<WtJob[]>>('/well-testing/jobs', { params }),
      get: (id: string) => apiClient.get<WtEnvelope<WtJobDetail>>(`/well-testing/jobs/${id}`),
      create: (body: WtJobWrite) => apiClient.post<WtEnvelope<WtJobDetail>>('/well-testing/jobs', body),
      /** Fields and/or `status` (with `cancel_reason` when cancelling). 409 invalid_transition / job_closed. */
      update: (id: string, body: WtJobWrite) => apiClient.patch<WtEnvelope<WtJobDetail>>(`/well-testing/jobs/${id}`, body),
      /** 409 job_not_deletable / job_has_logs. */
      remove: (id: string) => apiClient.delete<WtEnvelope<{ id: string; deleted: boolean }>>(`/well-testing/jobs/${id}`),
    },
    crew: {
      add: (jobId: string, body: WtCrewWrite) => apiClient.post<WtEnvelope<WtCrewMember>>(`/well-testing/jobs/${jobId}/crew`, body),
      update: (id: string, body: WtCrewWrite) => apiClient.patch<WtEnvelope<WtCrewMember>>(`/well-testing/crew/${id}`, body),
      remove: (id: string) => apiClient.delete<WtEnvelope<{ id: string; deleted: boolean }>>(`/well-testing/crew/${id}`),
    },
    jobEquipment: {
      /** One unit or a whole package; the answer is the rows added (an array). */
      add: (jobId: string, body: WtJobEquipmentWrite) =>
        apiClient.post<WtEnvelope<WtJobEquipment[]>>(`/well-testing/jobs/${jobId}/equipment`, body),
      update: (id: string, body: WtJobEquipmentWrite) => apiClient.patch<WtEnvelope<WtJobEquipment>>(`/well-testing/job-equipment/${id}`, body),
      /** 409 unit_has_hours. */
      remove: (id: string) => apiClient.delete<WtEnvelope<{ id: string; deleted: boolean }>>(`/well-testing/job-equipment/${id}`),
    },
    rentIns: {
      add: (jobId: string, body: WtRentInWrite) => apiClient.post<WtEnvelope<WtRentIn>>(`/well-testing/jobs/${jobId}/rent-ins`, body),
      update: (id: string, body: WtRentInWrite) => apiClient.patch<WtEnvelope<WtRentIn>>(`/well-testing/rent-ins/${id}`, body),
      /** 409 rent_in_has_hours. */
      remove: (id: string) => apiClient.delete<WtEnvelope<{ id: string; deleted: boolean }>>(`/well-testing/rent-ins/${id}`),
    },
    shiftLogs: {
      list: (jobId: string) => apiClient.get<WtEnvelope<WtShiftLogSummary[]>>(`/well-testing/jobs/${jobId}/shift-logs`),
      get: (id: string) => apiClient.get<WtEnvelope<WtShiftLog>>(`/well-testing/shift-logs/${id}`),
      create: (jobId: string, body: WtShiftLogWrite) =>
        apiClient.post<WtEnvelope<WtShiftLog> & { warnings?: WtShiftLogWarning[] }>(`/well-testing/jobs/${jobId}/shift-logs`, body),
      update: (id: string, body: WtShiftLogWrite) =>
        apiClient.patch<WtEnvelope<WtShiftLog> & { warnings?: WtShiftLogWarning[] }>(`/well-testing/shift-logs/${id}`, body),
      remove: (id: string) => apiClient.delete<WtEnvelope<{ id: string; deleted: boolean }>>(`/well-testing/shift-logs/${id}`),
    },
    /** The phone surface — SNAKE_CASE reads, crew only (403 not_on_crew). */
    field: {
      myJobs: () => apiClient.get<{ success: boolean; data: WtFieldJob[]; meta: WtFieldMeta }>('/well-testing/field/my-jobs'),
      job: (id: string) => apiClient.get<{ success: boolean; data: WtFieldJobDetail; meta: WtFieldMeta }>(`/well-testing/field/jobs/${id}`),
      /** `data: null` when no log is written for that day and shift. */
      log: (jobId: string, day: string, shift: string) =>
        apiClient.get<{ success: boolean; data: WtFieldShiftLog | null; meta: WtFieldMeta }>(`/well-testing/field/jobs/${jobId}/shift-logs/${day}/${shift}`),
      /** An upsert by day and shift: 201 created, 200 updated. */
      saveLog: (jobId: string, day: string, shift: string, body: WtShiftLogWrite) =>
        apiClient.put<{ success: boolean; data: WtFieldShiftLog; warnings?: WtShiftLogWarning[]; meta: WtFieldMeta }>(
          `/well-testing/field/jobs/${jobId}/shift-logs/${day}/${shift}`, body),
      /** WT-3. The last N live readings (newest first), the prefill, the interval and when the next is due. */
      readings: (jobId: string, params?: { limit?: number }) =>
        apiClient.get<{ success: boolean; data: WtFieldReadings; meta: WtFieldMeta }>(`/well-testing/field/jobs/${jobId}/readings`, { params }),
      /** 403 not_on_crew; 409 job_not_scheduled / job_closed / reading_exists; 400 future_read_at / tank_not_on_job. */
      saveReading: (jobId: string, body: WtReadingWrite) =>
        apiClient.post<{ success: boolean; data: Record<string, unknown>; meta: WtFieldMeta }>(`/well-testing/field/jobs/${jobId}/readings`, body),
    },
    // ─── WT-3 (fieldiq-api#1248) ───────────────────────────────────────────
    readingFields: () => apiClient.get<WtEnvelope<WtReadingFields>>('/well-testing/reading-fields'),
    periods: {
      list: (jobId: string) => apiClient.get<WtEnvelope<WtTestPeriod[]>>(`/well-testing/jobs/${jobId}/periods`),
      /** Opening a period closes the open one at the new start. 409 period_overlap / period_has_later_readings. */
      create: (jobId: string, body: { kind: string; started_at?: string; notes?: string | null }) =>
        apiClient.post<WtEnvelope<WtTestPeriod>>(`/well-testing/jobs/${jobId}/periods`, body),
      /** ended_at null reopens it. 409 period_open / period_has_later_readings. */
      update: (id: string, body: { ended_at?: string | null; notes?: string | null }) =>
        apiClient.patch<WtEnvelope<WtTestPeriod>>(`/well-testing/periods/${id}`, body),
    },
    readings: {
      /** Oldest first. */
      list: (jobId: string, params?: { from?: string; to?: string; include_superseded?: 'true'; limit?: number }) =>
        apiClient.get<WtEnvelope<WtReading[]>>(`/well-testing/jobs/${jobId}/readings`, { params }),
      series: (jobId: string, params: { fields: string; from?: string; to?: string }) =>
        apiClient.get<WtEnvelope<WtSeries>>(`/well-testing/jobs/${jobId}/readings/series`, { params }),
      get: (id: string) => apiClient.get<WtEnvelope<WtReading>>(`/well-testing/readings/${id}`),
      create: (jobId: string, body: WtReadingWrite) => apiClient.post<WtEnvelope<WtReading>>(`/well-testing/jobs/${jobId}/readings`, body),
      /** A NEW reading superseding this one; needs correction_reason. 409 reading_superseded. */
      correct: (id: string, body: WtReadingWrite & { correction_reason: string }) =>
        apiClient.post<WtEnvelope<WtReading>>(`/well-testing/readings/${id}/correct`, body),
      /**
       * WT-3b (fieldiq-api#1252): the shift's data-logger file (CSV / XLSX / XLS,
       * at most 10 MB). The AI maps the columns; the API proposes rows and SAVES
       * NOTHING. FormData: file. No Content-Type here — the request interceptor
       * strips it on multipart so the browser sets the boundary.
       * 503 `ai_unavailable`, 502 `ai_parse_failed`, 409 `job_not_scheduled` / `job_closed`.
       */
      importParse: (jobId: string, form: FormData) =>
        apiClient.post<WtEnvelope<WtImportParseResult>>(`/well-testing/jobs/${jobId}/readings/import/parse`, form),
      /** The rows a person kept. All-or-nothing (400 names `rows[i].field`); 201 when any inserted, 200 when all skipped. */
      importConfirm: (jobId: string, body: WtImportConfirmBody) =>
        apiClient.post<WtEnvelope<WtImportResult>>(`/well-testing/jobs/${jobId}/readings/import/confirm`, body),
      /** Abandon a parse (its agent run → dismissed). Saves nothing. */
      dismissImport: (jobId: string, runId: string) =>
        apiClient.post<WtEnvelope<{ runId: string; outcome: string }>>(`/well-testing/jobs/${jobId}/readings/import/runs/${runId}/dismiss`),
    },
    dailyVolumes: {
      list: (jobId: string) => apiClient.get<WtEnvelope<WtDailyVolumes>>(`/well-testing/jobs/${jobId}/daily-volumes`),
      /** Office roles only. 400 future_day; 409 job_not_scheduled / job_closed. */
      save: (jobId: string, body: WtDailyVolumeWrite) => apiClient.put<WtEnvelope<WtDailyVolume>>(`/well-testing/jobs/${jobId}/daily-volumes`, body),
    },
    flags: {
      /** Newest reading first. */
      list: (jobId: string, params?: { open?: 'true' }) => apiClient.get<WtEnvelope<WtFlag[]>>(`/well-testing/jobs/${jobId}/flags`, { params }),
      /** 409 flag_acknowledged. */
      ack: (id: string, body: { note?: string | null }) => apiClient.post<WtEnvelope<WtFlag>>(`/well-testing/flags/${id}/ack`, body),
    },
    reports: {
      list: (jobId: string) => apiClient.get<WtEnvelope<WtReport[]>>(`/well-testing/jobs/${jobId}/reports`),
      /** 409 report_exists / job_not_scheduled / job_cancelled. */
      create: (jobId: string, body: WtReportWrite) => apiClient.post<WtEnvelope<WtReportDetail>>(`/well-testing/jobs/${jobId}/reports`, body),
      get: (id: string) => apiClient.get<WtEnvelope<WtReportDetail>>(`/well-testing/reports/${id}`),
      /** A draft only (409 report_issued). */
      update: (id: string, body: WtReportWrite) => apiClient.patch<WtEnvelope<WtReportDetail>>(`/well-testing/reports/${id}`, body),
      remove: (id: string) => apiClient.delete<WtEnvelope<{ id: string; deleted: boolean }>>(`/well-testing/reports/${id}`),
      /** Numbers it WTR-NNNN. 409 job_not_completed / already_issued / report_changed; 400 signatory_unknown. */
      issue: (id: string) => apiClient.post<WtEnvelope<WtReportDetail>>(`/well-testing/reports/${id}/issue`, {}),
      /** A draft is stamped DRAFT. */
      pdf: (id: string) => apiClient.get<Blob>(`/well-testing/reports/${id}/pdf`, { responseType: 'blob' }),
    },
  },

  /**
   * NDT / Inspections — /api/v1/ndt-inspection/* (fieldiq-api#1134, batch 1).
   * Every response is `{ success, data, meta }`; read it with `unwrapNdt`
   * (hooks/useNdtInspection.ts). READS ARE camelCase, unlike PT and Downhole.
   * WRITES take snake_case keys, with lengths / activity / dose in the tenant's
   * units (`meta.units`) — the API converts, so nothing here does.
   *
   * An instrument is an EQUIPMENT row with an NDT specs row: `instruments.attach`
   * takes an id from `api.equipment.create` (or an existing unit); it never
   * makes one. Detach removes the specs only.
   */
  ndtInspection: {
    catalog: () => apiClient.get<NdtEnvelope<NdtCatalog>>('/ndt-inspection/catalog'),
    settings: {
      get: () => apiClient.get<NdtEnvelope<NdtSettings>>('/ndt-inspection/settings'),
      update: (body: NdtSettingsUpdate) => apiClient.put<NdtEnvelope<NdtSettings>>('/ndt-inspection/settings', body),
    },
    certs: {
      /** `status` filters on the API's computed standing. */
      list: (params?: { userId?: string; method?: string; status?: string }) =>
        apiClient.get<NdtEnvelope<NdtTechnicianCert[]>>('/ndt-inspection/technicians/certs', { params }),
      get: (id: string) => apiClient.get<NdtEnvelope<NdtTechnicianCert>>(`/ndt-inspection/technicians/certs/${id}`),
      create: (body: NdtCertWrite) => apiClient.post<NdtEnvelope<NdtTechnicianCert>>('/ndt-inspection/technicians/certs', body),
      update: (id: string, body: NdtCertWrite) =>
        apiClient.patch<NdtEnvelope<NdtTechnicianCert>>(`/ndt-inspection/technicians/certs/${id}`, body),
      remove: (id: string) => apiClient.delete<NdtEnvelope<{ id: string; deleted: boolean }>>(`/ndt-inspection/technicians/certs/${id}`),
    },
    welders: {
      list: (params?: { q?: string }) => apiClient.get<NdtEnvelope<NdtWelder[]>>('/ndt-inspection/welders', { params }),
      get: (id: string) => apiClient.get<NdtEnvelope<NdtWelder>>(`/ndt-inspection/welders/${id}`),
      /** 409 when another welder holds the stamp. */
      create: (body: NdtWelderWrite) => apiClient.post<NdtEnvelope<NdtWelder>>('/ndt-inspection/welders', body),
      update: (id: string, body: NdtWelderWrite) => apiClient.patch<NdtEnvelope<NdtWelder>>(`/ndt-inspection/welders/${id}`, body),
      remove: (id: string) => apiClient.delete<NdtEnvelope<{ id: string; deleted: boolean }>>(`/ndt-inspection/welders/${id}`),
      addQual: (welderId: string, body: NdtQualWrite) =>
        apiClient.post<NdtEnvelope<NdtWelderQual>>(`/ndt-inspection/welders/${welderId}/quals`, body),
      updateQual: (id: string, body: NdtQualWrite) =>
        apiClient.patch<NdtEnvelope<NdtWelderQual>>(`/ndt-inspection/welder-quals/${id}`, body),
      removeQual: (id: string) => apiClient.delete<NdtEnvelope<{ id: string; deleted: boolean }>>(`/ndt-inspection/welder-quals/${id}`),
    },
    instruments: {
      list: (params?: { kind?: string; status?: string; q?: string }) =>
        apiClient.get<NdtEnvelope<NdtInstrument[]>>('/ndt-inspection/instruments', { params }),
      get: (id: string) => apiClient.get<NdtEnvelope<NdtInstrumentDetail>>(`/ndt-inspection/instruments/${id}`),
      /** 404 for an unknown equipment id, 409 when it is already an NDT instrument. */
      attach: (body: NdtSpecsWrite & { equipment_id: string }) =>
        apiClient.post<NdtEnvelope<NdtInstrument>>('/ndt-inspection/instruments', body),
      update: (id: string, body: NdtSpecsWrite) => apiClient.patch<NdtEnvelope<NdtInstrument>>(`/ndt-inspection/instruments/${id}`, body),
      detach: (id: string) => apiClient.delete<NdtEnvelope<{ id: string; detached: boolean }>>(`/ndt-inspection/instruments/${id}`),
      /** Append-only. `due_on` may be omitted when the instrument has an interval. */
      recordCalibration: (id: string, body: NdtCalibrationCreate) =>
        apiClient.post<NdtEnvelope<NdtCalibration> & { instrument: NdtInstrument }>(
          `/ndt-inspection/instruments/${id}/calibrations`,
          body,
        ),
    },
    sources: {
      list: (params?: { status?: string; isotope?: string }) =>
        apiClient.get<NdtEnvelope<NdtSource[]>>('/ndt-inspection/sources', { params }),
      get: (id: string) => apiClient.get<NdtEnvelope<NdtSourceDetail>>(`/ndt-inspection/sources/${id}`),
      create: (body: NdtSourceWrite) => apiClient.post<NdtEnvelope<NdtSource>>('/ndt-inspection/sources', body),
      update: (id: string, body: NdtSourceWrite) => apiClient.patch<NdtEnvelope<NdtSource>>(`/ndt-inspection/sources/${id}`, body),
      /** 409 `source_checked_out` while someone holds it. */
      remove: (id: string) => apiClient.delete<NdtEnvelope<{ id: string; deleted: boolean }>>(`/ndt-inspection/sources/${id}`),
      /**
       * A custody event. 409 codes: already_checked_out, not_checked_out,
       * not_in_service, same_holder. 403 (field_worker): not_self, not_holder.
       */
      log: (id: string, body: NdtSourceLogCreate) =>
        apiClient.post<NdtEnvelope<NdtSourceLog> & { source: NdtSource }>(`/ndt-inspection/sources/${id}/logs`, body),
      /** Activity on a day (default the tenant's today), computed by the API. */
      decay: (id: string, on?: string) =>
        apiClient.get<NdtEnvelope<NdtDecay>>(`/ndt-inspection/sources/${id}/decay`, { params: on ? { on } : undefined }),
    },
    dosimetry: {
      /** A field_worker is answered their own records only, whatever `userId` says. */
      list: (params?: { userId?: string }) => apiClient.get<NdtEnvelope<NdtDose[]>>('/ndt-inspection/dosimetry', { params }),
      create: (body: NdtDoseWrite) => apiClient.post<NdtEnvelope<NdtDose>>('/ndt-inspection/dosimetry', body),
      update: (id: string, body: NdtDoseWrite) => apiClient.patch<NdtEnvelope<NdtDose>>(`/ndt-inspection/dosimetry/${id}`, body),
      remove: (id: string) => apiClient.delete<NdtEnvelope<{ id: string; deleted: boolean }>>(`/ndt-inspection/dosimetry/${id}`),
    },
    /*
     * Batch 2 (fieldiq-api#1144): jobs, crew, welds, exams, indications, repair
     * rates. Office reads camelCase; writes snake_case, lengths in meta.units.
     * A refusal is `{ code, error }` — read it with `ndtRefusal`
     * (lib/ndt-inspection.ts). An exam write also answers `warnings[]`.
     */
    jobs: {
      list: (params?: { status?: string; customerId?: string; from?: string; to?: string; q?: string }) =>
        apiClient.get<NdtEnvelope<NdtJob[]>>('/ndt-inspection/jobs', { params }),
      get: (id: string) => apiClient.get<NdtEnvelope<NdtJobDetail>>(`/ndt-inspection/jobs/${id}`),
      create: (body: NdtJobWrite) => apiClient.post<NdtEnvelope<NdtJob>>('/ndt-inspection/jobs', body),
      update: (id: string, body: NdtJobWrite) => apiClient.patch<NdtEnvelope<NdtJob>>(`/ndt-inspection/jobs/${id}`, body),
      remove: (id: string) => apiClient.delete<NdtEnvelope<{ id: string; deleted: boolean }>>(`/ndt-inspection/jobs/${id}`),
      addCrew: (jobId: string, body: NdtCrewWrite) => apiClient.post<NdtEnvelope<NdtCrew>>(`/ndt-inspection/jobs/${jobId}/crew`, body),
      welds: (jobId: string) => apiClient.get<NdtEnvelope<NdtWeld[]>>(`/ndt-inspection/jobs/${jobId}/welds`),
      addWeld: (jobId: string, body: NdtWeldWrite) => apiClient.post<NdtEnvelope<NdtWeld>>(`/ndt-inspection/jobs/${jobId}/welds`, body),
    },
    crew: {
      update: (id: string, body: NdtCrewWrite) => apiClient.patch<NdtEnvelope<NdtCrew>>(`/ndt-inspection/crew/${id}`, body),
      remove: (id: string) => apiClient.delete<NdtEnvelope<{ id: string; deleted: boolean }>>(`/ndt-inspection/crew/${id}`),
    },
    welds: {
      list: (params?: { jobId?: string; welderId?: string; externalRef?: string; standing?: string; q?: string }) =>
        apiClient.get<NdtEnvelope<NdtWeld[]>>('/ndt-inspection/welds', { params }),
      get: (id: string) => apiClient.get<NdtEnvelope<NdtWeldDetail>>(`/ndt-inspection/welds/${id}`),
      update: (id: string, body: NdtWeldWrite) => apiClient.patch<NdtEnvelope<NdtWeld>>(`/ndt-inspection/welds/${id}`, body),
      remove: (id: string) => apiClient.delete<NdtEnvelope<{ id: string; deleted: boolean }>>(`/ndt-inspection/welds/${id}`),
      addExam: (weldId: string, body: NdtExamWrite) =>
        apiClient.post<NdtEnvelope<NdtExam> & { warnings?: NdtShotWarning[] }>(`/ndt-inspection/welds/${weldId}/exams`, body),
    },
    exams: {
      update: (id: string, body: NdtExamWrite) =>
        apiClient.patch<NdtEnvelope<NdtExam> & { warnings?: NdtShotWarning[] }>(`/ndt-inspection/exams/${id}`, body),
      remove: (id: string) => apiClient.delete<NdtEnvelope<{ id: string; deleted: boolean }>>(`/ndt-inspection/exams/${id}`),
      addIndication: (examId: string, body: NdtIndicationWrite) =>
        apiClient.post<NdtEnvelope<NdtIndication>>(`/ndt-inspection/exams/${examId}/indications`, body),
    },
    indications: {
      remove: (id: string) => apiClient.delete<NdtEnvelope<{ id: string; deleted: boolean }>>(`/ndt-inspection/indications/${id}`),
    },
    repairRates: (params?: { jobId?: string; welderId?: string; from?: string; to?: string }) =>
      apiClient.get<NdtEnvelope<NdtRepairRates>>('/ndt-inspection/repair-rates', { params }),
    /**
     * Inspection reports (fieldiq-api#1152). Reads camelCase, writes snake_case.
     * A refusal is `{ code, error }` (+ `reportId` on final_exists) — read it
     * with `ndtReportRefusal` (lib/ndt-reports.ts). Regenerate only queues the
     * AI; the new draft lands later, so the screen refetches. The PDF is a blob:
     * read its error with `getBlobApiErrorMessage`.
     */
    reports: {
      list: (params?: { jobId?: string; status?: string; kind?: string; q?: string }) =>
        apiClient.get<NdtEnvelope<NdtReport[]>>('/ndt-inspection/reports', { params }),
      get: (id: string) => apiClient.get<NdtEnvelope<NdtReport>>(`/ndt-inspection/reports/${id}`),
      create: (body: NdtReportCreate) => apiClient.post<NdtEnvelope<NdtReport>>('/ndt-inspection/reports', body),
      update: (id: string, body: NdtReportUpdate) => apiClient.patch<NdtEnvelope<NdtReport>>(`/ndt-inspection/reports/${id}`, body),
      approveSummary: (id: string, body: { summary?: string }) =>
        apiClient.post<NdtEnvelope<NdtReport>>(`/ndt-inspection/reports/${id}/approve-summary`, body),
      regenerateSummary: (id: string) => apiClient.post<NdtEnvelope<NdtReport>>(`/ndt-inspection/reports/${id}/regenerate-summary`, {}),
      issue: (id: string) =>
        apiClient.post<NdtEnvelope<NdtReport> & { warnings?: NdtReportWarning[] }>(`/ndt-inspection/reports/${id}/issue`, {}),
      void: (id: string, body: { reason: string }) => apiClient.post<NdtEnvelope<NdtReport>>(`/ndt-inspection/reports/${id}/void`, body),
      pdf: (id: string) => apiClient.get<Blob>(`/ndt-inspection/reports/${id}/pdf`, { responseType: 'blob' }),
    },
    /**
     * Field tickets (batch 4, fieldiq-api#1157). Reads camelCase, writes
     * snake_case; refusals are `{ code, error }` — read with `ndtTicketRefusal`
     * (lib/ndt-tickets.ts). Never send `amount`: the API computes it. A rate or
     * rate_id is office-only (403 rate_office_only). The PDF is a blob.
     */
    tickets: {
      list: (params?: { status?: string; jobId?: string; customerId?: string; from?: string; to?: string }) =>
        apiClient.get<NdtEnvelope<NdtTicket[]>>('/ndt-inspection/tickets', { params }),
      get: (id: string) => apiClient.get<NdtEnvelope<NdtTicketDetail>>(`/ndt-inspection/tickets/${id}`),
      forJob: (jobId: string) => apiClient.get<NdtEnvelope<NdtTicket[]>>(`/ndt-inspection/jobs/${jobId}/tickets`),
      rebuild: (jobId: string) => apiClient.post<NdtEnvelope<NdtTicket[]>>(`/ndt-inspection/jobs/${jobId}/tickets`, {}),
      closeDay: (jobId: string, workDate: string) =>
        apiClient.post<NdtEnvelope<NdtTicketDetail>>(`/ndt-inspection/jobs/${jobId}/tickets/close-day`, { work_date: workDate }),
      update: (id: string, body: NdtTicketHeaderUpdate) => apiClient.patch<NdtEnvelope<NdtTicketDetail>>(`/ndt-inspection/tickets/${id}`, body),
      remove: (id: string) => apiClient.delete<NdtEnvelope<{ id: string; deleted: boolean }>>(`/ndt-inspection/tickets/${id}`),
      addLine: (id: string, body: NdtLineCreate) => apiClient.post<NdtEnvelope<NdtTicketDetail>>(`/ndt-inspection/tickets/${id}/lines`, body),
      updateLine: (lineId: string, body: NdtLineUpdate) =>
        apiClient.patch<NdtEnvelope<NdtTicketDetail>>(`/ndt-inspection/ticket-lines/${lineId}`, body),
      removeLine: (lineId: string, reason: string | null) =>
        apiClient.delete<NdtEnvelope<NdtTicketDetail>>(`/ndt-inspection/ticket-lines/${lineId}`, { data: reason ? { reason } : {} }),
      reprice: (id: string) => apiClient.post<NdtEnvelope<NdtTicketDetail>>(`/ndt-inspection/tickets/${id}/reprice`, {}),
      approve: (id: string) => apiClient.post<NdtEnvelope<NdtTicketDetail>>(`/ndt-inspection/tickets/${id}/approve`, {}),
      void: (id: string, reason: string) => apiClient.post<NdtEnvelope<NdtTicketDetail>>(`/ndt-inspection/tickets/${id}/void`, { reason }),
      pdf: (id: string) => apiClient.get<Blob>(`/ndt-inspection/tickets/${id}/pdf`, { responseType: 'blob' }),
    },
    /**
     * Invoices (batch 5, fieldiq-api#1166): approved field tickets → NDI-
     * invoices. Reads camelCase with no meta; writes snake_case; refusals are
     * `{ code, error }` — read with `ndtInvoiceRefusal` (lib/ndt-invoices.ts).
     * Mark paid has no NDT door: it is the shared PATCH /unified-invoices/:id
     * `{ status: 'paid' }`, called from the NDT invoice page only.
     */
    invoices: {
      list: (params?: { status?: string; customerId?: string; jobId?: string }) =>
        apiClient.get<NdtInvoiceEnvelope<NdtInvoiceRow[]>>('/ndt-inspection/invoices', { params }),
      billable: () => apiClient.get<NdtInvoiceEnvelope<NdtBillableJob[]>>('/ndt-inspection/invoices/billable'),
      generate: (body: NdtInvoiceGenerate) => apiClient.post<NdtInvoiceEnvelope<NdtInvoice>>('/ndt-inspection/invoices/generate', body),
      get: (id: string) => apiClient.get<NdtInvoiceEnvelope<NdtInvoice>>(`/ndt-inspection/invoices/${id}`),
      pdf: (id: string) => apiClient.get<Blob>(`/ndt-inspection/invoices/${id}/pdf`, { responseType: 'blob' }),
      recipient: (id: string) => apiClient.get<NdtInvoiceEnvelope<NdtInvoiceRecipient>>(`/ndt-inspection/invoices/${id}/recipient`),
      send: (id: string, body: { to?: string; cc?: string[] }) =>
        apiClient.post<NdtInvoiceEnvelope<NdtInvoice & { sentToName?: string | null }>>(`/ndt-inspection/invoices/${id}/send`, body),
      /** 409 `void_not_available` until fieldiq-api#1168 is live; then final, its tickets back to approved. */
      void: (id: string, reason: string) => apiClient.post<NdtInvoiceEnvelope<NdtInvoice>>(`/ndt-inspection/invoices/${id}/void`, { reason }),
      markPaid: (id: string) => apiClient.patch<{ success: boolean }>(`/unified-invoices/${id}`, { status: 'paid' }),
    },
    /**
     * AI digests (batch 6, fieldiq-api#1177 / #1178). Every run is a HITL draft
     * (agent_runs, outcome 'pending'), approved or dismissed through the shared
     * api.agentRuns doors. camelCase, `{ success, data }` with no meta. Roles:
     * admin, office_staff, supervisor. A run calls the model, so it can take a
     * while; the deterministic figures come back even when the draft does not.
     */
    ai: {
      repairRateDigest: (body: NdtAiWindow = {}) =>
        apiClient.post<{ success: boolean; data: NdtAiRunResult }>('/ndt-inspection/ai/repair-rate-digest', body),
      certExpiryDigest: () => apiClient.post<{ success: boolean; data: NdtAiRunResult }>('/ndt-inspection/ai/cert-expiry-digest', {}),
      doseReview: (body: NdtAiWindow = {}) =>
        apiClient.post<{ success: boolean; data: NdtAiRunResult }>('/ndt-inspection/ai/dose-review', body),
      ticketPreflight: (ticketId: string) =>
        apiClient.post<{ success: boolean; data: NdtAiRunResult }>(`/ndt-inspection/ai/ticket-preflight/${ticketId}`, {}),
      digests: (params?: { agent?: string }) =>
        apiClient.get<{ success: boolean; data: NdtAiDigest[] }>('/ndt-inspection/ai/digests', { params }),
      digest: (id: string) => apiClient.get<{ success: boolean; data: NdtAiDigest }>(`/ndt-inspection/ai/digests/${id}`),
    },
    /** The rate card (0529), read here only to pick from. camelCase, office surface; every NDT role may read. */
    rates: {
      list: (params?: { unit?: string; customerId?: string; activeOn?: string }) =>
        apiClient.get<NdtEnvelope<NdtRate[]>>('/ndt-inspection/rates', { params }),
    },
    /** The field surface: the same objects with snake_case keys (meta too). */
    field: {
      myJobs: () => apiClient.get<NdtEnvelope<NdtFieldJob[]>>('/ndt-inspection/field/my-jobs'),
      myJob: (id: string) => apiClient.get<NdtEnvelope<NdtFieldJobDetail>>(`/ndt-inspection/field/my-jobs/${id}`),
      addWeld: (jobId: string, body: NdtWeldWrite) =>
        apiClient.post<NdtEnvelope<NdtFieldWeld>>(`/ndt-inspection/field/jobs/${jobId}/welds`, body),
      addExam: (body: NdtExamWrite & { weld_id: string }) =>
        apiClient.post<NdtEnvelope<NdtFieldExam> & { warnings?: NdtShotWarning[] }>('/ndt-inspection/field/exams', body),
      /** Batch-4 field tickets: the same handlers, snake_case. */
      jobTickets: (jobId: string) => apiClient.get<NdtEnvelope<NdtFieldTicket[]>>(`/ndt-inspection/field/jobs/${jobId}/tickets`),
      ticket: (id: string) => apiClient.get<NdtEnvelope<NdtFieldTicketDetail>>(`/ndt-inspection/field/tickets/${id}`),
      closeDay: (jobId: string, workDate: string) =>
        apiClient.post<NdtEnvelope<NdtFieldTicketDetail>>(`/ndt-inspection/field/jobs/${jobId}/tickets/close-day`, { work_date: workDate }),
      addLine: (ticketId: string, body: NdtLineCreate) =>
        apiClient.post<NdtEnvelope<NdtFieldTicketDetail>>(`/ndt-inspection/field/tickets/${ticketId}/lines`, body),
      sign: (ticketId: string, body: NdtTicketSign) =>
        apiClient.post<NdtEnvelope<NdtFieldTicketDetail>>(`/ndt-inspection/field/tickets/${ticketId}/sign`, body),
    },
  },

  trucking: {
    /**
     * The trucking rate card (api#950). Reads: admin, office_staff, supervisor,
     * dispatcher. Writes: admin, office_staff. snake_case in, rows as stored
     * out. Delete is soft; `include_deleted: 'true'` lists them, and a restore
     * that would overlap a live rate is 409 TRUCKING_RATE_OVERLAP.
     */
    rates: {
      list: (params?: { kind?: string; customer_id?: string; truck_id?: string; on?: string; include_deleted?: 'true' }) =>
        apiClient.get<TruckingRatesListResponse>('/trucking/rates', { params }),
      create: (body: TruckingRateWrite) => apiClient.post<TruckingRateResponse>('/trucking/rates', body),
      update: (id: string, body: TruckingRateWrite) => apiClient.patch<TruckingRateResponse>(`/trucking/rates/${id}`, body),
      remove: (id: string) => apiClient.delete<TruckingRateResponse>(`/trucking/rates/${id}`),
      restore: (id: string) => apiClient.post<TruckingRateResponse>(`/trucking/rates/${id}/restore`),
    },
    /**
     * Daily trip inspections (api#855, #857). The checklist is chosen by the
     * company's country on the server; see lib/trip-inspections.ts.
     * Writes are snake_case like the rest of trucking's newer routes.
     */
    tripInspections: {
      checklist: () => apiClient.get<ApiEnvelope<Checklist>>('/trucking/trip-inspections/checklist'),
      list: (params?: Record<string, string>) =>
        apiClient.get<ApiEnvelope<TripInspectionListRow[]>>('/trucking/trip-inspections', { params }),
      get: (id: string) => apiClient.get<ApiEnvelope<TripInspectionDetail>>(`/trucking/trip-inspections/${id}`),
      lastPostTrip: (fleetId: string, before?: string) =>
        apiClient.get<ApiEnvelope<LastPostTrip | null>>('/trucking/trip-inspections/last-post-trip', {
          params: before ? { fleet_id: fleetId, before } : { fleet_id: fleetId },
        }),
      create: (data: Record<string, unknown>) =>
        apiClient.post<ApiEnvelope<TripInspectionCreated>>('/trucking/trip-inspections', data),
      certify: (id: string, defectId: string, data: { certification: string; notes?: string }) =>
        apiClient.post<ApiEnvelope<TripDefectCertified>>(`/trucking/trip-inspections/${id}/defects/${defectId}/certify`, data),
    },
    drivers: {
      list: (params?: Record<string, string>) => apiClient.get('/trucking/drivers', { params }),
      get: (id: string) => apiClient.get(`/trucking/drivers/${id}`),
      update: (id: string, data: any) => apiClient.put(`/trucking/drivers/${id}`, data),
      getPayRates: (driverId: string) =>
        apiClient.get(`/trucking/drivers/${driverId}/pay-rates`),
      savePayRates: (driverId: string, rates: {
        position_id?: string | null;
        pay_type: string;
        pct?: number | null;
        day_rate?: number | null;
        hourly_rate?: number | null;
      }[]) =>
        apiClient.post(`/trucking/drivers/${driverId}/pay-rates`, { rates }),
      importFile: (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return apiClient.post('/trucking/drivers/import', formData, { timeout: 120000 });
      },
      confirmImport: (data: any) => apiClient.post('/trucking/drivers/import/confirm', data),
    },
    fleet: {
      /**
       * The fleet lives in `trucking_fleet`, NOT in `trucks`.
       *
       * Both tables exist. `/trucking/trucks` reads `trucks`, which held 0 rows
       * estate-wide when measured 2026-09-04; `trucking_fleet` held 8. Its
       * status vocabulary is different too -- 'active' / 'maintenance' /
       * 'retired', not 'available' / 'dispatched' -- so a caller that mixes the
       * two silently counts nothing. Which of the two is canonical is an open
       * product question; until it is answered, anything that wants "the
       * tenant's vehicles" wants this one.
       */
      list: (params?: Record<string, string>) => apiClient.get('/trucking/fleet', { params }),
      importFile: (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return apiClient.post('/trucking/fleet/import', formData, { timeout: 120000 });
      },
      confirmImport: (data: any) => apiClient.post('/trucking/fleet/import/confirm', data),
    },
    /**
     * Dispatch console header figures and alerts. Both are real, tenant-scoped
     * and computed server-side; `metrics.todays_revenue` resolves the day in
     * the tenant's own timezone rather than the process zone.
     *
     * `metrics.available_trucks` counts active fleet units (api#860).
     *
     * `from` / `to` (the tenant's local days, both inclusive, at most 93 days)
     * set the period `double_booking` covers (api#960). Without them the API
     * uses the tenant's today. The other figures ignore them.
     */
    dispatch: {
      metrics: (period?: { from: string; to: string }) =>
        apiClient.get<ApiResponse<'GET /api/v1/trucking/dispatch/metrics'>>(
          '/trucking/dispatch/metrics',
          period ? { params: period } : undefined,
        ),
      alerts: () => apiClient.get('/trucking/dispatch/alerts'),
      /**
       * The Dispatch Calendar's time off (api#1026): approved requests for the
       * trucking drivers, each row carrying `trucking_driver_id` (matched through
       * `trucking_drivers.user_id`, never by name), plus `unlinked_drivers`, the
       * drivers on a non-retired unit with no login. Both dates are the tenant's
       * days, inclusive, and required. Casing's /time-off Calendar tab reads
       * `dispatch.timeOffCalendar`, not this.
       */
      timeOff: (from: string, to: string) =>
        apiClient.get<TruckingTimeOffEnvelope>('/trucking/dispatch/time-off', { params: { from, to } }),
    },
    reports: {
      /** Both dates are required; the API 400s without them. */
      customerRevenue: (startDate: string, endDate: string) =>
        apiClient.get('/trucking/reports/customer-revenue', {
          params: { start_date: startDate, end_date: endDate },
        }),
    },
    invoices: {
      list: (params?: Record<string, string>) => apiClient.get('/trucking/invoices', { params }),
    },
    positions: {
      list: () => apiClient.get('/trucking/positions/all'),
    },
    loads: {
      /**
       * Cancel a load (fe#815 cancel). `dryRunCharge` is the office's dry run
       * charge as typed: blank means no charge and is NOT sent (the API then
       * records nothing); "0" is sent and records the dry run as waived; more
       * than 0 bills one line on a new draft invoice. Only for a load that
       * departed: the API refuses a charge on one that never did (409
       * DRY_RUN_NOT_DEPARTED).
       */
      cancel: (id: string, reason?: string, dryRunCharge?: string) =>
        apiClient.patch<LoadCancelResponse>(`/trucking/loads/${id}/cancel`, {
          reason,
          ...(dryRunCharge != null && dryRunCharge.trim() !== '' ? { dry_run_charge: dryRunCharge.trim() } : {}),
        }),
      list: (params?: any) => apiClient.get('/trucking/loads', { params }),
      get: (id: string) => apiClient.get(`/trucking/loads/${id}`),
      /**
       * Book a load. THIS ROUTE IS camelCase on the way in — it is one of the
       * exceptions to the snake_case-on-writes rule in CLAUDE.md, verified
       * against `trucking-loads.routes.ts` rather than assumed.
       *
       * `loadType`, `pickupLocation`, `deliveryLocation` and `scheduledDate`
       * are required; the server assigns `bolNumber` and `status`.
       * `loadType` is cast to the `load_type` enum — see LOAD_TYPE_OPTIONS in
       * src/constants/load-types.ts for the labels that exist.
       */
      create: (data: LoadCreateBody) => apiClient.post('/trucking/loads', data),
      /**
       * Edit a load (camelCase keys, like create). Send only what changed:
       * a key's presence marks that rate kind 'manual' on the API (api#950).
       */
      update: (id: string, data: Record<string, unknown>) =>
        apiClient.patch<LoadUpdateResponse>(`/trucking/loads/${id}`, data),
      /**
       * The two transition doors the driver flow uses. Both stamp a timestamp
       * and `/depart` also moves the truck to `en_route`, which is why they
       * are not interchangeable with the status route below.
       */
      depart: (id: string, data?: { odometer_at_departure?: number | null }) =>
        apiClient.patch(`/trucking/loads/${id}/depart`, data ?? {}),
      arrive: (id: string) => apiClient.patch(`/trucking/loads/${id}/arrive`),
      /**
       * ⚠️ Only `booked` and `dispatched` still go through here. API PR #573
       * restricted this route to those two; every other status now 400s with a
       * pointer to its owning transition route. Use `depart`/`arrive`/`cancel`
       * or `POST /bols/:id/sign` for the rest.
       *
       * ⚠️ There is deliberately no `accept` or `decline` method. Both routes
       * exist on the API and both are dead: they write `status = 'accepted'` /
       * `'declined'`, which are not labels on the `load_status` enum
       * (`booked, dispatched, en_route, arrived, delivered, cancelled, signed`
       * — read from production 2026-09-04), into `accepted_at` / `declined_at`
       * columns that do not exist on `loads`. They 500 on every call. Adding a
       * wrapper here would only make the dead route easier to reach.
       */
      updateStatus: (id: string, data: { status: 'booked' | 'dispatched' }) =>
        apiClient.patch(`/trucking/loads/${id}/status`, data),
    },
    // Every `loads` row IS a BOL, and the API mounts the BOL views as a
    // sub-path of the loads router — `/trucking/bols` is not registered and
    // 404s. Specific-before-`:id` ordering there means `/loads/bols` resolves
    // to the BOL list, not to a load whose id is the word "bols".
    moveJobs: {
      /**
       * The master BOL PDF, as a blob. `window.open` to this route 401s: auth
       * is the Authorization header, and a new tab sends none (#815).
       */
      masterBolPdf: (id: string) => apiClient.get<Blob>(`/trucking/move-jobs/${id}/master-bol/pdf`, { responseType: 'blob' }),
    },
    bols: {
      list: (params?: any) => apiClient.get('/trucking/loads/bols', { params }),
      get: (id: string) => apiClient.get(`/trucking/loads/bols/${id}`),
      sign: (id: string, data: any) => apiClient.post(`/trucking/loads/bols/${id}/sign`, data),
      pdf: (id: string) => apiClient.get(`/trucking/loads/bols/${id}/pdf`, { responseType: 'blob' }),
    },
    weightTickets: {
      create: (data: any) => apiClient.post('/trucking/weight-tickets', data),
      get: (id: string) => apiClient.get(`/trucking/weight-tickets/${id}`),
    },
    me: {
      profile: () => apiClient.get('/trucking/drivers/me'),
      hos: () => apiClient.get('/trucking/drivers/me/hos'),
    },
    driverAvailability: (params?: any) => apiClient.get('/trucking/drivers/availability', { params }),
  },


  notifications: {
    list: (params?: { limit?: number; unread?: boolean }) => apiClient.get('/notifications', { params }),
    unreadCount: () => apiClient.get('/notifications/unread-count'),
    // PATCH, not POST: the API registers only PATCH /:id/read, so a POST 404'd and
    // clicking a notification never marked it read — the bell's count only grew.
    markRead: (id: string) => apiClient.patch<ApiResponse<'PATCH /api/v1/notifications/:id/read'>>(`/notifications/${id}/read`),
    readAll: () => apiClient.patch('/notifications/read-all'),
  },

  certifications: {
    pending: () => apiClient.get('/certifications/pending'),
    approve: (id: string, data?: { notes?: string }) => apiClient.post(`/certifications/${id}/approve`, data || {}),
    reject: (id: string, data: { notes: string }) => apiClient.post(`/certifications/${id}/reject`, data),
    flag: (id: string, data: { notes?: string }) => apiClient.post(`/certifications/${id}/flag`, data || {}),
  },

  aiAgent: {
    chat: (data: { conversationId?: string; message: string }) =>
      apiClient.post('/ai-agent/chat', data),
    chatStream: async (data: { conversationId?: string; message: string; voice_mode?: boolean }) => {
      const token = await storage.getToken();
      return connectStream(`${API_URL}/ai-agent/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(data),
      });
    },
    listConversations: () =>
      apiClient.get('/ai-agent/conversations'),
    getMessages: (conversationId: string) =>
      apiClient.get(`/ai-agent/conversations/${conversationId}/messages`),
    deleteConversation: (conversationId: string) =>
      apiClient.delete(`/ai-agent/conversations/${conversationId}`),
    voiceChat: async (audioBlob: Blob, conversationId?: string, ext = 'wav', voiceMode = true) => {
      const token = await storage.getToken();
      const formData = new FormData();
      formData.append('audio', audioBlob, `recording.${ext}`);
      if (conversationId) formData.append('conversationId', conversationId);
      formData.append('voice_mode', String(voiceMode));
      return connectStream(
        `${API_URL}/ai-agent/chat/voice`,
        {
          method: 'POST',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: formData,
        },
        UPLOAD_TIMEOUT_MS
      );
    },
    /*
     * `signal` is how a read-back in progress is abandoned. Streaming read-back
     * sends one request per sentence, so an interrupt — a mic tap, a new
     * message, leaving the screen — can leave several in flight, and a response
     * that lands after the interrupt is audio from a reply the user has already
     * moved on from. Aborting is what makes that impossible rather than rare.
     */
    tts: (text: string, voice?: string, signal?: AbortSignal) =>
      // Not a manifest route: the response is `audio/mpeg` bytes, not a JSON
      // envelope, so the shape is the Blob axios builds from `responseType`.
      apiClient.post<Blob>('/ai-agent/tts', { text, voice }, { responseType: 'blob', signal }),
    getProfile: () =>
      apiClient.get('/ai-agent/profile'),
    updateProfile: (data: { name?: string; avatar_url?: string }) =>
      apiClient.put('/ai-agent/profile', data),
  },

  helpVideos: {
    categories: () => apiClient.get('/help-videos/categories'),
    categoryBySlug: (slug: string) => apiClient.get(`/help-videos/categories/${slug}`),
  },

  adminHelpVideos: {
    list: () => apiClient.get('/admin/help-videos'),
    create: (data: any) => apiClient.post('/admin/help-videos', data),
    update: (id: string, data: any) => apiClient.put(`/admin/help-videos/${id}`, data),
    delete: (id: string) => apiClient.delete(`/admin/help-videos/${id}`),
    upload: (file: File, onProgress?: (pct: number) => void) => {
      const formData = new FormData();
      formData.append('video', file);
      return apiClient.post('/admin/help-videos/upload', formData, {
        timeout: 600000,
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
    },
    uploadThumbnail: (file: File) => {
      const formData = new FormData();
      formData.append('thumbnail', file);
      return apiClient.post('/admin/help-videos/upload', formData, { timeout: 120000 });
    },
    createCategory: (data: any) => apiClient.post('/admin/help-videos/categories', data),
    updateCategory: (id: string, data: any) => apiClient.put(`/admin/help-videos/categories/${id}`, data),
    deleteCategory: (id: string) => apiClient.delete(`/admin/help-videos/categories/${id}`),
  },

  // ─── Overview widgets (fieldiq-api#873) ──────────────────────────
  dashboard: {
    /** The company's resolved layout for THIS caller, plus the catalogue the picker lists. */
    layout: () => apiClient.get<ApiEnvelope<DashboardLayout>>('/dashboard/layout'),
    /**
     * Office roles, no vertical guard (api#1142). NDT certs, welder quals,
     * instruments, sources and dose as counts. DATA-gated: an area with no
     * rows is null, no NDT data at all is hasData:false — never zeros.
     */
    ndtCompliance: () => apiClient.get<ApiEnvelope<NdtCompliance>>('/dashboard/ndt-compliance'),
    /** Money roles. Every vertical's ticket revenue by day, per currency, with a status per vertical. */
    revenuePulse: (period: 'week' | 'month') =>
      apiClient.get<ApiEnvelope<RevenuePulse>>('/dashboard/revenue-pulse', { params: { period } }),
    /** Money roles. Every vertical's open book in five buckets, per currency, with a status per vertical. */
    receivables: () => apiClient.get<ApiEnvelope<Receivables>>('/dashboard/receivables'),
    /** Money roles. Work owed an invoice right now, per vertical, per currency. */
    unbilledWork: () => apiClient.get<ApiEnvelope<UnbilledWork>>('/dashboard/unbilled-work'),
    /** Money roles. Work approved in the window, split invoiced / not, per currency. */
    approvedVsInvoiced: (period: 'week' | 'month') =>
      apiClient.get<ApiEnvelope<ApprovedVsInvoiced>>('/dashboard/approved-vs-invoiced', { params: { period } }),
    /** Admin. Every key once, action_queue on and first; the API refuses anything else. */
    saveLayout: (body: DashboardLayoutWrite) =>
      apiClient.put<ApiEnvelope<DashboardLayout>>('/dashboard/layout', body),
    /** Admin. Deletes the saved row; the answer is the default layout. */
    resetLayout: () => apiClient.post<ApiEnvelope<DashboardLayout>>('/dashboard/layout/reset'),
    /** Casing, office roles. Per-status count and priced total; an unpriced status is null, not 0. */
    ticketPipeline: () => apiClient.get<ApiEnvelope<TicketPipeline>>('/dashboard/ticket-pipeline'),
    /**
     * The Action Queue widget's roles. Every non-casing vertical's items, each
     * shown only to roles that can open its link; `failures` is never empty-as-zero.
     */
    actionQueue: () => apiClient.get<ApiEnvelope<ActionQueue>>('/dashboard/action-queue'),
    /** Money roles. Every vertical's tickets by status, per currency; casing equals /ticket-pipeline. */
    pipeline: () => apiClient.get<ApiEnvelope<PipelineRead>>('/dashboard/pipeline'),
    /** Office roles. Every non-casing vertical's units by status and the four needing a look. */
    equipmentStatus: () => apiClient.get<ApiEnvelope<EquipmentStatusRead>>('/dashboard/equipment-status'),
    /** Downhole office roles. Open runs, longest first, against the company's alert hours. */
    toolsInHole: () => apiClient.get<ApiEnvelope<ToolsInHole>>('/dashboard/tools-in-hole'),
    /** Downhole office roles. Wells mobilizing, on location or released, with crew; DD/MWD only. */
    wellsOnLocation: () => apiClient.get<ApiEnvelope<WellsOnLocation>>('/dashboard/wells-on-location'),
    /** Office roles, pressure testing. Active and scheduled jobs; late = still scheduled after its day. */
    ptJobs: () => apiClient.get<ApiEnvelope<PtJobs>>('/dashboard/pt-jobs'),
    /** Office roles, well testing (WT-6). Open jobs, flowing first; late = still scheduled after its day. */
    wtJobs: () => apiClient.get<ApiEnvelope<WtJobs>>('/dashboard/wt-jobs'),
    wsJobs: () => apiClient.get<ApiEnvelope<WsJobs>>('/dashboard/ws-jobs'),
    /** Office roles. Open work orders per source; overdue_count null = no due dates, never 0. */
    workOrders: () => apiClient.get<ApiEnvelope<WorkOrders>>('/dashboard/work-orders'),
    /** Office roles. The last 7 days, newest first, each line filtered to the roles of the screen it opens; `failures` lists kinds that did not load. */
    activityFeed: () => apiClient.get<ApiEnvelope<ActivityFeed>>('/dashboard/activity-feed'),
    /** Office roles. The band's open work, to approve and equipment out, summed over the company's verticals with the breakdown. to_approve is `forbidden` outside the ticket pipeline's roles. */
    band: () => apiClient.get<ApiEnvelope<OverviewBandRead>>('/dashboard/band'),
  },

  // ─── Morning Brief ───────────────────────────────────────────────
  briefing: {
    /**
     * The 7am brief for this tenant, or `{ data: null, reason: 'no_brief_yet' }`.
     *
     * `data: null` is a REAL answer, not an error — a tenant provisioned after
     * 07:00 has no brief, and that is different from a brief full of zeros.
     * Unwrap it explicitly at the call site; `res.data?.data || res.data`
     * falls through to the envelope, which is truthy.
     *
     * The response type is hand-written in src/types/morning-brief.ts rather
     * than taken from the generated manifest: the manifest records this
     * endpoint as a 500 because its schema fixture is a pg_dump taken before
     * migration 0398 created the table.
     */
    today: () => apiClient.get<BriefingTodayResponse>('/briefing/today'),
  },

  agentRuns: {
    list: () => apiClient.get('/agent-runs'),
    get: (id: string) => apiClient.get(`/agent-runs/${id}`),
    approve: (id: string) => apiClient.post(`/agent-runs/${id}/approve`),
    dismiss: (id: string) => apiClient.post(`/agent-runs/${id}/dismiss`),
    /** Informational runs only (api#806). Hides the run for the whole company, not just the caller. */
    markRead: (id: string) => apiClient.post<{ success: true }>(`/agent-runs/${id}/read`),
  },

  // ─── Notification Preferences ────────────────────────────────────
  notificationPreferences: {
    get: () => apiClient.get('/notification-preferences'),
    update: (preferences: { channel: string; eventType: string; enabled: boolean }[]) =>
      apiClient.put('/notification-preferences', { preferences }),
    /** Today's "jobs need starting" alert to the CALLER only (api push-then-SMS PR). */
    testDailyAlert: () =>
      apiClient.post<{ success: true; data: { jobs: number; delivered: 'push' | 'sms' | null } }>(
        '/notification-preferences/daily-job-alert/test'),
  },

  // ─── Push devices (fieldiq-api 0524) ──────────────────────────────
  // Every door is the caller's OWN device. Bodies are written snake-free on
  // purpose: the API reads `deviceId` / `subscription` exactly as named.
  push: {
    config: () =>
      apiClient.get<{ success: true; data: { webPush: { enabled: boolean; publicKey: string | null } } }>('/mobile/push/config'),
    register: (body: { platform: 'web'; deviceId: string; subscription: { endpoint: string; keys: { p256dh: string; auth: string } } }) =>
      apiClient.post<{ success: true; message: string }>('/mobile/push/register', body),
    status: (deviceId: string) =>
      apiClient.get<{ success: true; data: {
        webPushEnabled: boolean;
        workingDevices: number;
        thisDevice: { registered: boolean; working: boolean; lastSuccessAt: string | null; lastFailureAt: string | null };
      } }>('/mobile/push/status', { params: { deviceId } }),
    unregister: (deviceId: string) =>
      apiClient.delete<{ success: true; data: { removed: number } }>('/mobile/push/register', { data: { deviceId } }),
    test: () =>
      apiClient.post<{ success: true; data: { attempted: number; delivered: number } }>('/mobile/push/test'),
    /** Every device registered to the caller (fieldiq-api push delivery receipts). */
    devices: (deviceId: string) =>
      apiClient.get<{ success: true; data: PushDevice[] }>('/mobile/push/devices', { params: { deviceId } }),
  },

  // ─── Company Settings (timezone, daily alert) ────────────────────
  companySettings: {
    get: () => apiClient.get('/settings/company'),
    update: (data: { timezone?: string; daily_alert_hour?: number }) =>
      apiClient.patch('/settings/company', data),
  },

  // ─── Alert Settings (dedicated timezone + daily alert hour) ──────
  // GET returns { time_zone, daily_alert_hour }; PATCH accepts { timezone, daily_alert_hour }.
  // Reads are open to any tenant user; writes are gated by requireRole('admin')
  // on the API (dispatcher elevation honored server-side).
  alertSettings: {
    get: () => apiClient.get('/company/alert-settings'),
    update: (data: { timezone?: string; daily_alert_hour?: number }) =>
      apiClient.patch('/company/alert-settings', data),
  },

  /**
   * companies.brief_hour (api#806): the tenant-local hour the in-app morning
   * brief is written. Separate from daily_alert_hour, which times the email.
   * GET is open to any tenant user; PATCH is requireRole('admin'), like alert-settings.
   */
  briefSettings: {
    get: () =>
      apiClient.get<{ success: true; data: { brief_hour: number | null; time_zone: string | null } }>('/company/brief-settings'),
    update: (data: { brief_hour: number }) =>
      apiClient.patch<{ success: true; data: { brief_hour: number | null; time_zone: string | null } }>('/company/brief-settings', data),
  },

  // ─── Email Branding ──────────────────────────────────────────────
  emailBranding: {
    get: () => apiClient.get('/settings/general/email-branding'),
    update: (data: { email_display_name?: string; email_reply_to?: string }) =>
      apiClient.patch('/settings/general/email-branding', data),
    // Sends a test email TO the SAVED email_reply_to (not the form value, not
    // the caller). Strict admin. 400 when no Reply-To is saved, 429 past 3 per
    // hour; both carry the message in `error`. Reads are wrapped:
    // { success, data: { sent, to, resendId } }.
    sendTest: () => apiClient.post('/settings/general/email-branding/test'),
  },

  // ─── Roles & Permissions ─────────────────────────────────────────
  // Returns the role matrix, current dispatcher_admin_access flag, and ALL
  // UI copy. Render directly from the response — nothing about roles or the
  // elevation toggle is hardcoded on the frontend.
  settingsRoles: {
    get: () => apiClient.get('/settings/roles'),
    // Body field is snake_case to match the backend's Zod .strict() schema.
    update: (data: { dispatcher_admin_access: boolean }) =>
      apiClient.patch('/settings/roles', data),
  },

  // ─── Clock rounding (API #823, migration 0423) ───────────────────
  // GET   /settings/clock-rounding  { clock_rounding_minutes: null | 15, allowed_values } — no role gate
  // PATCH /settings/clock-rounding  requireRole('admin', { strict: true }); body is
  //                                 exactly { clock_rounding_minutes } (Zod .strict()),
  //                                 snake_case; null is OFF.
  // Applies from the next clock-OUT: each shift snapshots timesheets.payable_minutes
  // when it closes, and a shift already closed keeps what it was closed with.
  clockRounding: {
    get: () => apiClient.get<ApiEnvelope<ClockRoundingSetting>>('/settings/clock-rounding'),
    update: (data: { clock_rounding_minutes: 15 | null }) =>
      apiClient.patch<ApiEnvelope<ClockRoundingSetting>>('/settings/clock-rounding', data),
  },

  // ─── Per-jurisdiction tax rates (API #530) ───────────────────────
  // GET  /settings/tax-rates      { home_jurisdiction, rows[] } — no role gate
  // PUT  /settings/tax-rates      requireRole('admin'), UPSERTS on
  //                               (jurisdiction, effective_from)
  // DELETE /settings/tax-rates/:id  requireRole('admin'); 404 (not 403) for
  //                               another tenant's id, so existence is not leaked
  //
  // The dedicated list rather than the `jurisdictions[]` that GET
  // /settings/regional-tax also returns: the writes live on THIS resource, so
  // list and mutation share one query key and one invalidation. Reading the
  // list out of regional-tax would mean a rate save invalidating a different
  // resource's cache, and that payload is copied into form state exactly once
  // on mount, so anything read through it is frozen at first load.
  //
  // Reads are snake_case here — `formatTaxRateRow` passes the column names
  // through rather than camelising, unlike most of this API. Writes are
  // snake_case as usual.
  taxRates: {
    list: () => apiClient.get('/settings/tax-rates'),
    save: (data: TaxRateWrite) => apiClient.put('/settings/tax-rates', data),
    remove: (id: string) => apiClient.delete(`/settings/tax-rates/${id}`),
    // The statutory rate per Canadian province/territory — the auto-fill for
    // "Add jurisdiction", from the same table tenant creation uses (api#961).
    standard: () => apiClient.get<ApiEnvelope<{ rows: StandardTaxRate[] }>>('/settings/standard-tax-rates'),
  },

  // ─── Customer Portal ─────────────────────────────────────────────
  portal: {
    dashboard: () => apiClient.get('/portal/dashboard'),
    invoices: (params?: any) => apiClient.get('/portal/invoices', { params }),
    invoice: (id: string) => apiClient.get(`/portal/invoices/${id}`),
    invoicePdf: (id: string) => apiClient.get(`/portal/invoices/${id}/pdf`, { responseType: 'blob' }),
    payInvoice: (id: string) => apiClient.post(`/portal/invoices/${id}/pay`),
    quotes: (params?: any) => apiClient.get('/portal/quotes', { params }),
    quote: (id: string) => apiClient.get(`/portal/quotes/${id}`),
    quotePdf: (id: string) => apiClient.get(`/portal/quotes/${id}/pdf`, { responseType: 'blob' }),
    acceptQuote: (id: string) => apiClient.post(`/portal/quotes/${id}/accept`),
    declineQuote: (id: string, reason?: string) => apiClient.post(`/portal/quotes/${id}/decline`, { reason }),
    profile: () => apiClient.get('/portal/profile'),
    updateProfile: (data: any) => apiClient.put('/portal/profile', data),
  },

  // ─── Timesheets / Clock In-Out ───────────────────────────────────
  timesheets: {
    status: () => apiClient.get('/timesheets/status'),
    // `pay_code_id` is OPTIONAL as of api migration 0333. It was required here,
    // and that requirement is the only reason the service's old throw never
    // surfaced as a 500 in the app: `pay_codes` has rows for exactly one tenant
    // (Smith, the demo), so on every live customer this call could not be
    // constructed at all and the button was simply disabled forever.
    //
    // `work_location` is 'shop' | 'field'. Omitting it stores NULL, which means
    // "nobody said" and is a real third state — it is NOT 'field'. The API
    // validates before it queries, so a bad value is a 400 with
    // code 'invalid_work_location' regardless of clock state.
    clockIn: (data: {
      pay_code_id?: string;
      work_location?: WorkLocation;
      source: string;
      lat?: number;
      lng?: number;
      job_id?: string;
      // Non-casing work (api#976, 0490) — never together with job_id.
      work_item_vertical?: string;
      work_item_id?: string;
    }) => apiClient.post('/timesheets/clock-in', data),
    // Re-link the caller's OPEN shift (api#999). Exactly one of a casing job,
    // a work item, or neither (= "No job"); the server audits the change.
    // The clock-in job picker (api#1039): only jobs the CALLER is on the crew
    // of, not completed/cancelled, and whose phase for them has not ended.
    // Same row shape as GET /jobs plus `my_phase`. Clock-in and switch-job
    // refuse anything this list would not show (403 not_on_crew, 409
    // job_done / phase_done). GET /jobs stays the office's whole list.
    clockInJobs: () => apiClient.get<ClockInJobsResponse>('/timesheets/clock-in-jobs'),
    switchJob: (data: { job_id?: string; work_item_vertical?: string; work_item_id?: string }) =>
      apiClient.post<SwitchJobResponse>('/timesheets/switch-job', data),
    // Resolves with the closed row plus `submitted` / `submission_error`:
    // clock-out hands the shift to the approval queue and says whether it did.
    clockOut: (data: { source: string; note?: string }) =>
      apiClient.post('/timesheets/clock-out', data),
    // A QUEUED clock tap (api#1164): applied once per client_request_id, at the
    // device's tap time when plausible, against the shift a clock-out names.
    // Sent only by lib/offline/clock-queue.ts.
    clockEvent: (data: Record<string, unknown>) =>
      apiClient.post<ClockEventResponse>('/timesheets/clock-events', data),
    standby: (data?: { note?: string }) =>
      apiClient.post('/timesheets/standby', data || {}),
    resume: () =>
      apiClient.post('/timesheets/resume', {}),
    list: (params?: Record<string, string>) =>
      apiClient.get('/timesheets', { params }),
    get: (id: string) => apiClient.get(`/timesheets/${id}`),
    /**
     * Hand a `draft` shift to the approval queue. NOT role-gated at the route —
     * the rule is relational: the worker may always submit their own, and an
     * approver role (admin / office_staff / supervisor, plus a dispatcher on a
     * tenant with `dispatcher_admin_access`) may submit someone else's.
     *
     * Since #594 clock-out submits the row itself, so a caller here is acting on
     * a shift that never made that transition — a pre-#594 row, or one whose
     * auto-submit reported `submission_error`.
     *
     * Refusals: 403 `not_yours_to_submit`, and a bare 404 when the row is not in
     * `draft` — which is also what a double-click gets, because the UPDATE is
     * predicated on the status it is leaving.
     */
    submit: (id: string) =>
      apiClient.post<TimesheetSubmitResponse>(`/timesheets/${id}/submit`, {}),
    /**
     * A shift over 16h is refused 409 `long_shift_unconfirmed` unless its
     * clock-out is corrected below that, or the body confirms it with a
     * non-blank `long_shift_note` (blank → 400 `long_shift_note_required`).
     * api#992; see lib/long-shift.ts.
     */
    approve: (
      id: string,
      data?: { note?: string; confirm_long_shift?: true; long_shift_note?: string },
    ) =>
      apiClient.post(`/timesheets/${id}/approve`, data || {}),
    /**
     * `note` is the optional note to the worker (api#981, <= 500 chars after
     * trimming, else 400). It is texted to them with the reason.
     */
    reject: (id: string, data: { reason: string; note?: string }) =>
      apiClient.post(`/timesheets/${id}/reject`, data),
    /**
     * The worker handing a REJECTED shift back. Owner-only and rejected-only,
     * so anything else is a bare 404. The route WRITES `worker_note` from the
     * body (`|| ''`), so the caller sends the note the shift already carries,
     * or the resubmit blanks it. Clears rejection_reason and the note.
     */
    resubmit: (id: string, data: { worker_note: string }) =>
      apiClient.post<TimesheetResubmitResponse>(`/timesheets/${id}/resubmit`, data),
    /**
     * The worker's OWN rows for one pay period — `employee_id = <JWT userId>`
     * on the server, so an admin opening the clock-in screen sees their own
     * shifts here and not the company's. GET /timesheets would also scope to
     * own rows for a hand, but would widen to everyone for an approver, and
     * this is the field screen.
     */
    myPeriod: (periodId: string) =>
      apiClient.get<ApiResponse<'GET /api/v1/timesheets/my/period/:periodId'>>(`/timesheets/my/period/${periodId}`),
    /**
     * A worker correcting their own shift (api #595). Own rows in
     * draft / rejected / submitted only; anything else is a 404 the caller
     * has to SHOW, not swallow — see TimesheetSelfEditBody.
     */
    update: (id: string, data: TimesheetSelfEditBody) =>
      apiClient.put<TimesheetSelfEditResponse>(`/timesheets/${id}`, data),
    /**
     * The caller's own missed shift — no clock punch (api#1148). Created and
     * submitted for someone else to approve. See ManualTimesheetBody.
     */
    createManual: (data: ManualTimesheetBody) =>
      apiClient.post<ManualTimesheetResponse>('/timesheets/manual', data),
    /**
     * The office correcting a hand's shift (api #716). Strict admin. Sends
     * only the fields that moved plus the required reason; every refusal's
     * `error` is rendered as written — see TimesheetAdminEditRefusal.
     */
    adminEdit: (id: string, data: TimesheetAdminEditBody) =>
      apiClient.post<TimesheetAdminEditResponse>(`/timesheets/${id}/admin-edit`, data),
    /** The full from/to trail. Bare `{ data }` envelope, no `success` key. */
    history: (id: string) =>
      apiClient.get<TimesheetAuditResponse>(`/audit/timesheet/${id}`),
  },

  payProfiles: {
    list: () => apiClient.get('/pay-profiles'),
    getByUser: (userId: string) => apiClient.get(`/pay-profiles/users/${userId}`),
    createByUser: (userId: string, data: any) => apiClient.post(`/pay-profiles/users/${userId}`, data),
    updateByUser: (userId: string, data: any) => apiClient.put(`/pay-profiles/users/${userId}`, data),
  },

  payCodes: {
    list: () => apiClient.get('/pay-codes'),
  },

  payrollReports: {
    hoursSummary: (periodId: string) => apiClient.get('/payroll/reports/hours-summary', { params: { period_id: periodId } }),
    otBreakdown: (periodId: string) => apiClient.get('/payroll/reports/ot-breakdown', { params: { period_id: periodId } }),
    otThresholdStatus: (year: number, month: number) => apiClient.get('/payroll/reports/ot-threshold-status', { params: { year, month } }),
    costByJob: (periodId: string) => apiClient.get('/payroll/reports/cost-by-job', { params: { period_id: periodId } }),
    paySummary: (periodId: string) => apiClient.get('/payroll/reports/pay-summary', { params: { period_id: periodId } }),
  },

  payPeriods: {
    current: () => apiClient.get('/pay-periods/current'),
    list: (params?: { limit?: number; offset?: number }) =>
      apiClient.get('/pay-periods', { params }),
    /**
     * 'approved' | 'exported' -> 'paid'. The "the money went out" action.
     *
     * NOTE THE ROUTER. This lives on `/pay-periods/*`, NOT the
     * `/payroll/pay-periods/*` surface that `approve` and `export` use above.
     * The two are different Hono apps and only one of them has this route.
     *
     * ONE-WAY. `reopenPeriod` admits only status 'approved' and DELETE /:id
     * requires 'open', so a paid period cannot be reopened, deleted or
     * regenerated through any route the app offers. The confirmation belongs
     * on the way in — see PayrollScreen's ConfirmModal.
     *
     * Replaces PATCH /:id/lock, deleted in API #446: it wrote status 'locked',
     * a label absent from the pay_period_status enum, so it was a 22P02 and
     * had never once succeeded. PATCH /:id/close (status 'pending', same
     * defect) is gone too. Neither was ever wired to this client.
     *
     * Answers 409 with `details.status` when the period is not in a state that
     * can be marked paid, and 404 for a period this tenant does not own.
     * Already-paid is idempotent and returns 200.
     */
    markPaid: (id: string) => apiClient.post(`/pay-periods/${id}/mark-paid`),
    /**
     * 'approved' -> 'open'. The exit `approved` never had in this client.
     *
     * Same router as `markPaid` — `/pay-periods/*`, not `/payroll/pay-periods/*`.
     * Strict admin, matching the approve route it undoes.
     *
     * In one transaction it clears approved_at/approved_by, returns every
     * approved timesheet in the period to 'draft', and writes a
     * `tenant_audit_log` row. Already-open is idempotent and returns 200.
     *
     * Answers 409 once the period has `exported_at` or any `payroll_exports`
     * row, or is in any status other than approved — that refusal is permanent,
     * which is why the control sits nowhere near Export. 404 for a period this
     * tenant does not own.
     */
    reopen: (id: string) => apiClient.post<PayPeriodReopenResponse>(`/pay-periods/${id}/reopen`),
  },

  payPeriodConfig: {
    get: () => apiClient.get('/pay-periods/config'),
    update: (data: any) => apiClient.put('/pay-periods/config', data),
    generate: (data: { count: number }) => apiClient.post('/pay-periods/generate', data),
    regenerate: (data: { count: number; force?: boolean }) => apiClient.post('/pay-periods/regenerate', data),
    deletePeriod: (id: string, opts?: { force?: boolean }) =>
      apiClient.delete(`/pay-periods/${id}`, opts?.force ? { params: { force: true } } : undefined),
    listPeriods: () => apiClient.get('/pay-periods'),
    listOvertimeRules: () => apiClient.get('/overtime-rules'),
    applyOvertimePreset: (presetKey: string, data: { vertical: string }) =>
      apiClient.post(`/overtime-rules/preset/${presetKey}`, data),
  },

  // ─── Unified Field App ──────────────────────────────────────────
  field: {
    today: () => apiClient.get('/field/today'),
    upcoming: (days = 7) => apiClient.get('/field/upcoming', { params: { days } }),
    forms: () => apiClient.get('/field/forms'),
    history: (params?: { type?: string; cursor?: string }) =>
      apiClient.get('/field/history', { params }),
    profile: () => apiClient.get('/field/profile'),
    /**
     * The caller's assigned work across their tenant's verticals (api#972).
     * Self-scoped, needs no driver record. `open` = not started + underway
     * (Clock In, JSA, Pre-Job pickers); `all` adds finished work (Incident).
     */
    myWork: (scope: 'open' | 'all' = 'open') =>
      apiClient.get<ApiResponse<'GET /api/v1/field/my-work'>>('/field/my-work', { params: { scope } }),
  },

  // ─── Crew Chat ───────────────────────────────────────────────────
  chat: {
    getMessages: (jobId: string, cursor?: string, limit = 50) =>
      apiClient.get(`/chat/jobs/${jobId}/messages`, { params: { cursor, limit } }),
    sendMessage: (jobId: string, message: string, replyToId?: string) =>
      apiClient.post(`/chat/jobs/${jobId}/messages`, { message, replyToId }),
    editMessage: (messageId: string, message: string) =>
      apiClient.put(`/chat/messages/${messageId}`, { message }),
    deleteMessage: (messageId: string) =>
      apiClient.delete(`/chat/messages/${messageId}`),
    unreadSummary: (since?: string) =>
      apiClient.get('/chat/unread-summary', { params: since ? { since } : {} }),
  },

  /**
   * The tenant's positions (GET /positions returns `lineItemTypes` since API
   * 0425). `list()` is active-only; pass `true` for the management view, under
   * a different React Query key so the pickers never see deactivated rows.
   *
   * THE WRITE BODY MIXES CASES, and that is the API's contract, not a typo
   * here: PATCH reads `name`, `isActive` and `line_item_types`. A camelCase
   * `lineItemTypes` is ignored with a 200. So the arguments are camelCase and
   * this block is the one place that spells the wire key.
   *
   * `remove` hard-deletes an unheld position and DEACTIVATES one any hand still
   * holds (primary or "can work as"), reporting which on the envelope's
   * `method` ('hard_delete' | 'soft_delete'). It never answers "in use" as an
   * error. Standard positions: 403 on rename, deactivate and delete; their
   * line item types can be set.
   */
  positions: {
    list: (includeInactive = false) =>
      apiClient.get<ApiEnvelope<PositionWire[]>>('/positions', includeInactive ? { params: { include_inactive: 'true' } } : undefined),
    create: (data: { name: string; lineItemTypes?: string[] }) =>
      apiClient.post<ApiEnvelope<PositionWire>>('/positions', {
        name: data.name,
        ...(data.lineItemTypes !== undefined ? { line_item_types: data.lineItemTypes } : {}),
      }),
    update: (id: string, data: { name?: string; isActive?: boolean; lineItemTypes?: string[] }) =>
      apiClient.patch<ApiEnvelope<PositionWire>>(`/positions/${id}`, {
        ...(data.name !== undefined ? { name: data.name } : {}),
        ...(data.isActive !== undefined ? { isActive: data.isActive } : {}),
        ...(data.lineItemTypes !== undefined ? { line_item_types: data.lineItemTypes } : {}),
      }),
    remove: (id: string) => apiClient.delete<PositionDeleteResponse>(`/positions/${id}`),
  },

  /**
   * Per-tenant equipment catalog. See `src/types/equipment-type.ts` for the
   * payload and for why a standard row refuses all three writes.
   *
   * Three things a call site has to know, none of which the signatures show:
   *
   *   - `list()` is active-only by default. Pass `true` for the management
   *     view. The two answers must NOT share a React Query key, or the page
   *     that wants deactivated rows poisons the Add Equipment dropdown that
   *     must not have them.
   *   - `create` answers 409 in two different situations: a plain duplicate,
   *     and a name held by a DEACTIVATED row. Only the second has a fix the
   *     user can act on, and the two are told apart by their message text.
   *   - `delete` reports which of two things it did on the ENVELOPE, as
   *     `method: 'soft_delete' | 'hard_delete'`, with no `data` key at all.
   *     `unwrap()` returns null for both — read `res.data.method` directly.
   */
  /**
   * Per-tenant line item type preferences — the types this company hides from
   * the ticket pickers and the pay-rate grid. See src/lib/line-item-prefs.ts
   * for the read shape.
   *
   * The write key is `hidden` — the one word, not `hidden_types`. Verified
   * against line-item-prefs.routes.ts (`parseHiddenTypes(body?.hidden)`) and
   * a live PUT on the Smith demo tenant; any other key is a 400 saying
   * "`hidden` must be an array". `update` REPLACES the set. The response
   * names any newly hidden type still carrying a pay rate or a live line
   * item, with both counts, and the save is not refused on that account —
   * the settings page warns instead.
   */
  /**
   * Rules learned from corrections of AI drafts (API 0422). Every route is
   * requireRole('admin', strict) — a dispatcher elevated to admin is refused,
   * and so is `owner`. Rules are proposed by the API, never created here.
   * Another tenant's rule is a 404.
   */
  aiRules: {
    list: () => apiClient.get<AiRuleListResponse>('/ai-rules'),
    activate: (id: string) => apiClient.post<AiRuleTransitionResponse>(`/ai-rules/${id}/activate`),
    dismiss: (id: string) => apiClient.post<AiRuleTransitionResponse>(`/ai-rules/${id}/dismiss`),
    delete: (id: string) => apiClient.delete<AiRuleTransitionResponse>(`/ai-rules/${id}`),
  },

  /**
   * GPS connectors (API 0440, 0442) — Motive, Samsara and Geotab. Strict admin, like aiRules.
   * Reads are the raw gps_connections columns, so they arrive SNAKE_case
   * (key_last4, last_sync_at); lib/gps-connections reads both spellings.
   * The key (Geotab: the password) is write-only: no read returns it. A connect is tested live by the
   * API before anything is stored, and a refused key is a 400 with the
   * provider's message in `error`.
   */
  gpsConnections: {
    list: () => apiClient.get<GpsConnectionListResponse>('/settings/gps-connections'),
    connect: (body: GpsConnectBody) =>
      apiClient.post<GpsConnectResponse>('/settings/gps-connections', body),
    disconnect: (provider: string) => apiClient.delete<GpsDisconnectResponse>(`/settings/gps-connections/${provider}`),
    sync: (provider: string) => apiClient.post<GpsSyncResponse>(`/settings/gps-connections/${provider}/sync`),
    matchReport: (provider: string) => apiClient.get<GpsMatchReportResponse>(`/settings/gps-connections/${provider}/match-report`),
  },

  lineItemPrefs: {
    get: () => apiClient.get('/line-item-prefs'),
    update: (hidden: string[]) => apiClient.put('/line-item-prefs', { hidden }),
  },

  /**
   * Structured pre-use forms per equipment type (api#1209). Cross-vertical, no
   * vertical guard. Reads camelCase, writes snake_case. Drafts are the only
   * editable state; publish / archive are admin-only (strict) on the API.
   */
  preuseTemplates: {
    list: (params?: { status?: string; equipment_type_id?: string }) =>
      apiClient.get<ApiEnvelope<PreuseTemplate[]>>('/preuse-templates', { params }),
    get: (id: string) => apiClient.get<ApiEnvelope<PreuseTemplateDetail>>(`/preuse-templates/${id}`),
    starters: () => apiClient.get<ApiEnvelope<PreuseStarter[]>>('/preuse-templates/starters'),
    /** The ACTIVE template for a unit's type, or `template: null`. Every internal role. */
    forEquipment: (equipmentId: string) =>
      apiClient.get<ApiEnvelope<PreuseForEquipment>>(`/preuse-templates/for-equipment/${equipmentId}`),
    create: (body: { equipment_type_id: string; name: string; description?: string | null; items?: PreuseItemWrite[] }) =>
      apiClient.post<ApiEnvelope<PreuseTemplateDetail>>('/preuse-templates', body),
    fromStarter: (body: { starter_key: string; equipment_type_id: string; name?: string }) =>
      apiClient.post<ApiEnvelope<PreuseTemplateDetail>>('/preuse-templates/from-starter', body),
    update: (id: string, body: { name?: string; description?: string | null; equipment_type_id?: string }) =>
      apiClient.patch<ApiEnvelope<PreuseTemplateDetail>>(`/preuse-templates/${id}`, body),
    /** Replace a draft's whole item list (the editor's save). */
    replaceItems: (id: string, items: PreuseItemWrite[]) =>
      apiClient.put<ApiEnvelope<PreuseTemplateDetail>>(`/preuse-templates/${id}/items`, { items }),
    duplicate: (id: string) => apiClient.post<ApiEnvelope<PreuseTemplateDetail>>(`/preuse-templates/${id}/duplicate`),
    publish: (id: string) =>
      apiClient.post<ApiEnvelope<PreuseTemplateDetail> & { meta?: { archivedTemplateId: string | null } }>(`/preuse-templates/${id}/publish`),
    archive: (id: string) => apiClient.post<ApiEnvelope<PreuseTemplateDetail>>(`/preuse-templates/${id}/archive`),
    /** Drafts only (soft delete). */
    remove: (id: string) => apiClient.delete<ApiEnvelope<{ id: string; deleted: boolean }>>(`/preuse-templates/${id}`),
  },

  /**
   * Pressure control — shared BOP certificates (api#1268, BOP-1). Cross-vertical,
   * no vertical guard. Reads camelCase, writes snake_case. Component, certificate
   * and event bodies are read through lib/pressure-control.ts, which tolerates
   * either casing; the types are hand-written from the route file (the manifest
   * records only refusals for the by-id routes).
   */
  pressureControl: {
    meta: () => apiClient.get<ApiEnvelope<PcMeta>>('/pressure-control/meta'),
    settings: () => apiClient.get<ApiEnvelope<PcSettings>>('/pressure-control/settings'),
    updateSettings: (body: PcSettingsWrite) => apiClient.put<ApiEnvelope<PcSettings>>('/pressure-control/settings', body),
    components: (params?: { component_class?: string; status?: string; q?: string; stack_parent_id?: string }) =>
      apiClient.get<PcEnvelope<PcComponent[]>>('/pressure-control/components', { params }),
    component: (equipmentId: string) => apiClient.get<PcEnvelope<PcComponentDetail>>(`/pressure-control/components/${equipmentId}`),
    /** Register an existing equipment row as a component. */
    createComponent: (body: PcComponentWrite) => apiClient.post<PcEnvelope<PcComponent>>('/pressure-control/components', body),
    updateComponent: (equipmentId: string, body: PcComponentWrite) =>
      apiClient.patch<PcEnvelope<PcComponent>>(`/pressure-control/components/${equipmentId}`, body),
    recordCertification: (equipmentId: string, body: PcCertificationWrite) =>
      apiClient.post<PcEnvelope<{ certification: PcCertification; component: PcComponent | null }>>(`/pressure-control/components/${equipmentId}/certifications`, body),
    recordEvent: (equipmentId: string, body: PcEventWrite) =>
      apiClient.post<PcEnvelope<{ event: PcServiceEvent; component: PcComponent | null }>>(`/pressure-control/components/${equipmentId}/events`, body),
    /** Any live equipment row: `data` is null when it is not a component. */
    equipmentStatus: (equipmentId: string) => apiClient.get<PcEnvelope<PcComponent | null>>(`/pressure-control/equipment/${equipmentId}/status`),

    // ── BOP-2 (api#1281, 0585). Records are append-only: no PUT/PATCH/DELETE. ──
    /** The module at a glance: components by status, gauges to calibrate, drill warnings. */
    status: () => apiClient.get<PcEnvelope<PcModuleStatus>>('/pressure-control/status'),
    calibrations: (params?: { equipment_id?: string; status?: string }) =>
      apiClient.get<ApiEnvelope<PcCalibration[]>>('/pressure-control/calibrations', { params }),
    /** admin / office_staff. */
    recordCalibration: (body: PcCalibrationWrite) => apiClient.post<ApiEnvelope<PcCalibration>>('/pressure-control/calibrations', body),
    gaugeCalibration: (equipmentId: string) =>
      apiClient.get<ApiEnvelope<PcGaugeCalibration>>(`/pressure-control/equipment/${equipmentId}/calibration`),
    tests: (params?: { equipment_id?: string; stack_equipment_id?: string; from?: string; to?: string; ids?: string }) =>
      apiClient.get<ApiEnvelope<PcTest[]>>('/pressure-control/tests', { params }),
    test: (id: string) => apiClient.get<ApiEnvelope<PcTest>>(`/pressure-control/tests/${id}`),
    /** admin / office_staff / supervisor. A 409 refuses an uncalibrated gauge or a component that may not be on a well. */
    recordTest: (body: PcTestWrite) =>
      apiClient.post<ApiEnvelope<PcTest> & { warnings?: Array<{ equipmentId: string; code: string; message: string }> }>('/pressure-control/tests', body),
    drills: (params?: { rig_equipment_id?: string; stack_equipment_id?: string; user_id?: string; from?: string; to?: string }) =>
      apiClient.get<ApiEnvelope<PcDrill[]>>('/pressure-control/drills', { params }),
    drill: (id: string) => apiClient.get<ApiEnvelope<PcDrill>>(`/pressure-control/drills/${id}`),
    /** Comma-separated ids. Always 200; a warning never blocks. */
    drillStatus: (params: { user_ids?: string; equipment_ids?: string }) =>
      apiClient.get<PcEnvelope<PcDrillStatus>>('/pressure-control/drills/status', { params }),
    /** admin / office_staff / supervisor. */
    recordDrill: (body: PcDrillWrite) =>
      apiClient.post<ApiEnvelope<{ drill: PcDrill; status: PcDrillStatus }>>('/pressure-control/drills', body),
    drillSettings: () => apiClient.get<ApiEnvelope<PcDrillSettings>>('/pressure-control/drill-settings'),
    updateDrillSettings: (body: { drill_warn_days: number }) =>
      apiClient.put<ApiEnvelope<PcDrillSettings>>('/pressure-control/drill-settings', body),
    testDefaults: () => apiClient.get<ApiEnvelope<PcTestDefault[]>>('/pressure-control/test-defaults'),
    /** Replaces the target's defaults (exactly one of component_class / equipment_id); a field left out is cleared. */
    putTestDefault: (body: PcTestDefaultWrite) => apiClient.put<ApiEnvelope<PcTestDefault>>('/pressure-control/test-defaults', body),
    deleteTestDefault: (id: string) => apiClient.delete<ApiEnvelope<{ id: string }>>(`/pressure-control/test-defaults/${id}`),
    /** The defaults that pre-fill a test of this component: its own, else its class's; null when neither. */
    componentTestDefaults: (equipmentId: string) =>
      apiClient.get<ApiEnvelope<PcTestDefault | null>>(`/pressure-control/components/${equipmentId}/test-defaults`),
    extensions: (equipmentId: string) =>
      apiClient.get<ApiEnvelope<PcExtension[]>>(`/pressure-control/components/${equipmentId}/extensions`),
    /** admin / office_staff. D036 Appendix 5: at most 30 days past the in-service expiry. */
    recordExtension: (equipmentId: string, body: PcExtensionWrite) =>
      apiClient.post<ApiEnvelope<{ extension: PcExtension; certState: Record<string, unknown> }>>(`/pressure-control/components/${equipmentId}/extensions`, body),

    // ── WL-0 (api#1333, 0610): certificate rules by component class. ──
    /** Every class, its rule if any, and the limits a new certificate would snapshot. */
    classRules: () => apiClient.get<ApiEnvelope<PcClassRuleRow[]>>('/pressure-control/class-rules'),
    /** admin / office_staff. Sets (or replaces) one class's limits; a 400 names the broken rule. */
    putClassRule: (componentClass: string, body: PcClassRuleWrite) =>
      apiClient.put<ApiEnvelope<PcClassRule>>(`/pressure-control/class-rules/${encodeURIComponent(componentClass)}`, body),
    /** admin / office_staff. The class falls back to the company settings; existing certificates keep their snapshot. */
    deleteClassRule: (componentClass: string) =>
      apiClient.delete<ApiEnvelope<{ componentClass: string; removed: boolean }>>(`/pressure-control/class-rules/${encodeURIComponent(componentClass)}`),
  },

  /**
   * The SHARED radiation module (fieldiq-api#1334, RAD-1). No vertical guard;
   * every route is role-gated (see lib/radiation.ts). Reads are camelCase with
   * a `meta` sibling carrying the tenant's units, zone and today; writes are
   * snake_case in the tenant's units. Bodies are read through lib/radiation.ts.
   */
  radiation: {
    catalog: () => apiClient.get<ApiEnvelope<unknown>>('/radiation/catalog'),
    settings: () => apiClient.get<ApiEnvelope<unknown>>('/radiation/settings'),
    /** admin / office_staff. Only the keys sent change. */
    updateSettings: (body: RadSettingsWrite) => apiClient.put<ApiEnvelope<unknown>>('/radiation/settings', body),
    status: () => apiClient.get<ApiEnvelope<unknown>>('/radiation/status'),
    sources: (params?: { status?: string; isotope?: string }) => apiClient.get<ApiEnvelope<unknown[]>>('/radiation/sources', { params }),
    /** The source plus its leakTests and events. */
    source: (id: string) => apiClient.get<ApiEnvelope<unknown>>(`/radiation/sources/${id}`),
    /** admin / office_staff. */
    createSource: (body: RadSourceWrite) => apiClient.post<ApiEnvelope<unknown>>('/radiation/sources', body),
    updateSource: (id: string, body: RadSourceWrite) => apiClient.patch<ApiEnvelope<unknown>>(`/radiation/sources/${id}`, body),
    /** Soft delete. 409 while checked out or lost in hole. */
    deleteSource: (id: string) => apiClient.delete<ApiEnvelope<unknown>>(`/radiation/sources/${id}`),
    /** Activity decayed to `on` (default: today in the tenant's zone). */
    decay: (id: string, on?: string) =>
      apiClient.get<ApiEnvelope<unknown>>(`/radiation/sources/${id}/decay`, on ? { params: { on } } : undefined),
    leakTests: (id: string) => apiClient.get<ApiEnvelope<unknown[]>>(`/radiation/sources/${id}/leak-tests`),
    /** admin / office_staff. A certificate is uploaded first against `body.id` (entityType radiation_leak_test). */
    recordLeakTest: (id: string, body: RadLeakTestWrite) =>
      apiClient.post<ApiEnvelope<unknown>>(`/radiation/sources/${id}/leak-tests`, body),
    events: (id: string) => apiClient.get<ApiEnvelope<unknown[]>>(`/radiation/sources/${id}/events`),
    /** admin / office_staff / supervisor / field_worker; closing events office + supervisor. */
    recordEvent: (id: string, body: RadEventWrite) =>
      apiClient.post<ApiEnvelope<unknown>>(`/radiation/sources/${id}/events`, body),
    /** An office role sees everyone's (optionally one user's); anyone else sees their own. */
    dosimetry: (params?: { userId?: string }) => apiClient.get<ApiEnvelope<unknown[]>>('/radiation/dosimetry', { params }),
    createDose: (body: RadDoseWrite) => apiClient.post<ApiEnvelope<unknown>>('/radiation/dosimetry', body),
    updateDose: (id: string, body: RadDoseWrite) => apiClient.patch<ApiEnvelope<unknown>>(`/radiation/dosimetry/${id}`, body),
    deleteDose: (id: string) => apiClient.delete<ApiEnvelope<unknown>>(`/radiation/dosimetry/${id}`),
  },

  equipmentTypes: {
    list: (includeInactive = false) =>
      apiClient.get('/equipment-types', includeInactive ? { params: { include_inactive: true } } : undefined),
    create: (data: { name: string }) => apiClient.post('/equipment-types', data),
    update: (id: string, data: EquipmentTypePayload) =>
      apiClient.patch(`/equipment-types/${id}`, data),
    delete: (id: string) => apiClient.delete(`/equipment-types/${id}`),
    /**
     * Service & inspection policies by type (API-9 / FE-5). Bodies snake_case,
     * responses camelCase. A row with equipment_id is a per-asset cadence override.
     */
    servicePolicies: {
      list: (typeId: string, equipmentId?: string) =>
        apiClient.get<ApiEnvelope<ServicePolicy[]>>(`/equipment-types/${typeId}/service-policies`, equipmentId ? { params: { equipment_id: equipmentId } } : undefined),
      create: (typeId: string, body: Record<string, unknown>) =>
        apiClient.post<ApiEnvelope<ServicePolicy>>(`/equipment-types/${typeId}/service-policies`, body),
      update: (typeId: string, policyId: string, body: Record<string, unknown>) =>
        apiClient.put<ApiEnvelope<ServicePolicy>>(`/equipment-types/${typeId}/service-policies/${policyId}`, body),
      remove: (typeId: string, policyId: string) =>
        apiClient.delete<ApiEnvelope<unknown>>(`/equipment-types/${typeId}/service-policies/${policyId}`),
      /** The effective cadence/intervals per asset, each with its source. */
      resolved: (equipmentIds: string[]) =>
        apiClient.get<ApiEnvelope<ResolvedAssetPolicy[]>>('/equipment-types/service-policies/resolved', { params: { equipment_ids: equipmentIds.join(',') } }),
    },
  },

  /**
   * Per-tenant service catalog — the labels offered on a job's Services
   * Required. `delete` soft-disables (standard types are never removable), and
   * `create` answers 409 on a duplicate name; call sites surface that inline
   * rather than as a generic failure.
   *
   * `triggersTorqueSheet` is settable on PATCH only (api #521) — the Needs PDF
   * queue on Dispatch keys off it. POST creates custom rows with it false.
   */
  serviceTypes: {
    /** Active rows by default. `includeInactive` is for a screen that must
     *  resolve a stored id to its row even after the tenant retired it. */
    list: (opts?: { includeInactive?: boolean }) =>
      apiClient.get('/service-types', opts?.includeInactive ? { params: { include_inactive: 'true' } } : undefined),
    create: (data: { name: string }) => apiClient.post('/service-types', data),
    update: (id: string, data: ServiceTypePayload) =>
      apiClient.patch(`/service-types/${id}`, data),
    delete: (id: string) => apiClient.delete(`/service-types/${id}`),
  },

  equipmentMaintenance: {
    list: (params?: Record<string, string>) => apiClient.get('/equipment-maintenance', { params }),
    get: (id: string) => apiClient.get(`/equipment-maintenance/${id}`),
    create: (data: any) => apiClient.post('/equipment-maintenance', data),
    update: (id: string, data: any) => apiClient.put(`/equipment-maintenance/${id}`, data),
    delete: (id: string) => apiClient.delete(`/equipment-maintenance/${id}`),
    summary: (params?: Record<string, string>) => apiClient.get('/equipment-maintenance/summary', { params }),
    recordPdf: (id: string) => apiClient.get(`/equipment-maintenance/${id}/pdf`, { responseType: 'blob' }),
    historyPdf: (params?: Record<string, string>) => apiClient.get('/equipment-maintenance/pdf', { params, responseType: 'blob' }),
    summaries: {
      list: (year: number) => apiClient.get('/equipment-maintenance/summaries', { params: { year } }),
      get: (id: string) => apiClient.get(`/equipment-maintenance/summaries/${id}`),
      generate: (data: { month: string; force?: boolean }) => apiClient.post('/equipment-maintenance/summaries/generate', data),
      updateStatus: (id: string, data: { status: 'draft' | 'reviewed' | 'published' }) => apiClient.patch(`/equipment-maintenance/summaries/${id}/status`, data),
    },
  },

  /**
   * Preventive maintenance schedules (per equipment unit).
   *
   * Note the path split: list/create/update/delete are nested under the unit,
   * but mark-performed and the bulk create are company-scoped and take the
   * schedule id (or a list of unit ids) directly.
   */
  equipmentMaintenanceSchedules: {
    list: (equipmentId: string) =>
      apiClient.get(`/equipment/${equipmentId}/maintenance-schedules`),
    create: (equipmentId: string, data: MaintenanceSchedulePayload) =>
      apiClient.post(`/equipment/${equipmentId}/maintenance-schedules`, data),
    update: (equipmentId: string, scheduleId: string, data: Partial<MaintenanceSchedulePayload>) =>
      apiClient.patch(`/equipment/${equipmentId}/maintenance-schedules/${scheduleId}`, data),
    delete: (equipmentId: string, scheduleId: string) =>
      apiClient.delete(`/equipment/${equipmentId}/maintenance-schedules/${scheduleId}`),
    markPerformed: (scheduleId: string, data: MarkPerformedPayload) =>
      apiClient.post(`/equipment/maintenance-schedules/${scheduleId}/mark-performed`, data),
    bulk: (data: BulkSchedulePayload) =>
      apiClient.post('/equipment/maintenance-schedules/bulk', data),
    due: (params?: Record<string, string>) =>
      apiClient.get('/equipment/maintenance-due', { params }),
  },

  /**
   * Worker expense receipts (a hand's own out-of-pocket on a job), distinct
   * from `assetExpenses` (a vehicle's) and `trucking.fleetExpenses`.
   *
   * Contract: fieldiq-api expenses.routes.ts (#514); see src/lib/expenses.ts.
   * Writes send snake_case like every other write here; reads go through the
   * parsers there.
   */
  /**
   * Scout, the Sales & CRM agent (api#1079, api#1080). Types: types/scout.ts.
   * brief/nudges are CRM_ROLES and the caller's own; settings, metrics, the
   * shadow report and labels are admin + office_staff; PUT settings is STRICT
   * admin. `from`/`to` are tenant-local days, YYYY-MM-DD.
   */
  /**
   * The tenant's AI budget and own Anthropic key (fieldiq-api 0583). The key is
   * write-only: only `key.last4` ever comes back.
   */
  aiSettings: {
    get: () => apiClient.get<ApiEnvelope<AiBudget>>('/ai-settings'),
    setKey: (apiKey: string) => apiClient.put<ApiEnvelope<AiBudget>>('/ai-settings/key', { api_key: apiKey }),
    removeKey: () => apiClient.delete<ApiEnvelope<AiBudget>>('/ai-settings/key'),
  },
  scout: {
    brief: () => apiClient.get<ApiEnvelope<ScoutBrief>>('/scout/brief'),
    settings: () => apiClient.get<ApiEnvelope<ScoutSettings>>('/scout/settings'),
    setMode: (mode: ScoutMode) => apiClient.put<ApiEnvelope<ScoutSettings>>('/scout/settings', { mode }),
    metrics: (params: { from?: string; to?: string } = {}) =>
      apiClient.get<ApiEnvelope<ScoutMetrics>>('/scout/metrics', { params }),
    shadowReport: (params: { from?: string; to?: string; status?: ScoutNudgeStatus } = {}) =>
      apiClient.get<ApiEnvelope<ScoutShadowReport>>('/scout/shadow-report', { params }),
    /** `null` clears a label. */
    label: (id: string, label: ScoutLabel | null) =>
      apiClient.post<ApiEnvelope<{ id: string; label: ScoutLabel | null }>>(`/scout/nudges/${id}/label`, { label }),
  },

  /**
   * Meetings (CRM-API-2). CRM_ROLES only; reads camelCase, writes snake_case.
   * The list is capped at 500 rows and unpaginated; it has no counts endpoint.
   */
  salesActivities: {
    list: (params?: SalesActivityListParams) =>
      apiClient.get<ApiEnvelope<SalesActivity[]>>('/sales-activities', params ? { params } : undefined),
    get: (id: string) => apiClient.get<ApiEnvelope<SalesActivityDetail>>(`/sales-activities/${id}`),
    create: (body: SalesActivityCreateBody) => apiClient.post<ApiEnvelope<SalesActivity>>('/sales-activities', body),
    update: (id: string, body: SalesActivityUpdateBody) =>
      apiClient.patch<ApiEnvelope<SalesActivity>>(`/sales-activities/${id}`, body),
    remove: (id: string) =>
      apiClient.delete<ApiEnvelope<{ id: string; deleted: boolean }>>(`/sales-activities/${id}`),
  },
  /** Opportunities (CRM-API-3). CRM_ROLES only; reads camelCase, writes snake_case. */
  opportunities: {
    list: (params?: OpportunityListParams) =>
      apiClient.get<ApiEnvelope<Opportunity[]>>('/opportunities', params ? { params } : undefined),
    /** Per-stage counts and known values; no grand total (operator, 2026-10-14). */
    pipeline: (params?: PipelineParams) =>
      apiClient.get<ApiEnvelope<Pipeline>>('/opportunities/pipeline', params ? { params } : undefined),
    get: (id: string) => apiClient.get<ApiEnvelope<OpportunityDetail>>(`/opportunities/${id}`),
    create: (body: OpportunityWriteBody) => apiClient.post<ApiEnvelope<OpportunityWriteResult>>('/opportunities', body),
    update: (id: string, body: OpportunityWriteBody) =>
      apiClient.patch<ApiEnvelope<OpportunityWriteResult>>(`/opportunities/${id}`, body),
    linkQuote: (id: string, quoteId: string) =>
      apiClient.post<ApiEnvelope<OpportunityLinkResult>>(`/opportunities/${id}/quotes/${quoteId}`),
    unlinkQuote: (id: string, quoteId: string) =>
      apiClient.delete<ApiEnvelope<OpportunityLinkResult>>(`/opportunities/${id}/quotes/${quoteId}`),
    linkJob: (id: string, jobId: string) =>
      apiClient.post<ApiEnvelope<OpportunityLinkResult>>(`/opportunities/${id}/jobs/${jobId}`),
    unlinkJob: (id: string, jobId: string) =>
      apiClient.delete<ApiEnvelope<OpportunityLinkResult>>(`/opportunities/${id}/jobs/${jobId}`),
    /** DELETE /opportunities/:id — owner or admin/office_staff; sales may not. Unlinks quotes/jobs/meetings. */
    remove: (id: string) => apiClient.delete<ApiEnvelope<{ id: string }>>(`/opportunities/${id}`),
    /**
     * Scout's pending stage moves (api#1174). Listing never moves anything;
     * approve moves the deal through the one write path, reject changes only
     * the run. 403 unless owner/admin/office_staff; 409 when already decided
     * or the deal moved since (the run is then superseded).
     */
    stageProposals: (params?: { opportunity_id?: string }) =>
      apiClient.get<ApiEnvelope<StageProposal[]>>('/opportunities/stage-proposals', params ? { params } : undefined),
    approveStageProposal: (runId: string) =>
      apiClient.post<ApiEnvelope<StageProposalDecision>>(`/opportunities/stage-proposals/${runId}/approve`),
    rejectStageProposal: (runId: string) =>
      apiClient.post<ApiEnvelope<StageProposalDecision>>(`/opportunities/stage-proposals/${runId}/reject`),
  },
  expenses: {
    /**
     * `paid_by` filters the two lists apart (0372) — 'personal' or 'company'.
     * ABSENT RETURNS BOTH, which is what every caller written before that
     * column asks for, so the key is omitted rather than sent empty; the API
     * 400s on a value outside the pair.
     */
    // `user_id` (0501) is honoured only for an office view role; the API narrows
    // everyone else to their own rows regardless. `expense_kind` / `customer_id`
    // filter the general expenses apart.
    list: (params?: {
      status?: string; ticket_id?: string; paid_by?: 'personal' | 'company';
      user_id?: string; expense_kind?: 'job' | 'general'; customer_id?: string;
    }) =>
      apiClient.get('/expenses', { params }),
    get: (id: string) => apiClient.get(`/expenses/${id}`),
    /**
     * APPROVED cost, net of recoverable GST, grouped by job (default), customer
     * or person (api#1023). Office view roles only — anyone else gets a 403,
     * which is a refusal to render as such, never as "no expenses".
     */
    summary: (params?: { group_by?: 'job' | 'customer' | 'user'; date_from?: string; date_to?: string; job_id?: string }) =>
      apiClient.get<ApiResponse<'GET /api/v1/expenses/summary'>>('/expenses/summary', { params }),
    create: (body: Record<string, unknown>) => apiClient.post('/expenses', body),
    /**
     * The monthly reimbursement report (api#1030). With `user_id`: one person's
     * month, every expense bucketed owed / reimbursed / payroll / company /
     * pending / rejected. Without: the office roll-up. Omit `month` to get the
     * TENANT's current month — the API decides it from companies.time_zone.
     */
    report: (params: { user_id?: string; month?: string }) =>
      apiClient.get<ExpenseReportResponse>('/expenses/report', { params }),
    /** The same report as a file — a Blob through the auth interceptor, never a bare link. */
    reportFile: (params: { user_id?: string; month?: string }, format: 'csv' | 'pdf') =>
      apiClient.get<Blob>('/expenses/report', { params: { ...params, format }, responseType: 'blob' }),
    /** Paid back outside payroll (api#1024). Report-path, approved rows only; 409 otherwise. */
    markReimbursed: (id: string) => apiClient.post<ExpenseReimbursedMarkResponse>(`/expenses/${id}/reimbursed`),
    /** Undo a mistaken mark. */
    unmarkReimbursed: (id: string) => apiClient.delete<ExpenseReimbursedMarkResponse>(`/expenses/${id}/reimbursed`),
    update: (id: string, body: Record<string, unknown>) => apiClient.patch(`/expenses/${id}`, body),
    /**
     * Approve, optionally setting or clearing the ticket the expense is on.
     *
     * The body is OPTIONAL and must stay that way on the wire: the API reads
     * `'ticket_id' in body`, so an absent key means "leave the ticket alone"
     * and `{ ticket_id: null }` means "clear it". Passing no argument sends no
     * body at all, which is what every pre-#537 caller did.
     */
    approve: (id: string, body?: { ticket_id: string | null }) =>
      body === undefined
        ? apiClient.post(`/expenses/${id}/approve`)
        : apiClient.post(`/expenses/${id}/approve`, body),
    reject: (id: string, body: { reason: string }) => apiClient.post(`/expenses/${id}/reject`, body),
    /**
     * OCR over the STORED photo — the one PhotoUploader already put in the
     * bucket — so the bytes the scan read are the bytes attached to the
     * expense. No second upload, nothing discarded after the scan.
     */
    ocr: (body: { receipt_upload_id: string } | { expense_id: string }) => apiClient.post('/expenses/ocr', body),
  },

  assetExpenses: {
    list: (params: Record<string, string>) => apiClient.get('/asset-expenses', { params }),
    create: (data: Record<string, any>) => apiClient.post('/asset-expenses', data),
    ocr: (formData: FormData) => apiClient.post('/asset-expenses/ocr', formData),
  },
};
